import {
  AlwaysDepth,
  AlwaysStencilFunc,
  EqualStencilFunc,
  KeepStencilOp,
  ReplaceStencilOp,
  ShaderMaterial,
  AmbientLight,
  Box3,
  BoxGeometry,
  Box3Helper,
  BufferGeometry,
  CatmullRomCurve3,
  CanvasTexture,
  Color,
  CircleGeometry,
  CylinderGeometry,
  DirectionalLight,
  DoubleSide,
  ExtrudeGeometry,
  Float32BufferAttribute,
  Group,
  HemisphereLight,
  LineBasicMaterial,
  LineSegments,
  LinearFilter,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  NeutralToneMapping,
  Object3D,
  Path,
  PCFShadowMap,
  PerspectiveCamera,
  Plane,
  PlaneGeometry,
  PMREMGenerator,
  PointLight,
  RepeatWrapping,
  Raycaster,
  Scene,
  Shape,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  TubeGeometry,
  Vector2,
  Vector3,
  Vector4,
  WebGLRenderer,
  WebGLRenderTarget
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { ScenePostProcessing } from "./ScenePostProcessing";
import { applyBoardTextureUvs, boardTextureOffset, decorDetailPixels, tileableDecorPixels } from "./surfaceAppearance";
import { decorTextureSize } from "./decorAppearance";
import {
  decorTextureUrl,
  resolveDecor,
  LEGACY_MATERIAL_DECOR
} from "../domain/decors";
import { frameBounds } from "./cameraFraming";
import { doorPolygonsOverlap, doorProgressAtAngle, stopDoorBeforeObstacle, type DoorObstacle } from "./doorMotion";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Reflector } from "three/examples/jsm/objects/Reflector.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { MATERIALS } from "../catalog";
import { cabinetPlinthFinish, plinthBackPlankMm } from "../domain/builtParts";
import { edgeFinishTarget } from "../domain/edgeFinish";
import { cornerPlan, cornerSide, cornerShelfPlan, cornerRailPlan, cornerPlinthPlan, CORNER_BACK_MM, cornerDoorLeaves, cornerDoorSpan, cornerFillerSpan, cornerBlindPanelSpan, cornerGapCoverSpan, cornerHingePostSpan, cornerNeighbourFillerSpan, cornerLegPoints, cornerMezzanineMm, cornerRun, cornerToPlan } from "../domain/corner";
import { activeSideMirrors, SIDE_MIRROR_THICKNESS_MM, sideMirrorSize, supportsSideMirror } from "../domain/sideMirrors";
import { facadePaintHex, facadeSupportsMilling, glassFrameWidthMm } from "../domain/facades";
import { chestTopEnamel, normalizeShelvingColorHex, shelvingCarcassColorHex } from "../domain/shelvingFinishes";
import {
  availableHangingHeight,
  fittingGarmentSequence,
  garmentFitsBelowRail,
  hangingSlots
} from "../domain/hanging";
import {
  internalItemMinimumY,
  resolveDividerMove,
  resolveInternalItemY,
  dragInternalItemY
} from "../domain/internalLayout";
import {
  cabinetBaseHeightMm,
  cabinetBaseSetbackMm,
  cabinetBaseType,
  installationSpanMm,
  leftFillerMm,
  rightFillerMm,
  topFillerMm
} from "../domain/installation";
import {
  applyRoomSize,
  BACK_WALL_LENGTH_MM,
  compositionJointsMm,
  createCompositionLayout,
  FULL_WALL_ROUTE_LENGTH_MM,
  materializeWallOffsets,
  isStackedModule,
  PERPENDICULAR_WALL_CLEARANCE_MM,
  ROOM_LAYOUT,
  cornerWorldPlan,
  cornerWorldFillerPlans,
  resolveModuleGroupWallOffset,
  type ModulePlacement,
  type WallSide
} from "../domain/layout";
import {
  baseModuleFor,
  ceilingHeightMm,
  doorLeafCount,
  cabinetDividerOffsets,
  hingedDoorLeafLayout,
  type HingedDoorLeafLayout,
  MAX_SLIDING_DOOR_WIDTH_MM,
  mezzanineFor,
  hasUnifiedFacade,
  hingeHeightRatios,
  mezzanineFlapLayout,
  type DoorHingeApplication
} from "../domain/moduleRules";
import { cellFrontLeaves, flapFront, flapHingeCount, FRONT_SPLIT_GAP_MM, type FlapFront, shelvingExcludedLifts } from "../domain/flapFronts";
import { moduleHeadroomMm, shelvingCellHeadroomMm } from "../domain/flapHeadroom";
import { CABINET_DRAWER_SEAM_MM, CABINET_DRAWERS_FINGER_PULL, cabinetDrawerCellId, cabinetDrawerFinish, cabinetDrawerLayout, type CabinetDrawerLayout } from "../domain/cabinetDrawers";
import {
  clampDividerX,
  interiorHorizontalRangeMm,
  interiorVerticalRangeMm,
  internalItemSpanMm,
  splitItemByCompartments,
  usableItemPieces,
  withStructuralPartitions
} from "../domain/compartments";
import { normalizeBackDecor } from "../domain/backPanel";
import { doorFacadeOverride, leafFinish, leafHandle } from "../domain/doorFacades";
import { drawerFrontFinish, normalizeShelfDecor } from "../domain/interiorDecor";
import { chestCellSplit, chestDrawerPaintFinish, chestPlinthEnamel, CHEST_TOP_FLAP_EXCLUDED_LIFTS, normalizeTopDecor, shelvingCellDrawerCountById } from "../domain/chest";
import {
  cabinetLightMode,
  cabinetLightPosition,
  cabinetLightTemperature,
  cabinetShelfLit,
  cabinetUprightStrips
} from "../domain/cabinetLighting";
import { fittingVerticalOffsets, SHOE_SHELF_LIP_MM, TROUSER_RACK_DROP_MM } from "../domain/wardrobeFittings";
import { CARCASS_MM, HALF_CARCASS_MM } from "../domain/carcass";
import {
  LED_PROFILE_EDGE_MM,
  LED_PROFILE_INSET_MM,
  LIGHT_TEMPERATURES,
  normalizeLightPosition,
  normalizeLightTemperature,
  shelvingCellLighting,
  shelvingCellLightTopClearMm,
  shelvingLightCompartments,
  shelvingLightZones,
  shelvingModuleLights,
  type LightTemperature
} from "../domain/lighting";
import { CellLightSystem, type CellLightLine } from "./cellLights";

/**
 * Подсветка считается в шейдере (см. cellLights.ts), единицы — как у света
 * комнаты: дневной ключевой свет даёт на полку около 1. Лента 0,42 на метр
 * даёт на дно проёма 350 мм примерно вдвое больше дневного света: днём она
 * заметна, но фактура светлого ЛДСП сохраняется (тонирование гасит пересвет
 * в белый, как у фотокамеры); вечером тот же свет — главный акцент.
 */
const LED_STRIP_INTENSITY = 0.42;
/** Мебельный точечный светильник: сила в центре луча. */
/**
 * Свет на пол: у кромки цоколя пол почти белый, в 10 см от неё заметно
 * светлее пола комнаты, к 40–50 см пятно растворяется.
 */
const LED_ACCENT_INTENSITY = 1.2;
const LED_ACCENT_KNEE = 8;
/**
 * Свет на стену. Лента в 25 мм от стены светит под 45°, как настоящая
 * подсветка 10–14 Вт/м за мебелью: у кромки стеллажа стена почти белая,
 * в 10–20 см от неё ярко светится, к 30 см ещё заметна, к полуметру ореол
 * растворяется. Высокое «колено» (cellLights) даёт стене у кромки уйти
 * в белый, но не выше порога ореола постобработки. Толщина светящейся
 * полосы — чтобы вышел ореол, а не оранжевая обводка по контуру.
 */
const LED_WALL_INTENSITY = 3;
const LED_WALL_KNEE = 10;
const LED_WALL_SOFTNESS = 0.03;
/** Доля переотражённого света в отсеке. */
const CELL_BOUNCE_SHARE = 0.1;
/**
 * Яркость рассеивателя: намного выше белого. После тонирования он белый с
 * оттенком света, а в постобработке вокруг него, как на фото, тёплый ореол.
 * Порог ореола (ScenePostProcessing) лежит между самой яркой освещённой
 * поверхностью (стена у кромки со светом на стену ≈ 2,5 + свет комнаты) и
 * этим значением, поэтому светится только лента, а не декор рядом с ней.
 */
const LED_DIFFUSER_BRIGHTNESS = 8;
/** Видимая губа рассеивателя под профилем, мм: тонкая полоска на экране терялась. */
const LED_DIFFUSER_LIP_MM = 3;
import {
  handleCenterFromOpeningEdgeMm,
  handleCenterYmm,
  handleLengthMm,
  integratedHandleStyle,
  integratedTopPartLengthMm,
  handleOrientation,
  handleProjectionMm
} from "../domain/handles";
import { clamp, maxHeight, snap } from "../domain/project";
import {
  getShelvingDiagonalSections,
  getShelvingGridLayout,
  gridColumnStart,
  gridDividerCenter,
  SHELVING_MAX_CLEAR_SPAN_MM,
  getShelvingCellBoards,
  getShelvingMainDividerSegment,
  parseShelvingCellId,
  shelvingLegXPositionsMm,
  shelvingSideBottomMm,
  shelvingSideHeightMm,
  shelvingExtraShelfHeight,
  shelvingExtraShelfRange,
  shelvingCellRect,
  shelvingCellShelfYs,
  SHELVING_EXTRA_SHELF_CLEARANCE_MM,
  withMovedShelvingShelf,
  withMovedShelvingDivider,
  shelvingCellDividerXs,
  shelvingExtraDividerRange,
  SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM,
  getShelvingSpanSections,
  getShelvingCellLayouts,
  shelvingCellId,
  shelvingCellBackPanelSizeMm,
  shelvingDrawerCount,
  drawerLimits,
  SHELVING_BACK_PANEL_THICKNESS_MM,
  shelvingLatticeBoards,
  SHELVING_LEG_HEIGHT_MM,
  SHELVING_MIN_CELL_HEIGHT_MM,
  SHELVING_MIN_CELL_WIDTH_MM,
  SHELVING_PANEL_THICKNESS_MM,
  parseShelvingGridBoardRef,
  shelvingGridBoardRef,
  shelvingCellFacadeRect,
  shelvingDrawerFacadeHeight,
  shelvingFacadeOverhangMm,
  shelvingTopOverSides,
  SHELVING_DRAWER_SEAM_MM,
  SHELVING_FACADE_THICKNESS_MM,
  SHELVING_FACADE_EDGE_MM,
  SHELVING_FACADE_SEAM_MM,
  SHELVING_INSET_GAP_MM,
  type ShelvingFacadeRect
} from "../domain/shelving";
import {
  planShelvingDecor,
  SHELVING_DECOR_ASSETS,
  type ShelvingDecorAsset,
  type ShelvingDecorAssetKey
} from "../domain/shelvingDecor";
import { SHELVING_SOUNDBAR, SHELVING_TV_MODELS, shelvingTvPlacement } from "../domain/shelvingTv";
import {
  endModulePlateOutlineMm,
  END_MODULE_BACK_MM,
  endModuleSide,
  endModuleTarget,
  endModuleTierCount
} from "../domain/endModules";
import type {
  FacadeColorType,
  FacadePatternType,
  HandleType,
  InternalItem,
  MaterialType,
  PlannerProject,
  ShelvingCellConfig,
  WardrobeModule
} from "../types";

const MM = 0.001;
const mm = (value: number) => value * MM;
/**
 * Размер «до стены» — на трети высоты модуля: на половине он шёл через
 * подпись «корпус …» у цепочки высот за краем ряда (приёмка 19.10).
 */
const WALL_GAP_LINE_SHARE = 0.3;

/**
 * Лицевая плоскость корпуса стеллажа: кромка стоек и полок выступает на 7 мм
 * перед торцом (addDetailPanel с z = 3 мм и глубиной 8 мм).
 */
const SHELVING_FRONT_PLANE = mm(7);
/** Центр накладного фасада: за ним кромка корпуса, между ними 0,5 мм. */
const SHELVING_OVERLAY_FACADE_Z = SHELVING_FRONT_PLANE + mm(0.5) + mm(SHELVING_FACADE_THICKNESS_MM) / 2;
/** Центр вкладного фасада: лицо вровень с кромкой корпуса. */
const SHELVING_INSET_FACADE_Z = SHELVING_FRONT_PLANE - mm(SHELVING_FACADE_THICKNESS_MM) / 2;
/**
 * У вкладной дверцы полки и перегородки ячейки отодвинуты от кромки:
 * дверца стоит в проёме и занимает эти 20 мм.
 */
const SHELVING_INSET_BOARD_RECESS = mm(SHELVING_FACADE_THICKNESS_MM + 2);

/**
 * Единый стиль размеров в 3D (шкафы, стеллажи, перетаскивание): линия 2 px
 * с засечками, число в миллиметрах на плашке. Два цвета по смыслу:
 * габариты и отступы — графит, внутренние размеры — фирменный терракотовый.
 */
const DIMENSION_STYLE = {
  outer: { line: 0x2f2a25, label: "#2f2a25" },
  inner: { line: 0xb0601a, label: "#b0601a" },
  lineWidth: 2
} as const;

/**
 * Предмет как 1–3 сферы вдоль длинной стороны: ваза — две сферы друг над
 * другом, ряд книг — несколько в ряд. Радиус — по средней стороне.
 */
function occluderSpheres(box: Box3): Vector4[] {
  const size = box.getSize(new Vector3());
  const center = box.getCenter(new Vector3());
  const axes = (["x", "y", "z"] as const).slice().sort((a, b) => size[b] - size[a]);
  const long = axes[0]!;
  const radius = Math.max(0.005, (size[axes[1]!] / 2) * 0.92);
  const count = Math.max(1, Math.min(3, Math.round(size[long] / (radius * 2))));
  return Array.from({ length: count }, (_, index) => {
    const point = center.clone();
    point[long] = box.min[long] + (size[long] * (index + 0.5)) / count;
    return new Vector4(point.x, point.y, point.z, count > 1 ? Math.max(radius, size[long] / count / 2 * 0.92) : radius);
  });
}
const PLINTH_HEIGHT = mm(72);
const ROOM_BASEBOARD_HEIGHT = mm(123);
const ROOM_BASEBOARD_PROJECTION = mm(36);
// 90°: открытая створка не заходит в проём, и выдвинутый ящик не проходит
// сквозь неё (приёмка 05.10, wf-02). При 80° край створки наклонялся внутрь.
const HINGED_DOOR_OPEN_ANGLE = (90 * Math.PI) / 180;
/** Рамка выделения изделия — на 4 мм снаружи корпуса (11.10). */
const SELECTION_FRAME_GAP = 0.004;
/** HK: ограничитель на 90°, чтобы не заходить в фасад следующего ряда (headroom = 0). */
const FLAP_UP_OPEN_ANGLE = Math.PI / 2;
const FLAP_DOWN_OPEN_ANGLE = (90 * Math.PI) / 180;
/** HF с ограничителем на 81°: складывается перед корпусом, без места над ним. */
const FLAP_FOLD_OPEN_ANGLE = (81 * Math.PI) / 180;
// Concealed hinges move the leaf clear of the adjacent half-overlay facade.
const HINGED_DOOR_LATERAL_TRAVEL = 0.011;
/** Камера считается движущейся ещё столько мс после последнего изменения. */
const CAMERA_SETTLE_MS = 180;
const DOOR_ANIMATION_DURATION = 0.32;
export const MIRROR_REFLECTION_LAYER = 2;
const MIRROR_BACKING_LAYER = 3;
const MIRROR_MAX_TEXTURE_DIMENSION = 2048;
const MIRROR_MAX_TEXTURE_PIXELS = 2_100_000;

const subtractIntervals = (
  start: number,
  end: number,
  removed: Array<{ start: number; end: number }>
) => {
  const epsilon = mm(0.05);
  const normalized = removed
    .map((interval) => ({
      start: Math.max(start, Math.min(end, interval.start)),
      end: Math.max(start, Math.min(end, interval.end))
    }))
    .filter((interval) => interval.end - interval.start > epsilon)
    .sort((first, second) => first.start - second.start);
  const remaining: Array<{ start: number; end: number }> = [];
  let cursor = start;
  normalized.forEach((interval) => {
    if (interval.start > cursor + epsilon) {
      remaining.push({ start: cursor, end: interval.start });
    }
    cursor = Math.max(cursor, interval.end);
  });
  if (cursor < end - epsilon) remaining.push({ start: cursor, end });
  return remaining;
};

type PanelMaterial = MeshStandardMaterial | MeshPhysicalMaterial | MeshBasicMaterial;

/** Чувствительность колеса и предел одного события (в «щелчках» по 100 px). */
export const CAMERA_ZOOM_SPEED = 1.52;
const CAMERA_ZOOM_MAX_NOTCHES = 1.2;
export const cameraZoomScale = (zoomSpeed: number, delta: number) =>
  Math.pow(0.95, zoomSpeed * Math.min(CAMERA_ZOOM_MAX_NOTCHES, Math.abs(delta * 0.01)));


interface PickMeta {
  kind:
    | "module"
    | "item"
    | "door"
    | "shelvingDivider"
    | "shelvingCellShelf"
    /** Перегородка внутри ячейки: тянется вбок (15.10). */
    | "shelvingCellDivider"
    | "shelvingCell";
  moduleId: string;
  itemId?: string;
  doorIndex?: number | string;
  cellId?: string;
  dividerAxis?: "column" | "row";
  dividerIndex?: number;
  dividerRow?: number;
  /** Колонка участка полки сетки (у стойки участок — dividerRow). */
  dividerColumn?: number;
  /** Доп. полка или перегородка ячейки: «shelf:main», «divider:0»… */
  boardRef?: string;
}

interface FacadePatternPoint {
  x: number;
  y: number;
}

interface FacadePatternBounds {
  left: number;
  right: number;
  bottom: number;
  top: number;
}

interface IntegratedHandleCut {
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
  gripOffsetX: number;
  gripOffsetY: number;
  gripWidth: number;
  gripHeight: number;
}

/**
 * Интегрированная ручка (приёмка 22.10, профиль — по видео Сергея 23.10): паз
 * под пальцы в торце фасада за гладкой передней губкой, лицевая сторона гладкая. edge — кромка створки (-1 левая, 1 правая по оси x
 * створки) или верхняя; from/to — участок кромки (у боковой — по высоте, у верхней —
 * по ширине от середины фасада), без них — вся кромка.
 */
interface IntegratedGroove {
  edge: -1 | 1 | "top";
  from?: number;
  to?: number;
}
/** Глубина паза интегрированной ручки в торец фасада, м. */
/** Высота полосы интегрированной ручки — 31 мм, как у профиля на чертеже. */
const INTEGRATED_GROOVE_WIDTH = 0.031;

interface PlannerSceneOptions {
  canInteractWithModule?: (moduleId: string) => boolean;
  onReady?: () => void;
  onError?: (issue: PlannerSceneIssue) => void;
  onAssetWarning?: (warning: PlannerSceneAssetWarning) => void;
  onDoorObstruction?: (message: string | null) => void;
  onSelectModule: (
    moduleId: string,
    source: "module" | "facade"
  ) => void;
  onClearSelection: () => void;
  /** Щелчок (не поворот камеры) по пустому месту 3D — закрыть выезжающую панель (15.10). */
  onEmptyClick?: () => void;
  onCommitModulePosition: (moduleId: string, wallOffset: number) => void;
  onCommitModuleUpdate: (
    moduleId: string,
    patch: Partial<WardrobeModule>
  ) => void;
  onCommitShelvingCellUpdate: (
    moduleId: string,
    cellId: string,
    patch: Partial<ShelvingCellConfig>
  ) => void;
  onHoverShelvingBoard?: (hovered: { moduleId: string; cellId: string; ref: string; touch?: boolean } | null) => void;
  /** Клик по стойке сетки без перетаскивания — открыть её поворот. */
  onClickShelvingUpright?: (moduleId: string, dividerIndex: number, row: number) => void;
  onSelectItem: (moduleId: string, itemId: string) => void;
  onSelectShelvingCell: (
    moduleId: string,
    cellId: string,
    /** cell — щёлкнули по проёму; facade — по фасаду или ящику; board — взялись за полку или стойку. */
    source: "cell" | "facade" | "board"
  ) => void;
  /** Деталь сдвинута только в своём отсеке (строка делится по отсекам). */
  onCommitItemPieceMove?: (moduleId: string, itemId: string, x: number, y: number) => void;
  onCommitItemPosition: (moduleId: string, itemId: string, y: number) => void;
  onCommitItemUpdate: (
    moduleId: string,
    itemId: string,
    patch: Partial<InternalItem>
  ) => void;
}

export interface PlannerSceneIssue {
  code: "context-lost" | "render-failed";
  message: string;
  technicalMessage?: string;
}

export interface PlannerSceneAssetWarning {
  code: "model-load-failed" | "texture-load-failed";
  message: string;
  resource: string;
  technicalMessage?: string;
}

interface ItemDragState {
  moduleId: string;
  itemId: string;
  startClientY: number;
  startClientX: number;
  startY: number;
  startX: number;
  currentY: number;
  lastDesiredY: number;
  currentX: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  object: Group;
  pointerId: number;
  type: InternalItem["type"];
  /**
   * Взялись за деталь в одном отсеке строки, которая стоит во всех отсеках:
   * двигается только она (центр отсека, мм), остальные остаются на месте.
   */
  pieceX?: number;
  /** Шкаф, где строка уже разделена по отсекам, — для упоров и размеров. */
  previewModule?: WardrobeModule;
  /** Высота, от которой отсчитывается положение перетаскиваемого объекта, мм. */
  positionOrigin: number;
  /**
   * Полки соседних изделий (шкаф, стеллаж рядом), мм от низа этого корпуса:
   * полка шкафа примагничивается к ним, как полки стеллажа (замечание № 18).
   */
  alignLevels: ShelfAlignLevel[];
  /** Выдвижная часть отсека, за которую взялись: щелчок выдвигает только её (16.10). */
  pullOutDoorIndex?: string;
  dragPlane: DragPlane | null;
}

interface ModuleDragState {
  moduleId: string;
  selectedModuleId: string;
  startClientX: number;
  startWallOffset: number;
  currentWallOffset: number;
  originalPosition: Vector3;
  originalRotationY: number;
  object: Group;
  pointerId: number;
  moved: boolean;
  sourcePlacement: ModulePlacement;
  attachedObjects: Array<{
    object: Group;
    originalPosition: Vector3;
    originalRotationY: number;
  }>;
}

interface ShelvingDividerDragState {
  moduleId: string;
  axis: "column" | "row";
  dividerIndex: number;
  startClientX: number;
  startClientY: number;
  startPosition: number;
  currentPosition: number;
  minPosition: number;
  maxPosition: number;
  cellSizes: number[];
  object: Object3D;
  pointerId: number;
  /** Для полки сетки: уровни доп. полок в ячейках, к ним она примагничивается. */
  alignLevels: ShelfAlignLevel[];
  dragPlane: DragPlane | null;
  /** Ряд участка стойки, за который взялись. */
  row?: number;
  /**
   * Стойка сетки: какие участки едут — ряд со своими ширинами двигается один,
   * остальные ряды — вместе (№ 19). Объекты и их начальные x.
   */
  movingSegments?: Array<{ object: Object3D; startX: number }>;
  /** Ряд со своей раскладкой: при отпускании меняются только его ширины. */
  customRow?: number;
  /** Колонка участка полки сетки, за который взялись. */
  column?: number;
}

/**
 * Плоскость перетаскивания: фасадная плоскость корпуса. Деталь идёт ровно за
 * пальцем или курсором — смещение берётся из точки на этой плоскости, а не
 * из пикселей с постоянным коэффициентом (замечание Сергея № 32).
 */
interface DragPlane {
  owner: Object3D;
  /** Где был указатель в начале, мм в координатах owner. */
  start: Vector3;
}

/** Уровень, к которому можно выровнять полку: центр полки и детали на нём. */
interface ShelfAlignLevel {
  y: number;
  targets: Array<
    | { kind: "grid"; index: number }
    /** Участок стойки сетки в одном ряду. */
    | { kind: "segment"; index: number; row: number }
    | { kind: "cell"; cellId: string; ref: string }
    /** Полка соседнего изделия: подсвечиваются сами эти объекты. */
    | { kind: "object"; objects: Object3D[] }
  >;
}

/** Насколько близко нужно подвести полку, чтобы она встала вровень с соседней, мм. */
const SHELF_ALIGN_SNAP_MM = 12;

interface ShelvingCellShelfDragState {
  /** Полка ездит вверх-вниз (нет поля), перегородка ячейки — вбок. */
  axis?: "x";
  moduleId: string;
  cellId: string;
  boardRef: string;
  /** Уровни соседних полок в проёме (мм от его низа) — для размеров при перетаскивании. */
  neighbours: number[];
  cellCenterX: number;
  /** Левый край проёма и ширина, мм: размерная линия идёт у левой стенки. */
  cellLeftX: number;
  cellWidth: number;
  /** Уровни полок соседних ячеек и сетки, мм от низа стеллажа: к ним полка примагничивается. */
  alignLevels: ShelfAlignLevel[];
  dragPlane: DragPlane | null;
  startClientX: number;
  startClientY: number;
  cellStartY: number;
  cellHeight: number;
  startPosition: number;
  currentPosition: number;
  minPosition: number;
  maxPosition: number;
  object: Object3D;
  pointerId: number;
}

interface DoorClickState {
  moduleId: string;
  doorIndex: number | string;
  startClientX: number;
  startClientY: number;
  pointerId: number;
  moved: boolean;
}

/** Тяга петли: ось у плеча (в корпусе) и точка у чашки (в створке). */
interface HingeLinkRuntime {
  pivot: Group;
  /** Растяжение вдоль тяги: расстояние до чашки / в закрытом положении. */
  stretch: Group;
  length: number;
  /** Ось у плеча, координаты корпуса. */
  armPin: Vector3;
  /** Точка у чашки в координатах детали створки (doorPart). */
  cupPin: Vector3;
  doorPart: Object3D;
  /** Направление тяги в закрытом положении (atan2(x, z)). */
  base: number;
}

interface DoorRuntime {
  key: string;
  /** flap — откидной фасад: вверх (side −1) или вниз (side 1) вокруг горизонтальной оси. */
  kind: "hinged" | "sliding" | "drawer" | "flap";
  moduleId: string;
  doorIndex: number | string;
  object: Group;
  side: number;
  width?: number;
  maxAngle?: number;
  closedX?: number;
  openShift?: number;
  closedZ?: number;
  openShiftZ?: number;
  minY?: number;
  maxY?: number;
  /** Concealed hinge hardware is visible only after the facade starts opening. */
  hardwareObjects?: Object3D[];
  /** Тяги петель-моделей: поворачиваются от плеча к чашке по ходу двери. */
  hingeLinks?: HingeLinkRuntime[];
  /**
   * Ручка с выносом (29.10): середина — на along м от кромки с петлями, half —
   * полудлина вдоль двери, от лица фасада (face — половина толщины) на projection.
   */
  handle?: { along: number; half: number; face: number; projection: number };
  /** Откидной фасад: положение по ходу открывания, складная часть HF и рычаги механизма. */
  flap?: FlapRuntime;
}

/** Положение оси откидного фасада (у верхней или нижней кромки) по ходу открывания. */
interface FlapPose {
  y: number;
  z: number;
  /** Поворот фасада вокруг горизонтальной оси. */
  rx: number;
  /** Поворот нижней части складного HF относительно верхней. */
  fold: number;
}

/** Рычаг механизма из двух звеньев: от корпуса подъёмника на боковине до фасада. */
interface FlapArm {
  x: number;
  /** Ось на корпусе механизма: [y, z] в координатах корпуса. */
  fixed: [number, number];
  /** Точка крепления на фасаде: [y, z] в координатах оси фасада (или складной части). */
  attach: [number, number];
  /** Крепление на нижней части складного HF: [y, z] её оси в координатах фасада. */
  foldOrigin?: [number, number];
  /** Длина каждого звена. */
  length: number;
  /** С какой стороны линии «ось — крепление» сгиб. */
  kneeSign: 1 | -1;
  /**
   * Прямая тяга: звенья лежат на одной линии от корпуса механизма до фасада.
   * У HL длина постоянна на основной дуге, остальные тяги телескопические.
   * Складное «колено» оставлено только у откидного вниз (fw-09).
   */
  straight?: boolean;
  links: [Group, Group];
}

interface FlapRuntime {
  /** Как выглядит открытый фасад — для описания визуализации. */
  openText: string;
  pose: (eased: number) => FlapPose;
  fold?: Group;
  arms: FlapArm[];
}

interface PlanPoint {
  x: number;
  z: number;
}

/** Точка крепления рычага на фасаде в координатах корпуса: [y, z]. */
const flapPoint = (pose: FlapPose, arm: Pick<FlapArm, "attach" | "foldOrigin">): [number, number] => {
  const rotate = (y: number, z: number, angle: number): [number, number] =>
    [y * Math.cos(angle) - z * Math.sin(angle), y * Math.sin(angle) + z * Math.cos(angle)];
  let [y, z] = arm.attach;
  if (arm.foldOrigin) {
    [y, z] = rotate(y, z, pose.fold);
    y += arm.foldOrigin[0];
    z += arm.foldOrigin[1];
  }
  [y, z] = rotate(y, z, pose.rx);
  return [y + pose.y, z + pose.z];
};

/** Звено рычага от точки до точки в плоскости YZ. */
const placeFlapLink = (link: Group, x: number, fromY: number, fromZ: number, toY: number, toZ: number) => {
  link.position.set(x, fromY, fromZ);
  link.rotation.x = Math.atan2(toZ - fromZ, toY - fromY);
  const length = Math.max(1e-4, Math.hypot(toY - fromY, toZ - fromZ));
  const bar = link.getObjectByName("flap-link-bar");
  if (bar) {
    // Масштабируем только длину стали: толщина и шарниры не растягиваются.
    bar.position.y = length / 2;
    bar.scale.y = Math.max(1e-4, length - mm(4));
    const end = link.getObjectByName("flap-link-end");
    if (end) end.position.y = length;
    link.scale.y = 1;
  } else link.scale.y = length;
};

const SHOW_SUN_PATCH = false;
/** Проставки 20 + 20 мм и утопление 22 мм для выдвижного наполнения за распашными дверями. */
const PULL_OUT_SPACERS = 0.04;
const PULL_OUT_RECESS = 0.022;
/** Внутреннее наполнение, которое выдвигается щелчком. */
const PULL_OUT_TYPES = new Set<InternalItem["type"]>(["drawer", "basket", "leatherBasket", "trouserRack"]);

const CLOTHING_PACK_REVISION = "20260728-2";
const clothingPackUrl = (file: string) =>
  `/models/clothing-pack/${file}?v=${CLOTHING_PACK_REVISION}`;
const DECOR_PACK_REVISION = "20260925-1";
const decorPackUrl = (file: string) =>
  `/models/decor-pack/${file}?v=${DECOR_PACK_REVISION}`;

/**
 * Петли по моделям Намика (29.10): накладная, полунакладная и вкладная с
 * ответной планкой, упрощены до ~1,8 тыс. треугольников на деталь
 * (tools/prepareHingeModels.mjs). У каждой две детали: «fixed» — планка,
 * клипса и плечо, «door» — чашка и тяги.
 */
const HINGE_MODEL_URL = "/models/hardware/hinges.glb?v=20261029-1";
type HingeModelType = "full-overlay" | "half-overlay" | "inset";
interface HingeModel {
  fixed: BufferGeometry;
  door: BufferGeometry;
  links?: BufferGeometry;
  /** Концы тяг в координатах модели: у плеча и у чашки. */
  armPin?: Vector3;
  cupPin?: Vector3;
  /** Середина чашки (по дну), координаты модели. */
  cupCenter?: Vector3;
}
const HINGE_MODEL_NAMES: Record<string, HingeModelType> = { overlay: "full-overlay", half: "half-overlay", inset: "inset" };
/** В модели задняя плоскость двери — на 36,5 мм перед планкой (ноль по Z — середина планки). */

const HINGE_MODEL_DOOR_BACK_Z = 0.0365;
/** Гнездо под чашку в двери: Ø35, глубина 15 мм (чашка — 14,8). */
const HINGE_CUP_RADIUS = 0.0175;
const HINGE_CUP_POCKET_DEPTH = 0.015;
const HINGE_CUP_OPENING_GEOMETRY = new CircleGeometry(HINGE_CUP_RADIUS, 40);
/** Стенки гнезда — изнутри (видно, глядя в отверстие); ось вдоль Z модели. */
const HINGE_CUP_POCKET_WALL_GEOMETRY = new CylinderGeometry(HINGE_CUP_RADIUS, HINGE_CUP_RADIUS, HINGE_CUP_POCKET_DEPTH, 40, 1, true)
  .rotateX(Math.PI / 2);
/**
 * Порядок отрисовки гнезда: сначала дверь, затем метка отверстия (трафарет),
 * затем в отверстии сбрасывается глубина, и в нём видны чашка, тяги и стенки
 * гнезда — дверь как будто высверлена под чашку.
 */
const HINGE_HOLE_MASK_ORDER = 20;
const HINGE_HOLE_RESET_ORDER = 21;
const HINGE_HOLE_CONTENT_ORDER = 22;
/** Геометрия петель из сцены GLB: по узлам «тип-деталь», в метрах модели. */
export const extractHingeModels = (root: Object3D) => {
  root.updateMatrixWorld(true);
  const models: Partial<Record<HingeModelType, HingeModel>> = {};
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const [name, part] = object.name.split("-");
    const type = HINGE_MODEL_NAMES[name ?? ""];
    if (!type || (part !== "fixed" && part !== "door" && part !== "links")) return;
    // Координаты в GLB целые (KHR_mesh_quantization) с масштабом в узле —
    // переводим в метры, чтобы ставить петли без оглядки на узел.
    const source = object.geometry as BufferGeometry;
    const geometry = new BufferGeometry();
    for (const attribute of ["position", "normal"] as const) {
      const values = source.getAttribute(attribute);
      if (!values) continue;
      const array = new Float32Array(values.count * 3);
      for (let index = 0; index < values.count; index += 1) {
        array[index * 3] = values.getX(index);
        array[index * 3 + 1] = values.getY(index);
        array[index * 3 + 2] = values.getZ(index);
      }
      geometry.setAttribute(attribute, new Float32BufferAttribute(array, 3));
    }
    if (source.index) geometry.setIndex(source.index.clone());
    geometry.applyMatrix4(object.matrixWorld);
    if (!geometry.getAttribute("normal")) geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    const extras = object.userData as { armPin?: number[]; cupPin?: number[]; cupCenter?: number[] };
    models[type] = {
      ...models[type],
      [part]: geometry,
      ...(part === "links" && extras.armPin && extras.cupPin ? { armPin: new Vector3(...extras.armPin), cupPin: new Vector3(...extras.cupPin) } : {}),
      ...(part === "door" && extras.cupCenter ? { cupCenter: new Vector3(...extras.cupCenter) } : {})
    } as HingeModel;
  });
  for (const [type, model] of Object.entries(models)) if (!model?.fixed || !model.door) delete models[type as HingeModelType];
  return models;
};

const DECOR_PACK_2_REVISION = "20260927-1";
const decorPack2Url = (file: string) =>
  `/models/decor-pack-2/${file}?v=${DECOR_PACK_2_REVISION}`;

const SHELVING_DECORATIVE_MODEL_URLS = Object.fromEntries(
  (Object.keys(SHELVING_DECOR_ASSETS) as ShelvingDecorAssetKey[]).map((key) => {
    const asset: ShelvingDecorAsset = SHELVING_DECOR_ASSETS[key];
    return [key, asset.pack === "decor-pack-2" ? decorPack2Url(asset.file) : decorPackUrl(asset.file)];
  })
) as { [Key in ShelvingDecorAssetKey]: string };

const DECORATIVE_MODEL_URLS = {
  foldedKnit: clothingPackUrl("folded-knit.glb"),
  foldedKnitLight: clothingPackUrl("folded-knit-light.glb"),
  foldedStack: clothingPackUrl("folded-stack.glb"),
  foldedStackWarm: clothingPackUrl("folded-stack-warm.glb"),
  rattanBasket: clothingPackUrl("rattan-basket.glb"),
  fabricBox: clothingPackUrl("fabric-box.glb"),
  toteBag: clothingPackUrl("tote-bag.glb"),
  hat: clothingPackUrl("hat.glb"),
  slippers: clothingPackUrl("slippers.glb"),
  hangingShirt: clothingPackUrl("hanging-shirt.glb"),
  hangingShirtBlue: clothingPackUrl("hanging-shirt-blue.glb"),
  hangingDress: clothingPackUrl("hanging-dress.glb"),
  hangingBlouse: clothingPackUrl("hanging-blouse.glb"),
  hangingVest: clothingPackUrl("hanging-vest.glb"),
  ...SHELVING_DECORATIVE_MODEL_URLS,
  // Телевизор и саундбар грузятся, только когда их ставят на изделие.
  tvStand: decorPack2Url(SHELVING_TV_MODELS.stand.file),
  tvWall: decorPack2Url(SHELVING_TV_MODELS.wall.file),
  soundbar: decorPack2Url(SHELVING_SOUNDBAR.file)
} as const;

type DecorativeModelKey = keyof typeof DECORATIVE_MODEL_URLS;
type ShelfDecorativeModelKey =
  | "foldedKnit"
  | "foldedKnitLight"
  | "foldedStack"
  | "foldedStackWarm"
  | "rattanBasket"
  | "fabricBox"
  | "toteBag"
  | "hat";
type HangingDecorativeModelKey =
  | "hangingShirt"
  | "hangingShirtBlue"
  | "hangingDress"
  | "hangingBlouse"
  | "hangingVest";

const HANGING_DECORATIVE_MODEL_KEYS: HangingDecorativeModelKey[] = [
  "hangingShirt",
  "hangingShirtBlue",
  "hangingDress",
  "hangingBlouse",
  "hangingVest"
];

const SHELF_DECORATIVE_MODEL_KEYS: ShelfDecorativeModelKey[] = [
  "foldedKnit",
  "foldedKnitLight",
  "foldedStack",
  "foldedStackWarm",
  "rattanBasket",
  "fabricBox",
  "toteBag",
  "hat"
];

const SHELVING_DECORATIVE_MODEL_KEYS = Object.keys(
  SHELVING_DECOR_ASSETS
) as ShelvingDecorAssetKey[];

const SHELF_DECORATIVE_DIMENSIONS_MM: Record<
  ShelfDecorativeModelKey,
  [number, number, number]
> = {
  foldedKnit: [414, 83, 287],
  foldedKnitLight: [421, 51, 310],
  foldedStack: [367, 146, 303],
  foldedStackWarm: [385, 127, 318],
  rattanBasket: [271, 200, 264],
  fabricBox: [311, 248, 220],
  toteBag: [374, 281, 197],
  hat: [384, 121, 390]
};

const HANGING_DECORATIVE_DIMENSIONS_MM: Record<
  HangingDecorativeModelKey,
  [number, number, number]
> = {
  hangingShirt: [266, 873, 592],
  hangingShirtBlue: [246, 876, 531],
  hangingDress: [87, 979, 333],
  hangingBlouse: [95, 791, 500],
  hangingVest: [74, 685, 391]
};

const GARMENT_MODEL_TOP_DROP_MM = 64;

const GARMENT_TINT_PALETTE = [
  "#e3ddd2",
  "#c9b49e",
  "#8a8178",
  "#788275",
  "#536879",
  "#424445",
  "#aa8175",
  "#d1d0ca"
] as const;

const HANGING_DECORATIVE_VARIANTS: Record<
  NonNullable<InternalItem["clothes"]>,
  HangingDecorativeModelKey[]
> = {
  shirts: ["hangingShirt", "hangingShirtBlue", "hangingBlouse"],
  jackets: ["hangingVest", "hangingShirtBlue"],
  shortDress: ["hangingDress", "hangingBlouse"],
  longDress: ["hangingDress"],
  coats: ["hangingDress", "hangingVest"]
};

interface HangingGarmentPlan {
  slotX: number;
  garmentType: NonNullable<InternalItem["clothes"]>;
  modelKey: HangingDecorativeModelKey;
  dimensionsMm: [number, number, number];
  requiredHeightMm: number;
  index: number;
  count: number;
}

interface ShelfDecorationPlan {
  modelKey: ShelfDecorativeModelKey;
  centerX: number;
  dimensionsMm: [number, number, number];
  index: number;
}

const isShared = (object: { userData: Record<string, unknown> }) =>
  object.userData.shared === true;

export class PlannerScene {
  private readonly container: HTMLElement;
  private readonly options: PlannerSceneOptions;
  private readonly renderer: WebGLRenderer;
  private postProcessing: ScenePostProcessing | null = null;
  private presentationMode = false;
  private dimensionsVisible = true;
  private readonly scene = new Scene();
  private readonly camera: PerspectiveCamera;
  private readonly controls: OrbitControls;
  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  private readonly mirrorDrawingBufferSize = new Vector2();
  private readonly cameraDirection = new Vector3();
  private readonly cameraConstraintOffset = new Vector3();
  private readonly desiredCameraPosition = new Vector3();
  private readonly cameraCollisionCandidate = new Vector3();
  private readonly labelWorldPosition = new Vector3();
  private readonly labelCameraOffset = new Vector3();
  private readonly textureLoader = new TextureLoader();
  private readonly decorativeModelLoader = new GLTFLoader();
  private readonly decorativeModels = new Map<DecorativeModelKey, Group>();
  private readonly decorativeModelRequests = new Map<
    DecorativeModelKey,
    Promise<boolean>
  >();
  private readonly decorativeModelFailures = new Set<DecorativeModelKey>();
  /** Геометрия петель из GLB; до загрузки (и в тестах) — петли из примитивов. */
  private hingeModels: Partial<Record<HingeModelType, HingeModel>> | null = null;
  private hingeModelRequested = false;
  private readonly roomRoot = new Group();
  private readonly modelRoot = new Group();
  private readonly dimensionRoot = new Group();
  private readonly overlayRoot = new Group();
  private readonly moduleObjects = new Map<string, Group>();
  private readonly itemObjects = new Map<string, Group>();
  private readonly shelvingCellObjects = new Map<string, Object3D>();
  private readonly cameraCollisionBoxes: Box3[] = [];
  private readonly materialCache = new Map<string, PanelMaterial>();
  /** Цвет петель изделия, которое сейчас строится (видео Сергея 23.10): серебристые или чёрные. */
  private hingeTone: "silver" | "black" = "silver";
  /**
   * Кромка корпуса изделия, которое сейчас строится (видео Сергея 24.10): у
   * плит корпуса в этом декоре торцы — в цвет фасадов или своего декора.
   */
  private edgeOverride: { code: string; key: string; edge: MeshPhysicalMaterial } | null = null;
  private readonly lightingMaterials = new Map<string, MeshBasicMaterial>();
  /** Подсветка ячеек, пола и стены — считается в шейдере материалов. */
  private readonly cellLights = new CellLightSystem();
  private facadePatternExclusion: FacadePatternBounds | null = null;
  private readonly environmentTexture: Texture;
  private readonly environmentRenderTarget: WebGLRenderTarget;
  private readonly resizeObserver: ResizeObserver;
  private project: PlannerProject | null = null;
  private modelSignature = "";
  private roomSignature = "";
  private layoutBoundsSignature = "";
  private selectionHelper: Box3Helper | LineSegments | null = null;
  /** Перетаскивания, которые уже сдвинулись дальше «мёртвой зоны» (см. dragNotStarted). */
  private readonly startedDrags = new WeakSet<object>();
  private selectionTarget: Object3D | null = null;
  /**
   * Деталь наполнения под курсором или та, которую держат (видео Сергея 31.10):
   * оранжевая рамка с полупрозрачной заливкой — видно, что именно возьмёшь.
   */
  private itemHover: { itemId: string; target: Object3D; helper: Box3Helper; fill: Mesh } | null = null;
  private boardHoverHelpers: Box3Helper[] = [];
  private boardHoverBoxes: Array<() => Box3 | null> = [];
  private hoveredBoardState: { moduleId: string; cellId: string; ref: string; touch?: boolean } | null = null;
  /** Ручка-стрелка под пальцем на выделенной касанием детали. */
  private touchHandle: Sprite | null = null;
  private readonly touchHandleTextures = new Map<"vertical" | "horizontal", CanvasTexture>();
  /** Размеры до соседних деталей, пока тянут полку или стойку. */
  private readonly dragMeasureRoot = new Group();
  private animationFrame = 0;
  private decorativeRefreshTimer: number | null = null;
  private hangingModelsReady = false;
  /** Предметы интерьера (книги, вазы, одежда, телевизор): кнопка «Предметы» их прячет. */
  private interiorPropsVisible = true;
  private shelfModelsReady = false;
  private shelvingDecorReady = false;
  private shelvingDecorRequested = false;
  private settleFrames = 0;
  private itemDrag: ItemDragState | null = null;
  private moduleDrag: ModuleDragState | null = null;
  private shelvingDividerDrag: ShelvingDividerDragState | null = null;
  private shelvingCellShelfDrag: ShelvingCellShelfDragState | null = null;
  private doorClick: DoorClickState | null = null;
  private readonly openDoors = new Set<string>();
  private readonly doorProgress = new Map<string, number>();
  private readonly doorObjects = new Map<string, DoorRuntime>();
  private readonly stoppedDoors = new Map<string, { opening: boolean; obstacle: DoorObstacle }>();
  private lastDoorAnimationTime = 0;
  /** Коробки мебели перестроены: камера могла оказаться внутри новой. */
  private cameraBoxesChanged = false;
  private preferredCameraDistance = 0;
  private roomDistanceClamped = false;
  private roomCutawaySuspended = false;
  /** Потолок и светильники на нём: прячутся, когда камера поднялась выше потолка. */
  private ceilingParts: Object3D[] = [];
  /** Стены с плинтусами и карнизами: прячется та, за которую камера вышла поверху. */
  private wallParts: Record<"back" | "left" | "right", Object3D[]> = { back: [], left: [], right: [] };
  private cameraWheelIntent = false;
  private disposed = false;
  private projectReady = false;
  private readyNotified = false;
  private lastReportedError = "";

  constructor(container: HTMLElement, options: PlannerSceneOptions) {
    this.container = container;
    this.options = options;
    this.requestHingeModels();

    this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: true,
      // Трафарет — для гнёзд под чашки петель в дверях (29.10).
      stencil: true,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true
    });
    // A small supersampling floor noticeably cleans long cabinet edges on
    // standard-density monitors without the cost of rendering everything at 2x.
    const pixelRatio = Math.min(
      Math.max(window.devicePixelRatio || 1, 1.25),
      2
    );
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = NeutralToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    // Если шейдер с подсветкой ячеек не собрался на этой видеокарте —
    // выключаем подсветку, а не всю сцену.
    this.renderer.debug.onShaderError = (gl, program, vertexShader, fragmentShader) => {
      const log = [gl.getProgramInfoLog(program), gl.getShaderInfoLog(vertexShader), gl.getShaderInfoLog(fragmentShader)]
        .filter(Boolean).join("\n");
      console.error("THREE.WebGLProgram: shader error", log);
      if (!this.cellLights.isDisabled && gl.getShaderSource(fragmentShader)?.includes("egelCellLights")) {
        console.warn("Подсветка ячеек отключена: видеокарта не собрала шейдер");
        this.cellLights.disable();
        this.invalidate(4);
      }
    };
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFShadowMap;
    this.renderer.domElement.className = "planner-canvas";
    this.container.appendChild(this.renderer.domElement);

    const environmentSource = new RoomEnvironment();
    const pmremGenerator = new PMREMGenerator(this.renderer);
    this.environmentRenderTarget =
      pmremGenerator.fromScene(environmentSource, 0.04);
    this.environmentTexture = this.environmentRenderTarget.texture;
    environmentSource.dispose();
    pmremGenerator.dispose();
    this.scene.environment = this.environmentTexture;

    // 4 cm is still close enough for cabinet inspection, while the healthier
    // near/far ratio preserves depth precision for millimetre-thin facade
    // layers when the camera is pulled several metres out of the room.
    this.camera = new PerspectiveCamera(34, 1, 0.04, 60);
    this.camera.position.set(3.25, 2.2, 4.25);
    this.camera.layers.enable(MIRROR_BACKING_LAYER);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 1.08, 0.1);
    this.controls.enableDamping = false;
    this.controls.minDistance = 0.28;
    this.controls.maxDistance = 16;
    this.controls.minPolarAngle = 0.38;
    // A little below the horizon, so a mezzanine near the ceiling can be
    // inspected from underneath the way a person standing in the room sees it.
    this.controls.maxPolarAngle = Math.PI * 0.56;
    // Panning follows the screen (right-drag up raises the view). The wheel
    // zooms in towards the pointer; see handleCameraWheelIntent for zoom-out.
    this.controls.screenSpacePanning = true;
    this.controls.zoomToCursor = false;
    // Шаг колеса — 7,5 % расстояния (было 11 %: один щелчок «слишком
    // близко приближал»; 5,5 % — «слишком медленно», приёмка 08.10). Колёса с высоким
    // разрешением и ускорением шлют за щелчок deltaY 200–500 — считаем
    // такое событие не больше чем за один щелчок.
    this.controls.zoomSpeed = CAMERA_ZOOM_SPEED;
    (this.controls as unknown as { _getZoomScale: (delta: number) => number })._getZoomScale =
      (delta: number) => cameraZoomScale(this.controls.zoomSpeed, delta);
    this.preferredCameraDistance = this.controls.getDistance();
    this.controls.addEventListener("change", this.handleControlsChange);
    this.controls.addEventListener("start", this.handleControlsStart);

    this.scene.add(
      this.roomRoot,
      this.modelRoot,
      this.dimensionRoot,
      this.overlayRoot
    );
    this.scene.background = new Color("#f2eee7");

    if (this.renderer.extensions.has("EXT_color_buffer_float")) {
      this.postProcessing = new ScenePostProcessing(this.renderer, this.scene, this.camera);
    }

    this.renderer.domElement.addEventListener("pointerdown", this.handlePointerDown);
    this.renderer.domElement.addEventListener("pointermove", this.handlePointerMove);
    this.renderer.domElement.addEventListener("pointerup", this.handlePointerUp);
    this.renderer.domElement.addEventListener("pointercancel", this.handlePointerUp);
    this.renderer.domElement.addEventListener("pointerleave", this.handlePointerLeave);
    this.renderer.domElement.addEventListener(
      "wheel",
      this.handleCameraWheelIntent,
      { capture: true, passive: true }
    );
    this.renderer.domElement.addEventListener(
      "pointerdown",
      this.handleZoomGestureStart,
      { capture: true }
    );
    this.renderer.domElement.addEventListener(
      "webglcontextlost",
      this.handleContextLost
    );
    this.renderer.domElement.addEventListener(
      "webglcontextrestored",
      this.handleContextRestored
    );

    this.resizeObserver = new ResizeObserver(this.resize);
    this.resizeObserver.observe(this.container);
    this.resize();
    this.loadDecorativeModels();
    // Только для проверки в разработке: доступ к камере из автотестов браузера.
    if ((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV) (window as unknown as { __egelScene?: unknown }).__egelScene = this;
  }

  setProject(project: PlannerProject) {
    this.project = project;
    applyRoomSize(project);
    this.pruneOpenDoors(project);
    const nextRoomSignature = JSON.stringify(project.room);
    const nextModelSignature = JSON.stringify({
      carcassMaterial: project.carcassMaterial,
      modules: project.modules,
      // Размеры до стен и положение шкафов зависят от ширины комнаты.
      roomWidth: BACK_WALL_LENGTH_MM
    });

    if (nextRoomSignature !== this.roomSignature) {
      this.roomSignature = nextRoomSignature;
      this.rebuildRoom(project);
      this.updateRoomCutaway();
    }

    if (nextModelSignature !== this.modelSignature) {
      this.modelSignature = nextModelSignature;
      // Объекты деталей пересобраны — подсветка встанет заново при движении курсора.
      this.setItemHover(null);
      this.rebuildModel(project);
    }

    this.updateSelection(
      project.selectedModuleId,
      project.selectedItemId,
      project.selectedShelvingCellId
    );
    this.projectReady = true;
    this.invalidate(18);
  }

  private doorKey(moduleId: string, doorIndex: number | string) {
    return `${moduleId}:${doorIndex}`;
  }

  private pruneOpenDoors(project: PlannerProject) {
    if (!this.openDoors.size && !this.doorProgress.size) return;
    const activeDoorKeys = new Set<string>();
    project.modules.forEach((module) => {
      if (module.type === "shelving") {
        (module.shelvingCells ?? []).forEach((cell) => {
          if (cell.content === "door") {
            activeDoorKeys.add(this.doorKey(module.id, `cell:${cell.id}`));
            // Вторая дверца пары (09.10).
            if (cell.doorOpening === "double") activeDoorKeys.add(this.doorKey(module.id, `cell:${cell.id}:2`));
          }
          if (cell.content === "drawers") {
            const count = shelvingCellDrawerCountById(module, cell);
            // Откидной над ящиками комода (16.10) помнит, открыт ли он.
            if (cell.chestTopFlap) activeDoorKeys.add(this.doorKey(module.id, `cell:${cell.id}`));
            for (let index = 0; index < count; index += 1) {
              activeDoorKeys.add(
                this.doorKey(module.id, `drawer:${cell.id}:${index}`)
              );
            }
          }
        });
        return;
      }
      // Наружные ящики шкафа выдвигаются так же, как ящики стеллажа: без
      // своих ключей они закрывались при любом обновлении сцены.
      const outerDrawers = module.type === "mezzanine" ? null : cabinetDrawerLayout(module);
      outerDrawers?.bays.forEach((bay) => {
        for (let index = 0; index < outerDrawers.count; index += 1) {
          activeDoorKeys.add(
            this.doorKey(module.id, `drawer:${cabinetDrawerCellId(bay.sectionIndex)}:${index}`)
          );
        }
      });
      // Выдвинутые ящики, корзины и брючницы внутри шкафа тоже помнят своё
      // положение: иначе щелчок по пустому месту их задвигал (приёмка 05.10, wf-02).
      module.items.forEach((item) => {
        if (!PULL_OUT_TYPES.has(item.type)) return;
        const pieces = module.type === "corner45" ? 1 : Math.max(1, usableItemPieces(module, item).length);
        for (let index = 0; index < pieces; index += 1) {
          activeDoorKeys.add(this.doorKey(module.id, `item:${item.id}:${index}`));
        }
      });
      if (module.door === "open") return;
      const count = doorLeafCount(module);
      for (let index = 0; index < count; index += 1) {
        activeDoorKeys.add(this.doorKey(module.id, index));
      }
    });
    this.openDoors.forEach((key) => {
      if (!activeDoorKeys.has(key)) this.openDoors.delete(key);
    });
    this.doorProgress.forEach((_, key) => {
      if (!activeDoorKeys.has(key)) this.doorProgress.delete(key);
    });
  }

  private toggleDoor(moduleId: string, doorIndex: number | string) {
    const key = this.doorKey(moduleId, doorIndex);
    const wasOpen = this.openDoors.has(key);
    const runtime = this.doorObjects.get(key);
    const stopped = this.stoppedDoors.get(key);
    let opening = !wasOpen;
    if (stopped && runtime?.kind === "hinged") {
      const currentAngle = Math.abs(runtime.object.rotation.y);
      const retry = this.resolveHingedDoorMotion(runtime, currentAngle,
        stopped.opening ? HINGED_DOOR_OPEN_ANGLE : 0);
      // After the neighbour has moved, another click continues from this pose.
      // If it is still blocked, the same click reverses the door instead.
      opening = Math.abs(retry.angle - currentAngle) > 0.001
        ? stopped.opening : !stopped.opening;
    }
    this.stoppedDoors.delete(key);
    this.options.onDoorObstruction?.(null);
    if (!this.doorProgress.has(key)) {
      this.doorProgress.set(key, wasOpen ? 1 : 0);
    }

    if (opening) this.openDoors.add(key);
    else this.openDoors.delete(key);

    this.refreshOverlayMeasurements();
    this.lastDoorAnimationTime = performance.now();
    this.invalidate(36);
  }

  private doorVisualProgress(key: string) {
    return this.doorProgress.get(key) ?? (this.openDoors.has(key) ? 1 : 0);
  }

  private easeDoorProgress(value: number) {
    return value * value * (3 - 2 * value);
  }

  private applyDoorRuntime(runtime: DoorRuntime, progress: number) {
    const eased = this.easeDoorProgress(progress);
    if (runtime.kind === "hinged") {
      const angle = (runtime.maxAngle ?? HINGED_DOOR_OPEN_ANGLE) * eased;
      runtime.object.rotation.y = runtime.side * angle;
      if (runtime.closedX !== undefined) {
        runtime.object.position.x = runtime.closedX -
          runtime.side * HINGED_DOOR_LATERAL_TRAVEL * Math.sin(angle);
      }
      const showHardware = progress > 0.025;
      runtime.hardwareObjects?.forEach((object) => {
        object.visible = showHardware;
      });
      runtime.hingeLinks?.forEach((link) => this.updateHingeLink(link));
      return;
    }

    if (runtime.kind === "flap") {
      if (runtime.flap) this.applyFlapPose(runtime.object, runtime.flap, eased);
      else runtime.object.rotation.x = runtime.side * (runtime.maxAngle ?? FLAP_DOWN_OPEN_ANGLE) * eased;
      const showHardware = progress > 0.025;
      runtime.hardwareObjects?.forEach((object) => {
        object.visible = showHardware;
      });
      return;
    }

    if (runtime.kind === "drawer") {
      runtime.object.position.z =
        (runtime.closedZ ?? 0) + (runtime.openShiftZ ?? 0) * eased;
      const followers = runtime.object.userData.slideFollowers as Group[] | undefined;
      followers?.forEach(rail => { rail.position.z = runtime.object.position.z / 2; });
      return;
    }

    runtime.object.position.x =
      (runtime.closedX ?? runtime.object.position.x) +
      (runtime.openShift ?? 0) * eased;
  }

  private updateDoorAnimations() {
    if (!this.doorObjects.size) return false;
    const now = performance.now();
    const delta =
      this.lastDoorAnimationTime > 0
        ? Math.min(0.06, (now - this.lastDoorAnimationTime) / 1000)
        : 1 / 60;
    this.lastDoorAnimationTime = now;

    let isAnimating = false;
    this.doorObjects.forEach((runtime, key) => {
      if (this.stoppedDoors.has(key)) return;
      const current = this.doorProgress.get(key) ?? (this.openDoors.has(key) ? 1 : 0);
      const target = this.openDoors.has(key) ? 1 : 0;
      const difference = target - current;
      if (Math.abs(difference) < 0.001) {
        this.doorProgress.set(key, target);
        this.applyDoorRuntime(runtime, target);
        return;
      }

      const step = Math.min(Math.abs(difference), delta / DOOR_ANIMATION_DURATION);
      const next = current + Math.sign(difference) * step;
      // Подъёмник по ходу открывания упирается в открытую дверь (29.10): шаги по
      // 1 % хода, чтобы фасад не «перепрыгнул» тонкую дверь за кадр.
      if (runtime.kind === "flap" && runtime.flap) {
        const samples = Math.max(1, Math.ceil(Math.abs(next - current) / 0.01));
        let safe = current;
        for (let sample = 1; sample <= samples; sample += 1) {
          const candidate = current + (next - current) * sample / samples;
          this.applyDoorRuntime(runtime, candidate);
          const obstacle = this.flapMotionObstacle(runtime);
          if (!obstacle) {
            safe = candidate;
            continue;
          }
          let blocked = candidate;
          for (let iteration = 0; iteration < 10; iteration += 1) {
            const middle = (safe + blocked) / 2;
            this.applyDoorRuntime(runtime, middle);
            if (this.flapMotionObstacle(runtime)) blocked = middle;
            else safe = middle;
          }
          this.applyDoorRuntime(runtime, safe);
          this.doorProgress.set(key, safe);
          this.stoppedDoors.set(key, { opening: difference > 0, obstacle });
          this.reportFlapObstruction(obstacle, difference > 0);
          isAnimating = true;
          return;
        }
      }
      if (runtime.kind === "hinged") {
        const motion = this.resolveHingedDoorMotion(runtime,
          HINGED_DOOR_OPEN_ANGLE * this.easeDoorProgress(current),
          HINGED_DOOR_OPEN_ANGLE * this.easeDoorProgress(next));
        if (motion.obstacle) {
          const progress = doorProgressAtAngle(motion.angle, HINGED_DOOR_OPEN_ANGLE);
          this.doorProgress.set(key, progress);
          this.applyDoorRuntime(runtime, progress);
          this.stoppedDoors.set(key, { opening: difference > 0, obstacle: motion.obstacle });
          this.reportDoorObstruction(motion.obstacle, difference > 0, motion.angle);
          isAnimating = true;
          return;
        }
      }
      this.doorProgress.set(key, next);
      this.applyDoorRuntime(runtime, next);
      isAnimating = true;
    });

    return isAnimating;
  }

  private refreshDoorCollisionLimits() {
    this.modelRoot.updateMatrixWorld(true);
    this.stoppedDoors.clear();
    this.options.onDoorObstruction?.(null);
    // Rebuild from closed poses so changes in dimensions cannot restore a door
    // on the far side of an obstacle. Keep each requested physical angle.
    const poses = new Map<string, number>();
    this.doorObjects.forEach((runtime, key) => {
      if (runtime.kind !== "hinged") return;
      poses.set(key, this.doorVisualProgress(key));
      runtime.maxAngle = HINGED_DOOR_OPEN_ANGLE;
      this.applyDoorRuntime(runtime, 0);
    });
    this.doorObjects.forEach((runtime, key) => {
      const pose = poses.get(key);
      if (pose === undefined) return;
      const motion = this.resolveHingedDoorMotion(runtime, 0,
        HINGED_DOOR_OPEN_ANGLE * this.easeDoorProgress(pose));
      const progress = motion.obstacle ? doorProgressAtAngle(motion.angle, HINGED_DOOR_OPEN_ANGLE) : pose;
      this.doorProgress.set(key, progress);
      if (motion.obstacle) this.stoppedDoors.set(key, {
        opening: this.openDoors.has(key), obstacle: motion.obstacle
      });
      this.applyDoorRuntime(runtime, progress);
    });
  }

  private reportFlapObstruction(obstacle: DoorObstacle, opening: boolean) {
    const cause = obstacle === "handle" ? "Мешает ручка соседней двери." : "Мешает открытая соседняя дверь.";
    this.options.onDoorObstruction?.(`${opening ? "Подъёмник остановился." : "Подъёмник не закрывается до конца."} ${cause} Закройте её и нажмите подъёмник ещё раз.`);
  }

  private reportDoorObstruction(obstacle: DoorObstacle, opening: boolean, angle: number) {
    const cause = obstacle === "door" ? "Мешает соседняя дверь."
      : obstacle === "flap" ? "Мешает открытый подъёмник: дверь упирается в его фасад или рычаги."
      : obstacle === "handle" ? "Мешает ручка: она упирается в соседний фасад или в ручку соседа."
      : obstacle === "room" ? "Дальше мешает стена." : "Дальше мешает соседний корпус.";
    const position = angle < 0.01 ? "Дверь не может открыться."
      : `Дверь остановилась на ${Math.round(angle * 180 / Math.PI)}°.`;
    const action = obstacle === "door"
      ? "Переместите соседнюю дверь и нажмите эту ещё раз, чтобы продолжить."
      : obstacle === "flap"
        ? "Закройте подъёмник и нажмите эту дверь ещё раз."
      : obstacle === "handle"
        ? "Поставьте петли с другой стороны или ручку без выноса: интегрированную или push-to-open."
        : "Измените сторону петель или расположение корпуса, если нужен больший угол.";
    const reverse = opening && angle > 0.01 ? " Пока проход занят, повторный клик закроет створку." : "";
    this.options.onDoorObstruction?.(`${position} ${cause} ${action}${reverse}`);
  }

  private resolveHingedDoorMotion(runtime: DoorRuntime, from: number, to: number) {
    if (!this.project || !runtime.width || from === to) return { angle: to };

    const layout = createCompositionLayout(this.project);
    const runtimePlacement = layout.placements.find(
      (placement) => placement.module.id === runtime.moduleId
    );
    const obstacles = layout.placements
      .filter(
        (placement) =>
          placement.module.id !== runtime.moduleId &&
          placement.wing === 0 &&
          (!runtimePlacement ||
            this.placementsVerticallyOverlap(runtimePlacement, placement))
      )
      .flatMap((placement) => {
        // Доборы углового неподвижны и стоят в плоскости дверей — тоже препятствие (29.10, 31.10).
        const fillers = placement.module.type === "corner45" ? cornerWorldFillerPlans(placement.module) : [];
        return [this.placementCollisionPolygon(placement), ...fillers];
      });
    // Подъёмники (29.10): фасад с рычагами там, где он сейчас, — на высоте этой двери.
    const doorBox = this.visibleWorldBox([runtime.object]);
    const flaps = doorBox.isEmpty() ? [] : this.flapObstacleBoxes().filter((flap) =>
      flap.minY < doorBox.max.y - mm(2) && flap.maxY > doorBox.min.y + mm(2));
    const otherHandles: PlanPoint[][] = [];
    const handleTargets: PlanPoint[][] = [];
    const otherDoors = this.otherDoorCollisionPolygons(
      runtime,
      runtimePlacement,
      layout.placements,
      otherHandles,
      handleTargets
    );

    return stopDoorBeforeObstacle(from, to, runtime.width, angle => {
      const panel = this.hingedDoorCollisionPolygon(runtime, angle);
      if (!panel) return undefined;
      if (!this.doorInsideRoom(panel)) return "room";
      if (otherDoors.some(other => doorPolygonsOverlap(panel, other))) return "door";
      if (obstacles.some(polygon => doorPolygonsOverlap(panel, polygon))) return "body";
      // Ручки с выносом (29.10): дверь упирается в ручку соседа, а её ручка —
      // в соседний фасад, добор, корпус или стену.
      const handle = this.hingedDoorHandlePolygon(runtime, angle);
      if (flaps.some((flap) => doorPolygonsOverlap(panel, flap.polygon) ||
        (handle !== null && doorPolygonsOverlap(handle, flap.polygon)))) return "flap";
      if (otherHandles.some(other => doorPolygonsOverlap(panel, other))) return "handle";
      if (handle && (!this.doorInsideRoom(handle) ||
        handleTargets.some(other => doorPolygonsOverlap(handle, other)) ||
        otherHandles.some(other => doorPolygonsOverlap(handle, other)) ||
        obstacles.some(polygon => doorPolygonsOverlap(handle, polygon)))) return "handle";
      return undefined;
    });
  }

  private placementCollisionPolygon(placement: ModulePlacement): PlanPoint[] {
    if (placement.module.type === "corner45" && placement.wing === 0) {
      return cornerWorldPlan(placement.module);
    }
    const width = mm(placement.module.width);
    const depth = mm(placement.module.depth);
    const clearance = mm(0.5);
    const corners = [
      this.modulePoint(placement, -width / 2, 0),
      this.modulePoint(placement, width / 2, 0),
      this.modulePoint(placement, width / 2, -depth),
      this.modulePoint(placement, -width / 2, -depth)
    ];
    const minX = Math.min(...corners.map((point) => point.x)) - clearance;
    const maxX = Math.max(...corners.map((point) => point.x)) + clearance;
    const minZ = Math.min(...corners.map((point) => point.z)) - clearance;
    const maxZ = Math.max(...corners.map((point) => point.z)) + clearance;
    return [
      { x: minX, z: minZ },
      { x: maxX, z: minZ },
      { x: maxX, z: maxZ },
      { x: minX, z: maxZ }
    ];
  }

  private placementsVerticallyOverlap(
    first: ModulePlacement,
    second: ModulePlacement
  ) {
    const clearance = mm(2);
    // Антресоль углового — часть его модуля, над ним.
    const top = (placement: ModulePlacement) => placement.y + mm(placement.module.height +
      (placement.module.type === "corner45" ? cornerMezzanineMm(placement.module) : 0));
    const firstTop = top(first);
    const secondTop = top(second);
    return (
      first.y < secondTop - clearance &&
      firstTop - clearance > second.y
    );
  }

  private hingedDoorCollisionPolygon(
    runtime: DoorRuntime,
    angle: number
  ): PlanPoint[] | null {
    if (!runtime.width || !runtime.object.parent) return null;

    runtime.object.parent.updateMatrixWorld(true);
    const hinge = new Vector3(
      runtime.closedX === undefined ? runtime.object.position.x : runtime.closedX -
        runtime.side * HINGED_DOOR_LATERAL_TRAVEL * Math.sin(angle),
      runtime.object.position.y,
      runtime.object.position.z
    );
    runtime.object.parent.localToWorld(hinge);

    const xAxis = new Vector3(1, 0, 0).transformDirection(
      runtime.object.parent.matrixWorld
    );
    const zAxis = new Vector3(0, 0, 1).transformDirection(
      runtime.object.parent.matrixWorld
    );
    const free = hinge
      .clone()
      .addScaledVector(xAxis, -runtime.side * runtime.width * Math.cos(angle))
      .addScaledVector(zAxis, runtime.width * Math.sin(angle));
    const direction = free.clone().sub(hinge).normalize();
    const halfThickness = mm(10.5); // 20 mm facade and a 1 mm joint between doors.
    const normal = { x: -direction.z * halfThickness, z: direction.x * halfThickness };
    return [
      { x: hinge.x + normal.x, z: hinge.z + normal.z },
      { x: free.x + normal.x, z: free.z + normal.z },
      { x: free.x - normal.x, z: free.z - normal.z },
      { x: hinge.x - normal.x, z: hinge.z - normal.z }
    ];
  }
  /**
   * Ручка в плане при угле двери: прямоугольник на лице фасада у открывающей
   * кромки, на вынос ручки вперёд. null — ручки с выносом нет.
   */
  private hingedDoorHandlePolygon(runtime: DoorRuntime, angle: number): PlanPoint[] | null {
    const handle = runtime.handle;
    if (!handle || handle.projection <= 0 || !runtime.width || !runtime.object.parent) return null;
    runtime.object.parent.updateMatrixWorld(true);
    const hinge = new Vector3(
      runtime.closedX === undefined ? runtime.object.position.x : runtime.closedX -
        runtime.side * HINGED_DOOR_LATERAL_TRAVEL * Math.sin(angle),
      runtime.object.position.y,
      runtime.object.position.z
    );
    runtime.object.parent.localToWorld(hinge);
    const xAxis = new Vector3(1, 0, 0).transformDirection(runtime.object.parent.matrixWorld);
    const zAxis = new Vector3(0, 0, 1).transformDirection(runtime.object.parent.matrixWorld);
    // Вдоль двери — от петель к открывающей кромке; наружу — нормаль лица фасада.
    const along = xAxis.clone().multiplyScalar(-runtime.side * Math.cos(angle)).addScaledVector(zAxis, Math.sin(angle));
    const outward = xAxis.clone().multiplyScalar(runtime.side * Math.sin(angle)).addScaledVector(zAxis, Math.cos(angle));
    const point = (s: number, n: number) => {
      const p = hinge.clone().addScaledVector(along, s).addScaledVector(outward, n);
      return { x: p.x, z: p.z };
    };
    const from = handle.along - handle.half;
    const to = handle.along + handle.half;
    const near = handle.face;
    const far = handle.face + handle.projection;
    return [point(from, near), point(to, near), point(to, far), point(from, far)];
  }
  /** Видимые детали в мировых координатах: фасад, ручка, рычаги (скрытые — нет). */
  private visibleWorldBox(objects: Object3D[]) {
    const box = new Box3();
    const part = new Box3();
    for (const root of objects) {
      if (!root.visible) continue;
      root.updateWorldMatrix(true, true);
      root.traverseVisible((object) => {
        if (!(object instanceof Mesh)) return;
        const geometry = object.geometry as BufferGeometry;
        if (!geometry.boundingBox) geometry.computeBoundingBox();
        if (!geometry.boundingBox) return;
        part.copy(geometry.boundingBox).applyMatrix4(object.matrixWorld);
        box.union(part);
      });
    }
    return box;
  }

  /**
   * Подъёмники в плане (29.10): прямоугольник фасада с рычагами и корпусами
   * механизма в текущем положении и его высота. Корпуса стоят по стенам
   * комнаты, поэтому коробка по осям мира — их честный контур.
   */
  private flapObstacleBoxes(exceptKey?: string) {
    const boxes: Array<{ key: string; polygon: PlanPoint[]; minY: number; maxY: number }> = [];
    this.doorObjects.forEach((runtime, key) => {
      if (runtime.kind !== "flap" || key === exceptKey) return;
      const box = this.visibleWorldBox([runtime.object, ...(runtime.hardwareObjects ?? [])]);
      if (box.isEmpty()) return;
      boxes.push({ key, minY: box.min.y, maxY: box.max.y, polygon: [
        { x: box.min.x, z: box.min.z }, { x: box.max.x, z: box.min.z },
        { x: box.max.x, z: box.max.z }, { x: box.min.x, z: box.max.z }
      ] });
    });
    return boxes;
  }

  /** Подъёмник в текущем положении задевает распашную дверь или её ручку. */
  private flapMotionObstacle(runtime: DoorRuntime): DoorObstacle | undefined {
    const flapBox = this.visibleWorldBox([runtime.object, ...(runtime.hardwareObjects ?? [])]);
    if (flapBox.isEmpty()) return undefined;
    const polygon: PlanPoint[] = [
      { x: flapBox.min.x, z: flapBox.min.z }, { x: flapBox.max.x, z: flapBox.min.z },
      { x: flapBox.max.x, z: flapBox.max.z }, { x: flapBox.min.x, z: flapBox.max.z }
    ];
    for (const other of this.doorObjects.values()) {
      if (other === runtime || other.kind !== "hinged") continue;
      const doorBox = this.visibleWorldBox([other.object]);
      if (doorBox.isEmpty() || doorBox.min.y >= flapBox.max.y - mm(2) || doorBox.max.y <= flapBox.min.y + mm(2)) continue;
      const angle = Math.abs(other.object.rotation.y);
      const panel = this.hingedDoorCollisionPolygon(other, angle);
      if (panel && doorPolygonsOverlap(polygon, panel)) return "door";
      const handle = this.hingedDoorHandlePolygon(other, angle);
      if (handle && doorPolygonsOverlap(polygon, handle)) return "handle";
    }
    return undefined;
  }



  private otherDoorCollisionPolygons(
    sourceRuntime: DoorRuntime,
    sourcePlacement: ModulePlacement | undefined,
    placements: ModulePlacement[],
    /** Сюда — ручки других дверей с выносом (29.10). */
    handles?: PlanPoint[][],
    /** Сюда — створки, в которые может упереться ручка этой двери. */
    handleTargets?: PlanPoint[][]
  ) {
    const polygons: PlanPoint[][] = [];
    this.doorObjects.forEach((runtime, key) => {
      if (key === sourceRuntime.key || runtime.kind !== "hinged") return;
      const placement = placements.find(
        (candidate) => candidate.module.id === runtime.moduleId
      );
      // По высоте сравниваем сами створки: у углового дверь антресоли — в том же модуле.
      const sourceBottom = (sourcePlacement?.y ?? 0) + (sourceRuntime.minY ?? 0);
      const sourceTop = (sourcePlacement?.y ?? 0) +
        (sourceRuntime.maxY ?? mm(sourcePlacement?.module.height ?? 0));
      const otherBottom = (placement?.y ?? 0) + (runtime.minY ?? 0);
      const otherTop = (placement?.y ?? 0) +
        (runtime.maxY ?? mm(placement?.module.height ?? 0));
      if (sourceTop <= otherBottom + mm(2) || otherTop <= sourceBottom + mm(2)) return;
      // Read the actual pose: another leaf may still be moving in this frame.
      const polygon = this.hingedDoorCollisionPolygon(runtime, Math.abs(runtime.object.rotation.y));
      if (polygon) polygons.push(polygon);
      // Две двери на одной стойке (или стыке) с петлями у неё открываются
      // друг от друга дальше 90° — их ручки в жизни не встречаются (29.10).
      if (this.doorsHingedBackToBack(sourceRuntime, runtime)) return;
      if (polygon) handleTargets?.push(polygon);
      const handle = this.hingedDoorHandlePolygon(runtime, Math.abs(runtime.object.rotation.y));
      if (handle) handles?.push(handle);
    });
    return polygons;
  }

  /** Двери в одной плоскости с петлями на общей стойке или стыке: петли рядом, стороны разные. */
  private doorsHingedBackToBack(first: DoorRuntime, second: DoorRuntime) {
    if (first.side === second.side || !first.object.parent || !second.object.parent) return false;
    const hingePoint = (runtime: DoorRuntime) => runtime.object.parent!.localToWorld(new Vector3(
      runtime.closedX ?? runtime.object.position.x, runtime.object.position.y, runtime.object.position.z));
    const axis = (runtime: DoorRuntime) => new Vector3(1, 0, 0).transformDirection(runtime.object.parent!.matrixWorld);
    first.object.parent.updateMatrixWorld(true);
    second.object.parent.updateMatrixWorld(true);
    if (axis(first).dot(axis(second)) < 0.999) return false;
    const a = hingePoint(first);
    const b = hingePoint(second);
    return Math.hypot(a.x - b.x, a.z - b.z) < mm(60);
  }

  private doorInsideRoom(points: PlanPoint[]) {
    const margin = mm(1);
    return points.every(
      (point) =>
        point.x >= ROOM_LAYOUT.leftWallX + margin &&
        point.x <= ROOM_LAYOUT.rightWallX - margin &&
        point.z >= ROOM_LAYOUT.backWallZ + margin &&
        point.z <= ROOM_LAYOUT.sideWallMaxZ - margin
    );
  }

  setView(view: "front" | "perspective") {
    const framing = this.viewFraming(view);
    if (!framing) return;
    this.camera.position.copy(framing.position);
    this.controls.target.copy(framing.target);
    this.roomDistanceClamped = false;
    this.syncPreferredCameraDistance();
    this.controls.update();
    this.invalidate(24);
  }

  /** Камера, в которую изделие попадает целиком: «Фронт» или общий вид. */
  private viewFraming(view: "front" | "perspective", aspect = this.camera.aspect) {
    if (!this.project) return null;
    const height = mm(maxHeight(this.project));
    const layout = createCompositionLayout(this.project);
    const targetX = (layout.bounds.minX + layout.bounds.maxX) * 0.5;
    const bounds = new Box3(
      new Vector3(layout.bounds.minX, 0, layout.bounds.minZ),
      new Vector3(layout.bounds.maxX, Math.max(0.5, height), layout.bounds.maxZ)
    );
    const corners = this.project.modules.filter(module => module.type === "corner45");
    // Один угловой — смотрим в угол, на обе двери сразу.
    const cornerView = corners.length === 1 ? cornerPlan(corners[0]!) : undefined;
    const angle = cornerView ? Math.atan2(cornerView.b - cornerView.d, cornerView.a - cornerView.d) : undefined;
    const direction = angle !== undefined
      ? new Vector3((cornerSide(corners[0]!) === "left" ? 1 : -1) * Math.sin(angle),
          view === "front" ? 0 : 0.12, Math.cos(angle))
      : view === "front" ? new Vector3(0, 0, 1) : new Vector3(targetX > 0.3 ? -0.42 : 0.42, 0.12, 1);
    return frameBounds(bounds, direction,
      // «Фронт» стоит дальше: изделие с размерами целиком и комната вокруг (приёмка 04.10, v3-02).
      this.camera.fov, aspect, this.presentationMode ? 1.34 : view === "front" ? 1.45 : 1.14
    );
  }

  /**
   * Картинка проекта для карточки в кабинете (10.10): общий вид изделия без
   * размеров и выделения, кадр aspect : 1 шириной width точек, JPEG. На один
   * кадр холст получает размер картинки и сразу возвращается обратно — до
   * отрисовки страницы, так что на экране ничего не мигает. Камера
   * пользователя не меняется.
   */
  getProjectPreview(width = 480, aspect = 2): string | null {
    if (!this.project?.modules.length) return null;
    const height = Math.round(width / aspect);
    const position = this.camera.position.clone();
    const quaternion = this.camera.quaternion.clone();
    const dimensionVisible = this.dimensionRoot.visible;
    const overlayVisible = this.overlayRoot.visible;
    const selectionVisible = this.selectionHelper?.visible ?? false;
    try {
      this.camera.clearViewOffset();
      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height, false);
      this.postProcessing?.setSize(width, height);
      const framing = this.viewFraming("perspective", aspect);
      if (!framing) return null;
      this.camera.position.copy(framing.position);
      this.camera.lookAt(framing.target);
      this.camera.updateMatrixWorld();
      this.dimensionRoot.visible = false;
      this.overlayRoot.visible = false;
      if (this.selectionHelper) this.selectionHelper.visible = false;
      if (!this.render(true)) return null;
      const source = this.renderer.domElement;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return null;
      context.drawImage(source, 0, 0, source.width, source.height, 0, 0, width, height);
      return canvas.toDataURL("image/jpeg", 0.82);
    } catch (error) {
      console.warn("Project preview unavailable", error);
      return null;
    } finally {
      this.camera.position.copy(position);
      this.camera.quaternion.copy(quaternion);
      this.camera.updateMatrixWorld();
      this.dimensionRoot.visible = dimensionVisible;
      this.overlayRoot.visible = overlayVisible;
      if (this.selectionHelper) this.selectionHelper.visible = selectionVisible;
      this.resize();
      this.render(true);
    }
  }

  resetCamera() {
    this.setView("perspective");
  }

  /** Показать или спрятать предметы интерьера; изделие при этом не меняется (ch-05, sg-11). */
  setInteriorPropsVisible(visible: boolean) {
    if (this.interiorPropsVisible === visible) return;
    this.interiorPropsVisible = visible;
    if (this.project) {
      this.modelSignature = "";
      this.setProject(this.project);
    }
  }

  setDimensionsVisible(visible: boolean) {
    this.dimensionsVisible = visible;
    this.dimensionRoot.visible = visible && !this.presentationMode;
    this.invalidate(2);
  }

  setPresentationMode(enabled: boolean) {
    this.presentationMode = enabled;
    this.applyViewOffset();
    this.dimensionRoot.visible = this.dimensionsVisible && !enabled;
    this.overlayRoot.visible = !enabled;
    this.invalidate(2);
  }

  capture() {
    const snapshot = this.getSnapshot();
    if (!snapshot) return;
    const link = document.createElement("a");
    link.href = snapshot;
    link.download = "egel-design-project.jpg";
    link.click();
  }

  getSnapshot() {
    return this.withoutViewOffset(() => this.takeSnapshot());
  }

  private takeSnapshot() {
    const selectionVisible = this.selectionHelper?.visible ?? false;
    if (this.selectionHelper) this.selectionHelper.visible = false;
    if (!this.render(true)) {
      if (this.selectionHelper) this.selectionHelper.visible = selectionVisible;
      return null;
    }
    const snapshot = this.renderer.domElement.toDataURL("image/jpeg", 0.92);
    if (this.selectionHelper) this.selectionHelper.visible = selectionVisible;
    this.render(true);
    return snapshot;
  }

  getProjectRenderSnapshot() {
    return this.withoutViewOffset(() => this.takeProjectRenderSnapshot());
  }

  private takeProjectRenderSnapshot() {
    if (!this.project) return null;

    const dimensionVisible = this.dimensionRoot.visible;
    const overlayVisible = this.overlayRoot.visible;
    const selectionVisible = this.selectionHelper?.visible ?? false;

    this.dimensionRoot.visible = false;
    this.overlayRoot.visible = false;
    if (this.selectionHelper) this.selectionHelper.visible = false;

    const rendered = this.render(true);
    const snapshot = rendered
      ? this.renderer.domElement.toDataURL("image/jpeg", 0.95)
      : null;

    this.dimensionRoot.visible = dimensionVisible;
    this.overlayRoot.visible = overlayVisible;
    if (this.selectionHelper) this.selectionHelper.visible = selectionVisible;
    this.render(true);

    return snapshot;
  }

  getProjectRenderStateSpecification() {
    if (!this.project) return "";

    const moduleStates = this.project.modules.map((module, moduleIndex) => {
      const moduleLabel = `MODULE ${moduleIndex + 1}`;
      if (module.type === "shelving") {
        const cellDoors = (module.shelvingCells ?? []).filter(
          (cell) => cell.content === "door" || cell.content === "drawers"
        );
        if (cellDoors.length === 0) {
          return `${moduleLabel} DOOR POSE LOCK: this shelving module has no cell doors or drawers.`;
        }
        const states = cellDoors.map((cell) => {
          if (cell.content === "drawers") {
            const drawerTotal = shelvingCellDrawerCountById(module, cell);
            return Array.from({ length: drawerTotal }, (_, index) => {
              const key = this.doorKey(module.id, `drawer:${cell.id}:${index}`);
              const progress = this.doorVisualProgress(key);
              const travel = Math.round((this.doorObjects.get(key)?.openShiftZ ?? 0) * this.easeDoorProgress(progress) / MM);
              return `drawer ${index + 1} from bottom in column ${cell.column + 1}, row ${cell.row + 1} is ${progress > 0.08 ? `OPEN, pulled forward ${travel} mm with its open-topped box visible` : "CLOSED"}`;
            }).join("; ");
          }
          const key = this.doorKey(module.id, `cell:${cell.id}`);
          const progress = this.doorVisualProgress(key);
          const runtime = this.doorObjects.get(key);
          if (runtime?.kind === "flap") {
            return `cell at column ${cell.column + 1}, row ${cell.row + 1} has a horizontal front that is ${progress > 0.08 ? `OPEN, ${runtime.flap?.openText ?? (runtime.side < 0 ? "lifted up" : "dropped down")}` : "CLOSED"}`;
          }
          if (cell.doorOpening === "double") {
            const second = this.doorVisualProgress(this.doorKey(module.id, `cell:${cell.id}:2`));
            return `cell at column ${cell.column + 1}, row ${cell.row + 1} has a pair of doors: left ${progress > 0.08 ? "OPEN" : "CLOSED"}, right ${second > 0.08 ? "OPEN" : "CLOSED"}`;
          }
          return `cell at column ${cell.column + 1}, row ${cell.row + 1} is ${progress > 0.08 ? "OPEN" : "CLOSED"}`;
        });
        return `${moduleLabel} DOOR POSE LOCK: ${states.join("; ")}.`;
      }

      if (module.door === "open") {
        return `${moduleLabel} DOOR POSE LOCK: this is an intentionally open-front module with NO door leaves.`;
      }

      const count = Math.max(1, doorLeafCount(module));
      const plinthHeightMm =
        module.type === "mezzanine" ? 0 : cabinetBaseHeightMm(module);
      // Над наружными ящиками двери начинаются выше цоколя.
      const outerDrawers = module.type === "mezzanine" ? null : cabinetDrawerLayout(module);
      const doorBottomMm = outerDrawers ? outerDrawers.doorBottomMm : plinthHeightMm + 2;
      const states = Array.from({ length: count }, (_, doorIndex) => {
        const key = this.doorKey(module.id, doorIndex);
        const progress = this.doorVisualProgress(key);
        const runtime = this.doorObjects.get(key);
        const verticalLock = `its lower edge stays ${doorBottomMm} mm above this module's bottom plane${module.type === "mezzanine" ? "" : " and therefore above the room floor"}; it stays upright and never touches the floor`;
        if (progress <= 0.08) {
          return `leaf ${doorIndex + 1} is CLOSED; ${verticalLock}`;
        }
        if (runtime?.kind === "hinged") {
          const radians =
            (runtime.maxAngle ?? HINGED_DOOR_OPEN_ANGLE) *
            this.easeDoorProgress(progress);
          const degrees = Math.max(5, Math.round((radians * 180) / Math.PI / 5) * 5);
          return `leaf ${doorIndex + 1} is OPEN at approximately ${degrees} degrees around its vertical hinge axis; ${verticalLock}`;
        }
        if (runtime?.kind === "flap") {
          return `horizontal front ${doorIndex + 1} is OPEN, ${runtime.flap?.openText ?? (runtime.side < 0 ? "lifted up around its top edge" : "dropped down around its bottom edge")}`;
        }
        return `leaf ${doorIndex + 1} is OPEN in its current sliding position; ${verticalLock}`;
      });
      const drawerStates = outerDrawers
        ? outerDrawers.bays.flatMap((bay) => Array.from({ length: outerDrawers.count }, (_, index) => {
            const key = this.doorKey(module.id, `drawer:${cabinetDrawerCellId(bay.sectionIndex)}:${index}`);
            const progress = this.doorVisualProgress(key);
            const travel = Math.round((this.doorObjects.get(key)?.openShiftZ ?? 0) * this.easeDoorProgress(progress) / MM);
            const where = outerDrawers.bays.length > 1 ? ` in bay ${bay.sectionIndex + 1}` : "";
            return `external drawer ${index + 1} from bottom${where} is ${progress > 0.08 ? `OPEN, pulled forward ${travel} mm with its open-topped box visible` : "CLOSED"}`;
          }))
        : [];
      return `${moduleLabel} DOOR POSE LOCK: ${[...states, ...drawerStates].join("; ")}. Preserve these exact visible door positions and the exposed or concealed interior.`;
    });

    return [
      "AUTHORITATIVE CURRENT DOOR POSES FROM THE LIVE 3D SCENE:",
      ...moduleStates,
      "An OPEN door must remain open in the final image. Never close it for a cleaner catalogue presentation. A CLOSED door must remain closed."
    ].join("\n");
  }

  getAiReferenceSnapshot(wall: WallSide = "back") {
    if (!this.project) return null;
    // Пока делается снимок без комнаты, срез стен и потолка не должен снова
    // включать их при обновлении камеры (проверка 07.10).
    this.roomCutawaySuspended = true;

    const cameraPosition = this.camera.position.clone();
    const controlsTarget = this.controls.target.clone();
    const preferredCameraDistance = this.preferredCameraDistance;
    const roomDistanceClamped = this.roomDistanceClamped;
    const sceneBackground = this.scene.background;
    const dimensionVisible = this.dimensionRoot.visible;
    const overlayVisible = this.overlayRoot.visible;
    const selectionVisible = this.selectionHelper?.visible ?? false;
    const roomMeshVisibility: Array<[Mesh, boolean]> = [];
    const filteredObjectVisibility: Array<[Object3D, boolean]> = [];
    const reflectorVisibility: Array<[Reflector, boolean]> = [];
    const mirrorBackingMaterials: Array<[Mesh, Mesh["material"]]> = [];
    const layout = createCompositionLayout(this.project);
    const visibleModuleIds = new Set(
      layout.placements
        .filter((placement) => placement.wall === wall)
        .map((placement) => placement.module.id)
    );
    if (!visibleModuleIds.size) {
      layout.placements.forEach((placement) =>
        visibleModuleIds.add(placement.module.id)
      );
    }
    const referenceMirrorMaterial = new MeshPhysicalMaterial({
      color: 0xb9c3c5,
      roughness: 0.12,
      metalness: 0.32,
      clearcoat: 0.72,
      clearcoatRoughness: 0.08,
      reflectivity: 0.76,
      envMapIntensity: 0.78
    });

    this.roomRoot.traverse((object) => {
      if (object instanceof Mesh) {
        roomMeshVisibility.push([object, object.visible]);
        object.visible = false;
      }
    });
    this.moduleObjects.forEach((object, moduleId) => {
      filteredObjectVisibility.push([object, object.visible]);
      object.visible = visibleModuleIds.has(moduleId);
    });
    this.modelRoot.children.forEach((object) => {
      const moduleId = object.userData.aiModuleId as string | undefined;
      if (!moduleId) return;
      filteredObjectVisibility.push([object, object.visible]);
      object.visible = visibleModuleIds.has(moduleId);
    });
    this.modelRoot.traverse((object) => {
      if (object instanceof Reflector) {
        reflectorVisibility.push([object, object.visible]);
        object.visible = false;
        return;
      }
      if (object instanceof Mesh && object.userData.mirrorSurface === true) {
        mirrorBackingMaterials.push([object, object.material]);
        object.material = referenceMirrorMaterial;
      }
    });

    this.dimensionRoot.visible = false;
    this.overlayRoot.visible = false;
    if (this.selectionHelper) this.selectionHelper.visible = false;
    // A neutral studio card keeps mirrors and glazing readable in the
    // reference. The client removes only the connected outer background,
    // leaving reflective facade pixels intact.
    this.scene.background = new Color("#eeeae3");
    this.modelRoot.updateMatrixWorld(true);
    const bounds = new Box3();
    visibleModuleIds.forEach((moduleId) => {
      const object = this.moduleObjects.get(moduleId);
      if (object?.visible) {
        bounds.union(new Box3().setFromObject(object));
      }
    });
    if (bounds.isEmpty()) {
      this.setView("front");
    } else {
      const center = bounds.getCenter(new Vector3());
      const size = bounds.getSize(new Vector3());
      const verticalFov = (this.camera.fov * Math.PI) / 180;
      const horizontalFov =
        2 *
        Math.atan(
          Math.tan(verticalFov / 2) * Math.max(0.5, this.camera.aspect)
        );
      const horizontalSize = wall === "back" ? size.x : size.z;
      const distance =
        Math.max(
          size.y / (2 * Math.tan(verticalFov / 2)),
          horizontalSize / (2 * Math.tan(horizontalFov / 2)),
          1.8
        ) * 1.18;
      this.controls.target.copy(center);
      if (wall === "left") {
        this.camera.position.set(center.x + distance, center.y, center.z);
      } else if (wall === "right") {
        this.camera.position.set(center.x - distance, center.y, center.z);
      } else {
        this.camera.position.set(center.x, center.y, center.z + distance);
      }
      this.roomDistanceClamped = false;
      this.syncPreferredCameraDistance();
      this.controls.update();
    }

    const rendered = this.render(true);
    const snapshot = rendered
      ? this.renderer.domElement.toDataURL("image/png")
      : null;

    roomMeshVisibility.forEach(([mesh, visible]) => {
      mesh.visible = visible;
    });
    filteredObjectVisibility.forEach(([object, visible]) => {
      object.visible = visible;
    });
    reflectorVisibility.forEach(([reflector, visible]) => {
      reflector.visible = visible;
    });
    mirrorBackingMaterials.forEach(([mesh, material]) => {
      mesh.material = material;
    });
    referenceMirrorMaterial.dispose();
    this.dimensionRoot.visible = dimensionVisible;
    this.overlayRoot.visible = overlayVisible;
    if (this.selectionHelper) this.selectionHelper.visible = selectionVisible;
    this.scene.background = sceneBackground;
    this.camera.position.copy(cameraPosition);
    this.controls.target.copy(controlsTarget);
    this.preferredCameraDistance = preferredCameraDistance;
    this.roomDistanceClamped = roomDistanceClamped;
    this.roomCutawaySuspended = false;
    this.controls.update();
    this.updateRoomCutaway();
    this.render(true);

    return snapshot;
  }

  dispose() {
    this.disposed = true;
    this.setItemHover(null);
    if (this.fullQualityTimer !== null) window.clearTimeout(this.fullQualityTimer);
    cancelAnimationFrame(this.animationFrame);
    if (this.decorativeRefreshTimer !== null) {
      window.clearTimeout(this.decorativeRefreshTimer);
      this.decorativeRefreshTimer = null;
    }
    this.resizeObserver.disconnect();
    this.controls.removeEventListener("change", this.handleControlsChange);
    this.controls.removeEventListener("start", this.handleControlsStart);
    this.controls.dispose();
    this.renderer.domElement.removeEventListener("pointerdown", this.handlePointerDown);
    this.renderer.domElement.removeEventListener("pointermove", this.handlePointerMove);
    this.renderer.domElement.removeEventListener("pointerup", this.handlePointerUp);
    this.renderer.domElement.removeEventListener("pointercancel", this.handlePointerUp);
    this.renderer.domElement.removeEventListener("pointerleave", this.handlePointerLeave);
    this.renderer.domElement.removeEventListener(
      "wheel",
      this.handleCameraWheelIntent,
      true
    );
    this.renderer.domElement.removeEventListener(
      "pointerdown",
      this.handleZoomGestureStart,
      true
    );
    this.renderer.domElement.removeEventListener(
      "webglcontextlost",
      this.handleContextLost
    );
    this.renderer.domElement.removeEventListener(
      "webglcontextrestored",
      this.handleContextRestored
    );
    this.disposeChildren(this.scene);
    this.disposeDecorativeModels();
    this.materialCache.forEach((material) => {
      material.map?.dispose();
      if ("bumpMap" in material) material.bumpMap?.dispose();
      if ("normalMap" in material) material.normalMap?.dispose();
      if ("roughnessMap" in material) material.roughnessMap?.dispose();
      material.dispose();
    });
    this.materialCache.clear();
    this.lightingMaterials.forEach((material) => {
      material.map?.dispose();
      material.dispose();
    });
    this.lightingMaterials.clear();
    this.cellLights.dispose();
    this.postProcessing?.dispose();
    this.environmentRenderTarget.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  /** Ширина панели поверх 3D справа, px. */
  private viewInset = 0;

  /**
   * Панель ячеек закрывает правую часть 3D. Смещаем картинку влево на
   * половину её ширины (не двигая камеру): изделие остаётся посередине
   * свободной части, а вращение и выбор мышью работают как раньше.
   */
  setViewInset(pixels: number) {
    this.viewInset = Math.max(0, pixels);
    this.applyViewOffset();
    this.invalidate(2);
  }

  private applyViewOffset() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    const inset = !this.presentationMode && this.viewInset > 0 && this.viewInset < width * 0.7 ? this.viewInset : 0;
    if (inset) this.camera.setViewOffset(width, height, inset / 2, 0, width, height);
    else this.camera.clearViewOffset();
  }

  /** Снимок для PDF и рендера — без сдвига под панель. */
  private withoutViewOffset<T>(action: () => T): T {
    if (!this.camera.view?.enabled) return action();
    this.camera.clearViewOffset();
    try {
      return action();
    } finally {
      this.applyViewOffset();
    }
  }

  private readonly resize = () => {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.camera.aspect = width / height;
    this.applyViewOffset();
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.updateDimensionLineResolution(width, height);
    this.postProcessing?.setSize(width, height);
    this.resizeMirrorRenderTargets();
    // Новый размер очищает холст: рисуем сразу, иначе браузер успевает
    // показать пустой кадр — экран «моргает» (11.10).
    if (this.projectReady) this.render(true);
    this.invalidate();
  };

  private getMirrorRenderTargetSize() {
    this.renderer.getDrawingBufferSize(this.mirrorDrawingBufferSize);
    const drawingWidth = Math.max(1, this.mirrorDrawingBufferSize.x);
    const drawingHeight = Math.max(1, this.mirrorDrawingBufferSize.y);
    const maximumDimension = Math.min(
      MIRROR_MAX_TEXTURE_DIMENSION,
      this.renderer.capabilities.maxTextureSize
    );
    const scale = Math.min(
      1,
      maximumDimension / drawingWidth,
      maximumDimension / drawingHeight,
      Math.sqrt(
        MIRROR_MAX_TEXTURE_PIXELS / (drawingWidth * drawingHeight)
      )
    );

    return {
      width: Math.max(1, Math.floor(drawingWidth * scale)),
      height: Math.max(1, Math.floor(drawingHeight * scale))
    };
  }

  private resizeMirrorRenderTargets() {
    const { width, height } = this.getMirrorRenderTargetSize();
    this.modelRoot.traverse((object) => {
      if (!(object instanceof Reflector)) return;
      const renderTarget = object.getRenderTarget();
      if (renderTarget.width === width && renderTarget.height === height) return;
      renderTarget.setSize(width, height);
      object.forceUpdate = true;
    });
  }

  private readonly handleControlsStart = () => this.invalidate(2);

  /**
   * Щипок и перетаскивание средней кнопкой приближают к центру вращения:
   * к точке под пальцем тянет только колесо, и только при приближении.
   */
  private readonly handleZoomGestureStart = () => {
    this.controls.zoomToCursor = false;
  };

  private readonly handleCameraWheelIntent = (event: WheelEvent) => {
    if (!this.controls.enabled || !this.controls.enableZoom) return;
    let deltaY = event.deltaY;
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) deltaY *= 16;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) deltaY *= 100;
    if (event.ctrlKey) deltaY *= 10;
    if (Math.abs(deltaY) < 1e-6) return;

    // Приближение — к точке под курсором. Отдаление — прямо назад от центра
    // вращения: «от курсора» камера уходила вверх, упиралась в потолок, и
    // ограничение комнаты тянуло её обратно вперёд — колесо «назад» приближало.
    const zoomingIn = deltaY < 0;
    this.controls.zoomToCursor = zoomingIn;
    const zoomScale = cameraZoomScale(this.controls.zoomSpeed, deltaY);
    // Камеру у стены или потолка ограничение держит ближе желаемого:
    // приближение считается от того места, где она стоит на самом деле.
    const currentDistance = this.camera.position.distanceTo(this.controls.target);
    this.preferredCameraDistance = clamp(
      zoomingIn
        ? Math.min(this.preferredCameraDistance, currentDistance) * zoomScale
        : this.preferredCameraDistance / zoomScale,
      this.controls.minDistance,
      this.controls.maxDistance
    );
    this.cameraWheelIntent = true;
    queueMicrotask(() => {
      this.cameraWheelIntent = false;
    });
  };

  private readonly handleControlsChange = () => {
    this.applyCameraConstraints();
    // Кадр — на следующем кадре браузера, а не на каждое событие (31.10):
    // колесо и тачпад шлют десятки событий за кадр, и синхронная отрисовка на
    // каждое копила очередь — зум «с задержкой» на слабых ноутбуках.
    this.cameraMovedAt = performance.now();
    if (this.fullQualityTimer !== null) window.clearTimeout(this.fullQualityTimer);
    // Камера остановилась — кадр в полном качестве (тени, мягкие тени углов, ореол).
    this.fullQualityTimer = window.setTimeout(() => {
      this.fullQualityTimer = null;
      this.invalidate(2);
    }, CAMERA_SETTLE_MS + 30);
    this.invalidate();
  };

  /** Камера сейчас движется: вращение, колесо, щипок. */
  private cameraInMotion() {
    return performance.now() - this.cameraMovedAt < CAMERA_SETTLE_MS;
  }

  /**
   * Скорость машины — по кадрам подряд в полном качестве (успокоение сцены,
   * анимации дверей): медиана дольше ~40 мс — машина слабая, и пока камера
   * движется, кадр облегчённый: без мягких теней углов, ореола и пересчёта
   * теней. Остановилась — снова полный кадр.
   */
  private recordFullQualityFrame(interval: number) {
    if (interval <= 0 || interval > 500) return;
    this.frameIntervals.push(interval);
    if (this.frameIntervals.length > 12) this.frameIntervals.shift();
    if (this.frameIntervals.length < 6) return;
    const sorted = [...this.frameIntervals].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)]!;
    if (median > 40) this.lightweightCameraMotion = true;
    else if (median < 24) this.lightweightCameraMotion = false;
  }

  private syncPreferredCameraDistance() {
    this.preferredCameraDistance = clamp(
      this.camera.position.distanceTo(this.controls.target),
      this.controls.minDistance,
      this.controls.maxDistance
    );
  }

  private restorePreferredCameraDistance() {
    const offset = this.cameraConstraintOffset
      .copy(this.camera.position)
      .sub(this.controls.target);
    const currentDistance = offset.length();
    if (currentDistance < 1e-6) return;
    this.camera.position
      .copy(this.controls.target)
      .addScaledVector(
        offset.multiplyScalar(1 / currentDistance),
        this.preferredCameraDistance
      );
  }

  private applyCameraConstraints() {
    // Где камера стояла на самом деле (после поворота или колеса): по нему
    // решаем, держать её перед мебелью или пустить за неё.
    const previousDistance = this.camera.position.distanceTo(this.controls.target);
    if (this.roomDistanceClamped) {
      // A ceiling or wall may shorten the visible radius temporarily. Restore
      // the user's requested radius before testing the new orbit direction so
      // the camera automatically moves back out as soon as there is room.
      this.restorePreferredCameraDistance();
    } else if (!this.cameraWheelIntent) {
      this.syncPreferredCameraDistance();
    }

    const roomClamped = this.constrainCameraToRoom();
    const furnitureClamped = this.constrainCameraToFurniture(previousDistance);
    this.roomDistanceClamped = roomClamped || furnitureClamped;
    if (!this.roomDistanceClamped) this.syncPreferredCameraDistance();
    this.updateRoomCutaway();
  }

  /**
   * Как в IKEA: камера выше потолка — потолок со светильниками прозрачный;
   * камера за стеной — эта стена прозрачная (за боковую камера уходит при
   * обычном повороте, за заднюю — поверху). Ограничения для
   * изделий по высоте потолка остаются (перепроверка 07.10, v3-01).
   */
  private updateRoomCutaway() {
    if (this.roomCutawaySuspended) return;
    if (this.project) applyRoomSize(this.project);
    const roomHeight = this.project ? mm(ceilingHeightMm(this.project)) : mm(3000);
    const camera = this.camera.position;
    const margin = mm(30);
    // Спрятанное от камеры остаётся в отражениях зеркал: стена или потолок
    // переходят на слой, который видят только камеры зеркал. Раньше их
    // выключали совсем, и при отдалённой камере зеркало переставало
    // отражать комнату (приёмка 08.10).
    const show = (parts: Object3D[], visible: boolean) => parts.forEach((part) => part.traverse((child) => {
      child.visible = true;
      if (visible) child.layers.set(0);
      else child.layers.set(MIRROR_REFLECTION_LAYER);
    }));
    show(this.ceilingParts, camera.y < roomHeight - margin);
    // Стена прячется чуть раньше, чем камера её пересечёт: иначе ближняя
    // плоскость отсечения на миг режет стену пополам.
    const wallMargin = mm(150);
    show(this.wallParts.back, camera.z > ROOM_LAYOUT.backWallZ + wallMargin);
    show(this.wallParts.left, camera.x > ROOM_LAYOUT.leftWallX + wallMargin);
    show(this.wallParts.right, camera.x < ROOM_LAYOUT.rightWallX - wallMargin);
  }

  private constrainCameraToRoom() {
    // Стены — по комнате этого проекта, даже если перед этим считали чужой.
    if (this.project) applyRoomSize(this.project);
    const roomHeight = this.project
      ? mm(ceilingHeightMm(this.project))
      : mm(3000);
    const cameraFloorMargin = mm(60);
    const targetWallMargin = mm(380);
    const targetFloorMargin = mm(380);
    const targetCeilingMargin = mm(180);
    const previousTargetX = this.controls.target.x;
    const previousTargetY = this.controls.target.y;
    const previousTargetZ = this.controls.target.z;
    const nextTargetX = clamp(
      previousTargetX,
      ROOM_LAYOUT.leftWallX + targetWallMargin,
      ROOM_LAYOUT.rightWallX - targetWallMargin
    );
    const nextTargetY = clamp(
      previousTargetY,
      targetFloorMargin,
      roomHeight - targetCeilingMargin
    );
    const nextTargetZ = clamp(
      previousTargetZ,
      ROOM_LAYOUT.backWallZ + targetWallMargin,
      ROOM_LAYOUT.sideWallMaxZ - targetWallMargin
    );

    // Move the camera together with a corrected target. Keeping their relative
    // vector intact prevents the small look-direction jump that used to appear
    // when panning or rotating close to a wall.
    this.camera.position.x += nextTargetX - previousTargetX;
    this.camera.position.y += nextTargetY - previousTargetY;
    this.camera.position.z += nextTargetZ - previousTargetZ;
    this.controls.target.set(nextTargetX, nextTargetY, nextTargetZ);

    const offset = this.cameraConstraintOffset
      .copy(this.camera.position)
      .sub(this.controls.target);
    const requestedDistance = offset.length();
    if (requestedDistance < 1e-6) return false;
    offset.multiplyScalar(1 / requestedDistance);

    let availableDistance = Number.POSITIVE_INFINITY;
    const keepNearest = (candidate: number) => {
      if (candidate >= 0) {
        availableDistance = Math.min(availableDistance, candidate);
      }
    };

    // Как в IKEA: стены и потолок камеру не держат — камера уходит за них, и
    // та стена (или потолок), за которой она оказалась, прячется
    // (updateRoomCutaway). Так видно изделие сбоку и сзади (приёмка 08.10).
    // Пол держит камеру; потолок — нет: над ним камера смотрит сверху,
    // а сам потолок становится прозрачным.
    if (offset.y < -1e-6) {
      keepNearest(
        (cameraFloorMargin - this.controls.target.y) / offset.y
      );
    }

    if (requestedDistance > availableDistance) {
      this.camera.position
        .copy(this.controls.target)
        .addScaledVector(
          offset,
          Math.max(mm(90), availableDistance - mm(8))
        );
      this.camera.lookAt(this.controls.target);
      return true;
    }
    this.camera.lookAt(this.controls.target);
    return false;
  }

  /**
   * Мебель не ловит камеру (приёмка 08.10: поворот и зум «тупили» и застревали
   * за шкафами). Камера на луче от центра вращения: если желаемая точка внутри
   * мебели, камера встаёт перед ней — там, где была, — или сразу за ней.
   * Желаемое расстояние при этом помнится (preferredCameraDistance): как только
   * точка выходит из мебели, камера туда и возвращается — колесо и поворот
   * продолжают работать.
   */
  private constrainCameraToFurniture(previousDistance: number) {
    if (!this.cameraCollisionBoxes.length) {
      this.cameraBoxesChanged = false;
      return false;
    }
    const target = this.controls.target;
    const direction = this.cameraCollisionCandidate.copy(this.camera.position).sub(target);
    const requested = direction.length();
    if (requested < 1e-6) return false;
    direction.multiplyScalar(1 / requested);
    let distance = requested;
    for (let index = 0; index <= this.cameraCollisionBoxes.length; index += 1) {
      const point = this.desiredCameraPosition.copy(target).addScaledVector(direction, distance);
      const box = this.cameraCollisionBoxes.find((candidate) => candidate.containsPoint(point));
      if (!box) break;
      // Отрезок луча внутри коробки.
      let enter = Number.NEGATIVE_INFINITY;
      let exit = Number.POSITIVE_INFINITY;
      (["x", "y", "z"] as const).forEach((axis) => {
        const component = direction[axis];
        if (Math.abs(component) < 1e-9) return;
        const near = ((component > 0 ? box.min[axis] : box.max[axis]) - target[axis]) / component;
        const far = ((component > 0 ? box.max[axis] : box.min[axis]) - target[axis]) / component;
        enter = Math.max(enter, near);
        exit = Math.min(exit, far);
      });
      if (!Number.isFinite(exit)) break;
      // Центр вращения внутри мебели — только наружу. Иначе — с той стороны,
      // где камера уже стояла; после перестройки сцены — с ближней.
      const canStayInFront = enter > this.controls.minDistance + mm(5);
      const inFront = canStayInFront && (this.cameraBoxesChanged
        ? requested - enter <= exit - requested
        : previousDistance <= enter + mm(30));
      distance = inFront ? enter - mm(5) : exit + mm(5);
      if (inFront) break;
    }
    this.cameraBoxesChanged = false;
    if (Math.abs(distance - requested) < 1e-9) return false;
    this.camera.position.copy(target).addScaledVector(direction, distance);
    this.camera.lookAt(target);
    return true;
  }

  private readonly handleContextLost = (event: Event) => {
    event.preventDefault();
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = 0;
    this.reportError({
      code: "context-lost",
      message:
        "Браузер временно остановил 3D. Обычно помогает обновление страницы или включение аппаратного ускорения."
    });
  };

  private readonly handleContextRestored = () => {
    this.readyNotified = false;
    this.lastReportedError = "";
    this.invalidate(24);
  };

  /** Объект стоит в модели сцены (а не в превью или снятой группе). */
  private isInModel(object: Object3D) {
    for (let current: Object3D | null = object; current; current = current.parent) {
      if (current === this.modelRoot) return true;
    }
    return false;
  }

  private invalidate(frames = 1) {
    this.settleFrames = Math.max(this.settleFrames, frames);
    if (this.animationFrame || this.disposed) return;
    this.animationFrame = requestAnimationFrame(this.renderLoop);
  }

  /** Куда плавно доезжает перетаскиваемая деталь наполнения. */
  private itemDragVisualTarget: { object: Object3D; y: number } | null = null;
  /** Последнее движение камеры, мс (performance.now). */
  private cameraMovedAt = 0;
  private fullQualityTimer: number | null = null;
  /** Слабая машина: пока камера движется — облегчённый кадр (31.10). */
  private lightweightCameraMotion = false;
  private frameIntervals: number[] = [];
  private chainedFrame = false;
  private lastLoopTime = 0;

  private updateItemDragVisual() {
    const target = this.itemDragVisualTarget;
    if (!target) return false;
    const delta = target.y - target.object.position.y;
    if (Math.abs(delta) < 0.0004) {
      target.object.position.y = target.y;
      this.itemDragVisualTarget = null;
      this.refreshSelectionBox();
      this.refreshItemHover();
      return false;
    }
    target.object.position.y += delta * 0.38;
    this.refreshSelectionBox();
    this.refreshItemHover();
    return true;
  }

  private readonly renderLoop = (time?: number) => {
    this.animationFrame = 0;
    if (this.disposed) return;
    const doorsAnimating = this.updateDoorAnimations() || this.updateItemDragVisual();
    this.controls.update();
    // Кадры подряд в полном качестве — мерка скорости машины.
    const now = time ?? performance.now();
    if (this.chainedFrame && !this.cameraInMotion()) this.recordFullQualityFrame(now - this.lastLoopTime);
    this.lastLoopTime = now;
    const rendered = this.render();
    if (!rendered) {
      this.settleFrames = 0;
      this.chainedFrame = false;
      return;
    }
    this.settleFrames -= 1;
    this.chainedFrame = this.settleFrames > 0 || doorsAnimating;
    // Изменение камеры во время кадра уже заказало следующий — второй не нужен.
    if (this.chainedFrame && !this.animationFrame) {
      this.animationFrame = requestAnimationFrame(this.renderLoop);
    }
  };

  /** full — всегда полный кадр (снимки, PDF), даже пока камера движется. */
  private render(full = false) {
    try {
      this.updateDimensionLabelScales();
      this.cellLights.update(this.scene, (object) => this.isInModel(object));
      this.modelRoot.traverse((object) => {
        if (object instanceof Reflector) object.forceUpdate = true;
      });
      // Слабая машина и камера движется — облегчённый кадр: без постобработки
      // и без пересчёта теней (от поворота камеры они не меняются).
      const light = !full && this.lightweightCameraMotion && this.cameraInMotion();
      if (this.renderer.shadowMap.autoUpdate === light) {
        this.renderer.shadowMap.autoUpdate = !light;
        if (!light) this.renderer.shadowMap.needsUpdate = true;
      }
      if (light) {
        this.renderer.setRenderTarget(null);
        this.renderer.render(this.scene, this.camera);
      } else if (this.postProcessing) {
        try {
          this.postProcessing.render(
            [this.dimensionRoot, this.overlayRoot],
            [this.roomRoot, this.modelRoot]
          );
        } catch (error) {
          // An optional visual effect must not make the configurator unusable
          // on a device that cannot allocate its render targets.
          console.warn("Enhanced shading unavailable; using standard rendering", error);
          this.postProcessing.dispose();
          this.postProcessing = null;
          this.renderer.setRenderTarget(null);
          this.renderer.render(this.scene, this.camera);
        }
      } else {
        this.renderer.render(this.scene, this.camera);
      }
      this.lastReportedError = "";
      if (this.projectReady && !this.readyNotified) {
        this.readyNotified = true;
        this.options.onReady?.();
      }
      return true;
    } catch (error) {
      this.reportError({
        code: "render-failed",
        message:
          "Не удалось отрисовать 3D-сцену. Обновите приложение и проверьте аппаратное ускорение браузера.",
        technicalMessage: this.errorMessage(error)
      });
      return false;
    }
  }

  private reportError(issue: PlannerSceneIssue) {
    const key = `${issue.code}:${issue.technicalMessage ?? issue.message}`;
    if (key === this.lastReportedError) return;
    this.lastReportedError = key;
    console.error("Planner scene failure", issue);
    this.options.onError?.(issue);
  }

  private reportAssetWarning(
    warning: Omit<PlannerSceneAssetWarning, "technicalMessage">,
    error?: unknown
  ) {
    if (this.disposed) return;
    this.options.onAssetWarning?.({
      ...warning,
      technicalMessage: error ? this.errorMessage(error) : undefined
    });
  }

  private errorMessage(error: unknown) {
    return error instanceof Error ? error.message : String(error);
  }

  private updateDimensionLabelScales() {
    const viewportHeight = Math.max(1, this.container.clientHeight);
    const halfFov = (this.camera.fov * Math.PI) / 360;
    this.camera.getWorldDirection(this.cameraDirection);

    // Все размеры — и постоянные, и при перетаскивании — одного экранного размера.
    const scaleLabel = (object: Object3D) => {
      if (!(object instanceof Sprite) || object.userData.dimensionLabel !== true) {
        return;
      }

      object.getWorldPosition(this.labelWorldPosition);
      this.labelCameraOffset
        .copy(this.labelWorldPosition)
        .sub(this.camera.position);
      const cameraDepth = Math.max(
        0.12,
        this.labelCameraOffset.dot(this.cameraDirection)
      );
      const worldUnitsPerPixel =
        (2 * cameraDepth * Math.tan(halfFov)) / viewportHeight;
      const pixelWidth = Number(object.userData.pixelWidth) || 64;
      const pixelHeight = Number(object.userData.pixelHeight) || 30;

      object.scale.set(
        pixelWidth * worldUnitsPerPixel,
        pixelHeight * worldUnitsPerPixel,
        1
      );
    };
    this.overlayRoot.traverse(scaleLabel);
    if (this.dimensionRoot.visible) this.dimensionRoot.traverse(scaleLabel);
  }

  private loadDecorativeModels() {
    // Decorative assets enter the scene as one complete, deterministic set.
    // This prevents legacy placeholders and partially loaded combinations from
    // changing while the user edits the wardrobe.
    // Декор ячеек стеллажа — отдельный набор: он грузится, только когда в
    // проекте появляется стеллаж (см. requestShelvingDecor).
    const decorativeKeys = [
      ...HANGING_DECORATIVE_MODEL_KEYS,
      ...SHELF_DECORATIVE_MODEL_KEYS,
      "slippers" as const
    ];
    void Promise.all(
      decorativeKeys.map((key) =>
        this.requestDecorativeModel(key, false)
      )
    ).then(() => {
      if (this.disposed) return;
      this.hangingModelsReady = true;
      this.shelfModelsReady = true;
      this.scheduleDecorativeRefresh();
    });
  }

  /** Весь набор декора ячеек стеллажа, одним разом: расстановка не меняется по мере загрузки. */
  private requestShelvingDecor() {
    if (this.shelvingDecorReady || this.shelvingDecorRequested) return;
    this.shelvingDecorRequested = true;
    void Promise.all(
      SHELVING_DECORATIVE_MODEL_KEYS.map((key) => this.requestDecorativeModel(key, false))
    ).then(() => {
      if (this.disposed) return;
      this.shelvingDecorReady = true;
      this.scheduleDecorativeRefresh();
    });
  }

  /** Загрузка моделей петель; после неё сцена перестраивается с ними. */
  private requestHingeModels() {
    if (this.hingeModelRequested) return;
    this.hingeModelRequested = true;
    this.decorativeModelLoader.load(HINGE_MODEL_URL, (gltf) => {
      if (this.disposed) {
        this.disposeObjectResources(gltf.scene);
        return;
      }
      const models = extractHingeModels(gltf.scene);
      this.disposeObjectResources(gltf.scene);
      if (!Object.keys(models).length) return;
      this.hingeModels = models;
      this.scheduleDecorativeRefresh();
    }, undefined, (error) => {
      // Без моделей петли остаются из примитивов — это не ошибка для покупателя.
      console.warn("Hinge models failed to load", error);
    });
  }

  /**
   * Петля по модели (29.10). Ноль модели — грань боковины или стойки, на
   * которой планка; Y — вверх; Z — вглубь корпуса. Планка с плечом — в
   * корпусе (fixedParent), чашка с тягами — в створке (doorParent, координаты
   * относительно её начала в закрытом положении). false — моделей нет.
   */
  private addHingeModels(options: {
    fixedParent: Group | null;
    doorParent: Group;
    application: HingeModelType;
    /** X грани опоры, на которой планка, в координатах корпуса. */
    supportFaceX: number;
    /** С какой стороны от грани опора: +1 — справа (петли справа). */
    sideOut: number;
    /** Z задней плоскости закрытой двери в координатах корпуса. */
    doorBackZ: number;
    /** Начало створки (doorParent) в координатах корпуса при закрытой двери. */
    doorOrigin: { x: number; z: number };
    doorHeight: number;
    baseY: number;
    meta: PickMeta;
  }) {
    const model = this.hingeModels?.[options.application] ?? this.hingeModels?.["full-overlay"];
    if (!model) return null;
    const material = this.getHingeMaterial(this.hingeTone);
    const links: HingeLinkRuntime[] = [];
    // Точка модели (X, Y, Z) → корпус: x = грань + sideOut·X, z = задняя плоскость двери − (Z + 36,5 мм).
    const toCarcass = (point: Vector3, y: number) => new Vector3(
      options.supportFaceX + options.sideOut * point.x, y + point.y,
      options.doorBackZ - HINGE_MODEL_DOOR_BACK_Z - point.z);
    hingeHeightRatios(options.doorHeight / MM).forEach((ratio) => {
      const y = options.baseY + mm(2) + options.doorHeight * ratio;
      const place = (geometry: BufferGeometry, parent: Object3D, originX: number, originY: number, originZ: number) => {
        const mesh = new Mesh(geometry, material);
        mesh.position.set(options.supportFaceX - originX, y - originY, options.doorBackZ - HINGE_MODEL_DOOR_BACK_Z - originZ);
        mesh.scale.set(options.sideOut, 1, -1);
        mesh.userData.pick = options.meta;
        // Геометрия одна на все петли — при перестройке сцены её не освобождать.
        mesh.userData.sharedAsset = true;
        // Зеркало петли не отражает: чашка в 3 мм за лицом двери иначе попадала
        // в допуск отсечки и «проступала» в зеркальном фасаде (29.10).
        mesh.layers.set(MIRROR_BACKING_LAYER);
        parent.add(mesh);
        return mesh;
      };
      if (options.fixedParent) place(model.fixed, options.fixedParent, 0, 0, 0);
      const cup = place(model.door, options.doorParent, options.doorOrigin.x, 0, options.doorOrigin.z);
      // Гнездо под чашку (29.10): в двери — отверстие Ø35, в нём видна чашка.
      if (model.cupCenter) {
        cup.renderOrder = HINGE_HOLE_CONTENT_ORDER;
        const addPart = (geometry: BufferGeometry, partMaterial: Material, z: number, order: number) => {
          const part = new Mesh(geometry, partMaterial);
          part.position.set(model.cupCenter!.x, model.cupCenter!.y, z);
          part.renderOrder = order;
          part.userData.pick = options.meta;
          part.userData.sharedAsset = true;
          part.layers.set(MIRROR_BACKING_LAYER);
          cup.add(part);
          return part;
        };
        const back = -HINGE_MODEL_DOOR_BACK_Z;
        addPart(HINGE_CUP_OPENING_GEOMETRY, this.getHingeHoleMaskMaterial(), back + 0.0002, HINGE_HOLE_MASK_ORDER);
        addPart(HINGE_CUP_OPENING_GEOMETRY, this.getHingeHoleResetMaterial(), back + 0.0002, HINGE_HOLE_RESET_ORDER);
        const pocket = this.getHingePocketMaterial(this.hingeTone);
        addPart(HINGE_CUP_POCKET_WALL_GEOMETRY, pocket, back - HINGE_CUP_POCKET_DEPTH / 2, HINGE_HOLE_CONTENT_ORDER);
        addPart(HINGE_CUP_OPENING_GEOMETRY, pocket, back - HINGE_CUP_POCKET_DEPTH, HINGE_HOLE_CONTENT_ORDER);
      }
      // Тяги: ось у конца плеча неподвижна, второй конец смотрит на чашку.
      if (options.fixedParent && model.links && model.armPin && model.cupPin) {
        const armPin = toCarcass(model.armPin, y);
        const cupClosed = toCarcass(model.cupPin, y);
        const base = Math.atan2(cupClosed.x - armPin.x, cupClosed.z - armPin.z);
        // Ось у плеча → поворот на чашку → растяжение вдоль тяги: расстояние от
        // плеча до чашки по ходу двери меняется, а тяга должна доставать до неё.
        const pivot = new Group();
        pivot.position.copy(armPin);
        const along = new Group();
        along.rotation.y = base;
        const stretch = new Group();
        const back = new Group();
        back.rotation.y = -base;
        pivot.add(along);
        along.add(stretch);
        stretch.add(back);
        options.fixedParent.add(pivot);
        place(model.links, back, armPin.x, armPin.y, armPin.z).renderOrder = HINGE_HOLE_CONTENT_ORDER;
        // Точка у чашки — в координатах самой чашки (модели): так она едет с дверью.
        links.push({ pivot, stretch, armPin, cupPin: model.cupPin.clone(), doorPart: cup, base,
          length: Math.max(1e-4, Math.hypot(cupClosed.x - armPin.x, cupClosed.z - armPin.z)) });
      }
    });
    return links;
  }

  /** Тяга петли поворачивается вокруг оси у плеча на точку у чашки. */
  private updateHingeLink(link: HingeLinkRuntime) {
    // Точка у чашки — из координат чашки в координаты корпуса, где ось тяги.
    const frame = link.pivot.parent;
    if (!frame) return;
    link.doorPart.updateWorldMatrix(true, false);
    frame.updateWorldMatrix(true, false);
    const point = frame.worldToLocal(link.doorPart.localToWorld(link.cupPin.clone()));
    link.pivot.rotation.y = Math.atan2(point.x - link.armPin.x, point.z - link.armPin.z) - link.base;
    link.stretch.scale.z = Math.hypot(point.x - link.armPin.x, point.z - link.armPin.z) / link.length;
  }

  private requestDecorativeModel(
    key: DecorativeModelKey,
    refreshOnLoad = true
  ): Promise<boolean> {
    if (this.decorativeModels.has(key)) return Promise.resolve(true);
    if (this.decorativeModelFailures.has(key)) return Promise.resolve(false);
    const pending = this.decorativeModelRequests.get(key);
    if (pending) return pending;

    const url = DECORATIVE_MODEL_URLS[key];
    const request = new Promise<boolean>((resolve) => {
      this.decorativeModelLoader.load(
        url,
        (gltf) => {
          if (this.disposed) {
            this.disposeObjectResources(gltf.scene);
            resolve(false);
            return;
          }
          this.prepareDecorativeModel(key, gltf.scene);
          this.decorativeModels.set(key, gltf.scene);
          if (refreshOnLoad) this.scheduleDecorativeRefresh();
          resolve(true);
        },
        undefined,
        (error) => {
          this.decorativeModelFailures.add(key);
          this.reportAssetWarning(
            {
              code: "model-load-failed",
              message:
                "Часть декоративного наполнения не загрузилась. Основные функции шкафа доступны.",
              resource: url
            },
            error
          );
          resolve(false);
        }
      );
    });
    this.decorativeModelRequests.set(key, request);
    void request.finally(() => {
      if (this.decorativeModelRequests.get(key) === request) {
        this.decorativeModelRequests.delete(key);
      }
    });
    return request;
  }

  private scheduleDecorativeRefresh() {
    if (!this.project || this.disposed) return;
    if (this.decorativeRefreshTimer !== null) {
      window.clearTimeout(this.decorativeRefreshTimer);
    }
    this.decorativeRefreshTimer = window.setTimeout(() => {
      this.decorativeRefreshTimer = null;
      if (!this.project || this.disposed) return;
      this.modelSignature = "";
      this.setProject(this.project);
    }, 90);
  }

  private prepareDecorativeModel(key: DecorativeModelKey, root: Group) {
    const fabricMaterials = new Map<Material, MeshPhysicalMaterial>();
    const anisotropy = Math.min(
      8,
      this.renderer.capabilities.getMaxAnisotropy()
    );
    root.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      object.castShadow = true;
      object.receiveShadow = true;
      object.userData.sharedAsset = true;
      const sourceMaterials = Array.isArray(object.material)
        ? object.material
        : [object.material];
      const materials = sourceMaterials.map((material) => {
        if (!(material instanceof MeshStandardMaterial) ||
          !material.name.toLowerCase().includes("fabric") || key === "rattanBasket") return material;
        let fabric = fabricMaterials.get(material);
        if (!fabric) {
          fabric = new MeshPhysicalMaterial();
          // Copy the standard PBR channels without reading Physical-only fields
          // from GLB materials. Keep the artist's textures and normal maps.
          MeshStandardMaterial.prototype.copy.call(fabric, material);
          fabric.sheen = key.startsWith("folded") ? 0.32 : 0.22;
          fabric.sheenColor.set(0xdedbd5);
          fabric.sheenRoughness = 0.86;
          fabric.metalness = 0;
          fabricMaterials.set(material, fabric);
        }
        return fabric;
      });
      object.material = Array.isArray(object.material) ? materials : materials[0]!;
      materials.forEach((material) => {
        material.userData.shared = true;
        // Стекло (песочные часы, ваза с эвкалиптом): прозрачность вместо
        // преломления — преломление требует ещё одного прохода отрисовки сцены.
        if (material instanceof MeshPhysicalMaterial && material.transmission > 0) {
          material.transmission = 0;
          material.transparent = true;
          material.opacity = Math.min(material.opacity, 0.3);
          material.depthWrite = false;
          material.roughness = Math.min(material.roughness, 0.1);
        }
        if (material instanceof MeshStandardMaterial) {
          const materialName = material.name.toLowerCase();
          if (materialName.includes("hanger metal")) {
            material.metalness = Math.max(0.68, material.metalness);
            material.roughness = Math.min(0.36, material.roughness);
          }
          material.envMapIntensity = Math.min(
            materialName.includes("hanger metal") ? 0.88 : 0.72,
            material.envMapIntensity
          );
          [
            material.map,
            material.normalMap,
            material.roughnessMap,
            material.metalnessMap
          ].forEach((texture) => {
            if (texture) texture.anisotropy = anisotropy;
          });
        }
      });
    });
    fabricMaterials.forEach((_replacement, original) => original.dispose());
  }

  private createDecorativeModel(
    key: DecorativeModelKey,
    maxSize: Vector3,
    position: Vector3,
    meta: PickMeta,
    seed: string,
    options: {
      anchor?: "bottom" | "top";
      yawRange?: number;
      preserveScale?: boolean;
      tint?: string;
      tintStrength?: number;
      /** Correct an asset's authored facing direction before fitting it. */
      baseYaw?: number;
    } = {}
  ) {
    if (!this.interiorPropsVisible) return null;
    const source = this.decorativeModels.get(key);
    if (!source) {
      void this.requestDecorativeModel(key);
      return null;
    }

    const content = source.clone(true);
    content.rotation.y += options.baseYaw ?? 0;
    if (options.tint) {
      this.tintDecorativeContent(
        content,
        options.tint,
        options.tintStrength ?? 0.18
      );
    }
    content.updateMatrixWorld(true);
    const sourceBox = new Box3().setFromObject(content);
    const sourceSize = sourceBox.getSize(new Vector3());
    if (
      sourceSize.x < 1e-6 ||
      sourceSize.y < 1e-6 ||
      sourceSize.z < 1e-6
    ) {
      return null;
    }

    if (
      options.preserveScale &&
      (sourceSize.x > maxSize.x + 1e-5 ||
        sourceSize.y > maxSize.y + 1e-5 ||
        sourceSize.z > maxSize.z + 1e-5)
    ) {
      return null;
    }
    const scale = options.preserveScale
      ? 1
      : Math.min(
          maxSize.x / sourceSize.x,
          maxSize.y / sourceSize.y,
          maxSize.z / sourceSize.z
        );
    content.scale.multiplyScalar(scale);
    content.updateMatrixWorld(true);
    const fittedBox = new Box3().setFromObject(content);
    const fittedSize = fittedBox.getSize(new Vector3());
    const center = fittedBox.getCenter(new Vector3());
    content.position.x -= center.x;
    content.position.y -=
      options.anchor === "top" ? fittedBox.max.y : fittedBox.min.y;
    content.position.z -= center.z;
    content.traverse((object) => {
      if (object instanceof Mesh) object.userData.pick = meta;
    });

    const wrapper = new Group();
    wrapper.add(content);
    wrapper.position.copy(position);
    const rotationSeed = [...seed].reduce(
      (sum, character) => sum + character.charCodeAt(0),
      0
    );
    const yawRange = options.yawRange ?? 0.036;
    wrapper.rotation.y = (((rotationSeed % 9) - 4) / 4) * yawRange;
    // Предмет на полке отбрасывает мягкую тень от подсветки.
    wrapper.userData.lightOccluder = true;
    return { object: wrapper, size: fittedSize };
  }

  private tintDecorativeContent(root: Object3D, tint: string, strength: number) {
    const tintColor = new Color(tint);
    root.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      const cloneMaterial = (material: Material) => {
        const clone = material.clone();
        clone.userData = {
          ...clone.userData,
          shared: false,
          retainTextures: true
        };
        if (
          clone instanceof MeshStandardMaterial ||
          clone instanceof MeshPhysicalMaterial ||
          clone instanceof MeshBasicMaterial
        ) {
          clone.color.lerp(tintColor, clamp(strength, 0, 1));
        }
        return clone;
      };
      object.material = Array.isArray(object.material)
        ? object.material.map((material) => cloneMaterial(material))
        : cloneMaterial(object.material);
    });
  }

  private disposeDecorativeModels() {
    this.decorativeModels.forEach((model) =>
      this.disposeObjectResources(model)
    );
    this.decorativeModels.clear();
    this.decorativeModelRequests.clear();
    this.decorativeModelFailures.clear();
    this.hangingModelsReady = false;
    this.shelfModelsReady = false;
    this.shelvingDecorReady = false;
    this.shelvingDecorRequested = false;
  }

  private disposeObjectResources(root: Object3D) {
    const geometries = new Set<BufferGeometry>();
    const materials = new Set<
      MeshStandardMaterial | MeshPhysicalMaterial | MeshBasicMaterial
    >();
    const textures = new Set<Texture>();
    root.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      geometries.add(object.geometry);
      const objectMaterials = Array.isArray(object.material)
        ? object.material
        : [object.material];
      objectMaterials.forEach((material) => {
        if (
          material instanceof MeshStandardMaterial ||
          material instanceof MeshPhysicalMaterial ||
          material instanceof MeshBasicMaterial
        ) {
          materials.add(material);
        }
        if (material instanceof MeshStandardMaterial) {
          [
            material.map,
            material.normalMap,
            material.roughnessMap,
            material.metalnessMap,
            material.aoMap,
            material.emissiveMap,
            material.alphaMap
          ].forEach((texture) => {
            if (texture) textures.add(texture);
          });
        }
      });
    });
    textures.forEach((texture) => texture.dispose());
    materials.forEach((material) => material.dispose());
    geometries.forEach((geometry) => geometry.dispose());
  }

  private rebuildRoom(project: PlannerProject) {
    this.disposeChildren(this.roomRoot);
    applyRoomSize(project);
    // Ширина комнаты задаётся в «Комнате»; плиты пола, стен и потолка — по ней.
    const halfWidth = ROOM_LAYOUT.rightWallX;
    const slabWidth = halfWidth * 2 + 0.15;
    this.cellLights.markMaterialsDirty();
    const roomHeight = mm(ceilingHeightMm(project));
    const isEvening = project.room.lighting === "evening";
    const isSunny = project.room.lighting === "sunny";
    this.scene.environment = this.environmentTexture;
    // Reflected light reveals finishes inside cells without recoloring the decor.
    this.scene.environmentIntensity = isEvening ? 0.2 : isSunny ? 0.3 : 0.36;
    // Фон за пределами комнаты — нейтральный, а не цвет стен: при своём
    // ярком цвете стен он заливал весь экран вокруг комнаты (приёмка 04.10, start-08).
    this.scene.background = new Color("#f2f0eb").lerp(
      new Color(isEvening ? "#4b4038" : "#e7e6e1"),
      isEvening ? 0.18 : 0.025
    );
    this.renderer.toneMappingExposure = isEvening ? 1.02 : isSunny ? 1.02 : 1.04;

    const hemi = new HemisphereLight(
      isEvening ? 0xffe4c9 : isSunny ? 0xfffcf5 : 0xf4f7ff,
      isEvening ? 0x57453c : isSunny ? 0xbab4aa : 0xbac0c5,
      isEvening ? 0.3 : 0.6
    );
    this.roomRoot.add(hemi);

    const ambient = new AmbientLight(
      isEvening ? 0xffdec0 : 0xffffff,
      isEvening ? 0.06 : 0.04
    );
    this.roomRoot.add(ambient);

    const keyLight = new DirectionalLight(
      isEvening ? 0xffd9b6 : isSunny ? 0xfff1dc : 0xfffcf7,
      isEvening ? 0.72 : isSunny ? 2.1 : 1.25
    );
    keyLight.position.set(-3.9, 5.1, 4.5);
    keyLight.target.position.set(0.15, 1.05, -0.75);
    keyLight.castShadow = true;
    const shadowMapSize = Math.min(
      2048,
      this.renderer.capabilities.maxTextureSize
    );
    keyLight.shadow.mapSize.set(shadowMapSize, shadowMapSize);
    keyLight.shadow.camera.left = -(halfWidth + 0.625);
    keyLight.shadow.camera.right = halfWidth + 0.625;
    keyLight.shadow.camera.top = 4.1;
    keyLight.shadow.camera.bottom = -4.1;
    keyLight.shadow.camera.near = 0.4;
    keyLight.shadow.camera.far = 13;
    keyLight.shadow.bias = -0.00006;
    keyLight.shadow.normalBias = 0.0025;
    // PCF (not PCFSoft) honors radius, giving overcast/daylight a soft edge.
    keyLight.shadow.radius = isSunny ? 1.4 : 4.5;
    this.roomRoot.add(keyLight, keyLight.target);

    const fill = new DirectionalLight(
      isEvening ? 0x8ca1bf : isSunny ? 0xe3ecf7 : 0xe4ebf2,
      isEvening ? 0.18 : isSunny ? 0.25 : 0.4
    );
    fill.position.set(4.1, 3.4, 2.2);
    fill.target.position.set(-0.4, 1.2, -0.8);
    this.roomRoot.add(fill, fill.target);

    const warmBounce = new PointLight(
      isEvening ? 0xffa85c : isSunny ? 0xffddb5 : 0xe8edf2,
      isEvening ? 0.56 : isSunny ? 0.1 : 0.035,
      6.5,
      2
    );
    warmBounce.position.set(2.8, 2.35, 2.25);
    this.roomRoot.add(warmBounce);

    const wallMaterial = new MeshStandardMaterial({
      map: this.createPlasterTexture(project.room.wallColor),
      roughness: 0.97,
      metalness: 0,
      emissive: new Color(project.room.wallColor),
      emissiveIntensity: isEvening ? 0.006 : 0.002
    });
    const ceilingMaterial = new MeshStandardMaterial({
      color: 0xf8f6f0,
      roughness: 0.98,
      emissive: 0xf8f6f0,
      emissiveIntensity: isEvening ? 0.008 : 0.004
    });
    const floorMaterial = this.createFloorMaterial(project.room.floor);
    const trimMaterial = new MeshStandardMaterial({
      color: 0xe7e1d7,
      roughness: 0.74,
      emissive: 0xe7e1d7,
      emissiveIntensity: isEvening ? 0.006 : 0.003
    });

    this.addPanel(this.roomRoot, 0, -0.018, 0.55, slabWidth, 0.036, 6.2, floorMaterial);
    this.wallParts = {
      back: [this.addPanel(this.roomRoot, 0, roomHeight / 2, -2.25, slabWidth, roomHeight, 0.05, wallMaterial)],
      left: [this.addPanel(this.roomRoot, -(halfWidth + 0.025), roomHeight / 2, 0.55, 0.05, roomHeight, 5.6, wallMaterial)],
      right: [this.addPanel(this.roomRoot, halfWidth + 0.025, roomHeight / 2, 0.55, 0.05, roomHeight, 5.6, wallMaterial)]
    };
    this.ceilingParts = [this.addPanel(this.roomRoot, 0, roomHeight + 0.02, 0.55, slabWidth, 0.04, 5.6, ceilingMaterial)];
    this.addRoomTrim(trimMaterial, roomHeight);
    this.addMirrorReflectionStage(
      roomHeight,
      wallMaterial,
      ceilingMaterial,
      floorMaterial,
      trimMaterial
    );
    this.roomRoot.traverse((object) => {
      if (object instanceof Mesh) object.castShadow = false;
    });

    // Солнечное «пятно окна» на полу убрано: окна в комнате нет, и пятно
    // выглядело как отражение несуществующего окна (приёмка 04.10, start-09).
    if (isSunny && SHOW_SUN_PATCH) {
      const sunPatch = new Mesh(
        new PlaneGeometry(2.8, 1.85),
        new MeshBasicMaterial({
          map: this.createSunPatchTexture(),
          transparent: true,
          opacity: 0.55,
          depthWrite: false
        })
      );
      sunPatch.rotation.x = -Math.PI / 2;
      sunPatch.rotation.z = -0.18;
      sunPatch.position.set(-2.05, 0.0045, 0.95);
      sunPatch.receiveShadow = false;
      this.roomRoot.add(sunPatch);
    }

    [-1.8, 0, 1.8].map((x) => x * Math.min(1, halfWidth / 3.725)).forEach((x) => {
      const light = new PointLight(
        0xffd7a0,
        isEvening ? 1.1 : 0.025,
        3.6,
        2
      );
      light.position.set(x, roomHeight - 0.1, -0.4);
      this.roomRoot.add(light);
      const spot = new Mesh(
        new CylinderGeometry(0.052, 0.061, 0.022, 32),
        new MeshStandardMaterial({
          color: 0x393733,
          roughness: 0.34,
          metalness: 0.58,
          emissive: 0xffc77c,
          emissiveIntensity: isEvening ? 0.48 : 0.035
        })
      );
      spot.position.set(x, roomHeight - 0.005, -0.4);
      this.roomRoot.add(spot);
      this.ceilingParts.push(spot);
    });
  }

  private addMirrorReflectionStage(
    roomHeight: number,
    wallMaterial: MeshStandardMaterial,
    ceilingMaterial: MeshStandardMaterial,
    floorMaterial: PanelMaterial,
    trimMaterial: MeshStandardMaterial
  ) {
    // Keep the reflected wall well behind the open edge of the real room.
    // At the 16 m camera limit a nearby wall projects only a tiny, almost
    // uniform patch into a narrow mirror. The deeper continuation preserves
    // enough floor, wall and window perspective to read as a reflection.
    const frontWallZ = ROOM_LAYOUT.sideWallMaxZ + 6;
    const visibleRoomFrontZ = 3.35;
    const extensionDepth = frontWallZ - visibleRoomFrontZ + 0.18;
    const extensionCenterZ =
      visibleRoomFrontZ + extensionDepth / 2 - 0.09;
    const mirrorOnly = <T extends Object3D>(object: T) => {
      object.traverse((child) => {
        child.layers.set(MIRROR_REFLECTION_LAYER);
        child.userData.mirrorReflectionStage = true;
      });
      return object;
    };

    // The configurator deliberately has no visible front wall so the user can
    // look into the room. A real mirror would nevertheless reflect the room
    // behind the viewer. This continuation closes that missing side only for
    // reflector cameras; the main camera never enables this layer.
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        0,
        roomHeight / 2,
        frontWallZ,
        ROOM_LAYOUT.rightWallX * 2 + 0.15,
        roomHeight,
        0.06,
        wallMaterial
      )
    );
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        0,
        -0.018,
        extensionCenterZ,
        ROOM_LAYOUT.rightWallX * 2 + 0.15,
        0.036,
        extensionDepth,
        floorMaterial
      )
    );
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        0,
        roomHeight + 0.02,
        extensionCenterZ,
        ROOM_LAYOUT.rightWallX * 2 + 0.15,
        0.04,
        extensionDepth,
        ceilingMaterial
      )
    );
    [-(ROOM_LAYOUT.rightWallX + 0.025), ROOM_LAYOUT.rightWallX + 0.025].forEach((x) => {
      mirrorOnly(
        this.addPanel(
          this.roomRoot,
          x,
          roomHeight / 2,
          extensionCenterZ,
          0.05,
          roomHeight,
          extensionDepth,
          wallMaterial
        )
      );
    });

    const windowWidth = 3.1;
    const windowHeight = Math.min(1.68, roomHeight * 0.58);
    const windowX = 0;
    const windowY = Math.min(
      roomHeight - windowHeight / 2 - 0.32,
      1.55
    );
    const windowMaterial = new MeshPhysicalMaterial({
      color: 0xaebfc6,
      roughness: 0.2,
      metalness: 0.03,
      clearcoat: 0.62,
      clearcoatRoughness: 0.16,
      emissive: 0x9baeb6,
      emissiveIntensity: 0.045,
      envMapIntensity: 0.8
    });
    const windowZ = frontWallZ - 0.042;
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        windowX,
        windowY,
        windowZ,
        windowWidth,
        windowHeight,
        0.012,
        windowMaterial
      )
    );

    const frameZ = frontWallZ - 0.052;
    const frameThickness = 0.055;
    [-1, 1].forEach((side) => {
      mirrorOnly(
        this.addPanel(
          this.roomRoot,
          windowX + side * (windowWidth / 2 + frameThickness / 2),
          windowY,
          frameZ,
          frameThickness,
          windowHeight + frameThickness * 2,
          0.028,
          trimMaterial
        )
      );
      mirrorOnly(
        this.addPanel(
          this.roomRoot,
          windowX,
          windowY + side * (windowHeight / 2 + frameThickness / 2),
          frameZ,
          windowWidth,
          frameThickness,
          0.028,
          trimMaterial
        )
      );
    });
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        windowX,
        windowY - windowHeight / 2 - 0.055,
        frontWallZ - 0.095,
        windowWidth + 0.22,
        0.06,
        0.14,
        trimMaterial
      )
    );

    const windowFrameMaterial = new MeshStandardMaterial({
      color: 0x4f5558,
      roughness: 0.38,
      metalness: 0.34
    });
    const mullionDepth = 0.034;
    [-windowWidth / 4, 0, windowWidth / 4].forEach((offsetX) => {
      mirrorOnly(
        this.addPanel(
          this.roomRoot,
          windowX + offsetX,
          windowY,
          frameZ - 0.012,
          0.045,
          windowHeight,
          mullionDepth,
          windowFrameMaterial
        )
      );
    });
    [-windowHeight * 0.19, windowHeight * 0.22].forEach((offsetY) => {
      mirrorOnly(
        this.addPanel(
          this.roomRoot,
          windowX,
          windowY + offsetY,
          frameZ - 0.012,
          windowWidth,
          0.045,
          mullionDepth,
          windowFrameMaterial
        )
      );
    });

    const curtainMaterial = new MeshStandardMaterial({
      color: 0xc5b39f,
      roughness: 0.94,
      metalness: 0
    });
    [-1, 1].forEach((side) => {
      mirrorOnly(
        this.addPanel(
          this.roomRoot,
          windowX + side * (windowWidth / 2 + 0.22),
          windowY - 0.04,
          frontWallZ - 0.085,
          0.34,
          windowHeight + 0.2,
          0.055,
          curtainMaterial
        )
      );
    });

    const consoleMaterial = new MeshStandardMaterial({
      color: 0x80664f,
      roughness: 0.76,
      metalness: 0
    });
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        0,
        0.34,
        frontWallZ - 0.22,
        2.25,
        0.56,
        0.38,
        consoleMaterial
      )
    );
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        0,
        0.64,
        frontWallZ - 0.25,
        2.34,
        0.055,
        0.44,
        consoleMaterial
      )
    );

    const rugMaterial = new MeshStandardMaterial({
      color: 0xa99177,
      roughness: 1,
      metalness: 0
    });
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        0,
        0.006,
        frontWallZ - 3.05,
        2.8,
        0.012,
        2.25,
        rugMaterial
      )
    );
    mirrorOnly(
      this.addPanel(
        this.roomRoot,
        0,
        0.07,
        frontWallZ - 0.055,
        ROOM_LAYOUT.rightWallX * 2,
        0.14,
        0.04,
        trimMaterial
      )
    );
  }

  private rebuildModel(project: PlannerProject) {
    this.itemDragVisualTarget = null;
    this.disposeChildren(this.modelRoot);
    this.cellLights.clear();
    this.disposeChildren(this.dimensionRoot);
    this.disposeChildren(this.overlayRoot);
    this.moduleObjects.clear();
    this.itemObjects.clear();
    this.shelvingCellObjects.clear();
    this.doorObjects.clear();
    this.selectionHelper = null;
    this.selectionTarget = null;

    const layout = createCompositionLayout(project);
    const highest = mm(maxHeight(project));

    project.modules.forEach((module) => {
      const moduleRoot = new Group();
      moduleRoot.name = module.id;
      // Кромка корпуса этого изделия (24.10): действует, пока оно строится.
      const edgeTarget = edgeFinishTarget(project, module);
      this.edgeOverride = edgeTarget
        ? {
            code: module.decor ?? project.carcassDecor ?? "",
            key: edgeTarget.kind === "decor" ? `decor:${edgeTarget.decor}` : `color:${edgeTarget.hex}`,
            edge: edgeTarget.kind === "decor" ? this.getDecorEdgeMaterial(edgeTarget.decor) : this.getSolidEdgeMaterial(edgeTarget.hex)
          }
        : null;
      const placements = layout.placements.filter(
        (placement) => placement.module.id === module.id
      );

      if (module.type === "corner45" && placements.length === 2) {
        moduleRoot.add(
          this.buildCornerModule(
            module,
            placements[0]!,
            placements[1]!,
            module.decor ?? project.carcassDecor ?? ""
          )
        );
      } else {
        placements.forEach((placement) => {
          const moduleGroup =
            module.type === "shelving"
              ? this.buildShelvingModule(
                  module,
                  module.decor ?? project.carcassDecor ?? ""
                )
              : module.type === "end"
                ? this.buildEndModule(
                    module,
                    module.decor ?? project.carcassDecor ?? ""
                  )
              : this.buildModule(
                  module,
                  module.decor ?? project.carcassDecor ?? "",
                  placement.includeItems
                );
          if (
            placement.wing === 0 &&
            module.type !== "mezzanine" &&
            module.type !== "corner45" &&
            module.type !== "shelving" &&
            module.type !== "end"
          ) {
            const mezzanine = mezzanineFor(project, module.id);
            this.addInstallationPanels(
              moduleGroup,
              module,
              mm(module.height + (mezzanine?.height ?? 0)),
              module.decor ?? project.carcassDecor ?? "",
              { kind: "module", moduleId: module.id },
              placement,
              mezzanine
                ? {
                    height: mm(mezzanine.height),
                    materialKey:
                      mezzanine.decor ?? project.carcassDecor ?? "",
                    meta: { kind: "module", moduleId: mezzanine.id }
                  }
                : undefined
            );
          }
          moduleGroup.position.set(placement.x, placement.y, placement.z);
          moduleGroup.rotation.y = placement.yaw;
          moduleRoot.add(moduleGroup);
        });
      }

      this.edgeOverride = null;
      this.modelRoot.add(moduleRoot);
      this.moduleObjects.set(module.id, moduleRoot);
    });

    this.refreshDoorCollisionLimits();

    if (layout.placements.length) {
      const contactShadowMaterial = new MeshBasicMaterial({
        map: this.createContactShadowTexture(),
        transparent: true,
        opacity: 0.25,
        depthWrite: false
      });
      layout.placements
        .filter(
          (placement) =>
            placement.y <= mm(4) && placement.module.type !== "mezzanine"
        )
        .forEach((placement) => {
          const width = mm(installationSpanMm(placement.module));
          const depth = mm(placement.module.depth);
          const contactRoot = new Group();
          contactRoot.userData.aiModuleId = placement.module.id;
          contactRoot.position.set(placement.x, 0, placement.z);
          contactRoot.rotation.y = placement.yaw;

          const contactShadow = new Mesh(
            new PlaneGeometry(width + mm(100), depth + mm(120)),
            contactShadowMaterial
          );
          contactShadow.rotation.x = -Math.PI / 2;
          contactShadow.position.set(0, 0.0035, -depth / 2);
          contactShadow.castShadow = false;
          contactShadow.receiveShadow = false;
          contactShadow.renderOrder = -1;
          contactRoot.add(contactShadow);
          this.modelRoot.add(contactRoot);
        });

      this.addDimensionOverlays(layout, highest);
    }
    const nextBoundsSignature = JSON.stringify({
      minX: Math.round(layout.bounds.minX * 1000),
      maxX: Math.round(layout.bounds.maxX * 1000),
      minZ: Math.round(layout.bounds.minZ * 1000),
      maxZ: Math.round(layout.bounds.maxZ * 1000),
      highest: Math.round(highest * 1000)
    });
    if (!layout.placements.length) {
      this.layoutBoundsSignature = "";
    } else if (nextBoundsSignature !== this.layoutBoundsSignature) {
      const isInitialLayout = this.layoutBoundsSignature.length === 0;
      this.layoutBoundsSignature = nextBoundsSignature;
      // Center the first loaded composition only. Re-centering after every
      // edit makes a manually positioned camera jump when a module is dropped.
      if (isInitialLayout) {
        this.controls.target.set(
          (layout.bounds.minX + layout.bounds.maxX) * 0.5,
          highest * 0.48,
          (layout.bounds.minZ + layout.bounds.maxZ) / 2
        );
        if (project.modules.some(module => module.type === "corner45")) this.setView("perspective");
      }
    }
    this.refreshCameraCollisionBoxes(layout.placements);
    this.applyCameraConstraints();
    this.assignCellLightOccluders();
    // Рамка наведения держалась за старые детали — перевесить на новые.
    if (this.hoveredBoardState) this.setHoveredBoard(this.hoveredBoardState);
  }

  /**
   * Предметы в подсвеченных отсеках — вазы, книги, шкатулки — становятся
   * сферами-заслонками: подсветка даёт от них мягкую тень на полку и задник.
   */
  private assignCellLightOccluders() {
    const volumes = this.cellLights.all;
    if (!volumes.length) return;
    this.modelRoot.updateMatrixWorld(true);
    const owners = new Set(volumes.map((volume) => volume.owner));
    const inverse = new Matrix4();
    const center = new Vector3();
    owners.forEach((owner) => {
      inverse.copy(owner.matrixWorld).invert();
      const boxes: Box3[] = [];
      owner.traverse((object) => {
        if (!object.userData.lightOccluder || !object.visible) return;
        const box = new Box3().setFromObject(object);
        if (!box.isEmpty()) boxes.push(box.applyMatrix4(inverse));
      });
      volumes.filter((volume) => volume.owner === owner).forEach((volume) => {
        const inside = new Box3(volume.min, volume.max);
        volume.occluders = boxes
          .filter((box) => inside.containsPoint(box.getCenter(center)))
          .flatMap((box) => occluderSpheres(box));
      });
    });
  }

  private refreshCameraCollisionBoxes(placements: ModulePlacement[]) {
    this.cameraCollisionBoxes.length = 0;
    const cameraRadius = mm(105);

    placements.forEach((placement) => {
      const width = mm(placement.module.width);
      const height = mm(
        placement.module.height + topFillerMm(placement.module)
      );
      const depth = mm(placement.module.depth);
      const frontAllowance =
        placement.module.door === "open" ? mm(12) : mm(42);
      const cosine = Math.cos(placement.yaw);
      const sine = Math.sin(placement.yaw);
      const box = new Box3();

      [
        -width / 2 - mm(leftFillerMm(placement.module)),
        width / 2 + mm(rightFillerMm(placement.module))
      ].forEach((localX) => {
        [-depth, frontAllowance].forEach((localZ) => {
          [0, height].forEach((localY) => {
            this.cameraCollisionCandidate.set(
              placement.x + localX * cosine + localZ * sine,
              placement.y + localY,
              placement.z - localX * sine + localZ * cosine
            );
            box.expandByPoint(this.cameraCollisionCandidate);
          });
        });
      });

      if (!box.isEmpty()) {
        box.expandByScalar(cameraRadius);
        this.cameraCollisionBoxes.push(box);
      }
    });
    this.cameraBoxesChanged = true;
  }

  private addDimensionOverlays(
    layout: ReturnType<typeof createCompositionLayout>,
    highest: number
  ) {
    const existingOverlayObjects = new Set(this.overlayRoot.children);
    this.addIkeaDimensions(layout.bounds, highest, layout.placements);
    this.addModuleDimensions(layout.placements);
    this.addModuleGapDimensions(layout.placements);
    this.addModuleDepthDimensions(layout.placements);
    this.addInternalItemDimensions(layout.placements);
    this.overlayRoot.children
      .filter((object) => !existingOverlayObjects.has(object))
      .forEach((object) => this.dimensionRoot.add(object));
  }

  private refreshOverlayMeasurements() {
    if (!this.project) return;
    this.disposeChildren(this.dimensionRoot);
    const layout = createCompositionLayout(this.project);
    this.addDimensionOverlays(layout, mm(maxHeight(this.project)));
    this.updateSelection(
      this.project.selectedModuleId,
      this.project.selectedItemId,
      this.project.selectedShelvingCellId
    );
  }

  private buildModule(
    module: WardrobeModule,
    materialKey: string,
    includeItems = true
  ) {
    const group = new Group();
    group.name = module.id;
    const width = mm(module.width);
    const height = mm(module.height);
    const depth = mm(module.depth);
    const thickness = mm(CARCASS_MM);
    const plinthHeight =
      module.type === "mezzanine" ? 0 : mm(cabinetBaseHeightMm(module));
    const sideBottom =
      cabinetBaseType(module) === "legs" ? plinthHeight : 0;
    const sideHeight = Math.max(thickness, height - sideBottom);
    const sideCenterY = sideBottom + sideHeight / 2;
    const innerBodyHeight = Math.max(thickness, height - plinthHeight - thickness * 2);
    const innerBodyCenterY = plinthHeight + thickness + innerBodyHeight / 2;
    const innerWidth = Math.max(0.1, width - thickness * 2);
    const carcass = this.getDecorMaterial(materialKey);
    const moduleMeta: PickMeta = { kind: "module", moduleId: module.id };

    this.addPanel(group, -width / 2 + thickness / 2, sideCenterY, -depth / 2, thickness, sideHeight, depth, carcass, moduleMeta);
    this.addPanel(group, width / 2 - thickness / 2, sideCenterY, -depth / 2, thickness, sideHeight, depth, carcass, moduleMeta);
    if (this.project && supportsSideMirror(module)) {
      const mirror = sideMirrorSize(module);
      activeSideMirrors(this.project, module).forEach((side) => {
        const sign = side === "left" ? -1 : 1;
        this.addSideMirror(group, sign * width / 2, mm(mirror.bottom + mirror.height / 2), -depth / 2,
          sign * Math.PI / 2, mm(mirror.width), mm(mirror.height), moduleMeta);
      });
    }
    this.addPanel(group, 0, plinthHeight + thickness / 2, -depth / 2, innerWidth, thickness, depth, carcass, moduleMeta);
    this.addPanel(group, 0, height - thickness / 2, -depth / 2, innerWidth, thickness, depth, carcass, moduleMeta);
    this.addPanel(group, 0, innerBodyCenterY, -depth + mm(4), innerWidth, innerBodyHeight, mm(8),
      this.getDecorMaterial(normalizeBackDecor(module) ?? materialKey), moduleMeta);
    cabinetDividerOffsets(module).forEach((offset) => {
      this.addPanel(
        group,
        mm(offset),
        innerBodyCenterY,
        -depth / 2,
        thickness,
        innerBodyHeight,
        depth,
        carcass,
        moduleMeta
      );
    });
    // Наружные ящики внизу: неподвижная полка над ними в каждом пролёте и сами ящики.
    const cabinetDrawers = module.type === "mezzanine" ? null : cabinetDrawerLayout(module);
    if (cabinetDrawers) {
      cabinetDrawers.bays.forEach((bay) => {
        this.addPanel(group, mm((bay.innerLeft + bay.innerRight) / 2),
          mm((cabinetDrawers.shelfBottomMm + cabinetDrawers.shelfTopMm) / 2), -depth / 2,
          mm(bay.innerRight - bay.innerLeft), thickness, depth, carcass, moduleMeta);
      });
      this.addCabinetDrawers(group, module, cabinetDrawers, depth, materialKey, moduleMeta);
    }
    if (module.type !== "mezzanine") {
      this.addModulePlinth(
        group,
        width,
        depth,
        materialKey,
        moduleMeta,
        module
      );
    }

    this.addModuleEdgeDetails(
      group,
      module,
      width,
      height,
      depth,
      innerWidth,
      thickness,
      plinthHeight,
      sideBottom,
      sideHeight,
      materialKey,
      moduleMeta
    );

    if (includeItems) {
      // Общие стойки идут от дна до крышки: каждая полка, ящик, штанга и
      // корзина ставится в свой отсек, а не насквозь через стойку.
      const layout = withStructuralPartitions(module);
      module.items.forEach((item) => {
        const pieces = item.type === "divider"
          ? undefined
          : usableItemPieces(module, item).map(
              (piece) => [mm(piece.left), mm(piece.right)] as [number, number]
            );
        const itemGroup = this.buildItem(layout, item, innerWidth, depth, materialKey, pieces);
        itemGroup.position.set(
          item.type === "divider" ? mm(item.x ?? 0) : 0,
          mm(item.y),
          0
        );
        group.add(itemGroup);
        this.itemObjects.set(item.id, itemGroup);
        if (item.type === "led" || cabinetShelfLit(item)) this.addWardrobeLedLight(group, module, item, depth, materialKey);
      });
      if (cabinetLightMode(module) === "uprights") this.addWardrobeUprightLights(group, module, depth, materialKey);
    }

    this.addShelfHoles(group, module, width, height, depth, plinthHeight);
    // Единый фасад: створки корпуса идут до верха антресоли, у антресоли своих нет.
    const project = this.project;
    const unifiedUnder = project && module.type === "mezzanine"
      ? hasUnifiedFacade(project, baseModuleFor(project, module))
      : false;
    if (!unifiedUnder) {
      const mezzanine = project && hasUnifiedFacade(project, module) ? mezzanineFor(project, module.id) : undefined;
      this.addDoors(group, module, width, height, depth, materialKey, mezzanine ? mm(mezzanine.height) : 0);
    }
    return group;
  }

  private buildEndModule(
    module: WardrobeModule,
    materialKey: string
  ) {
    const group = new Group();
    group.name = module.id;
    const width = mm(module.width);
    const height = mm(module.height);
    const depth = mm(module.depth);
    const thickness = mm(CARCASS_MM);
    const baseType = cabinetBaseType(module);
    const baseHeight = mm(cabinetBaseHeightMm(module));
    const sideBottom = baseType === "legs" ? baseHeight : 0;
    const sideHeight = Math.max(thickness, height - sideBottom);
    const sideCenterY = sideBottom + sideHeight / 2;
    const adjacentDirection = endModuleSide(module) === "left" ? 1 : -1;
    const adjacentX = adjacentDirection * (width / 2 - thickness / 2);
    const carcass = this.getDecorMaterial(materialKey);
    const meta: PickMeta = { kind: "module", moduleId: module.id };
    const addSupportLegs = (hiddenBehindPlinth: boolean) => {
      const metal = this.getMetalMaterial(
        hiddenBehindPlinth ? 0x555654 : 0x383a3b
      );
      const legHeight = Math.max(mm(24), baseHeight);
      // Опоры за цоколем — внутри рамки, а не в толще планок: раньше они
      // проступали сквозь заднюю и боковую планку тёмными прорезями (приёмка 08.10).
      const points: Array<readonly [number, number]> = hiddenBehindPlinth
        ? [
            [-width * 0.32, -depth + thickness + mm(16)],
            [width * 0.32, -depth + thickness + mm(16)],
            [adjacentX - adjacentDirection * (thickness / 2 + mm(16)),
              -Math.max(mm(28), mm(cabinetBaseSetbackMm(module)) + mm(10)) - mm(16)]
          ]
        : [
            [-width * 0.32, -depth + mm(48)],
            [width * 0.32, -depth + mm(48)],
            [adjacentDirection * width * 0.32, -mm(58)]
          ];
      const topRadius = hiddenBehindPlinth ? mm(8) : mm(13);
      const bottomRadius = hiddenBehindPlinth ? mm(9) : mm(15);
      points.forEach(([x, z]) => {
        const leg = new Mesh(
          new CylinderGeometry(topRadius, bottomRadius, legHeight, 18),
          metal
        );
        leg.position.set(x, legHeight / 2, z);
        leg.castShadow = true;
        leg.receiveShadow = true;
        leg.userData.pick = meta;
        group.add(leg);
      });
    };

    this.addPanel(
      group,
      adjacentX,
      sideCenterY,
      -depth / 2,
      thickness,
      sideHeight,
      depth,
      carcass,
      meta
    );
    // Задник — между примыкающей боковиной и краем (не по центру: раньше у
    // края оставались 8 мм, и из-под задника торчали панели).
    const backCenterX = -adjacentDirection * thickness / 2;
    this.addPanel(
      group,
      backCenterX,
      sideCenterY,
      -depth + mm(END_MODULE_BACK_MM) / 2,
      Math.max(mm(80), width - thickness),
      sideHeight,
      mm(END_MODULE_BACK_MM),
      this.getDecorMaterial(materialKey),
      meta
    );

    const tiers = endModuleTierCount(module);
    const shelfBottom = baseHeight + thickness / 2;
    const shelfTop = height - thickness / 2;
    for (let index = 0; index <= tiers; index += 1) {
      const y = shelfBottom + ((shelfTop - shelfBottom) * index) / tiers;
      this.addEndShelfPlate(
        group,
        module,
        width,
        depth,
        y,
        thickness,
        carcass,
        meta
      );
    }

    if (baseType === "plinth") {
      const plinth = this.getPlinthDecorMaterial(materialKey);
      const setback = mm(cabinetBaseSetbackMm(module));
      const railDepth = Math.max(mm(80), depth - setback);
      const railCenterZ = -(depth + setback) / 2;
      this.addPanel(
        group,
        adjacentX,
        baseHeight / 2,
        railCenterZ,
        thickness,
        baseHeight,
        railDepth,
        plinth,
        meta
      );
      this.addPanel(
        group,
        backCenterX,
        baseHeight / 2,
        -depth + thickness / 2,
        Math.max(mm(80), width - thickness),
        baseHeight,
        thickness,
        plinth,
        meta
      );
      addSupportLegs(true);
    } else if (baseType === "legs") {
      addSupportLegs(false);
    }

    return group;
  }

  private addEndShelfPlate(
    parent: Group,
    module: WardrobeModule,
    width: number,
    depth: number,
    centerY: number,
    thickness: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    // Контур панели — общий со сметой (endModulePlateOutlineMm).
    const outline = endModulePlateOutlineMm(module);
    const shape = new Shape();
    outline.forEach(([x, z], index) => {
      if (index === 0) shape.moveTo(mm(x), mm(z));
      else shape.lineTo(mm(x), mm(z));
    });
    shape.closePath();

    const geometry = new ExtrudeGeometry(shape, {
      depth: thickness,
      bevelEnabled: false,
      curveSegments: 1
    });
    const shelf = new Mesh(geometry, material);
    shelf.rotation.x = Math.PI / 2;
    shelf.position.y = centerY + thickness / 2;
    shelf.castShadow = true;
    shelf.receiveShadow = true;
    shelf.userData.pick = meta;
    parent.add(shelf);
  }

  private buildShelvingModule(
    module: WardrobeModule,
    materialKey: string
  ) {
    const group = new Group();
    group.name = module.id;
    const width = mm(module.width);
    const height = mm(module.height);
    const depth = mm(module.depth);
    const thickness = mm(SHELVING_PANEL_THICKNESS_MM);
    const grid = getShelvingGridLayout(module);
    const diagonalSections = getShelvingDiagonalSections(module, grid);
    // Внутри диагональных секций и объединённых ячеек полок и стоек сетки нет.
    const spanSections = getShelvingSpanSections(module, grid);
    const baseHeight = mm(grid.baseHeight);
    const cellBottom = mm(grid.cellBottom);
    const cellTop = mm(grid.cellTop);
    const innerHeight = Math.max(thickness, cellTop - cellBottom);
    const innerCenterY = (cellBottom + cellTop) / 2;
    const sideBottom = mm(shelvingSideBottomMm(module));
    const sideHeight = mm(shelvingSideHeightMm(module));
    const sideCenterY = sideBottom + sideHeight / 2;
    // Корпус в эмали (28.10) или однотонная отделка готовой модели.
    const finishHex = shelvingCarcassColorHex(module);
    const carcass = finishHex
      ? this.getShelvingFinishMaterial(finishHex)
      : this.getDecorMaterial(materialKey);
    const edge = finishHex ? carcass : this.carcassEdgeMaterial(materialKey);
    // Задник может быть в своём декоре: корпус под дерево, задник чёрный.
    const ownBack = normalizeBackDecor(module);
    const back = ownBack ? this.getDecorMaterial(ownBack) : carcass;
    // Комод: столешница в своём декоре, цоколь — в эмали фасадов (15.10).
    const ownTop = normalizeTopDecor(module);
    const topEnamel = chestTopEnamel(module);
    const top = topEnamel ? this.getShelvingFinishMaterial(topEnamel.hex) : ownTop ? this.getDecorMaterial(ownTop) : carcass;
    const topEdge = topEnamel ? top : ownTop ? this.getDecorEdgeMaterial(ownTop) : edge;
    const enamelPlinth = chestPlinthEnamel(module);
    const plinthMaterial = enamelPlinth
      ? this.getFacadeMaterial("satin", materialKey, enamelPlinth.facadeColor, "plain", width, enamelPlinth.facadeColorHex)
      : carcass;
    const shadow = this.getSoftShadowMaterial(0.065);
    const moduleMeta: PickMeta = { kind: "module", moduleId: module.id };

    // With visible legs the whole carcass starts above the supports. A
    // floor-standing or plinth carcass keeps full-height outer sides.
    this.addPanel(
      group,
      -width / 2 + thickness / 2,
      sideCenterY,
      -depth / 2,
      thickness,
      sideHeight,
      depth,
      carcass,
      moduleMeta
    );
    this.addPanel(
      group,
      width / 2 - thickness / 2,
      sideCenterY,
      -depth / 2,
      thickness,
      sideHeight,
      depth,
      carcass,
      moduleMeta
    );
    this.addPanel(
      group,
      0,
      baseHeight + thickness / 2,
      -depth / 2,
      width - thickness * 2,
      thickness,
      depth,
      carcass,
      moduleMeta
    );
    // Своя столешница комода лежит на боковинах во всю ширину: торцы и кромки в её декоре.
    const topOver = shelvingTopOverSides(module);
    const topWidth = topOver ? width : width - thickness * 2;
    this.addPanel(
      group,
      0,
      height - thickness / 2,
      -depth / 2,
      topWidth,
      thickness,
      depth,
      top,
      moduleMeta
    );

    [-1, 1].forEach((side) => {
      this.addDetailPanel(
        group,
        side * (width / 2 - thickness / 2),
        sideCenterY,
        mm(3),
        thickness + mm(2),
        sideHeight - mm(2),
        mm(8),
        edge,
        moduleMeta
      );
    });
    [baseHeight + thickness / 2, height - thickness / 2].forEach((y, index) => {
      this.addDetailPanel(
        group,
        0,
        y,
        mm(3),
        index === 1 ? topWidth : width - thickness * 2,
        thickness + mm(2),
        mm(8),
        index === 1 ? topEdge : edge,
        moduleMeta
      );
    });

    if (module.shelvingBackPanel) {
      this.addPanel(
        group,
        0,
        innerCenterY,
        -depth + mm(SHELVING_BACK_PANEL_THICKNESS_MM / 2 + 1),
        width - thickness * 2,
        innerHeight,
        mm(SHELVING_BACK_PANEL_THICKNESS_MM),
        back,
        moduleMeta
      );
    }

    // Щелчок по стойке выбирает видимую ячейку справа от неё (у объединённой — её саму).
    const visibleCells = getShelvingCellLayouts(module, grid);
    const visibleCellAt = (column: number, row: number) => visibleCells.find((layout) =>
      column >= layout.column && column < layout.column + layout.columnSpan &&
      row >= layout.row && row < layout.row + layout.rowSpan)?.id;
    grid.columnDividerCenters.forEach((centerMm, dividerIndex) => {
      const divider = new Group();
      divider.position.x = mm(centerMm);
      divider.userData.shelvingDividerDragOwner = true;
      divider.userData.pick = {
        kind: "shelvingDivider",
        moduleId: module.id,
        dividerAxis: "column",
        dividerIndex
      } satisfies PickMeta;
      grid.cellHeights.forEach((cellHeightMm, row) => {
        const hiddenInsideDiagonal = spanSections.some(
          (section) =>
            dividerIndex >= section.column &&
            dividerIndex < section.column + section.columnSpan - 1 &&
            row >= section.row &&
            row < section.row + section.rowSpan
        );
        if (hiddenInsideDiagonal) return;
        const segmentLayout = getShelvingMainDividerSegment(
          module,
          dividerIndex,
          row,
          grid
        );
        if (!segmentLayout) return;
        const centerY = mm(segmentLayout.y);
        const segmentHeight = mm(cellHeightMm) + mm(0.8);
        // У ряда со своими ширинами стойка стоит по-своему (№ 19).
        const rowOffsetX = mm(segmentLayout.x - centerMm);
        const segment = new Group();
        segment.position.set(rowOffsetX, centerY, -depth / 2);
        segment.rotation.y = ((segmentLayout.rotationY ?? 0) * Math.PI) / 180;
        segment.userData.pick = {
          kind: "shelvingDivider",
          moduleId: module.id,
          cellId: visibleCellAt(dividerIndex + 1, row) ?? segmentLayout.cellId,
          dividerAxis: "column",
          dividerIndex,
          dividerRow: row
        } satisfies PickMeta;
        this.addPanel(
          segment,
          0,
          0,
          0,
          thickness,
          segmentHeight,
          depth,
          carcass
        );
        this.addDetailPanel(
          segment,
          0,
          0,
          depth / 2 + mm(3),
          thickness + mm(2),
          segmentHeight,
          mm(8),
          edge
        );
        segment.userData.gridSegment = { axis: "column", index: dividerIndex, segment: row };
        divider.add(segment);
        // Кромка стойки узкая, мышью в неё трудно попасть: невидимая полоса
        // 40 мм перед ней ловит наведение и перетаскивание.
        const strip = this.createGridHitStrip(mm(40), segmentHeight - mm(8), segment.userData.pick as PickMeta);
        strip.position.set(rowOffsetX, centerY, -depth / 2);
        strip.userData.gridRowStrip = row;
        strip.rotation.y = segment.rotation.y;
        strip.children[0]!.position.z = depth / 2 - mm(10);
        divider.add(strip);
      });
      group.add(divider);
    });

    grid.rowDividerCenters.forEach((centerMm, dividerIndex) => {
      const shelf = new Group();
      shelf.position.y = mm(centerMm);
      shelf.userData.shelvingDividerDragOwner = true;
      shelf.userData.pick = {
        kind: "shelvingDivider",
        moduleId: module.id,
        dividerAxis: "row",
        dividerIndex
      } satisfies PickMeta;
      const innerLeft = -width / 2 + thickness;
      const innerRight = width / 2 - thickness;
      const diagonalOpenings = spanSections
        .filter(
          (section) =>
            dividerIndex >= section.row &&
            dividerIndex < section.row + section.rowSpan - 1
        )
        .map((section) => ({
          start: mm(section.left),
          end: mm(section.right)
        }));
      subtractIntervals(innerLeft, innerRight, diagonalOpenings).forEach(
        (interval) => {
        const shelfWidth = interval.end - interval.start;
        const centerX = (interval.start + interval.end) / 2;
        if (shelfWidth < mm(20)) return;
        this.addPanel(
          shelf,
          centerX,
          0,
          -depth / 2,
          shelfWidth,
          thickness,
          depth,
          carcass
        );
        this.addDetailPanel(
          shelf,
          centerX,
          0,
          mm(3),
          shelfWidth,
          thickness + mm(2),
          mm(8),
          edge
        );
      });
      // Невидимые полосы по колонкам: легче навести и понятно, какой
      // участок полки уберёт Delete.
      (grid.rowCellWidths[dividerIndex] ?? grid.cellWidths).forEach((cellWidthMm, column) => {
        const insideSpan = spanSections.some((section) =>
          column >= section.column && column < section.column + section.columnSpan &&
          dividerIndex >= section.row && dividerIndex < section.row + section.rowSpan - 1);
        if (insideSpan) return;
        const strip = this.createGridHitStrip(mm(cellWidthMm) - mm(8), mm(40), {
          kind: "shelvingDivider",
          moduleId: module.id,
          dividerAxis: "row",
          dividerIndex,
          dividerColumn: column
        });
        strip.position.x = mm(gridColumnStart(grid, dividerIndex, column) + cellWidthMm / 2);
        strip.children[0]!.position.z = -mm(10);
        shelf.add(strip);
      });
      group.add(shelf);
    });

    this.addShelvingDiagonalSections(
      group,
      module,
      diagonalSections,
      depth,
      carcass,
      back
    );

    this.addShelvingCellContents(
      group,
      module,
      grid,
      depth,
      materialKey,
      carcass,
      edge,
      finishHex,
      back
    );
    this.addShelvingModuleLighting(group, module, width, height, depth, mm(grid.baseHeight));

    if (module.shelvingBase === "legs") {
      const legMaterial = this.getMetalMaterial(0x383a3b);
      const legHeight = mm(SHELVING_LEG_HEIGHT_MM);
      const legRadius = mm(14);
      const legZ = depth / 2 - mm(54);
      shelvingLegXPositionsMm(module.width).forEach((positionMm) => {
        [-1, 1].forEach((front) => {
          const leg = new Mesh(
            new CylinderGeometry(legRadius, legRadius * 1.12, legHeight, 20),
            legMaterial
          );
          leg.position.set(
            mm(positionMm),
            legHeight / 2,
            -depth / 2 + front * legZ
          );
          leg.castShadow = true;
          leg.receiveShadow = true;
          leg.userData.pick = moduleMeta;
          // Ножки отбрасывают мягкие тени от света на пол.
          leg.userData.lightOccluder = true;
          group.add(leg);
        });
      });
    } else if (module.shelvingBase !== "floor") {
      // Цоколь стеллажа и комода — заподлицо с боковинами (27.10).
      this.addModulePlinth(
        group,
        width,
        depth,
        materialKey,
        moduleMeta,
        undefined,
        plinthMaterial,
        true
      );
    }

    this.addDetailPanel(
      group,
      0,
      baseHeight + thickness + mm(1),
      -mm(15),
      Math.max(mm(80), width - mm(60)),
      mm(3),
      mm(30),
      shadow,
      moduleMeta
    );
    this.addShelvingTv(group, module, moduleMeta);
    return group;
  }

  /** Телевизор на столешнице или на стене над изделием — только для вида, как декор. */
  private addShelvingTv(group: Group, module: WardrobeModule, meta: PickMeta) {
    if (!this.project) return;
    const placement = shelvingTvPlacement(this.project, module);
    if (!placement) return;
    const place = (
      key: "tvStand" | "tvWall" | "soundbar",
      dimensionsMm: readonly [number, number, number],
      bottomMm: number,
      centerZmm: number
    ) => {
      const fitted = this.createDecorativeModel(
        key,
        new Vector3(mm(dimensionsMm[0] + 4), mm(dimensionsMm[1] + 4), mm(dimensionsMm[2] + 4)),
        new Vector3(0, mm(bottomMm), mm(centerZmm)),
        meta,
        `${module.id}:${key}`,
        { anchor: "bottom", preserveScale: true, yawRange: 0 }
      );
      if (!fitted) return;
      fitted.object.name = `shelving-tv:${module.id}:${key}`;
      group.add(fitted.object);
    };
    if (placement.mount === "stand") {
      place("tvStand", SHELVING_TV_MODELS.stand.dimensionsMm, placement.bottomMm, placement.centerZmm);
      if (placement.soundbar) {
        place("soundbar", SHELVING_SOUNDBAR.dimensionsMm, placement.soundbar.bottomMm, placement.soundbar.centerZmm);
      }
    } else {
      place("tvWall", SHELVING_TV_MODELS.wall.dimensionsMm, placement.bottomMm, placement.centerZmm);
    }
  }

  private addShelvingDiagonalSections(
    group: Group,
    module: WardrobeModule,
    sections: ReturnType<typeof getShelvingDiagonalSections>,
    depth: number,
    carcass: PanelMaterial,
    back: PanelMaterial = carcass
  ) {
    const thickness = mm(SHELVING_PANEL_THICKNESS_MM);
    sections.forEach((section) => {
      const meta: PickMeta = {
        kind: "shelvingCell",
        moduleId: module.id,
        cellId: section.cell.id
      };
      const centerX = mm((section.left + section.right) / 2);
      const centerY = mm((section.bottom + section.top) / 2);

      if (section.cell.backPanel && !module.shelvingBackPanel) {
        const backPanel = shelvingCellBackPanelSizeMm(
          section.width,
          section.height
        );
        this.addPanel(
          group,
          centerX,
          centerY,
          -depth + mm(5),
          mm(backPanel.width),
          mm(backPanel.height),
          mm(SHELVING_BACK_PANEL_THICKNESS_MM),
          back,
          meta
        );
      }

      // Доски решётки — общие с техкартой и сметой (shelvingLatticeBoards).
      shelvingLatticeBoards(section, module.depth).forEach((board) => {
        const panel = this.addPanel(
          group,
          centerX + mm(board.x),
          centerY + mm(board.y),
          -depth / 2 + mm(5),
          mm(board.length),
          thickness,
          mm(board.depth),
          carcass
        );
        // Real lattice panels are long, slotted half way through and fitted
        // into one another. Keep each 3D board whole; metadata is assigned
        // after construction so addPanel uses a square-edged box rather than
        // rounding every end and creating visible gaps at the joints.
        panel.userData.pick = meta;
        panel.rotation.z = board.angle;
      });
    });
  }

  private addShelvingCellContents(
    group: Group,
    module: WardrobeModule,
    grid: ReturnType<typeof getShelvingGridLayout>,
    depth: number,
    materialKey: string,
    carcass: PanelMaterial,
    edge: PanelMaterial,
    finishHex?: string,
    back: PanelMaterial = carcass
  ) {
    const cellConfigs = new Map(
      (module.shelvingCells ?? []).map((cell) => [cell.id, cell])
    );
    const diagonalSections = getShelvingDiagonalSections(module, grid);
    const diagonalCellIds = new Set(
      diagonalSections.flatMap((section) => section.coveredCellIds)
    );
    const hitMaterial = this.getShelvingCellHitMaterial();

    // Цвет стенок и задника: от него зависит, сколько света отражается в отсеке.
    const wallColor = new Color(finishHex ?? resolveDecor(materialKey).hex);
    const ownBack = normalizeBackDecor(module);
    const backColor = ownBack ? new Color(resolveDecor(ownBack).hex) : wallColor;
    // Обычные и объединённые ячейки: у объединённой проём — вся её площадь.
    getShelvingCellLayouts(module, grid).forEach((layout) => {
      {
        const { id, column, row } = layout;
        const cellWidthMm = layout.width;
        const cellHeightMm = layout.height;
        const cellLeftMm = layout.left;
        const cellBottomMm = layout.bottom;
        const meta: PickMeta = {
          kind: "shelvingCell",
          moduleId: module.id,
          cellId: id
        };
        const config = layout.config;
        const x = mm(cellLeftMm + cellWidthMm / 2);
        const y = mm(cellBottomMm + cellHeightMm / 2);
        const cellWidth = mm(cellWidthMm);
        const cellHeight = mm(cellHeightMm);
        void column; void row;

        if (config?.backPanel && !module.shelvingBackPanel) {
          const backPanel = shelvingCellBackPanelSizeMm(
            cellWidthMm,
            cellHeightMm
          );
          this.addPanel(
            group,
            x,
            y,
            -depth + mm(5),
            mm(backPanel.width),
            mm(backPanel.height),
            mm(8),
            back,
            meta
          );
        }

        const facadeRect = config?.content === "door" || config?.content === "drawers"
          ? shelvingCellFacadeRect(module, layout, grid)
          : null;
        // Вкладная дверца стоит в проёме: полки и перегородки за ней отодвинуты.
        const recess = facadeRect?.mount === "inset" && config?.content === "door" ? SHELVING_INSET_BOARD_RECESS : 0;
        const cellBoards = config ? getShelvingCellBoards(config, cellWidthMm, cellHeightMm, module.depth) : [];
        cellBoards.filter(board => board.kind === "divider").forEach(upright => {
          const dividerX = mm(cellLeftMm + upright.x);
          const divider = new Group();
          const boardMeta: PickMeta = { ...meta, kind: "shelvingCellDivider", boardRef: upright.ref };
          const boardDepth = Math.max(mm(60), mm(upright.depth) - recess);
          divider.position.set(dividerX, y, -depth / 2 - recess / 2);
          divider.rotation.y = ((upright.rotationY ?? 0) * Math.PI) / 180;
          divider.userData.pick = boardMeta;
          divider.userData.shelvingBoard = { cellId: id, ref: upright.ref };
          this.addPanel(divider, 0, 0, 0,
            mm(upright.width), mm(upright.height), boardDepth, carcass, boardMeta);
          this.addDetailPanel(divider, 0, 0, boardDepth / 2 + mm(3),
            mm(upright.width + 2), mm(upright.height), mm(8),
            edge, boardMeta);
          group.add(divider);
        });
        // Каждая полка ячейки — своя группа: её можно тянуть, подсветить и удалить.
        const shelfRefs = [...new Set(cellBoards.filter(board => board.kind === "shelf").map(board => board.ref))];
        shelfRefs.forEach(ref => {
          const boards = cellBoards.filter(board => board.kind === "shelf" && board.ref === ref);
          const shelf = new Group();
          shelf.position.y = mm(cellBottomMm + boards[0]!.y);
          const shelfMeta = {
            kind: "shelvingCellShelf",
            moduleId: module.id,
            cellId: id,
            boardRef: ref
          } satisfies PickMeta;
          shelf.userData.pick = shelfMeta;
          shelf.userData.shelvingBoard = { cellId: id, ref };
          boards.forEach(board => {
            const shelfX = mm(cellLeftMm + board.x);
            if (board.glass) {
              this.addGlassShelf(shelf, shelfX, -depth / 2 - recess / 2, mm(board.width),
                mm(board.height), Math.max(mm(80), depth - mm(10) - recess));
              return;
            }
            this.addPanel(
              shelf,
              shelfX,
              0,
              -depth / 2 - recess / 2,
              mm(board.width),
              mm(SHELVING_PANEL_THICKNESS_MM),
              Math.max(mm(80), depth - mm(10) - recess),
              carcass
            );
            this.addDetailPanel(
              shelf,
              shelfX,
              0,
              mm(3) - recess,
              mm(board.width),
              mm(20),
              mm(8),
              edge
            );
          });
          group.add(shelf);
        });

        // Комод (16.10): колонка из двух частей — сверху откидной вверх, полка, снизу ящики.
        const split = config?.content === "drawers" ? chestCellSplit(module, config, layout, grid) : null;
        if (config?.content === "drawers" && facadeRect && split) {
          this.addShelvingCellDrawers(group, module, config, x, mm(cellBottomMm), cellWidth,
            mm(split.drawersClearMm), depth, materialKey, meta, split.drawersRect, finishHex);
          const shelf = new Group();
          shelf.position.y = mm(cellBottomMm + split.shelfY);
          shelf.userData.pick = meta;
          this.addPanel(shelf, x, 0, -depth / 2, cellWidth - mm(2), mm(SHELVING_PANEL_THICKNESS_MM),
            Math.max(mm(80), depth - mm(10)), carcass, meta);
          group.add(shelf);
          const flapConfig: ShelvingCellConfig = config.chestTopFlapGlass
            ? { ...config, facade: "glass", facadePattern: "plain", handle: config.handle === "integrated" ? "bar" : config.handle }
            : config;
          const front = flapFront("up", config.doorLift, split.flapHeightMm, Infinity, CHEST_TOP_FLAP_EXCLUDED_LIFTS);
          if (front) this.addShelvingCellFlap(group, module, flapConfig, split.flapRect, front, materialKey, finishHex);
        } else if (config?.content === "drawers" && facadeRect) {
          this.addShelvingCellDrawers(
            group,
            module,
            config,
            x,
            mm(cellBottomMm),
            cellWidth,
            cellHeight,
            depth,
            materialKey,
            meta,
            facadeRect,
            finishHex
          );
        } else if (config?.content === "door" && facadeRect) {
          this.addShelvingCellDoor(
            group,
            module,
            config,
            facadeRect,
            materialKey,
            meta,
            finishHex
          );
        } else {
          this.addShelvingCellDecorativeProps(
            group,
            module,
            config,
            id,
            cellLeftMm,
            cellBottomMm,
            cellWidthMm,
            cellHeightMm,
            depth / MM,
            cellBoards,
            meta
          );
        }

        const hit = new Mesh(
          new BoxGeometry(
            Math.max(mm(30), cellWidth - mm(8)),
            Math.max(mm(30), cellHeight - mm(8)),
            Math.max(mm(30), depth - mm(20))
          ),
          hitMaterial
        );
        hit.position.set(x, y, -depth / 2);
        hit.castShadow = false;
        hit.receiveShadow = false;
        hit.userData.pick = meta;
        group.add(hit);
        this.shelvingCellObjects.set(id, hit);
        if (shelvingCellLighting(config) !== "none") {
          this.addShelvingCellLighting(group, layout, config!, depth, meta, {
            walls: wallColor,
            back: backColor,
            openBack: !module.shelvingBackPanel && !config?.backPanel
          }, shelvingCellLightTopClearMm(module, layout, grid));
        }
      }
    });

    diagonalSections.forEach((section) => {
      const meta: PickMeta = {
        kind: "shelvingCell",
        moduleId: module.id,
        cellId: section.cell.id
      };
      const hit = new Mesh(
        new BoxGeometry(
          Math.max(mm(30), mm(section.width) - mm(8)),
          Math.max(mm(30), mm(section.height) - mm(8)),
          Math.max(mm(30), depth - mm(20))
        ),
        hitMaterial
      );
      hit.position.set(
        mm((section.left + section.right) / 2),
        mm((section.bottom + section.top) / 2),
        -depth / 2
      );
      hit.castShadow = false;
      hit.receiveShadow = false;
      hit.userData.pick = meta;
      group.add(hit);
      this.shelvingCellObjects.set(section.cell.id, hit);
    });
  }

  /**
   * Светящаяся часть профиля — рассеиватель. Он ярче всего в сцене: после
   * тонирования почти белый, с оттенком выбранного света.
   */
  private getLightingMaterial(kind: "strip", temperature: LightTemperature) {
    const key = `${kind}:${temperature}`;
    const cached = this.lightingMaterials.get(key);
    if (cached) return cached;
    const color = new Color(LIGHT_TEMPERATURES[temperature].strip).multiplyScalar(LED_DIFFUSER_BRIGHTNESS);
    const material = new MeshBasicMaterial({ color });
    this.lightingMaterials.set(key, material);
    return material;
  }

  /** Цвет света с учётом силы: тёплый или холодный, на метр ленты. */
  private lightLineColor(temperature: LightTemperature, intensity: number) {
    // При равной RGB-мощности холодная лента на экране кажется ярче и раньше
    // выбеливает декор. Компенсация выравнивает 3000 K и 6000 K по восприятию.
    const perceivedBalance = temperature === "cold" ? 0.75 : 1;
    return new Color(LIGHT_TEMPERATURES[temperature].light).multiplyScalar(intensity * perceivedBalance);
  }

  /**
   * Переотражённый свет в отсеке: часть света ленты отражается от дна,
   * стенок и задника и ровно подсвечивает весь отсек. Светлый декор
   * отражает много, чёрный — почти ничего.
   */
  private cellBounceLight(lights: CellLightLine[], size: Vector3, walls: Color, back: Color, openBack: boolean) {
    const { x: width, y: height, z: depth } = size;
    const backArea = openBack ? 0 : width * height;
    const sideArea = 2 * width * depth + 2 * height * depth;
    const area = Math.max(1e-4, backArea + sideArea);
    const opening = width * height * (openBack ? 2 : 1);
    const albedo = new Color(0, 0, 0)
      .add(walls.clone().multiplyScalar(sideArea / area))
      .add(back.clone().multiplyScalar(backArea / area));
    const reflectance = Math.min(0.85, albedo.r * 0.2126 + albedo.g * 0.7152 + albedo.b * 0.0722);
    // Поток: у рассеивателя π·I·L, у светильника с узким лучом 2π·I·L/(n+1).
    const flux = new Color(0, 0, 0);
    lights.forEach((light) => {
      const length = light.a.distanceTo(light.b);
      const share = light.lobe <= 1 ? Math.PI : (2 * Math.PI) / (light.lobe + 1);
      flux.add(light.color.clone().multiplyScalar(share * length));
    });
    const kept = reflectance * (area / (area + opening));
    // Многократные отражения усиливают свет в белом отсеке, но не больше чем в полтора раза:
    // иначе белая ячейка превращается в ровный световой короб.
    return flux.multiply(albedo).multiplyScalar(CELL_BOUNCE_SHARE / area * Math.min(1.5, 1 / Math.max(0.2, 1 - kept)));
  }

  private addShelvingCellLighting(
    group: Group,
    layout: { left: number; bottom: number; width: number; height: number },
    config: ShelvingCellConfig,
    depth: number,
    meta: PickMeta,
    surfaces: { walls: Color; back: Color; openBack: boolean },
    /** Под верхом ячейки стоят механизмы откидного фасада — профили в стойках ниже них. */
    topClearMm = 0
  ) {
    const lighting = shelvingCellLighting(config);
    if (lighting === "none") return;
    const temperature = normalizeLightTemperature(config.lightTemperature);
    const position = normalizeLightPosition(config.lightPosition);
    const inset = mm(LED_PROFILE_INSET_MM);
    // Ось профиля: у передней кромки или у задней стенки ячейки.
    const lineZ = position === "front" ? -mm(LED_PROFILE_EDGE_MM) : -depth + mm(LED_PROFILE_EDGE_MM);
    // Рассеиватель смотрит в ячейку и назад, как угловой профиль: задник
    // освещён заметно, а не только дно; у задней стенки — сильнее на задник.
    const backTilt = position === "front" ? 0.45 : 0.5;
    const profile = this.getMetalMaterial(0xb8bbbd);
    const strip = this.getLightingMaterial("strip", temperature);
    const lineColor = this.lightLineColor(temperature, LED_STRIP_INTENSITY);
    const lighting3d = new Group();
    lighting3d.userData.shelvingLighting = { lighting, temperature, position };
    const top = lighting === "horizontal";
    const sides = lighting === "vertical";

    // Профили — по отсекам между перегородками на всю высоту ячейки; свет
    // заперт в зонах: непрозрачная доп. полка его перекрывает, стекло — нет (10.10).
    const zones = shelvingLightZones(config, layout.width, layout.height);
    const lip = mm(LED_DIFFUSER_LIP_MM);
    const backZ = surfaces.openBack ? -depth - mm(60) : -depth;
    shelvingLightCompartments(config, layout.width, layout.height).forEach((part) => {
      const left = mm(layout.left + part.left);
      const right = mm(layout.left + part.right);
      const bottom = mm(layout.bottom + part.bottom);
      const upper = mm(layout.bottom + part.top);
      const width = right - left;
      const centerX = (left + right) / 2;
      const topLength = Math.max(mm(20), width - inset * 2);
      const topLightY = upper - mm(6) - lip - mm(0.8);
      if (top) {
        this.addDetailPanel(lighting3d, centerX, upper - mm(3), lineZ, topLength, mm(6), mm(16), profile, meta);
        this.addDetailPanel(lighting3d, centerX, upper - mm(6) - lip / 2, lineZ, topLength - mm(4), lip, mm(12), strip, meta);
      }
      const sideClear = topClearMm > 0 ? mm(topClearMm) : 0;
      const sideFrom = bottom + inset;
      const sideTo = upper - inset - sideClear;
      const hasSides = sides && sideTo - sideFrom > mm(20);
      if (hasSides) {
        const middle = (sideFrom + sideTo) / 2;
        [1, -1].forEach((side) => {
          const x = side > 0 ? left + mm(3) : right - mm(3);
          this.addDetailPanel(lighting3d, x, middle, lineZ, mm(6), sideTo - sideFrom, mm(16), profile, meta);
          this.addDetailPanel(lighting3d, x + side * (mm(3) + lip / 2), middle, lineZ, lip, sideTo - sideFrom - mm(4), mm(12), strip, meta);
        });
      }
      zones.filter((zone) => Math.abs(zone.left - part.left) < 0.5 && Math.abs(zone.right - part.right) < 0.5).forEach((zone) => {
        const zoneBottom = mm(layout.bottom + zone.bottom);
        const zoneTop = mm(layout.bottom + zone.top);
        const lights: CellLightLine[] = [];
        if (top && zone.top >= part.top - 0.5) {
          lights.push({
            a: new Vector3(centerX - topLength / 2 + mm(2), topLightY, lineZ),
            b: new Vector3(centerX + topLength / 2 - mm(2), topLightY, lineZ),
            direction: new Vector3(0, -1, -backTilt).normalize(),
            color: lineColor,
            lobe: 1
          });
        }
        const from = Math.max(sideFrom + mm(2), zoneBottom + mm(2));
        const to = Math.min(sideTo - mm(2), zoneTop - mm(2));
        if (hasSides && to - from > mm(20)) {
          [1, -1].forEach((side) => {
            const x = (side > 0 ? left + mm(3) : right - mm(3)) + side * (mm(3) + lip + mm(0.8));
            lights.push({
              a: new Vector3(x, from, lineZ),
              b: new Vector3(x, to, lineZ),
              direction: new Vector3(side, 0, -backTilt).normalize(),
              color: lineColor,
              lobe: 1
            });
          });
        }
        if (!lights.length) return;
        // Свет заперт в зоне: непрозрачные полки, стойки и задник его не пропускают.
        // Без задней стенки свет доходит до стены за стеллажом.
        this.cellLights.add({
          owner: group,
          min: new Vector3(left, zoneBottom, backZ),
          max: new Vector3(right, zoneTop, 0),
          lights,
          bounce: this.cellBounceLight(lights, new Vector3(width, zoneTop - zoneBottom, depth), surfaces.walls, surfaces.back, surfaces.openBack)
        });
      });
    });
    group.add(lighting3d);
  }

  /**
   * LED-полка шкафа светит вниз, в свой отсек: до ближайшей полки, ящика
   * или корзины под ней, либо до дна.
   */
  private addWardrobeLedLight(group: Group, module: WardrobeModule, item: InternalItem, depth: number, materialKey: string) {
    const temperature = cabinetLightTemperature(module);
    const back = cabinetLightPosition(module) === "back";
    const stripZ = back ? -depth + mm(40) : -mm(30);
    const color = this.lightLineColor(temperature, LED_STRIP_INTENSITY);
    const walls = new Color(resolveDecor(materialKey).hex);
    const blocking = new Set<InternalItem["type"]>(["shelf", "led", "drawer", "basket", "leatherBasket", "shoeRack", "trouserRack"]);
    const [interiorBottom] = interiorVerticalRangeMm(module);
    const underside = item.y - HALF_CARCASS_MM;
    usableItemPieces(module, item).forEach((piece) => {
      const floors = module.items
        .filter((other) => other.id !== item.id && blocking.has(other.type))
        .filter((other) => usableItemPieces(module, other)
          .some((part) => part.right > piece.left + 1 && part.left < piece.right - 1))
        .map((other) => internalItemSpanMm(module, other)[1])
        .filter((top) => top <= underside + 0.5);
      const floor = Math.max(interiorBottom, ...floors);
      if (underside - floor < 40) return;
      const halfStrip = mm(piece.width) * 0.41;
      const lights: CellLightLine[] = [{
        a: new Vector3(mm(piece.center) - halfStrip, mm(item.y - 12 - 3), stripZ),
        b: new Vector3(mm(piece.center) + halfStrip, mm(item.y - 12 - 3), stripZ),
        // У кромки свет падает на вещи и немного вглубь, у задней стенки — заливает её.
        direction: new Vector3(0, -1, back ? -0.15 : -0.3).normalize(),
        color,
        lobe: 1
      }];
      const size = new Vector3(mm(piece.width), mm(underside - floor), depth);
      this.cellLights.add({
        owner: group,
        min: new Vector3(mm(piece.left), mm(floor), -depth),
        max: new Vector3(mm(piece.right), mm(underside), 0),
        lights,
        bounce: this.cellBounceLight(lights, size, walls, walls, false)
      });
    });
  }

  /**
   * Подсветка шкафа «в стойках»: две ленты по стенкам каждого пролёта, на всю
   * высоту над дном. Свет заперт в пролёте.
   */
  private addWardrobeUprightLights(group: Group, module: WardrobeModule, depth: number, materialKey: string) {
    const strips = cabinetUprightStrips(module);
    if (!strips.length) return;
    const temperature = cabinetLightTemperature(module);
    const color = this.lightLineColor(temperature, LED_STRIP_INTENSITY);
    const walls = new Color(resolveDecor(materialKey).hex);
    const stripMaterial = this.getLightingMaterial("strip", temperature);
    const profile = this.getMetalMaterial(0xb8bbbd);
    const back = cabinetLightPosition(module) === "back";
    const stripZ = back ? -depth + mm(40) : -mm(40);
    const meta: PickMeta = { kind: "module", moduleId: module.id };
    const fixtures = new Group();
    fixtures.userData.cabinetUprightLight = true;
    const sections = new Map<number, CellLightLine[]>();
    const bounds = new Map<number, { left: number; right: number; bottom: number; top: number }>();
    strips.forEach((strip) => {
      const inward = strip.side === "left" ? 1 : -1;
      const height = strip.top - strip.bottom;
      const centerY = mm((strip.top + strip.bottom) / 2);
      this.addDetailPanel(fixtures, mm(strip.x), centerY, stripZ, mm(8), mm(height), mm(14), profile, meta);
      this.addDetailPanel(fixtures, mm(strip.x) + inward * mm(4.5), centerY, stripZ, mm(LED_DIFFUSER_LIP_MM), mm(height - 4), mm(10), stripMaterial, meta);
      const lines = sections.get(strip.section) ?? [];
      lines.push({
        a: new Vector3(mm(strip.x) + inward * mm(6), mm(strip.bottom), stripZ),
        b: new Vector3(mm(strip.x) + inward * mm(6), mm(strip.top), stripZ),
        direction: new Vector3(inward, 0, back ? -0.15 : -0.3).normalize(),
        color,
        lobe: 1
      });
      sections.set(strip.section, lines);
      const box = bounds.get(strip.section) ?? { left: strip.x, right: strip.x, bottom: strip.bottom, top: strip.top };
      box.left = Math.min(box.left, strip.x - 4);
      box.right = Math.max(box.right, strip.x + 4);
      bounds.set(strip.section, box);
    });
    sections.forEach((lights, section) => {
      const box = bounds.get(section)!;
      const size = new Vector3(mm(box.right - box.left), mm(box.top - box.bottom), depth);
      this.cellLights.add({
        owner: group,
        min: new Vector3(mm(box.left), mm(box.bottom), -depth),
        max: new Vector3(mm(box.right), mm(box.top), 0),
        lights,
        bounce: this.cellBounceLight(lights, size, walls, walls, false)
      });
    });
    group.add(fixtures);
  }

  /** Подсветка всего стеллажа: свет на пол из-под цоколя и ореол на стене за ним. */
  private addShelvingModuleLighting(group: Group, module: WardrobeModule, width: number, height: number, depth: number, baseHeight: number) {
    const lights = shelvingModuleLights(module);
    if (!lights.length) return;
    const profile = this.getMetalMaterial(0xb8bbbd);
    lights.forEach((light) => {
      const strip = this.getLightingMaterial("strip", light.temperature);
      const accent = new Group();
      accent.userData.shelvingModuleLight = light.kind;
      if (light.kind === "plinth") {
        const length = mm(light.lengthMm);
        // На ножках лента под дном у кромки и светит под весь стеллаж;
        // у цоколя — в свесе дна перед цоколем, на пол перед стеллажом.
        const legs = module.shelvingBase === "legs";
        const stripY = Math.max(mm(4), baseHeight - mm(4));
        const stripZ = legs ? -mm(30) : -mm(14);
        this.addDetailPanel(accent, 0, stripY, stripZ, length, mm(6), mm(14), profile);
        this.addDetailPanel(accent, 0, stripY - mm(3) - mm(LED_DIFFUSER_LIP_MM) / 2, stripZ, length - mm(4), mm(LED_DIFFUSER_LIP_MM), mm(10), strip);
        this.cellLights.add({
          owner: group,
          min: new Vector3(-width / 2 - 0.7, -0.05, legs ? -depth - mm(60) : -mm(29)),
          max: new Vector3(width / 2 + 0.7, baseHeight, 0.9),
          lights: [{
            a: new Vector3(-length / 2 + mm(2), stripY - mm(3) - mm(LED_DIFFUSER_LIP_MM) - mm(0.8), stripZ),
            b: new Vector3(length / 2 - mm(2), stripY - mm(3) - mm(LED_DIFFUSER_LIP_MM) - mm(0.8), stripZ),
            direction: new Vector3(0, -1, legs ? 0 : 0.35).normalize(),
            color: this.lightLineColor(light.temperature, LED_ACCENT_INTENSITY),
            lobe: 1,
            radius: mm(15)
          }],
          bounce: new Color(0, 0, 0),
          // Световое пятно на полу растворяется, а не обрывается прямоугольной
          // границей расчётного объёма.
          edgeFade: new Vector3(0.28, 0, 0.32),
          knee: LED_ACCENT_KNEE
        });
      } else {
        // Контур на тыльной стороне, профиль под 45° светит на стену и наружу:
        // на стене вокруг стеллажа — ореол, ярче всего у кромки.
        const stripZ = -depth - mm(8);
        const color = this.lightLineColor(light.temperature, LED_WALL_INTENSITY);
        this.addDetailPanel(accent, 0, height - mm(30), stripZ, width - mm(60), mm(8), mm(14), profile);
        const wallLights: CellLightLine[] = [{
          a: new Vector3(-width / 2 + mm(34), height - mm(30), stripZ - mm(7)),
          b: new Vector3(width / 2 - mm(34), height - mm(30), stripZ - mm(7)),
          direction: new Vector3(0, 1, -1).normalize(),
          color,
          lobe: 1,
          radius: LED_WALL_SOFTNESS
        }];
        [-1, 1].forEach((side) => {
          this.addDetailPanel(accent, side * (width / 2 - mm(30)), height / 2, stripZ, mm(8), height - mm(60), mm(14), profile);
          wallLights.push({
            a: new Vector3(side * (width / 2 - mm(30)), mm(34), stripZ - mm(7)),
            b: new Vector3(side * (width / 2 - mm(30)), height - mm(34), stripZ - mm(7)),
            direction: new Vector3(side, 0, -1).normalize(),
            color,
            lobe: 1,
            radius: LED_WALL_SOFTNESS
          });
        });
        this.cellLights.add({
          owner: group,
          min: new Vector3(-width / 2 - 1.3, -0.05, -depth - 0.35),
          max: new Vector3(width / 2 + 1.3, height + 1.3, -depth - mm(3)),
          lights: wallLights,
          bounce: new Color(0, 0, 0),
          // Смягчены только свободные внешние края. По глубине граница жёсткая,
          // чтобы ореол оставался на стене за мебелью.
          edgeFade: new Vector3(0.42, 0.42, 0),
          knee: LED_WALL_KNEE
        });
      }
      group.add(accent);
    });
  }

  private addShelvingCellDecorativeProps(
    group: Group,
    module: WardrobeModule,
    config: ShelvingCellConfig | undefined,
    cellId: string,
    cellLeftMm: number,
    cellBottomMm: number,
    cellWidthMm: number,
    cellHeightMm: number,
    depthMm: number,
    cellBoards: ReturnType<typeof getShelvingCellBoards>,
    meta: PickMeta
  ) {
    if (config?.content === "diagonal") return;
    if (!this.shelvingDecorReady) {
      this.requestShelvingDecor();
      return;
    }
    // Декор расставляется только в простых ячейках: одна полка и одна перегородка.
    if (new Set(cellBoards.map((board) => board.ref)).size > 2 ||
      cellBoards.filter((board) => board.kind === "divider").length > 1) return;

    const upright = cellBoards.find((board) => board.kind === "divider");
    const uprightHalfWidth = upright
      ? (upright.projectedWidth ?? upright.width) / 2 + 4
      : 0;
    const dividerX = upright ? cellLeftMm + upright.x : null;
    const horizontalZones = dividerX === null
      ? [{ left: cellLeftMm, right: cellLeftMm + cellWidthMm }]
      : [
          { left: cellLeftMm, right: dividerX - uprightHalfWidth },
          {
            left: dividerX + uprightHalfWidth,
            right: cellLeftMm + cellWidthMm
          }
        ];

    const shelf = cellBoards.find((board) => board.kind === "shelf");
    // Вещи стоят прямо на полке: у стеклянной она тоньше.
    const shelfHalfHeight = (shelf?.height ?? SHELVING_PANEL_THICKNESS_MM) / 2 + 3;
    const shelfY = shelf
      ? cellBottomMm + shelvingExtraShelfHeight(
          cellHeightMm,
          config?.extraShelfPosition
        )
      : null;
    const verticalZones = shelfY === null
      ? [{ bottom: cellBottomMm, top: cellBottomMm + cellHeightMm }]
      : [
          { bottom: cellBottomMm, top: shelfY - shelfHalfHeight },
          {
            bottom: shelfY + shelfHalfHeight,
            top: cellBottomMm + cellHeightMm
          }
        ];

    horizontalZones.forEach((horizontal, horizontalIndex) => {
      verticalZones.forEach((vertical, verticalIndex) => {
        const width = horizontal.right - horizontal.left;
        const height = vertical.top - vertical.bottom;
        if (width <= 0 || height <= 0) return;
        const plans = planShelvingDecor(
          {
            seed: `${cellId}:${horizontalIndex}:${verticalIndex}`,
            widthMm: width,
            heightMm: height,
            depthMm,
            centerXmm: (horizontal.left + horizontal.right) / 2,
            bottomMm: vertical.bottom,
            topMm: vertical.top
          },
          module.decorDensity
        );

        plans.forEach((plan, planIndex) => {
          const [widthMm, heightMm, depthMm] = plan.dimensionsMm;
          const fitted = this.createDecorativeModel(
            plan.key,
            new Vector3(mm(widthMm + 2), mm(heightMm + 2), mm(depthMm + 2)),
            new Vector3(
              mm(plan.centerXmm),
              mm(plan.anchorYmm),
              mm(plan.centerZmm)
            ),
            meta,
            `${cellId}:${horizontalIndex}:${verticalIndex}:${planIndex}`,
            {
              anchor: plan.anchor,
              baseYaw: plan.baseYaw,
              preserveScale: true,
              yawRange: plan.key.startsWith("booksRow") ? 0.012 : 0.026
            }
          );
          if (!fitted) return;
          fitted.object.name = `shelving-decor:${cellId}:${plan.key}`;
          group.add(fitted.object);
        });
      });
    });
  }

  private addShelvingCellDrawers(
    group: Group,
    module: WardrobeModule,
    config: ShelvingCellConfig,
    x: number,
    bottom: number,
    width: number,
    height: number,
    depth: number,
    materialKey: string,
    meta: PickMeta,
    facadeRect: ShelvingFacadeRect,
    inheritedFinishHex?: string,
    /**
     * Наружные ящики шкафа используют ту же сборку: своё число ящиков и высота
     * фасада, свои глубина и толщина фасада, без привязки к ячейке стеллажа.
     */
    options: { count?: number; frontHeightMm?: number; frontZ?: number; frontThickness?: number; cellId?: string | null; seamMm?: number } = {}
  ) {
    const count = options.count ?? shelvingDrawerCount(config.drawerCount, height / MM, drawerLimits(module));
    // Фасады ящиков делят фасад ячейки (накладной или вкладной) швами по 3 мм;
    // коробы и направляющие стоят в проёме. У цокольных ящиков шкафа — зацеп 20 мм.
    const reveal = mm(options.seamMm ?? SHELVING_DRAWER_SEAM_MM);
    // Свес комода (15.10) забирает только нижний фасад: остальные стоят как без свеса.
    const overhang = options.cellId === null || config.row !== 0 ? 0 : mm(shelvingFacadeOverhangMm(module));
    const frontHeight = Math.max(mm(46), mm(options.frontHeightMm ?? shelvingDrawerFacadeHeight(facadeRect.height - overhang / MM, count)));
    const frontWidth = Math.max(mm(80), mm(facadeRect.width));
    const frontX = mm(facadeRect.left + facadeRect.width / 2);
    const frontBottom = mm(facadeRect.bottom);
    const frontZ = options.frontZ ?? (facadeRect.mount === "inset" ? SHELVING_INSET_FACADE_Z : SHELVING_OVERLAY_FACADE_Z);
    const frontThickness = options.frontThickness ?? mm(SHELVING_FACADE_THICKNESS_MM);
    const frontBack = frontZ - frontThickness / 2;
    const facadeMaterialKey = config.facadeDecor ?? materialKey;
    const facadeType = config.facade ?? "wood";
    const facade =
      facadeType === "wood" && !config.facadeDecor && inheritedFinishHex
        ? this.getShelvingFinishMaterial(inheritedFinishHex)
        : this.getFacadeMaterial(
            facadeType,
            facadeMaterialKey,
            config.facadeColor,
            config.facadePattern,
            frontWidth,
            config.facadeColorHex
          );
    // Комод: ящики покрашены внутри в цвет эмали фасадов (15.10).
    const painted = options.cellId === null ? null : chestDrawerPaintFinish(module);
    const body = painted
      ? this.getFacadeMaterial("satin", materialKey, painted.facadeColor, "plain", frontWidth, painted.facadeColorHex)
      : this.getDrawerMaterial();
    const metal = this.getMetalMaterial(0x444543);
    const seam = this.getSoftShadowMaterial(inheritedFinishHex ? 0.34 : 0.24);
    // Короб начинается сразу за фасадом: у вкладного он на 14 мм глубже в корпусе.
    const boxFront = Math.min(mm(2), frontBack - mm(1));
    const boxDepth = Math.max(mm(70), depth - mm(46) + boxFront);
    const openShiftZ = Math.min(boxDepth * 0.75, mm(420));

    for (let boundary = 1; boundary < count; boundary += 1) {
      this.addDetailPanel(
        group,
        frontX,
        frontBottom + overhang + boundary * frontHeight + (boundary - 0.5) * reveal,
        frontBack - mm(1),
        Math.max(mm(60), frontWidth - mm(8)),
        Math.max(mm(1.4), reveal * 0.5),
        mm(1),
        seam,
        meta
      );
    }

    for (let index = 0; index < count; index += 1) {
      const centerY =
        frontBottom + overhang + frontHeight / 2 + index * (frontHeight + reveal);
      // Нижний фасад длиннее на свес — вниз; короб и ручка там же, где без свеса.
      const faceExtra = index === 0 ? overhang : 0;
      const faceY = centerY - faceExtra / 2;
      const drawerId = `drawer:${config.id}:${index}`;
      const key = this.doorKey(module.id, drawerId);
      const drawerMeta: PickMeta = {
        kind: "door",
        moduleId: module.id,
        doorIndex: drawerId,
        ...(options.cellId === null ? {} : { cellId: options.cellId ?? config.id })
      };
      const drawer = new Group();
      drawer.position.z =
        openShiftZ * this.easeDoorProgress(this.doorVisualProgress(key));
      drawer.userData.pick = drawerMeta;
      group.add(drawer);
      this.doorObjects.set(key, {
        key,
        kind: "drawer",
        moduleId: module.id,
        doorIndex: drawerId,
        object: drawer,
        side: 0,
        closedZ: 0,
        openShiftZ,
        minY: centerY - frontHeight / 2,
        maxY: centerY + frontHeight / 2
      });
      const boxWidth = Math.max(mm(60), width - mm(24));
      const boardThickness = mm(12);
      const bottomThickness = mm(6);
      // Короб — в проёме, даже если накладной фасад заходит на полку под ним.
      const boxBottom = Math.max(centerY - frontHeight / 2 + mm(4), bottom + mm(3));
      const sidesTop = Math.min(centerY + frontHeight / 2 - mm(10), bottom + height - mm(12));
      const boxHeight = Math.max(mm(12), sidesTop - boxBottom - bottomThickness);
      const boxCenterZ = boxFront - boxDepth / 2;
      // An open-topped box: the facade, sides, back and bottom move together.
      this.addPanel(drawer, x, boxBottom + bottomThickness / 2, boxCenterZ,
        boxWidth, bottomThickness, boxDepth, body, drawerMeta);
      for (const side of [-1, 1]) {
        this.addPanel(drawer,
          x + side * (boxWidth - boardThickness) / 2,
          boxBottom + bottomThickness + boxHeight / 2,
          boxCenterZ, boardThickness, boxHeight, boxDepth, body, drawerMeta);
      }
      this.addPanel(drawer, x, boxBottom + bottomThickness + boxHeight / 2,
        boxFront - boxDepth + boardThickness / 2,
        boxWidth - boardThickness * 2, boxHeight, boardThickness, body, drawerMeta);
      // Направляющие скрытого монтажа — под дном короба, их не видно (Сергей, 15.10):
      // в 3D не рисуем, в смете они в цене ящика.
      const facadeHeight = Math.max(mm(46), frontHeight) + faceExtra;
      // Интегрированная ручка ящика — выборка по верхней кромке (приёмка 22.10).
      const integratedHandleCut: IntegratedHandleCut | undefined = undefined;
      const groove = config.handle === "integrated" && facadeType !== "glass" && facadeType !== "mirror"
        ? this.addIntegratedGrooveFront(drawer, frontX, faceY, frontZ, frontWidth, facadeHeight, frontThickness,
            facade, drawerMeta, config.integratedHandleStyle === "topPart"
              // Участок по центру верхней кромки (видео Сергея 24.10), остальное — фасад во всю высоту.
              ? { edge: "top", from: -mm(integratedTopPartLengthMm(frontWidth / MM)) / 2, to: mm(integratedTopPartLengthMm(frontWidth / MM)) / 2 }
              : { edge: "top" }, mm(2))
        : null;
      let front: Mesh;
      if (groove) {
        front = groove.leaf;
      } else {
        front = this.addRoundedPanel(
          drawer,
          frontX,
          faceY,
          frontZ,
          frontWidth,
          facadeHeight,
          frontThickness,
          facade,
          drawerMeta,
          mm(2)
        );
      }
      if (facadeType === "mirror") {
        // Use the same backing exclusion and live reflection as a hinged
        // mirror door. Parenting to the drawer keeps it on the moving front.
        front.userData.mirrorSurface = true;
        front.layers.set(MIRROR_BACKING_LAYER);
        this.addMirrorReflection(
          drawer, frontX, faceY, frontZ + frontThickness / 2 + mm(5), frontWidth, facadeHeight, drawerMeta
        );
      } else {
        const main = groove?.main ?? { x: frontX, y: faceY, width: frontWidth, height: facadeHeight };
        this.addFacadePatternDetails(
          drawer,
          main.x,
          main.y,
          frontZ,
          main.width,
          main.height,
          frontThickness,
          facadeType,
          config.facadePattern,
          facade,
          drawerMeta,
          integratedHandleCut
        );
      }
      if (config.handle !== "integrated" && config.handle !== "push" && config.handle !== "glassGrip") {
        this.addRoundedPanel(
          drawer,
          frontX,
          centerY,
          frontZ + frontThickness / 2 + mm(4),
          config.handle === "profile"
            ? Math.min(frontWidth * 0.72, mm(240))
            : Math.min(frontWidth * 0.42, mm(150)),
          config.handle === "recessed" ? mm(14) : mm(8),
          mm(10),
          metal,
          drawerMeta,
          mm(3)
        );
      }
    }
  }

  /**
   * Наружные ящики шкафа: та же сборка, что у ящиков стеллажа (короб,
   * направляющие, фасад, ручка), в каждом пролёте. Фасады — как у дверей:
   * 20 мм, накладные, в плоскости дверей.
   */
  private addCabinetDrawers(
    group: Group,
    module: WardrobeModule,
    layout: CabinetDrawerLayout,
    depth: number,
    materialKey: string,
    meta: PickMeta
  ) {
    // Отделка ящиков — как у дверей, но без стекла и зеркала (см. cabinetDrawerFinish).
    const finish = cabinetDrawerFinish(module);
    layout.bays.forEach((bay) => {
      const config: ShelvingCellConfig = {
        id: cabinetDrawerCellId(bay.sectionIndex),
        column: bay.sectionIndex,
        row: 0,
        backPanel: false,
        extraShelf: false,
        content: "drawers",
        drawerCount: layout.count,
        facade: finish.facade,
        facadePattern: finish.facadePattern,
        facadeDecor: module.facadeDecor,
        facadeColor: finish.facadeColor,
        facadeColorHex: finish.facadeColorHex,
        // Ручек нет — зацеп 20 мм над каждым фасадом (видео Сергея 31.10).
        handle: CABINET_DRAWERS_FINGER_PULL ? "push" : finish.handle
      };
      const openingBottom = layout.carcassFloorMm;
      const openingTop = layout.shelfBottomMm;
      this.addShelvingCellDrawers(
        group,
        module,
        config,
        mm((bay.innerLeft + bay.innerRight) / 2),
        mm(openingBottom),
        mm(bay.innerRight - bay.innerLeft),
        mm(openingTop - openingBottom),
        depth,
        materialKey,
        meta,
        {
          mount: "overlay",
          left: bay.frontLeft,
          bottom: layout.frontsBottomMm,
          width: bay.frontRight - bay.frontLeft,
          height: layout.frontsTopMm - layout.frontsBottomMm
        },
        undefined,
        { count: layout.count, frontHeightMm: layout.frontHeightMm, frontZ: mm(13), frontThickness: mm(20), cellId: null,
          seamMm: CABINET_DRAWER_SEAM_MM }
      );
    });
  }

  private addShelvingCellDoor(
    group: Group,
    module: WardrobeModule,
    config: ShelvingCellConfig,
    facadeRect: ShelvingFacadeRect,
    materialKey: string,
    cellMeta: PickMeta,
    inheritedFinishHex?: string
  ) {
    // Фасад ячейки: одна распашная дверца, две дверцы или откидной фасад
    // вверх или вниз (09.10) — как в смете и техкарте (cellFrontLeaves).
    const front = cellFrontLeaves(config, facadeRect.width, facadeRect.height,
      this.project ? shelvingCellHeadroomMm(this.project, module, config.id) : Infinity, shelvingExcludedLifts(module));
    if (front.flap) {
      this.addShelvingCellFlap(group, module, config, facadeRect, front.flap, materialKey, inheritedFinishHex);
      void cellMeta;
      return;
    }
    front.leaves.forEach((leaf, index) => {
      this.addShelvingCellHingedLeaf(
        group,
        module,
        config,
        { ...facadeRect, left: facadeRect.left + leaf.leftMm, width: leaf.widthMm },
        leaf.hingeSide ?? -1,
        index === 0 ? `cell:${config.id}` : `cell:${config.id}:${index + 1}`,
        materialKey,
        inheritedFinishHex
      );
    });
    void cellMeta;
  }

  /** Материал фасада ячейки: свой декор, эмаль или отделка стеллажа. */
  private cellFacadeMaterial(config: ShelvingCellConfig, materialKey: string, widthMeters: number, inheritedFinishHex?: string) {
    const facadeMaterialKey = config.facadeDecor ?? materialKey;
    const facadeType = config.facade ?? "wood";
    return facadeType === "wood" && !config.facadeDecor && inheritedFinishHex
      ? this.getShelvingFinishMaterial(inheritedFinishHex)
      : this.getFacadeMaterial(
          facadeType,
          facadeMaterialKey,
          config.facadeColor,
          config.facadePattern,
          widthMeters,
          config.facadeColorHex
        );
  }

  /**
   * Откидной фасад: ось у верхней кромки (вверх, подъёмник Aventos) или у
   * нижней (вниз). Складной HF — две части с швом; механизм внутри виден,
   * только когда фасад открыт.
   */
  private addFlapFront(
    parent: Group,
    options: {
      moduleId: string;
      doorId: string | number;
      meta: PickMeta;
      centerX: number;
      bottom: number;
      width: number;
      height: number;
      z: number;
      thickness: number;
      front: FlapFront;
      material: PanelMaterial;
      facade: WardrobeModule["facade"];
      materialKey: string;
      pattern: FacadePatternType | undefined;
      handle: HandleType | undefined;
      oneSidedPaint: boolean;
      mechanismDepth: number;
      /** Реальный проём, а не накладной фасад: крепления внутри боковин и под крышкой. */
      opening?: { left: number; right: number; top: number; frontZ: number; bodyTop: number };
    }
  ) {
    // Откидные фасады (10.10): у каждого механизма своё движение и видимые
    // рычаги. HL — фасад поднимается параллельно над корпусом, HS — поднимается
    // и ложится сверху, HF — складывается из двух частей, HK — поворачивается
    // вверх на оси у верхней кромки; откидной вниз — на петлях внизу и складных
    // кронштейнах по бокам.
    const { front, width, height, bottom, meta } = options;
    const up = front.direction === "up";
    const lift = up ? front.lift ?? "HK" : undefined;
    const key = this.doorKey(options.moduleId, options.doorId);
    const progress = this.doorVisualProgress(key);
    const t = options.thickness;
    const z0 = options.z;
    const zEdge = z0 - t / 2 - mm(1);
    const yTop = bottom + height;
    const gap = mm(FRONT_SPLIT_GAP_MM);
    const folding = lift === "HF";
    const part = folding ? (height - gap) / 2 : height;
    const opening = options.opening ?? {
      left: options.centerX - width / 2 + mm(16), right: options.centerX + width / 2 - mm(16),
      top: yTop - mm(16), frontZ: z0 - t / 2 - mm(0.5), bodyTop: yTop + mm(2)
    };
    // Ось у наружной верхней кромки: при повороте толщина уходит вниз, а не в соседний фасад.
    const hingeOffset = lift === "HK" || folding
      ? t / 2 + (options.facade === "mirror" ? mm(6.2) : 0) : 0;
    const leafZ = -hingeOffset;
    const pivotZ = z0 + hingeOffset;
    const pullOut = Math.max(mm(2), opening.frontZ - (z0 - t / 2) + mm(2));
    const axisZ = Math.min(zEdge - mm(24), opening.frontZ - mm(26));
    // HL — параллелограмм с постоянной длиной тяг, а не вертикальный сдвиг с растущими рычагами.
    const hlEndAngle = (140 * Math.PI) / 180;
    const hlAttachY = -height * 0.88;
    const inner = leafZ - t / 2 - mm(up ? 6 : 4);
    // Вложенный фасад сначала освобождает кромку проёма; короткий ход адаптера
    // предшествует дуге параллелограмма и не увеличивает запас места сверху.
    const hlRelease = z0 - t / 2 < opening.frontZ ? pullOut : 0;
    const hlDz = z0 + hlRelease + inner - axisZ;
    const hlRise = height + mm(20); // Не больше LIFT_SPECS.HL.headroomMm(height).
    const hlSin2 = Math.sin(hlEndAngle) ** 2;
    const hlLength = (hlRise * Math.cos(hlEndAngle) + Math.sqrt(hlRise ** 2 + hlSin2 * hlDz ** 2)) / hlSin2;
    const hlStartAngle = Math.asin(hlDz / hlLength);
    const hlFixedY = yTop + hlAttachY + hlLength * Math.cos(hlStartAngle);
    const ss = (from: number, to: number, value: number) => {
      const k = Math.min(1, Math.max(0, (value - from) / (to - from)));
      return k * k * (3 - 2 * k);
    };
    const pose = (e: number): FlapPose => {
      switch (lift) {
        case "HL": {
          if (hlRelease && e < 0.08) return {
            y: yTop, z: z0 + hlRelease * ss(0, 0.08, e), rx: 0, fold: 0
          };
          const travel = hlRelease ? ss(0.08, 1, e) : e;
          const angle = hlStartAngle + (hlEndAngle - hlStartAngle) * travel;
          return { y: hlFixedY - hlLength * Math.cos(angle) - hlAttachY,
            z: axisZ + hlLength * Math.sin(angle) - inner, rx: 0, fold: 0 };
        }
        case "HS": {
          // Сначала вынос и поворот ПЕРЕД крышкой, затем уход назад НАД ней.
          const turn = ss(0.08, 0.68, e);
          const over = ss(0.68, 1, e);
          const clearance = Math.max(mm(30), opening.frontZ - (z0 - t / 2) + mm(30));
          return {
            y: yTop + (opening.bodyTop - yTop + mm(60)) * turn,
            z: z0 + clearance * ss(0, 0.08, e) - height * 0.68 * over,
            rx: -(Math.PI / 2) * turn,
            fold: 0
          };
        }
        case "HF": {
          const rx = -FLAP_FOLD_OPEN_ANGLE * ss(0.08, 1, e);
          return { y: yTop, z: pivotZ + pullOut * ss(0, 0.08, e), rx, fold: -2 * rx };
        }
        case "HK":
          return { y: yTop, z: pivotZ + pullOut * ss(0, 0.08, e), rx: -FLAP_UP_OPEN_ANGLE * ss(0.08, 1, e), fold: 0 };
        default:
          return { y: bottom, z: z0, rx: FLAP_DOWN_OPEN_ANGLE * e, fold: 0 };
      }
    };

    const pivot = new Group();
    pivot.position.x = options.centerX;
    pivot.userData.pick = meta;
    const leaf = (target: Group, centerY: number, partHeight: number, centerZ = leafZ) => this.addFacadeLeaf(
      target, 0, centerY, centerZ, width, partHeight, t, options.material, options.facade, options.materialKey,
      options.pattern, meta, undefined, undefined, options.oneSidedPaint
    );
    // Складной шарнир — позади изнанки. Ось в середине плиты заставляла половинки пересекаться.
    const foldOrigin: [number, number] = [-(part + gap / 2), leafZ - t / 2 - mm(10)];
    let fold: Group | undefined;
    if (folding) {
      leaf(pivot, -part / 2, part);
      fold = new Group();
      fold.position.set(0, foldOrigin[0], foldOrigin[1]);
      fold.userData.pick = meta;
      leaf(fold, -(gap / 2 + part / 2), part, leafZ - foldOrigin[1]);
      const handle = new Group();
      handle.position.z = leafZ - foldOrigin[1];
      this.addHorizontalFrontHandle(handle, options.handle, 0, -(gap / 2 + part) + mm(38), width, meta);
      fold.add(handle);
      pivot.add(fold);
    } else {
      leaf(pivot, up ? -height / 2 : height / 2, height);
      const handle = new Group();
      handle.position.z = leafZ;
      this.addHorizontalFrontHandle(handle, options.handle, 0, up ? -height + mm(38) : height - mm(38), width, meta);
      pivot.add(handle);
    }

    // Механизм: корпуса на боковинах, рычаги к фасаду, петли.
    // Корпуса механизмов — светло-серый пластик, рычаги и петли — сталь.
    const metal = this.getMechanismCoverMaterial();
    const dark = this.getMetalMaterial(0xa7abaf);
    const fixedHardware = new Group();
    fixedHardware.userData.pick = meta;
    const movingHardware = new Group();
    movingHardware.userData.pick = meta;
    const foldHardware = fold ? new Group() : undefined;
    if (foldHardware && fold) {
      foldHardware.userData.pick = meta;
      fold.add(foldHardware);
    }
    const arms: FlapArm[] = [];
    const housingDepth = Math.min(mm(260), Math.max(mm(90), options.mechanismDepth));
    const housingTop = up ? opening.top - mm(8) : yTop - mm(14);
    const housingHeight = Math.min(height * 0.6, lift === "HL"
      ? Math.max(mm(150), housingTop - hlFixedY + mm(24))
      : lift === "HS" ? mm(150) : mm(110));
    const addArm = (
      x: number,
      fixed: [number, number],
      attach: [number, number],
      knee: "back" | "forward" | "straight",
      onFold = false,
      minLength = 0
    ) => {
      const arm: FlapArm = {
        x, fixed, attach, foldOrigin: onFold ? foldOrigin : undefined, length: 0, kneeSign: 1,
        links: [this.createFlapLink(dark, up), this.createFlapLink(dark, up)]
      };
      let longest = 0;
      for (let step = 0; step <= 20; step += 1) {
        const point = flapPoint(pose(step / 20), arm);
        longest = Math.max(longest, Math.hypot(point[0] - fixed[0], point[1] - fixed[1]));
      }
      arm.length = Math.max(longest / 2 * 1.006, minLength);
      // Сгиб — внутрь корпуса у закрытого фасада или вперёд у открытого (у HL и HS рычаги выходят перед крышкой).
      if (knee === "straight") {
        arm.straight = true;
      } else {
        const reference = flapPoint(pose(knee === "back" ? 0 : 1), arm);
        const normalZ = reference[0] - fixed[0];
        arm.kneeSign = knee === "back" ? (normalZ >= 0 ? -1 : 1) : (normalZ >= 0 ? 1 : -1);
      }
      arm.links.forEach((link) => fixedHardware.add(link));
      arms.push(arm);
      if (up) {
        const target = onFold ? foldHardware! : movingHardware;
        const backing = options.facade === "satin" && options.oneSidedPaint ? mm(0.35) : 0;
        this.addPanel(target, x - options.centerX, attach[0], attach[1] + mm(3) - backing,
          mm(16), mm(24), mm(6), dark, meta);
      }
    };

    [-1, 1].forEach((sign) => {
      const housingX = up ? (sign < 0 ? opening.left + mm(12) : opening.right - mm(12))
        : options.centerX + sign * (width / 2 - mm(26));
      const armX = housingX - sign * mm(17);
      if (up) {
        this.addPanel(fixedHardware, housingX, housingTop - housingHeight / 2, zEdge - mm(16) - housingDepth / 2,
          mm(22), housingHeight, housingDepth, metal, meta);
        const axis: [number, number] = [housingTop - housingHeight * 0.62, axisZ];
        if (lift === "HL") {
          // Две параллельные тяги в разных плоскостях не трутся друг о друга
          // в сложенном положении; поперечная ось связывает внутреннюю тягу с корпусом.
          const span = Math.min(height * 0.1, housingTop - hlFixedY - mm(20));
          addArm(armX, [hlFixedY, axisZ], [hlAttachY, inner], "straight");
          addArm(armX - sign * mm(14), [hlFixedY + span, axisZ], [hlAttachY + span, inner], "straight");
          const shaft = new Mesh(new CylinderGeometry(mm(3), mm(3), mm(34), 12), dark);
          shaft.name = "flap-hl-cross-shaft";
          shaft.rotation.z = Math.PI / 2;
          shaft.position.set(housingX - sign * mm(22), hlFixedY + span, axisZ);
          fixedHardware.add(shaft);
        } else if (lift === "HS") {
          // Крепление ближе к нижней кромке: рычаг выходит из-под крышки перед корпусом.
          addArm(armX, axis, [-height * 0.75, inner], "straight");
        } else if (lift === "HF") {
          // Телескопическая тяга держит НИЖНЮЮ половину, оставаясь внутри складки.
          addArm(armX, axis, [-(gap / 2 + part * 0.6), leafZ - foldOrigin[1] - t / 2 - mm(6)], "straight", true);
        } else {
          addArm(armX, axis, [-height * 0.38, inner], "straight");
        }
      } else {
        // Откидной вниз: корпус кронштейна вверху боковины, складной рычаг к фасаду.
        const axis: [number, number] = [yTop - mm(70), zEdge - mm(45)];
        this.addPanel(fixedHardware, housingX, axis[0], axis[1] - mm(20), mm(22), mm(46), mm(80), metal, meta);
        addArm(armX, axis, [height * 0.58, inner], "back");
      }
    });

    // Петли: у откидного вниз — внизу, у HF — на шве между частями.
    const hingeCount = flapHingeCount(front, width / MM);
    const hingeXs = Array.from({ length: hingeCount }, (_, index) =>
      -width / 2 + mm(90) + index * (width - mm(180)) / Math.max(1, hingeCount - 1));
    hingeXs.forEach((x) => {
      if (!up) {
        this.addPanel(movingHardware, x, mm(30), inner - mm(4), mm(46), mm(34), mm(10), dark, meta);
        this.addPanel(fixedHardware, options.centerX + x, bottom + mm(14), zEdge - mm(28), mm(40), mm(6), mm(44), dark, meta);
      } else if (foldHardware) {
        this.addPanel(movingHardware, x, -part + mm(18), inner - mm(4), mm(46), mm(30), mm(10), dark, meta);
        this.addPanel(foldHardware, x, -gap / 2 - mm(18), leafZ - foldOrigin[1] - t / 2 - mm(8), mm(46), mm(30), mm(10), dark, meta);
      }
    });
    pivot.add(movingHardware);
    parent.add(fixedHardware);

    const openText = lift === "HL" ? "lifted vertically, parallel to the cabinet, so it stands above the opening"
      : lift === "HS" ? "swung up and over so it lies flat above the cabinet top"
        : lift === "HF" ? "folded upwards in two parts like a horizontal bi-fold"
          : lift === "HK" ? "swung up around its top edge and held open"
            : "dropped down around its bottom edge to horizontal, held by side stays";
    const flap: FlapRuntime = { openText, pose, fold, arms };
    this.applyFlapPose(pivot, flap, this.easeDoorProgress(progress));
    const hardware: Object3D[] = [fixedHardware, movingHardware, ...(foldHardware ? [foldHardware] : [])];
    hardware.forEach((object) => { object.visible = progress > 0.025; });
    this.doorObjects.set(key, {
      key,
      kind: "flap",
      moduleId: options.moduleId,
      doorIndex: options.doorId,
      object: pivot,
      side: up ? -1 : 1,
      width,
      minY: bottom,
      maxY: bottom + height,
      hardwareObjects: hardware,
      flap
    });
    parent.add(pivot);
  }

  /** Звено рычага: брусок единичной длины вдоль оси Y, длина задаётся масштабом. */
  private createFlapLink(material: PanelMaterial, detailed = true) {
    const link = new Group();
    const bar = new Mesh(new BoxGeometry(mm(detailed ? 10 : 12), 1, mm(detailed ? 4 : 5)), material);
    if (detailed) bar.name = "flap-link-bar";
    bar.position.y = 0.5;
    bar.castShadow = true;
    link.add(bar);
    // Откидные вниз сохраняют прежний профиль и складные кронштейны.
    if (!detailed) return link;
    ["flap-link-start", "flap-link-end"].forEach((name) => {
      const pin = new Mesh(new CylinderGeometry(mm(3), mm(3), mm(12), 12), material);
      pin.name = name;
      pin.rotation.z = Math.PI / 2;
      link.add(pin);
    });
    return link;
  }

  /** Ставит фасад, складную часть и рычаги в положение по ходу открывания. */
  private applyFlapPose(object: Group, flap: FlapRuntime, eased: number) {
    const pose = flap.pose(eased);
    object.position.y = pose.y;
    object.position.z = pose.z;
    object.rotation.x = pose.rx;
    if (flap.fold) flap.fold.rotation.x = pose.fold;
    flap.arms.forEach((arm) => {
      const [ay, az] = flapPoint(pose, arm);
      const [py, pz] = arm.fixed;
      const dy = ay - py;
      const dz = az - pz;
      const distance = Math.max(1e-6, Math.hypot(dy, dz));
      const half = distance / 2;
      const rise = arm.straight || half >= arm.length ? 0 : Math.sqrt(arm.length * arm.length - half * half);
      // Сгиб — по нормали к линии «ось — крепление».
      const ky = py + dy / 2 - (dz / distance) * rise * arm.kneeSign;
      const kz = pz + dz / 2 + (dy / distance) * rise * arm.kneeSign;
      placeFlapLink(arm.links[0], arm.x, py, pz, ky, kz);
      placeFlapLink(arm.links[1], arm.x, ky, kz, ay, az);
    });
  }

  private addShelvingCellFlap(
    group: Group,
    module: WardrobeModule,
    config: ShelvingCellConfig,
    facadeRect: ShelvingFacadeRect,
    front: FlapFront,
    materialKey: string,
    inheritedFinishHex?: string
  ) {
    const width = Math.max(mm(70), mm(facadeRect.width));
    const height = Math.max(mm(70), mm(facadeRect.height));
    const doorId = `cell:${config.id}`;
    const meta: PickMeta = { kind: "door", moduleId: module.id, doorIndex: doorId, cellId: config.id };
    const cell = getShelvingCellLayouts(module).find((layout) => layout.id === config.id);
    this.addFlapFront(group, {
      moduleId: module.id,
      doorId,
      meta,
      centerX: mm(facadeRect.left + facadeRect.width / 2),
      bottom: mm(facadeRect.bottom),
      width,
      height,
      z: facadeRect.mount === "inset" ? SHELVING_INSET_FACADE_Z : SHELVING_OVERLAY_FACADE_Z,
      thickness: mm(18),
      front,
      material: this.cellFacadeMaterial(config, materialKey, width, inheritedFinishHex),
      facade: config.facade ?? "wood",
      materialKey: config.facadeDecor ?? materialKey,
      pattern: config.facadePattern,
      handle: config.handle,
      oneSidedPaint: config.facadePaintSides !== "two",
      mechanismDepth: mm(module.depth) * 0.45,
      opening: cell ? {
        left: mm(cell.left), right: mm(cell.left + cell.width), top: mm(cell.bottom + cell.height),
        frontZ: SHELVING_FRONT_PLANE, bodyTop: mm(module.height)
      } : undefined
    });
  }

  /** Горизонтальная ручка откидного фасада: у Tip-On и зацепа ручки нет. */
  private addHorizontalFrontHandle(
    parent: Group,
    handle: HandleType | undefined,
    x: number,
    y: number,
    frontWidth: number,
    meta: PickMeta
  ) {
    if (!handle || handle === "push" || handle === "glassGrip" || handle === "integrated") return;
    const metal = this.getMetalMaterial(handle === "profile" ? 0x8f928d : 0x323334);
    if (handle === "profile") {
      this.addRoundedPanel(parent, x, y, mm(22), Math.max(mm(80), frontWidth * 0.62), mm(10), mm(7), metal, meta, mm(3));
      return;
    }
    const length = Math.max(mm(96), Math.min(mm(260), frontWidth * 0.3));
    if (handle === "recessed") {
      this.addRoundedPanel(parent, x, y, mm(21), length, mm(26), mm(5), this.getSoftShadowMaterial(0.32), meta, mm(6));
      return;
    }
    const bar = new Mesh(new CylinderGeometry(mm(5), mm(5), length, 16), metal);
    bar.rotation.z = Math.PI / 2;
    bar.position.set(x, y, mm(24));
    bar.userData.pick = meta;
    bar.castShadow = true;
    parent.add(bar);
  }

  private addShelvingCellHingedLeaf(
    group: Group,
    module: WardrobeModule,
    config: ShelvingCellConfig,
    facadeRect: ShelvingFacadeRect,
    hingeSide: -1 | 1,
    doorId: string,
    materialKey: string,
    inheritedFinishHex?: string
  ) {
    // Накладная дверца закрывает торцы стоек и полок, вкладная стоит в проёме
    // вровень с кромкой; ось петли — у края дверцы, по её середине.
    const doorWidth = Math.max(mm(70), mm(facadeRect.width));
    const doorHeight = Math.max(mm(70), mm(facadeRect.height));
    const x = mm(facadeRect.left + facadeRect.width / 2);
    const bottom = mm(facadeRect.bottom);
    const height = doorHeight;
    const inset = facadeRect.mount === "inset";
    const side = hingeSide;
    const key = this.doorKey(module.id, doorId);
    const progress = this.doorVisualProgress(key);
    const doorMeta: PickMeta = {
      kind: "door",
      moduleId: module.id,
      doorIndex: doorId,
      cellId: config.id
    };
    const hinge = new Group();
    hinge.position.set(x + side * doorWidth / 2, 0, inset ? SHELVING_INSET_FACADE_Z : SHELVING_OVERLAY_FACADE_Z);
    hinge.rotation.y =
      side * HINGED_DOOR_OPEN_ANGLE * this.easeDoorProgress(progress);
    hinge.userData.pick = doorMeta;
    const facadeMaterialKey = config.facadeDecor ?? materialKey;
    const facadeType = config.facade ?? "wood";
    const material =
      facadeType === "wood" && !config.facadeDecor && inheritedFinishHex
        ? this.getShelvingFinishMaterial(inheritedFinishHex)
        : this.getFacadeMaterial(
            facadeType,
            facadeMaterialKey,
            config.facadeColor,
            config.facadePattern,
            doorWidth,
            config.facadeColorHex
          );
    this.doorObjects.set(key, {
      key,
      kind: "hinged",
      moduleId: module.id,
      doorIndex: doorId,
      object: hinge,
      side,
      width: doorWidth,
      closedX: x + side * doorWidth / 2,
      maxAngle: HINGED_DOOR_OPEN_ANGLE,
      minY: bottom,
      maxY: bottom + height
    });

    const leaf = new Group();
    leaf.position.set(-side * doorWidth / 2, 0, 0);
    // Интегрированная ручка дверцы ячейки — выборка по всей открывающей кромке (22.10).
    const integratedHandleCut: IntegratedHandleCut | undefined = undefined;
    const integratedGroove: IntegratedGroove | undefined = config.handle === "integrated"
      ? { edge: (side > 0 ? -1 : 1) as -1 | 1 }
      : undefined;
    this.addFacadeLeaf(
      leaf,
      0,
      bottom + height / 2,
      0,
      doorWidth,
      doorHeight,
      mm(18),
      material,
      config.facade ?? "wood",
      facadeMaterialKey,
      config.facadePattern,
      doorMeta,
      integratedHandleCut,
      config.handle === "glassGrip" ? -side : undefined,
      config.facadePaintSides !== "two",
      integratedGroove
    );
    this.hingeTone = module.hingeColor === "black" ? "black" : "silver";
    // Опора петли: у крайней колонки — боковина (накладная петля), между
    // колонками — общая стойка (полунакладная), у вкладной дверцы — грань проёма.
    const hingeEdge = mm(side > 0 ? facadeRect.left + facadeRect.width : facadeRect.left);
    const outerEdge = Math.abs(Math.abs(hingeEdge) - mm(module.width) / 2) < mm(4);
    const application: HingeModelType = inset ? "inset" : outerEdge ? "full-overlay" : "half-overlay";
    const cover = inset ? -mm(SHELVING_INSET_GAP_MM)
      : outerEdge ? mm(SHELVING_PANEL_THICKNESS_MM - SHELVING_FACADE_EDGE_MM)
        : mm(SHELVING_PANEL_THICKNESS_MM / 2 - SHELVING_FACADE_SEAM_MM / 2);
    const facadeZ = inset ? SHELVING_INSET_FACADE_Z : SHELVING_OVERLAY_FACADE_Z;
    const modelled = this.addHingeModels({
      fixedParent: group, doorParent: leaf, application,
      supportFaceX: hingeEdge - side * cover, sideOut: side,
      doorBackZ: facadeZ - mm(SHELVING_FACADE_THICKNESS_MM) / 2, doorOrigin: { x, z: facadeZ },
      doorHeight, baseY: bottom, meta: doorMeta
    });
    if (!modelled) {
      this.addHingeDetails(
        leaf,
        side,
        doorWidth,
        doorHeight,
        doorMeta,
        bottom,
        inset ? "inset" : "cell"
      );
    } else if (modelled.length) {
      const cellRuntime = this.doorObjects.get(key);
      if (cellRuntime) cellRuntime.hingeLinks = modelled;
    }
    this.addShelvingCellHandle(
      leaf,
      config,
      -side * doorWidth * 0.36,
      bottom + height / 2,
      doorHeight,
      doorMeta
    );
    const cellHandleRuntime = this.doorObjects.get(key);
    if (cellHandleRuntime && handleProjectionMm(config.handle) > 0) {
      // Скоба дверцы ячейки — в 0,36 ширины от середины, к открывающей кромке.
      cellHandleRuntime.handle = { along: doorWidth * 0.86, half: mm(5), face: mm(9), projection: mm(20) };
    }
    hinge.add(leaf);
    group.add(hinge);
  }

  private addShelvingCellHandle(
    parent: Group,
    config: ShelvingCellConfig,
    x: number,
    y: number,
    doorHeight: number,
    meta: PickMeta
  ) {
    if (config.handle === "push" || config.handle === "glassGrip") return;
    if (config.handle === "integrated") {
      return;
    }
    const metal = this.getMetalMaterial(
      config.handle === "profile" ? 0x8f928d : 0x323334
    );
    const length = Math.max(mm(48), Math.min(mm(150), doorHeight * 0.42));
    if (config.handle === "recessed") {
      this.addRoundedPanel(
        parent,
        x,
        y,
        mm(21),
        mm(26),
        length,
        mm(5),
        this.getSoftShadowMaterial(0.32),
        meta,
        mm(6)
      );
      return;
    }
    if (config.handle === "profile") {
      this.addRoundedPanel(
        parent,
        x,
        y,
        mm(22),
        mm(10),
        Math.max(mm(60), Math.min(doorHeight - mm(28), doorHeight * 0.62)),
        mm(7),
        metal,
        meta,
        mm(3)
      );
      return;
    }
    const handle = new Mesh(
      new CylinderGeometry(mm(5), mm(5), length, 16),
      metal
    );
    handle.position.set(x, y, mm(24));
    handle.userData.pick = meta;
    handle.castShadow = true;
    parent.add(handle);
  }

  private buildCornerModule(
    module: WardrobeModule,
    first: ModulePlacement,
    _second: ModulePlacement,
    materialKey: string
  ) {
    // Угловой по картинке Сергея (28.10): один прямой корпус вдоль задней или
    // боковой стены, уходит вглубь угла за соседний шкаф. Перед глухой частью —
    // добор в отделке дверей, дальше одна дверь с петлями у угла (31.10).
    const group = new Group();
    group.name = `${module.id}-corner`;
    const plan = cornerPlan(module);
    const left = cornerSide(module) === "left";
    const height = mm(module.height);
    const depth = mm(module.depth);
    const thickness = mm(CARCASS_MM);
    const t = CARCASS_MM;
    const plinthHeight = mm(cabinetBaseHeightMm(module));
    const baseType = cabinetBaseType(module);
    const sideBottom = baseType === "legs" ? plinthHeight : 0;
    const sideHeight = height - sideBottom;
    const bodyHeight = Math.max(thickness, height - plinthHeight - thickness * 2);
    const carcass = this.getDecorMaterial(materialKey);
    const back = this.getDecorMaterial(normalizeBackDecor(module) ?? materialKey);
    const shelfMaterial = this.getDecorMaterial(normalizeShelfDecor(module) ?? materialKey);
    const meta: PickMeta = { kind: "module", moduleId: module.id };
    // Плечо по задней стене — ширина модуля (у корпуса у боковой стены — глубина с дверью).
    const cornerX = first.x + (left ? -1 : 1) * mm(module.width) / 2;
    const backZ = first.z - depth;
    const worldX = (x: number) => cornerX + (left ? 1 : -1) * x;
    const toWorld = ([x, z]: [number, number]) => new Vector2(worldX(mm(x)), backZ + mm(z));
    /** Плита по прямоугольнику вдоль корпуса u0..u1 × v0..v1 (мм), снизу y, высотой h (сцена). */
    const slab = (u0: number, u1: number, v0: number, v1: number, y: number, h: number,
      material: PanelMaterial = carcass, owner: Group = group, pick: PickMeta = meta) => {
      const [x0, z0] = cornerToPlan(module, u0, v0);
      const [x1, z1] = cornerToPlan(module, u1, v1);
      return this.addPanel(owner, worldX(mm((x0 + x1) / 2)), y + h / 2, backZ + mm((z0 + z1) / 2),
        mm(Math.abs(x1 - x0)), h, mm(Math.abs(z1 - z0)), material, pick);
    };
    const mezzanine = mm(cornerMezzanineMm(module));
    group.userData.selectionOutline = { points: plan.points.map(toWorld).map((point) => [point.x, point.y]), height: height + mezzanine };
    // Размерные линии при перетаскивании полки — у двери, чуть внутри.
    const span = cornerDoorSpan(module);
    const [anchorX, anchorZ] = cornerToPlan(module, span.center, plan.d - 80);
    group.userData.cornerMeasureAnchor = [worldX(mm(anchorX)), backZ + mm(anchorZ)];

    const { length: L, d } = plan;
    const bottomY = plinthHeight;
    const topY = height - thickness;
    const bodyY = plinthHeight + thickness;
    // Боковины у стены в углу и у наружного торца, верх и дно между ними, задняя стенка.
    slab(0, t, 0, d, sideBottom, sideHeight);
    slab(L - t, L, 0, d, sideBottom, sideHeight);
    slab(t, L - t, 0, d, topY, thickness);
    slab(t, L - t, 0, d, bottomY, thickness);
    slab(t, L - t, 0, CORNER_BACK_MM, bodyY, bodyHeight, back);
    // Глухая панель перед глухой частью и стойка под петли у угла — одна
    // Г-образная деталь (31.10): петли двери — у добора, полки закрыты спереди.
    const hingePost = cornerHingePostSpan(module);
    const blindPanel = cornerBlindPanelSpan(module);
    for (const part of [hingePost, blindPanel]) {
      if (!part) continue;
      slab(part.from, part.to, part.back, part.front, bodyY, topY - bodyY);
      if (mezzanine > 0) slab(part.from, part.to, part.back, part.front, height + thickness, Math.max(thickness, mezzanine - thickness * 2));
    }
    // Щель за доборами, между лицом углового и соседом, — сверху планка вровень с крышами.
    const gapCover = cornerGapCoverSpan(module);
    if (gapCover) slab(gapCover.from, gapCover.to, gapCover.near, gapCover.face, height + mezzanine - thickness, thickness);
    // Антресоль над угловым — такой же корпус на нём, на одной высоте с антресолями соседей.
    if (mezzanine > 0) {
      slab(0, t, 0, d, height, mezzanine);
      slab(L - t, L, 0, d, height, mezzanine);
      slab(t, L - t, 0, d, height + mezzanine - thickness, thickness);
      slab(t, L - t, 0, d, height, thickness);
      slab(t, L - t, 0, CORNER_BACK_MM, height + thickness, Math.max(thickness, mezzanine - thickness * 2), back);
    }

    // Зеркало — только на наружном торце: торец в углу стоит у стены.
    if (this.project && activeSideMirrors(this.project, module).length) {
      const mirror = sideMirrorSize(module);
      const [mx, mz] = cornerToPlan(module, L, d / 2);
      const yaw = cornerRun(module) === "back" ? (left ? 1 : -1) * Math.PI / 2 : 0;
      this.addSideMirror(group, worldX(mm(mx)), mm(mirror.bottom + mirror.height / 2), backZ + mm(mz), yaw,
        mm(mirror.width), mm(mirror.height), meta);
    }

    // Цоколь — рамка под корпусом; передняя планка в цвет фасадов, без фрезеровки.
    if (plinthHeight > 0 && baseType === "plinth") {
      const plinth = this.getPlinthDecorMaterial(materialKey);
      const finish = cabinetPlinthFinish(module);
      const planks = cornerPlinthPlan(module);
      for (const [key, plank] of Object.entries(planks)) {
        const material = key === "front" && finish
          ? finish.kind === "enamel"
            ? this.getFacadeMaterial(finish.facade, materialKey, finish.facadeColor, "plain", mm(plank.length), finish.facadeColorHex)
            : finish.decor ? this.getPlinthDecorMaterial(finish.decor) : plinth
          : plinth;
        const [x0, z0] = plank.from;
        const [x1, z1] = plank.to;
        const alongX = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
        const piece = (px0: number, px1: number, pz0: number, pz1: number) =>
          this.addPanel(group, worldX(mm((px0 + px1) / 2)), plinthHeight / 2, backZ + mm((pz0 + pz1) / 2),
            mm(Math.abs(px1 - px0)), plinthHeight, mm(Math.abs(pz1 - pz0)), material, meta);
        if (alongX) piece(Math.min(x0, x1), Math.max(x0, x1), z0 - t / 2, z0 + t / 2);
        else piece(x0 - t / 2, x0 + t / 2, Math.min(z0, z1), Math.max(z0, z1));
      }
    } else if (plinthHeight > 0 && baseType === "legs") {
      const legMaterial = this.getMetalMaterial(0x383a3b);
      for (const [x, z] of cornerLegPoints(module)) {
        const leg = new Mesh(new CylinderGeometry(mm(15), mm(15) * 1.12, plinthHeight, 20), legMaterial);
        leg.position.set(worldX(mm(x)), plinthHeight / 2, backZ + mm(z));
        leg.castShadow = true;
        leg.receiveShadow = true;
        leg.userData.pick = meta;
        group.add(leg);
      }
    }

    // Наполнение: полки во всю длину и штанги — вглубь угла, за соседний шкаф.
    const along = cornerRun(module) === "back";
    module.items.forEach((item) => {
      const itemGroup = new Group();
      if (item.type === "shelf") {
        this.addHorizontalLPanel(itemGroup, cornerShelfPlan(module).map(toWorld), 0, thickness, shelfMaterial,
          { kind: "item", moduleId: module.id, itemId: item.id });
        itemGroup.position.y = mm(item.y);
      } else if (item.type === "rail") {
        const rail = cornerRailPlan(module);
        const geometry = this.buildItem(module, item, mm(rail.inner - 4), depth, materialKey);
        const localAxisZ = -depth / 2 + mm(18) + depth * 0.035;
        const [cx, cz] = [(rail.from[0] + rail.to[0]) / 2, (rail.from[1] + rail.to[1]) / 2];
        if (along) {
          geometry.position.set(worldX(mm(cx)), 0, backZ + mm(rail.offset) - localAxisZ);
        } else {
          // Корпус у боковой стены: штанга вдоль неё, деталь повёрнута лицом в комнату.
          geometry.rotation.y = (left ? 1 : -1) * Math.PI / 2;
          geometry.position.set(worldX(mm(rail.offset) - localAxisZ), 0, backZ + mm(cz));
        }
        itemGroup.add(geometry);
        itemGroup.position.y = mm(item.y);
      }
      group.add(itemGroup);
      this.itemObjects.set(item.id, itemGroup);
    });

    if (module.door === "open") return group;
    // Одна дверь, петли у угла, у добора (cornerDoorLeaves). Корпус у боковой
    // стены смотрит в комнату боком: его локальная ось X — вправо для того,
    // кто стоит перед ним.
    for (const leaf of cornerDoorLeaves(module)) {
      const upper = leaf.index === 1;
      const frame = new Group();
      frame.name = `${module.id}-corner-door${upper ? "-mezzanine" : ""}`;
      if (along) {
        frame.position.set(worldX(mm(span.center)), upper ? height : 0, backZ + depth);
      } else {
        frame.position.set(worldX(depth), upper ? height : 0, backZ + mm(span.center));
        frame.rotation.y = (left ? 1 : -1) * Math.PI / 2;
      }
      group.add(frame);
      // Дверь антресоли — как у антресоли шкафа: без цоколя, на её высоту.
      const doorModule: WardrobeModule = upper
        ? { ...module, type: "single", width: span.width + 4, height: cornerMezzanineMm(module), baseType: "floor", baseHeight: 0, cabinetDrawers: undefined }
        : { ...module, type: "single", width: span.width + 4, cabinetDrawers: undefined };
      this.addDoors(frame, doorModule, mm(span.width + 4), upper ? mezzanine : height, depth, materialKey, 0, [leaf]);
      // Створки одного модуля по высоте: дверь антресоли не мешает нижней двери.
      this.doorObjects.forEach((runtime) => {
        if (runtime.moduleId !== module.id || runtime.doorIndex !== leaf.index) return;
        runtime.minY = upper ? height : 0;
        runtime.maxY = upper ? height + mezzanine : height;
      });
    }
    // Доборы — углового (у соседа) и соседа (у угла), в отделке дверей, неподвижные (31.10).
    const filler = cornerFillerSpan(module);
    const neighbourFiller = cornerNeighbourFillerSpan(module);
    const fillerMaterial = module.facade === "glass" || module.facade === "mirror"
      ? carcass
      : this.getFacadeMaterial(module.facade, module.facadeDecor ?? materialKey, module.facadeColor, "plain",
        mm(filler.widthMm), module.facadeColorHex);
    const doorHeight = Math.max(mm(220), height - plinthHeight - mm(8));
    for (const strip of neighbourFiller.widthMm ? [filler, neighbourFiller] : [filler]) {
      slab(strip.from, strip.to, strip.near, strip.face, plinthHeight + mm(2), doorHeight, fillerMaterial);
      if (mezzanine > 0) slab(strip.from, strip.to, strip.near, strip.face, height + mm(2), Math.max(mm(220), mezzanine - mm(8)), fillerMaterial);
    }
    return group;
  }

  private addHorizontalLPanel(
    parent: Group,
    points: Vector2[],
    centerY: number,
    thickness: number,
    material: PanelMaterial,
    meta?: PickMeta
  ) {
    const shape = new Shape(points);
    const geometry = new ExtrudeGeometry(shape, {
      depth: thickness,
      bevelEnabled: false
    });
    const mesh = new Mesh(geometry, material);
    geometry.rotateX(Math.PI / 2);
    this.applyPhysicalWoodUvs(mesh, 0, centerY, 0, parent, meta);
    mesh.position.y = centerY + thickness / 2;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (meta) mesh.userData.pick = meta;
    parent.add(mesh);
    return mesh;
  }

  private localToWorld(placement: ModulePlacement, localX: number, localZ: number) {
    const cos = Math.cos(placement.yaw);
    const sin = Math.sin(placement.yaw);
    return {
      x: placement.x + cos * localX + sin * localZ,
      z: placement.z - sin * localX + cos * localZ
    };
  }

  private addPlacedPanel(
    parent: Group,
    placement: ModulePlacement,
    localX: number,
    y: number,
    localZ: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta?: PickMeta
  ) {
    const position = this.localToWorld(placement, localX, localZ);
    const panel = this.addPanel(
      parent,
      position.x,
      y,
      position.z,
      width,
      height,
      depth,
      material,
      meta
    );
    panel.rotation.y = placement.yaw;
    return panel;
  }

  private addCornerShelfHoles(
    group: Group,
    module: WardrobeModule,
    cornerX: number,
    backZ: number,
    frontZ: number,
    sideEndZ: number,
    leftX: number,
    _frontX: number,
    height: number
  ) {
    const material = new MeshBasicMaterial({
      color: 0x5e5143,
      transparent: true,
      opacity: 0.58
    });
    material.userData.shared = false;
    const meta: PickMeta = { kind: "module", moduleId: module.id };
    const hole = mm(4);
    const rows = [
      { x: leftX + mm(54), z: backZ + mm(12), rotation: 0 },
      { x: cornerX - mm(54), z: backZ + mm(12), rotation: 0 },
      { x: cornerX - mm(12), z: frontZ + mm(54), rotation: Math.PI / 2 },
      { x: cornerX - mm(12), z: sideEndZ - mm(54), rotation: Math.PI / 2 }
    ];

    for (let y = mm(118); y < height - mm(82); y += mm(64)) {
      rows.forEach((row) => {
        const holeMesh = this.addDetailPanel(
          group,
          row.x,
          y,
          row.z,
          hole,
          hole,
          mm(1.5),
          material,
          meta
        );
        holeMesh.rotation.y = row.rotation;
      });
    }
  }

  private addCornerConnector(
    parent: Group,
    first: ModulePlacement,
    second: ModulePlacement,
    materialKey: string
  ) {
    const width = mm(first.module.width);
    const jointX = first.x + Math.cos(first.yaw) * width * 0.5;
    const jointZ = first.z - Math.sin(first.yaw) * width * 0.5;
    const averageYaw = (first.yaw + second.yaw) * 0.5;
    const backX = Math.sin(averageYaw);
    const backZ = Math.cos(averageYaw);
    const post = new Mesh(
      new BoxGeometry(mm(92), mm(first.module.height - 8), mm(92)),
      this.getDecorMaterial(materialKey)
    );
    post.position.set(
      jointX - backX * mm(34),
      mm(first.module.height) * 0.5,
      jointZ - backZ * mm(34)
    );
    post.rotation.y = averageYaw;
    post.castShadow = true;
    post.receiveShadow = true;
    post.userData.pick = {
      kind: "module",
      moduleId: first.module.id
    } satisfies PickMeta;
    parent.add(post);
  }

  private addModuleEdgeDetails(
    group: Group,
    module: WardrobeModule,
    width: number,
    height: number,
    depth: number,
    innerWidth: number,
    thickness: number,
    plinthHeight: number,
    bodyBottom: number,
    bodyHeight: number,
    materialKey: string,
    meta: PickMeta
  ) {
    const shadow = this.getSoftShadowMaterial(0.025);
    const strongShadow = this.getSoftShadowMaterial(0.055);
    const backLine = this.getBackLineMaterial();
    const bodyCenterY = bodyBottom + bodyHeight / 2;

    [-1, 1].forEach((side) => {
      this.addDetailPanel(
        group,
        side * (width / 2 - thickness - mm(2)),
        bodyCenterY,
        -depth + mm(11),
        mm(3),
        Math.max(thickness, bodyHeight - thickness * 2.6),
        mm(4),
        shadow,
        meta
      );
    });

    this.addDetailPanel(
      group,
      0,
      height - thickness - mm(1),
      -mm(15),
      innerWidth - mm(32),
      mm(2),
      mm(26),
      shadow,
      meta
    );
    this.addDetailPanel(
      group,
      0,
      plinthHeight + thickness + mm(1),
      -mm(15),
      innerWidth - mm(32),
      mm(2),
      mm(26),
      shadow,
      meta
    );

    this.addDetailPanel(
      group,
      0,
      plinthHeight + mm(6),
      mm(6),
      Math.max(mm(80), width - mm(28)),
      mm(12),
      mm(14),
      strongShadow,
      meta
    );

    if (innerWidth > mm(520)) {
      this.addDetailPanel(
        group,
        0,
        bodyCenterY,
        -depth + mm(9),
        mm(1.4),
        Math.max(thickness, bodyHeight - thickness * 2.4),
        mm(2),
        backLine,
        meta
      );
    }

    if (module.height > 1500) {
      this.addDetailPanel(
        group,
        0,
        plinthHeight + bodyHeight * 0.52,
        -depth + mm(9),
        innerWidth - mm(36),
        mm(1.2),
        mm(2),
        backLine,
        meta
      );
    }
  }

  private addModulePlinth(
    group: Group,
    width: number,
    depth: number,
    materialKey: string,
    meta: PickMeta,
    module?: WardrobeModule,
    materialOverride?: PanelMaterial,
    /** Стеллаж: передняя планка между боковинами, вровень с их кромкой. */
    flush = false
  ) {
    const baseType = module ? cabinetBaseType(module) : "plinth";
    if (baseType === "floor") return;
    const plinth = materialOverride ?? this.getPlinthDecorMaterial(materialKey);
    const shadow = this.getSoftShadowMaterial(0.08);
    const plinthHeight = mm(
      module ? cabinetBaseHeightMm(module) : PLINTH_HEIGHT / MM
    );
    const setback = flush ? 0 : mm(
      module ? cabinetBaseSetbackMm(module) : 28
    );
    const frontZ = flush ? -mm(CARCASS_MM) / 2 : -setback;
    if (baseType === "legs") {
      const legMaterial = this.getMetalMaterial(0x383a3b);
      const legRadius = mm(15);
      const legX = Math.max(0, width / 2 - mm(54));
      const frontLegZ = -Math.max(setback, mm(54));
      const backLegZ = -depth + mm(54);
      [-1, 1].forEach((side) => {
        [backLegZ, frontLegZ].forEach((legZ) => {
          const leg = new Mesh(
            new CylinderGeometry(
              legRadius,
              legRadius * 1.12,
              plinthHeight,
              20
            ),
            legMaterial
          );
          leg.position.set(
            side * legX,
            plinthHeight / 2,
            legZ
          );
          leg.castShadow = true;
          leg.receiveShadow = true;
          leg.userData.pick = meta;
          group.add(leg);
        });
      });
      return;
    }
    const plinthWidth = Math.max(mm(80), width - mm(flush ? CARCASS_MM * 2 : 28));
    // Передняя планка — в цвет фасадов, без фрезеровки (видео Сергея 23.10).
    const finish = module && !materialOverride ? cabinetPlinthFinish(module) : null;
    const front = !finish ? plinth
      : finish.kind === "enamel"
        ? this.getFacadeMaterial(finish.facade, materialKey, finish.facadeColor, "plain", plinthWidth, finish.facadeColorHex)
        : finish.decor ? this.getPlinthDecorMaterial(finish.decor) : plinth;
    this.addPanel(
      group,
      0,
      plinthHeight / 2,
      frontZ,
      plinthWidth,
      plinthHeight,
      mm(CARCASS_MM),
      front,
      meta
    );
    // Боковые планки — за передней: у планки заподлицо их торцы не видны.
    const returnStart = flush ? mm(CARCASS_MM) : setback;
    const returnDepth = Math.max(mm(80), depth - returnStart);
    [-1, 1].forEach((side) => {
      this.addPanel(
        group,
        side * (width / 2 - mm(23)),
        plinthHeight / 2,
        -(depth + returnStart) / 2,
        mm(CARCASS_MM),
        plinthHeight,
        returnDepth,
        plinth,
        meta
      );
    });
    // Задняя планка у стены: рамка замкнута, сзади под корпусом нет прорези.
    this.addPanel(
      group,
      0,
      plinthHeight / 2,
      -depth + mm(CARCASS_MM) / 2,
      mm(plinthBackPlankMm(width / MM)),
      plinthHeight,
      mm(CARCASS_MM),
      plinth,
      meta
    );
    if (flush) return;
    this.addDetailPanel(
      group,
      0,
      mm(6),
      -Math.min(mm(22), depth * 0.05),
      Math.max(mm(80), width - mm(46)),
      mm(12),
      mm(14),
      shadow,
      meta
    );
  }

  private addInstallationPanels(
    group: Group,
    module: WardrobeModule,
    installedHeight: number,
    materialKey: string,
    meta: PickMeta,
    placement: ModulePlacement,
    stackedModule?: {
      height: number;
      materialKey: string;
      meta: PickMeta;
    }
  ) {
    const left = mm(leftFillerMm(module));
    const right = mm(rightFillerMm(module));
    const top = mm(topFillerMm(module));
    if (left <= 0 && right <= 0 && top <= 0) return;

    const width = mm(module.width);
    const thickness = mm(CARCASS_MM);
    const material = this.getDecorMaterial(materialKey);
    const stackedMaterial = stackedModule
      ? this.getDecorMaterial(stackedModule.materialKey)
      : material;
    const stackedBottom = Math.max(
      0,
      installedHeight - (stackedModule?.height ?? 0)
    );
    const fillerBottom =
      cabinetBaseType(module) === "legs"
        ? mm(cabinetBaseHeightMm(module))
        : 0;
    const routeSpan = installationSpanMm(module);
    const wallToleranceMm = 1;
    const notchLeft =
      (placement.wall === "back" &&
        placement.wallOffset <=
          PERPENDICULAR_WALL_CLEARANCE_MM + wallToleranceMm) ||
      (placement.wall === "right" &&
        placement.wallOffset <=
          BACK_WALL_LENGTH_MM +
            PERPENDICULAR_WALL_CLEARANCE_MM +
            wallToleranceMm);
    const notchRight =
      (placement.wall === "back" &&
        placement.wallOffset + routeSpan >=
          BACK_WALL_LENGTH_MM -
            PERPENDICULAR_WALL_CLEARANCE_MM -
            wallToleranceMm) ||
      (placement.wall === "left" &&
        placement.wallOffset + routeSpan >=
          -PERPENDICULAR_WALL_CLEARANCE_MM - wallToleranceMm);
    const addSideFiller = (
      side: -1 | 1,
      fillerWidth: number,
      notchForBaseboard: boolean
    ) => {
      if (fillerWidth <= 0) return;
      const centerX = side * (width / 2 + fillerWidth / 2);
      const addFillerBand = (
        bottom: number,
        top: number,
        bandWidth: number,
        bandMaterial: PanelMaterial,
        bandMeta: PickMeta,
        alignToCabinetEdge = false
      ) => {
        const bandHeight = Math.max(0, top - bottom);
        if (bandHeight <= 0 || bandWidth <= 0) return;
        this.addPanel(
          group,
          alignToCabinetEdge
            ? side * (width / 2 + bandWidth / 2)
            : centerX,
          bottom + bandHeight / 2,
          -thickness / 2,
          bandWidth,
          bandHeight,
          thickness,
          bandMaterial,
          bandMeta
        );
      };

      const regularBottom = notchForBaseboard
        ? Math.max(fillerBottom, ROOM_BASEBOARD_HEIGHT)
        : fillerBottom;
      const lowerTop = Math.min(installedHeight, stackedBottom);
      addFillerBand(
        regularBottom,
        lowerTop,
        fillerWidth,
        material,
        meta
      );
      if (stackedModule) {
        addFillerBand(
          Math.max(regularBottom, stackedBottom),
          installedHeight,
          fillerWidth,
          stackedMaterial,
          stackedModule.meta
        );
      } else {
        addFillerBand(
          Math.max(regularBottom, lowerTop),
          installedHeight,
          fillerWidth,
          material,
          meta
        );
      }

      if (!notchForBaseboard) return;
      const lowerWidth = Math.max(
        0,
        fillerWidth - ROOM_BASEBOARD_PROJECTION
      );
      const lowerHeight = Math.max(
        0,
        Math.min(installedHeight, ROOM_BASEBOARD_HEIGHT) - fillerBottom
      );
      addFillerBand(
        fillerBottom,
        fillerBottom + lowerHeight,
        lowerWidth,
        material,
        meta,
        true
      );
    };
    if (left > 0) {
      addSideFiller(-1, left, notchLeft);
    }
    if (right > 0) {
      addSideFiller(1, right, notchRight);
    }
    if (top > 0) {
      this.addPanel(
        group,
        (right - left) / 2,
        installedHeight + top / 2,
        -thickness / 2,
        width + left + right,
        top,
        thickness,
        stackedModule ? stackedMaterial : material,
        stackedModule?.meta ?? meta
      );
    }
  }

  private wallPlacementGroups(placements: ModulePlacement[]) {
    const groups = new Map<ModulePlacement["wall"], ModulePlacement[]>();
    placements
      .filter(
        (placement) =>
          placement.wing === 0 &&
          placement.module.type !== "corner45" &&
          !isStackedModule(placement.module)
      )
      .forEach((placement) => {
        const group = groups.get(placement.wall) ?? [];
        group.push(placement);
        groups.set(placement.wall, group);
      });

    groups.forEach((group) =>
      group.sort((first, second) => first.wallOffset - second.wallOffset)
    );
    return groups;
  }

  private internalItemVerticalSpan(
    module: WardrobeModule,
    item: InternalItem
  ): [number, number] {
    if (item.type === "shelf" || item.type === "led") {
      return [item.y - HALF_CARCASS_MM, item.y + HALF_CARCASS_MM];
    }
    if (item.type === "drawer" || item.type === "basket") {
      const height = item.height ?? (item.type === "drawer" ? 180 : 160);
      return [item.y - height, item.y];
    }
    const fitting = fittingVerticalOffsets(item.type, item.height);
    if (fitting) return [item.y + fitting[0], item.y + fitting[1]];
    if (item.type === "pantograph") {
      return [item.y - 390, item.y + 20];
    }
    if (item.type === "divider") {
      const height = item.height ?? Math.min(1400, module.height - 180);
      return [item.y - height / 2, item.y + height / 2];
    }
    if (item.type === "rail") {
      const garments = this.hangingGarmentPlans(module, item);
      const lowestVisibleGarment = garments.length
        ? item.y - Math.max(...garments.map((garment) => garment.requiredHeightMm))
        : item.y - 13;
      return [lowestVisibleGarment, item.y + 13];
    }
    return [item.y - 20, item.y + 20];
  }

  private isDecorativeSpaceAvailable(
    module: WardrobeModule,
    ownerItemId: string,
    bottom: number,
    top: number,
    horizontal: [number, number]
  ) {
    const lowerLimit =
      module.type === "mezzanine" ? CARCASS_MM : cabinetBaseHeightMm(module);
    if (bottom < lowerLimit || top > module.height - CARCASS_MM) return false;
    const clearance = 4;

    return module.items.every((candidate) => {
      if (candidate.id === ownerItemId) return true;
      if (candidate.type === "rail") {
        // Сама штанга тоже занимает место: сумка или шляпа не должна её
        // «проглатывать», даже если одежды под штангой нет (приёмка 04.10, wf-03).
        const barOverlaps = bottom < candidate.y + 14 + clearance && top > candidate.y - 14 - clearance;
        if (barOverlaps && usableItemPieces(module, candidate)
          .some((piece) => piece.right > horizontal[0] && piece.left < horizontal[1])) return false;
        return this.hangingGarmentPlans(module, candidate).every((garment) => {
          const garmentBottom = candidate.y - garment.requiredHeightMm;
          const verticallyOverlaps =
            bottom < candidate.y + 13 + clearance &&
            top > garmentBottom - clearance;
          if (!verticallyOverlaps) return true;

          const halfWidth = garment.dimensionsMm[0] / 2 + 12;
          return (
            garment.slotX + halfWidth < horizontal[0] ||
            garment.slotX - halfWidth > horizontal[1]
          );
        });
      }
      const [candidateBottom, candidateTop] =
        this.internalItemVerticalSpan(module, candidate);
      const verticallyOverlaps =
        bottom < candidateTop + clearance &&
        top > candidateBottom - clearance;
      if (!verticallyOverlaps) return true;

      if (candidate.type === "divider") {
        const dividerX = candidate.x ?? 0;
        return (
          dividerX + 18 < horizontal[0] ||
          dividerX - 18 > horizontal[1]
        );
      }
      return false;
    });
  }

  private shelfDecorationSpans(
    module: WardrobeModule,
    ownerItemId: string,
    innerWidth: number,
    bottom: number,
    top: number
  ): Array<[number, number]> {
    const left = -innerWidth / 2 + mm(28);
    const right = innerWidth / 2 - mm(28);
    if (right - left < mm(150)) return [];

    const blocked = module.items
      .filter((candidate) => {
        if (candidate.id === ownerItemId || candidate.type !== "divider") {
          return false;
        }
        const [candidateBottom, candidateTop] =
          this.internalItemVerticalSpan(module, candidate);
        return bottom < candidateTop && top > candidateBottom;
      })
      .map((candidate) => mm(candidate.x ?? 0))
      .sort((first, second) => first - second);

    const spans: Array<[number, number]> = [];
    let cursor = left;
    blocked.forEach((dividerX) => {
      const next = Math.max(left, dividerX - mm(30));
      if (next > cursor) spans.push([cursor, next]);
      cursor = Math.max(cursor, dividerX + mm(30));
    });
    if (cursor < right) spans.push([cursor, right]);
    return spans.filter(([start, end]) => end - start >= mm(150));
  }

  private markDecorativeProp(
    object: Group,
    ownerItemId: string,
    localBottom: number,
    localTop: number,
    horizontal: [number, number]
  ) {
    object.userData.decorativeProp = true;
    object.userData.ownerItemId = ownerItemId;
    object.userData.localBottomMm = localBottom;
    object.userData.localTopMm = localTop;
    object.userData.horizontalMinMm = horizontal[0];
    object.userData.horizontalMaxMm = horizontal[1];
  }

  private seededVariant<T>(seed: string, variants: readonly T[]) {
    let hash = 2166136261;
    for (let index = 0; index < seed.length; index += 1) {
      hash ^= seed.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return variants[Math.abs(hash) % variants.length]!;
  }

  private seededVariantOrder<T>(seed: string, variants: readonly T[]) {
    const first = this.seededVariant(seed, variants);
    const firstIndex = variants.indexOf(first);
    return [
      ...variants.slice(firstIndex),
      ...variants.slice(0, firstIndex)
    ];
  }

  private hangingGarmentPlans(
    module: WardrobeModule,
    item: InternalItem,
    innerWidth = mm(Math.max(100, module.width - 36)),
    availableDepth = mm(Math.max(100, module.depth - 36))
  ): HangingGarmentPlan[] {
    if (item.type !== "rail") return [];
    const garmentTypes = fittingGarmentSequence(module, item);
    if (!garmentTypes.length) return [];
    const availableModelHeightMm = Math.max(
      0,
      availableHangingHeight(module, item) - GARMENT_MODEL_TOP_DROP_MM
    );
    const availableModelDepthMm = availableDepth / MM + 18;
    const fittingModelHeights = garmentTypes.flatMap((garmentType) =>
      (HANGING_DECORATIVE_VARIANTS[garmentType] ??
        HANGING_DECORATIVE_VARIANTS.shirts)
        .map((modelKey) => HANGING_DECORATIVE_DIMENSIONS_MM[modelKey])
        .filter(
          (dimensions) =>
            dimensions[1] <= availableModelHeightMm + 2 &&
            dimensions[2] <= availableModelDepthMm + 2
        )
        .map((dimensions) => dimensions[1])
    );
    if (!fittingModelHeights.length) return [];

    const lowestPossibleGarmentMm =
      item.y -
      GARMENT_MODEL_TOP_DROP_MM -
      Math.max(...fittingModelHeights);
    const hangingSegments = this.horizontalSegmentsForDividers(
      module,
      lowestPossibleGarmentMm,
      item.y + 14,
      innerWidth,
      27,
      36,
      72
    );
    const slots = hangingSlots(
      hangingSegments.map(
        ([segmentStart, segmentEnd]) =>
          [segmentStart / MM, segmentEnd / MM] as [number, number]
      ),
      module.decorDensity === "minimal"
        ? 92
        : module.decorDensity === "full"
          ? 40
          : 52
    );

    return slots.flatMap((slot, index) => {
      const horizontalRoomMm = Math.max(
        0,
        2 *
          Math.min(
            slot.x - slot.segmentStart,
            slot.segmentEnd - slot.x
          )
      );
      const desiredType = garmentTypes[index % garmentTypes.length]!;
      const typeOrder = Array.from(
        new Set([desiredType, ...garmentTypes])
      );
      let selected:
        | {
            garmentType: NonNullable<InternalItem["clothes"]>;
            modelKey: HangingDecorativeModelKey;
          }
        | undefined;

      for (const garmentType of typeOrder) {
        const variants = this.seededVariantOrder(
          `${item.id}-${index}-${garmentType}`,
          HANGING_DECORATIVE_VARIANTS[garmentType] ??
            HANGING_DECORATIVE_VARIANTS.shirts
        );
        const preferredVariants =
          garmentType === "shirts" && index % 4 !== 1
            ? [
                "hangingBlouse" as const,
                ...variants.filter((modelKey) => modelKey !== "hangingBlouse")
              ]
            : garmentType === "jackets" && index % 3 !== 1
              ? [
                  "hangingVest" as const,
                  ...variants.filter((modelKey) => modelKey !== "hangingVest")
                ]
              : variants;
        const modelKey = preferredVariants.find((candidateKey) => {
          const dimensions = HANGING_DECORATIVE_DIMENSIONS_MM[candidateKey];
          return (
            dimensions[0] <= horizontalRoomMm + 2 &&
            dimensions[1] <= availableModelHeightMm + 2 &&
            dimensions[2] <= availableModelDepthMm + 2
          );
        });
        if (!modelKey) continue;
        selected = { garmentType, modelKey };
        break;
      }
      if (!selected) return [];

      const dimensionsMm =
        HANGING_DECORATIVE_DIMENSIONS_MM[selected.modelKey];
      return [
        {
          slotX: slot.x,
          garmentType: selected.garmentType,
          modelKey: selected.modelKey,
          dimensionsMm,
          requiredHeightMm: GARMENT_MODEL_TOP_DROP_MM + dimensionsMm[1],
          index,
          count: slots.length
        } satisfies HangingGarmentPlan
      ];
    });
  }

  private shelfDecorationPlans(
    seed: string,
    span: [number, number],
    availableDepth: number,
    density: WardrobeModule["decorDensity"] = "normal"
  ): ShelfDecorationPlan[] {
    const sideClearance = 14;
    const gap = 22;
    const usableStart = span[0] / MM + sideClearance;
    const usableEnd = span[1] / MM - sideClearance;
    const usableWidth = Math.max(0, usableEnd - usableStart);
    if (usableWidth < 250) return [];

    const normalCount = usableWidth >= 930 ? 3 : usableWidth >= 610 ? 2 : 1;
    const targetCount =
      density === "minimal"
        ? 1
        : density === "full"
          ? Math.min(4, normalCount + 1)
          : normalCount;
    const availableDepthMm = availableDepth / MM;
    const candidates = this.seededVariantOrder(
      seed,
      SHELF_DECORATIVE_MODEL_KEYS
    ).filter(
      (modelKey) =>
        SHELF_DECORATIVE_DIMENSIONS_MM[modelKey][2] <= availableDepthMm + 2
    );

    const findCombination = (
      desiredCount: number,
      startIndex = 0,
      selected: ShelfDecorativeModelKey[] = [],
      usedWidth = 0
    ): ShelfDecorativeModelKey[] | null => {
      if (selected.length === desiredCount) return selected;
      for (let index = startIndex; index < candidates.length; index += 1) {
        const modelKey = candidates[index]!;
        const width = SHELF_DECORATIVE_DIMENSIONS_MM[modelKey][0];
        const nextWidth = usedWidth + width + (selected.length ? gap : 0);
        if (nextWidth > usableWidth + 1) continue;
        const result = findCombination(
          desiredCount,
          index + 1,
          [...selected, modelKey],
          nextWidth
        );
        if (result) return result;
      }
      return null;
    };

    let selectedKeys: ShelfDecorativeModelKey[] = [];
    for (let count = targetCount; count >= 1; count -= 1) {
      const combination = findCombination(count);
      if (combination) {
        selectedKeys = combination;
        break;
      }
    }
    if (!selectedKeys.length) return [];

    const totalWidth =
      selectedKeys.reduce(
        (sum, modelKey) =>
          sum + SHELF_DECORATIVE_DIMENSIONS_MM[modelKey][0],
        0
      ) +
      gap * Math.max(0, selectedKeys.length - 1);
    let cursor = (usableStart + usableEnd - totalWidth) / 2;
    return selectedKeys.map((modelKey, index) => {
      const dimensionsMm = SHELF_DECORATIVE_DIMENSIONS_MM[modelKey];
      const centerX = cursor + dimensionsMm[0] / 2;
      cursor += dimensionsMm[0] + gap;
      return { modelKey, centerX, dimensionsMm, index };
    });
  }

  private addShelfDecorativeProps(
    parent: Group,
    module: WardrobeModule,
    item: InternalItem,
    innerWidth: number,
    innerDepth: number,
    centerZ: number,
    meta: PickMeta
  ) {
    if (!this.shelfModelsReady) return;
    const localBottom = 12;
    const availableDepth = innerDepth - mm(50);
    if (availableDepth < mm(190)) return;
    const spans = this.shelfDecorationSpans(
      module,
      item.id,
      innerWidth,
      item.y + localBottom,
      item.y + localBottom + 310
    );

    spans.forEach((span, spanIndex) => {
      this.shelfDecorationPlans(
        `${item.id}-${spanIndex}`,
        span,
        availableDepth,
        module.decorDensity
      ).forEach((plan) => {
        const fitted = this.createDecorativeModel(
          plan.modelKey,
          new Vector3(
            mm(plan.dimensionsMm[0] + 2),
            mm(plan.dimensionsMm[1] + 2),
            availableDepth
          ),
          new Vector3(
            mm(plan.centerX),
            mm(localBottom),
            centerZ - mm(6 + ((plan.index % 3) - 1) * 5)
          ),
          meta,
          `${item.id}-${spanIndex}-${plan.index}`,
          {
            preserveScale: true,
            yawRange: plan.modelKey === "hat" ? 0.07 : 0.024
          }
        );
        if (!fitted) return;

        const localTop = localBottom + fitted.size.y / MM;
        const horizontal: [number, number] = [
          plan.centerX - fitted.size.x / MM / 2 - 10,
          plan.centerX + fitted.size.x / MM / 2 + 10
        ];
        this.markDecorativeProp(
          fitted.object,
          item.id,
          localBottom,
          localTop,
          horizontal
        );
        fitted.object.visible = this.isDecorativeSpaceAvailable(
          module,
          item.id,
          item.y + localBottom,
          item.y + localTop,
          horizontal
        );
        parent.add(fitted.object);
      });
    });
  }

  /** Отдельная деталь в своём отсеке: начало координат — середина отсека. */
  private itemPieceHolder(parent: Group, left: number, right: number) {
    const holder = new Group();
    holder.position.x = (left + right) / 2;
    holder.userData.itemPiece = { left, right };
    parent.add(holder);
    return holder;
  }

  /**
   * Выдвижная часть внутреннего ящика, корзины или брючницы: щелчок по ней
   * выдвигает её, как ящики стеллажа (приёмка 04.10, wf-02/wf-04).
   */
  private pullOutSlide(holder: Group, moduleId: string, itemId: string, pieceIndex: number, innerDepth: number, recess = 0) {
    const slide = new Group();
    holder.add(slide);
    const doorIndex = `item:${itemId}:${pieceIndex}`;
    const key = this.doorKey(moduleId, doorIndex);
    const openShiftZ = innerDepth * 0.7;
    const closedZ = -recess;
    slide.position.z = closedZ + openShiftZ * this.easeDoorProgress(this.doorVisualProgress(key));
    slide.userData.pullOutDoorIndex = doorIndex;
    this.doorObjects.set(key, { key, kind: "drawer", moduleId, doorIndex, object: slide, side: 0, closedZ, openShiftZ });
    return slide;
  }

  /** Выдвижная часть отсека, по которой попали щелчком (у строки на несколько отсеков их несколько). */
  private pullOutDoorIndexAt(object: Object3D | null) {
    for (let current = object; current; current = current.parent) {
      const doorIndex = current.userData.pullOutDoorIndex;
      if (typeof doorIndex === "string") return doorIndex;
    }
    return undefined;
  }

  /**
   * Выдвинуть или задвинуть выдвижную деталь; закрытые двери шкафа открываются сами.
   * Ящики в соседних отсеках разделены стойкой и выдвигаются каждый сам по себе:
   * щелчок двигает только тот, по которому попали (приёмка 16.10, «Гардероб до потолка»).
   */
  private toggleItemPullOut(moduleId: string, itemId: string, doorIndex?: string) {
    const prefix = this.doorKey(moduleId, `item:${itemId}:`);
    const single = doorIndex?.startsWith(`item:${itemId}:`) ? this.doorKey(moduleId, doorIndex) : undefined;
    const keys = [...this.doorObjects.keys()].filter(key => single ? key === single : key.startsWith(prefix));
    if (!keys.length) return false;
    const opening = !keys.some(key => this.openDoors.has(key));
    if (opening) {
      // Ящик за дверью: сначала открываем двери этого корпуса.
      [...this.doorObjects.values()]
        .filter(runtime => runtime.moduleId === moduleId && runtime.kind !== "drawer" && !this.openDoors.has(runtime.key))
        .forEach(runtime => this.toggleDoor(moduleId, runtime.doorIndex));
    }
    keys.forEach(key => {
      if (!this.doorProgress.has(key)) this.doorProgress.set(key, opening ? 0 : 1);
      if (opening) this.openDoors.add(key); else this.openDoors.delete(key);
    });
    this.lastDoorAnimationTime = performance.now();
    this.invalidate(36);
    return true;
  }

  private buildItem(
    module: WardrobeModule,
    item: InternalItem,
    innerWidth: number,
    depth: number,
    materialKey: string,
    pieces: Array<[number, number]> = [[-innerWidth / 2, innerWidth / 2]]
  ) {
    const group = new Group();
    group.name = item.id;
    const meta: PickMeta = { kind: "item", moduleId: module.id, itemId: item.id };
    // За распашными дверями выдвижное наполнение — на проставках и утоплено.
    const behindDoors = module.door === "hinged";
    const carcass = this.getDecorMaterial(materialKey);
    // Полки и ящики могут быть в своём декоре (16.10).
    const shelfKey = normalizeShelfDecor(module) ?? materialKey;
    const shelfMaterial = this.getDecorMaterial(shelfKey);
    const metal = this.getMetalMaterial();
    const innerDepth = depth - mm(36);
    const centerZ = -depth / 2 + mm(18);
    let selectionSize = new Vector3(innerWidth, mm(42), mm(54));
    let selectionPosition = new Vector3(0, 0, centerZ);

    if (item.type === "shelf" || item.type === "led") {
      pieces.forEach(([left, right]) => {
        const holder = this.itemPieceHolder(group, left, right);
        const pieceWidth = right - left;
        this.addPanel(holder, 0, 0, centerZ, pieceWidth, mm(CARCASS_MM), innerDepth, shelfMaterial, meta);
        this.addShelfFrontDetail(holder, pieceWidth, centerZ, shelfKey, meta);
        this.addShelfSupports(holder, pieceWidth, innerDepth, centerZ, meta);
        this.addInternalShadowPatch(holder, 0, -mm(28), -depth + mm(17), pieceWidth - mm(34), mm(58), 0.12, meta);
        if (item.type === "led" || cabinetShelfLit(item)) {
          // Лента под полкой: у передней кромки или у задней стенки (замечание № 13).
          const temperature = cabinetLightTemperature(module);
          const led = new Mesh(
            new BoxGeometry(pieceWidth * 0.82, mm(5), mm(7)),
            this.getLightingMaterial("strip", temperature)
          );
          led.position.set(0, -mm(12), cabinetLightPosition(module) === "back" ? -depth + mm(40) : -mm(30));
          led.userData.pick = meta;
          holder.add(led);
        }
      });
      selectionSize = new Vector3(innerWidth, mm(30), innerDepth);
      if (item.type === "shelf") {
        this.addShelfDecorativeProps(
          group,
          module,
          item,
          innerWidth,
          innerDepth,
          centerZ,
          meta
        );
      }
    }

    if (item.type === "rail" || item.type === "pantograph") {
      const railZ = centerZ + depth * 0.035;
      // Своя штанга в каждом отсеке, с опорами на боковинах, стойках и перегородках.
      pieces.forEach(([left, right]) => {
        const holder = this.itemPieceHolder(group, left, right);
        const pieceWidth = right - left;
        const barLength = pieceWidth - mm(54);
        if (barLength >= mm(80)) {
          // Штанга овальная хромированная 30 × 15 мм, как в комплекте Сергея (видео 23.10).
          const bar = new Mesh(
            new CylinderGeometry(mm(7.5), mm(7.5), barLength, 28),
            item.type === "rail" ? this.getChromeMaterial() : metal
          );
          bar.rotation.z = Math.PI / 2;
          if (item.type === "rail") bar.scale.set(2, 1, 1);
          else bar.scale.set(5 / 3, 1, 5 / 3);
          bar.position.set(0, 0, railZ);
          bar.castShadow = true;
          bar.userData.pick = meta;
          holder.add(bar);
        }
        this.addRailSupports(holder, pieceWidth, railZ, item.type === "pantograph", meta);
        if (item.type === "pantograph") {
          this.addInternalShadowPatch(holder, 0, -mm(190), -depth + mm(17), pieceWidth - mm(42), mm(420), 0.1, meta);
          [-1, 1].forEach((side) => {
            const arm = new Mesh(new BoxGeometry(mm(18), mm(360), mm(24)), metal);
            arm.position.set(side * (pieceWidth / 2 - mm(26)), -mm(165), railZ);
            arm.rotation.z = side * 0.08;
            arm.userData.pick = meta;
            holder.add(arm);
          });
        }
      });
      selectionSize = new Vector3(innerWidth - mm(48), mm(48), mm(48));
      selectionPosition = new Vector3(0, 0, railZ);

      if (item.type === "pantograph") {
        selectionSize = new Vector3(innerWidth, mm(390), mm(80));
        selectionPosition = new Vector3(0, -mm(165), railZ);
      } else {
        pieces.forEach(([left, right]) => {
          this.addInternalShadowPatch(group, (left + right) / 2, -mm(370), -depth + mm(17), right - left - mm(48), mm(680), 0.085, meta);
        });
        this.addClothes(
          group,
          module,
          item,
          innerWidth,
          innerDepth,
          railZ,
          meta
        );
      }
    }

    if (item.type === "drawer" || item.type === "basket") {
      const height = mm(item.height ?? (item.type === "drawer" ? 180 : 160));
      selectionSize = new Vector3(innerWidth, height, innerDepth);
      selectionPosition = new Vector3(0, -height / 2, centerZ);
      // Ящик и корзина — отдельные в каждом отсеке, от стойки до стойки.
      pieces.forEach(([left, right], pieceIndex) => {
        const holder = this.itemPieceHolder(group, left, right);
        // Ящик утапливается внутри себя (addInternalDrawer), корзина — целиком.
        const slide = this.pullOutSlide(holder, module.id, item.id, pieceIndex, innerDepth,
          item.type === "basket" && behindDoors ? PULL_OUT_RECESS : 0);
        const pieceWidth = right - left;
        if (item.type === "drawer") {
          const front = this.drawerFrontMaterial(module, materialKey, pieceWidth);
          this.addInternalDrawer(slide, pieceWidth, height, depth, innerDepth, centerZ, front.material, front.key, meta, behindDoors);
        } else {
          this.addInternalBasket(slide, pieceWidth - (behindDoors ? PULL_OUT_SPACERS : 0), height, depth, innerDepth, centerZ, meta);
        }
      });
    }

    if (item.type === "leatherBasket") {
      const height = mm(item.height ?? 160);
      selectionSize = new Vector3(innerWidth, height, innerDepth);
      selectionPosition = new Vector3(0, -height / 2, centerZ);
      pieces.forEach(([left, right], pieceIndex) => {
        const holder = this.itemPieceHolder(group, left, right);
        const slide = this.pullOutSlide(holder, module.id, item.id, pieceIndex, innerDepth, behindDoors ? PULL_OUT_RECESS : 0);
        this.addLeatherBasket(slide, right - left - (behindDoors ? PULL_OUT_SPACERS : 0), height, depth, innerDepth, centerZ, meta);
      });
    }

    if (item.type === "shoeRack") {
      // Обувница — полка из декора корпуса с бортиком по лицу (замечание № 26).
      const lip = mm(SHOE_SHELF_LIP_MM);
      selectionSize = new Vector3(innerWidth, mm(CARCASS_MM) + lip, innerDepth);
      selectionPosition = new Vector3(0, lip / 2, centerZ);
      pieces.forEach(([left, right]) => {
        const holder = this.itemPieceHolder(group, left, right);
        const pieceWidth = right - left;
        this.addPanel(holder, 0, 0, centerZ, pieceWidth, mm(CARCASS_MM), innerDepth, shelfMaterial, meta);
        this.addShelfFrontDetail(holder, pieceWidth, centerZ, shelfKey, meta);
        this.addShelfSupports(holder, pieceWidth, innerDepth, centerZ, meta);
        this.addPanel(holder, 0, mm(HALF_CARCASS_MM) + lip / 2, -mm(8), pieceWidth, lip, mm(16), shelfMaterial, meta);
        this.addInternalShadowPatch(holder, 0, -mm(28), -depth + mm(17), pieceWidth - mm(34), mm(58), 0.12, meta);
      });
      this.addShoeShelfProps(group, module, item, pieces, innerDepth, centerZ, meta);
    }

    if (item.type === "trouserRack") {
      const drop = mm(item.height ?? TROUSER_RACK_DROP_MM);
      selectionSize = new Vector3(innerWidth, drop, innerDepth);
      selectionPosition = new Vector3(0, -drop / 2, centerZ);
      pieces.forEach(([left, right], pieceIndex) => {
        const holder = this.itemPieceHolder(group, left, right);
        const slide = this.pullOutSlide(holder, module.id, item.id, pieceIndex, innerDepth, behindDoors ? PULL_OUT_RECESS : 0);
        this.addTrouserRack(slide, right - left - (behindDoors ? PULL_OUT_SPACERS : 0), drop, depth, innerDepth, centerZ, `${item.id}-${pieceIndex}`, meta);
      });
    }

    if (item.type === "divider") {
      const dividerHeight = mm(
        item.height ?? Math.min(1400, module.height - 180)
      );
      selectionSize = new Vector3(
        mm(38),
        dividerHeight,
        innerDepth
      );
      selectionPosition = new Vector3(0, 0, centerZ);
      this.addPanel(
        group,
        0,
        0,
        centerZ,
        mm(CARCASS_MM),
        dividerHeight,
        innerDepth,
        carcass,
        meta
      );
      this.addInternalShadowPatch(
        group,
        mm(20),
        -mm(40),
        -depth + mm(17),
        mm(72),
        Math.max(mm(120), dividerHeight - mm(80)),
        0.07,
        meta
      );
    }

    const selectionAnchor = new Mesh(
      new BoxGeometry(
        Math.max(mm(10), selectionSize.x),
        Math.max(mm(10), selectionSize.y),
        Math.max(mm(10), selectionSize.z)
      ),
      new MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false
      })
    );
    selectionAnchor.position.copy(selectionPosition);
    selectionAnchor.raycast = () => {};
    selectionAnchor.userData.isSelectionAnchor = true;
    group.add(selectionAnchor);
    group.userData.selectionAnchor = selectionAnchor;

    return group;
  }

  /**
   * Лицевая панель внутреннего ящика (16.10): в декоре корпуса, в своём декоре
   * или в отделке фасадов (декор ЛДСП-фасада или эмаль его цвета).
   */
  private drawerFrontMaterial(module: WardrobeModule, materialKey: string, width: number) {
    const finish = drawerFrontFinish(module);
    if (finish.kind === "decor") return { material: this.getDecorMaterial(finish.code), key: finish.code };
    if (finish.kind === "facade") {
      const key = module.facadeDecor ?? materialKey;
      return {
        material: this.getFacadeMaterial(module.facade, key, module.facadeColor, "plain", width, module.facadeColorHex),
        key
      };
    }
    return { material: this.getDecorMaterial(materialKey), key: materialKey };
  }

  private addInternalDrawer(
    group: Group,
    innerWidth: number,
    height: number,
    depth: number,
    innerDepth: number,
    centerZ: number,
    carcass: MeshStandardMaterial,
    materialKey: string,
    meta: PickMeta,
    behindHingedDoors = false
  ) {
    const frontCenterY = -height / 2;
    const drawerMaterial = this.getDrawerMaterial();
    // За распашными дверями ящик ставят на проставки 20 мм и чуть утапливают,
    // чтобы фасад не задевал петли и открытые двери (приёмка 04.10, wf-02).
    const spacer = behindHingedDoors ? mm(20) : 0;
    innerWidth -= spacer * 2;
    const outer = group;
    if (behindHingedDoors) {
      group = new Group();
      group.position.z = -mm(22);
      outer.add(group);
    }
    const boxWidth = innerWidth - mm(10);
    const boxDepth = innerDepth - mm(24);
    const boxThickness = mm(12);
    const boxSideHeight = Math.max(mm(52), height - mm(34));
    const boxSideCenterY = -height + boxThickness + boxSideHeight / 2;

    this.addPanel(group, 0, -height + boxThickness / 2, centerZ, boxWidth, boxThickness, boxDepth, drawerMaterial, meta);
    [-1, 1].forEach((side) => {
      this.addPanel(
        group,
        side * (boxWidth / 2 - boxThickness / 2),
        boxSideCenterY,
        centerZ,
        boxThickness,
        boxSideHeight,
        boxDepth,
        drawerMaterial,
        meta
      );
    });
    this.addPanel(
      group,
      0,
      boxSideCenterY,
      centerZ - boxDepth / 2 + boxThickness / 2,
      boxWidth - boxThickness * 2,
      boxSideHeight,
      boxThickness,
      drawerMaterial,
      meta
    );
    this.addInternalShadowPatch(outer, 0, -height - mm(12), -depth + mm(17), innerWidth - mm(44), mm(95), 0.12, meta);
    this.addRoundedPanel(group, 0, frontCenterY, -mm(4), innerWidth - mm(6), height, mm(20), carcass, meta, mm(2.4));
    this.addDrawerDetails(group, innerWidth, height, frontCenterY, centerZ, materialKey, meta);
  }

  private addInternalBasket(
    group: Group,
    innerWidth: number,
    height: number,
    depth: number,
    innerDepth: number,
    centerZ: number,
    meta: PickMeta
  ) {
    const wireMaterial = this.getMetalMaterial(0x73736f);
    this.addInternalShadowPatch(group, 0, -height / 2, -depth + mm(17), innerWidth - mm(44), height + mm(62), 0.09, meta);
    const bottom = new Mesh(
      new BoxGeometry(innerWidth - mm(18), mm(8), innerDepth - mm(38)),
      wireMaterial
    );
    bottom.position.set(0, -height, centerZ);
    bottom.userData.pick = meta;
    group.add(bottom);
    for (let i = 0; i < 7; i += 1) {
      const x = -innerWidth / 2 + mm(34) + (i * (innerWidth - mm(68))) / 6;
      const wire = new Mesh(new CylinderGeometry(mm(3), mm(3), height, 10), wireMaterial);
      wire.position.set(x, -height / 2, centerZ - innerDepth * 0.45);
      wire.userData.pick = meta;
      group.add(wire);
    }
    this.addBasketFrame(group, innerWidth, height, innerDepth, centerZ, wireMaterial, meta);
  }

  /** Выдвижная корзина, обтянутая эко-кожей: короб со строчкой по верху. */
  private addLeatherBasket(
    group: Group,
    pieceWidth: number,
    height: number,
    depth: number,
    innerDepth: number,
    centerZ: number,
    meta: PickMeta
  ) {
    const leather = this.getLeatherMaterial();
    const stitch = this.getLeatherMaterial(true);
    const runner = this.getMetalMaterial(0x8a8a86);
    const wall = mm(12);
    const width = pieceWidth - mm(26);
    const boxDepth = innerDepth - mm(30);
    const z = centerZ + mm(4);
    this.addInternalShadowPatch(group, 0, -height / 2, -depth + mm(17), width - mm(20), height + mm(50), 0.09, meta);
    this.addDetailPanel(group, 0, -height + mm(5), z, width, mm(10), boxDepth, leather, meta);
    [-1, 1].forEach((side) => {
      this.addDetailPanel(group, side * (width / 2 - wall / 2), -height / 2, z, wall, height, boxDepth, leather, meta);
      // Направляющая под корзиной у боковины.
      this.addDetailPanel(group, side * (pieceWidth / 2 - mm(6)), -height + mm(14), z, mm(10), mm(12), boxDepth, runner, meta);
    });
    this.addDetailPanel(group, 0, -height / 2, z - boxDepth / 2 + wall / 2, width - wall * 2, height, wall, leather, meta);
    this.addDetailPanel(group, 0, -height / 2, z + boxDepth / 2 - wall / 2, width - wall * 2, height, wall, leather, meta);
    // Строчка по верхней кромке и вырез-ручка спереди.
    this.addDetailPanel(group, 0, -mm(9), z + boxDepth / 2 + mm(0.6), width - mm(24), mm(2), mm(1.2), stitch, meta);
    this.addDetailPanel(group, 0, -mm(30), z + boxDepth / 2 + mm(0.6), Math.min(mm(120), width * 0.3), mm(22), mm(1.4), stitch, meta);
  }

  /** Выдвижная брючница: рамка на направляющих, перекладины вглубь, на них брюки. */
  private addTrouserRack(
    group: Group,
    pieceWidth: number,
    drop: number,
    depth: number,
    innerDepth: number,
    centerZ: number,
    seed: string,
    meta: PickMeta
  ) {
    const metal = this.getMetalMaterial(0x6f706e);
    const frameWidth = pieceWidth - mm(26);
    const front = centerZ + innerDepth / 2 - mm(20);
    const back = centerZ - innerDepth / 2 + mm(30);
    const rodY = -mm(34);
    this.addInternalShadowPatch(group, 0, -drop / 2, -depth + mm(17), frameWidth - mm(20), drop, 0.08, meta);
    [-1, 1].forEach((side) => {
      this.addDetailPanel(group, side * (frameWidth / 2), -mm(18), (front + back) / 2, mm(14), mm(36), front - back, metal, meta);
    });
    this.addRodBetween(group, new Vector3(-frameWidth / 2, rodY, front), new Vector3(frameWidth / 2, rodY, front), mm(6), metal, meta);
    const count = Math.max(2, Math.floor((frameWidth - mm(60)) / mm(52)));
    const step = (frameWidth - mm(60)) / Math.max(1, count - 1);
    for (let index = 0; index < count; index += 1) {
      const x = -frameWidth / 2 + mm(30) + index * step;
      this.addRodBetween(group, new Vector3(x, rodY, back), new Vector3(x, rodY, front), mm(4.5), metal, meta);
      // Брюки висят не на каждой перекладине.
      if (index % 3 === 2) continue;
      const fabric = this.getTrouserMaterial(this.seededVariant(`${seed}-${index}`, [0, 1, 2, 3]));
      const length = drop - mm(70 + (index % 2) * 40);
      this.addDetailPanel(group, x, rodY - length / 2, (front + back) / 2 - mm(10), mm(9), length, (front - back) * 0.72, fabric, meta);
    }
  }

  /** Обувь на обувнице — стоит на полке за бортиком. */
  private addShoeShelfProps(
    parent: Group,
    module: WardrobeModule,
    item: InternalItem,
    pieces: Array<[number, number]>,
    innerDepth: number,
    centerZ: number,
    meta: PickMeta
  ) {
    if (!this.shelfModelsReady || module.decorDensity === "minimal") return;
    const localBottom = 9;
    pieces.forEach(([left, right], pieceIndex) => {
      const width = right - left - mm(40);
      if (width < mm(160)) return;
      const xOffset = (left + right) / 2;
      const maxSize = new Vector3(Math.min(mm(315), width), mm(110), Math.min(mm(270), innerDepth - mm(60)));
      const fitted = this.createDecorativeModel(
        "slippers",
        maxSize,
        new Vector3(xOffset, mm(localBottom), centerZ - mm(10)),
        meta,
        `${item.id}-shoes-${pieceIndex}`,
        { preserveScale: true, yawRange: 0.055 }
      );
      if (!fitted) return;
      const localTop = localBottom + fitted.size.y / MM;
      fitted.object.visible = this.isDecorativeSpaceAvailable(
        module,
        item.id,
        item.y + localBottom,
        item.y + localTop,
        [(xOffset - fitted.size.x / 2) / MM, (xOffset + fitted.size.x / 2) / MM]
      );
      parent.add(fitted.object);
    });
  }

  private addInternalShadowPatch(
    group: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    opacity: number,
    meta: PickMeta
  ) {
    if (width <= mm(12) || height <= mm(12)) return;
    const patch = this.addDetailPanel(
      group,
      x,
      y,
      z,
      width,
      height,
      mm(1.2),
      this.getSoftShadowMaterial(opacity),
      meta
    );
    // Полупрозрачная тень на задней стенке, а не деталь.
    patch.userData.shadowPatch = true;
  }

  private addShelfFrontDetail(
    group: Group,
    innerWidth: number,
    _centerZ: number,
    materialKey: string,
    meta: PickMeta
  ) {
    const edge = this.carcassEdgeMaterial(materialKey);
    const shadow = this.getSoftShadowMaterial(0.16);
    this.addDetailPanel(
      group,
      0,
      0,
      -mm(4),
      innerWidth,
      mm(CARCASS_MM),
      mm(8),
      edge,
      meta
    );
    this.addDetailPanel(
      group,
      0,
      -mm(11),
      -mm(10),
      innerWidth - mm(22),
      mm(2.5),
      mm(18),
      shadow,
      meta
    );
  }

  private dividerItemsOverlappingRange(
    module: WardrobeModule,
    bottomMm: number,
    topMm: number
  ) {
    const rangeBottom = Math.min(bottomMm, topMm);
    const rangeTop = Math.max(bottomMm, topMm);
    return module.items.filter((item) => {
      if (item.type !== "divider") return false;
      const height = item.height ?? Math.min(1400, module.height - 180);
      const dividerBottom = item.y - height / 2;
      const dividerTop = item.y + height / 2;
      return rangeBottom <= dividerTop && rangeTop >= dividerBottom;
    });
  }

  private horizontalSegmentsForDividers(
    module: WardrobeModule,
    bottomMm: number,
    topMm: number,
    innerWidth: number,
    edgeInsetMm: number,
    dividerClearanceMm: number,
    minimumLengthMm: number
  ) {
    const left = -innerWidth / 2 + mm(edgeInsetMm);
    const right = innerWidth / 2 - mm(edgeInsetMm);
    const zones = this.dividerItemsOverlappingRange(
      module,
      bottomMm,
      topMm
    )
      .map((divider) => {
        const center = mm(divider.x ?? 0);
        const halfZone = mm(9 + dividerClearanceMm);
        return {
          min: center - halfZone,
          max: center + halfZone
        };
      })
      .sort((first, second) => first.min - second.min);
    if (!zones.length) return [[left, right] as [number, number]];

    const segments: Array<[number, number]> = [];
    let cursor = left;
    zones.forEach((zone) => {
      const zoneMin = clamp(zone.min, left, right);
      const zoneMax = clamp(zone.max, left, right);
      if (zoneMin - cursor >= mm(minimumLengthMm)) {
        segments.push([cursor, zoneMin]);
      }
      cursor = Math.max(cursor, zoneMax);
    });
    if (right - cursor >= mm(minimumLengthMm)) {
      segments.push([cursor, right]);
    }
    return segments;
  }

  private addShelfSupports(
    group: Group,
    innerWidth: number,
    innerDepth: number,
    centerZ: number,
    meta: PickMeta
  ) {
    const metal = this.getMetalMaterial(0x8a857a);
    // Полкодержатель — штырь из боковины под полку, а не «висящий» в воздухе
    // в 34 мм от стенки (приёмка 05.10, sc-14).
    const supportX = innerWidth / 2 - mm(7.5);
    const supportZ = innerDepth / 2 - mm(34);
    [-1, 1].forEach((side) => {
      [-1, 1].forEach((frontBack) => {
        const pin = new Mesh(
          new CylinderGeometry(mm(4.8), mm(4.8), mm(15), 14),
          metal
        );
        pin.rotation.z = Math.PI / 2;
        pin.position.set(
          side * supportX,
          -mm(13),
          centerZ + frontBack * supportZ
        );
        pin.castShadow = true;
        pin.userData.pick = meta;
        group.add(pin);
      });
    });
  }

  private addRailSupports(
    group: Group,
    innerWidth: number,
    railZ: number,
    pantograph: boolean,
    meta: PickMeta
  ) {
    // Держатели овальной штанги — хромированные, овальные (видео Сергея 23.10).
    const metal = pantograph ? this.getMetalMaterial(0x74736e) : this.getChromeMaterial();
    const cupMaterial = pantograph ? this.getMetalMaterial(0xa7a39a) : this.getChromeMaterial();
    const sideX = innerWidth / 2 - mm(32);

    [-1, 1].forEach((side) => {
      const cup = new Mesh(
        pantograph ? new CylinderGeometry(mm(18), mm(18), mm(9), 24) : new CylinderGeometry(mm(12), mm(12), mm(9), 28),
        cupMaterial
      );
      cup.rotation.z = Math.PI / 2;
      if (!pantograph) cup.scale.set(1.75, 1, 1);
      cup.position.set(side * sideX, 0, railZ);
      cup.castShadow = true;
      cup.userData.pick = meta;
      group.add(cup);

      const plate = new Mesh(
        new RoundedBoxGeometry(mm(8), mm(64), mm(48), 2, mm(3)),
        metal
      );
      plate.position.set(side * (innerWidth / 2 - mm(12)), -mm(4), railZ);
      plate.castShadow = true;
      plate.receiveShadow = true;
      plate.userData.pick = meta;
      group.add(plate);

      if (pantograph) {
        const pivot = new Mesh(
          new CylinderGeometry(mm(13), mm(13), mm(8), 18),
          cupMaterial
        );
        pivot.rotation.z = Math.PI / 2;
        pivot.position.set(side * (innerWidth / 2 - mm(27)), -mm(72), railZ);
        pivot.castShadow = true;
        pivot.userData.pick = meta;
        group.add(pivot);
      }
    });
  }

  private addDrawerDetails(
    group: Group,
    innerWidth: number,
    height: number,
    frontCenterY: number,
    centerZ: number,
    materialKey: string,
    meta: PickMeta
  ) {
    const edge = this.carcassEdgeMaterial(materialKey);
    const shadow = this.getSoftShadowMaterial(0.22);
    const metal = this.getMetalMaterial(0x77736b);
    const frontWidth = innerWidth - mm(6);

    this.addDetailPanel(
      group,
      0,
      frontCenterY + height / 2 - mm(2.5),
      mm(7),
      frontWidth - mm(16),
      mm(4),
      mm(4),
      edge,
      meta
    );
    this.addDetailPanel(
      group,
      0,
      frontCenterY - height / 2 + mm(2.5),
      mm(7),
      frontWidth - mm(16),
      mm(4),
      mm(4),
      edge,
      meta
    );
    this.addDetailPanel(
      group,
      0,
      frontCenterY + height / 2 + mm(4),
      mm(8),
      frontWidth - mm(26),
      mm(3),
      mm(3),
      shadow,
      meta
    );

    const handle = new Mesh(
      new RoundedBoxGeometry(innerWidth * 0.38, mm(12), mm(18), 2, mm(4)),
      metal
    );
    handle.position.set(0, frontCenterY + mm(4), mm(10));
    handle.userData.pick = meta;
    handle.castShadow = true;
    group.add(handle);

    [-1, 1].forEach((side) => {
      const rail = new Mesh(
        new BoxGeometry(mm(10), mm(8), Math.max(mm(90), Math.abs(centerZ) * 0.95)),
        metal
      );
      rail.position.set(
        side * (innerWidth / 2 - mm(18)),
        frontCenterY + height * 0.08,
        centerZ + mm(14)
      );
      rail.userData.pick = meta;
      rail.castShadow = true;
      group.add(rail);
    });
  }

  private addBasketFrame(
    group: Group,
    innerWidth: number,
    height: number,
    innerDepth: number,
    centerZ: number,
    material: MeshStandardMaterial,
    meta: PickMeta
  ) {
    const frontZ = centerZ + innerDepth / 2 - mm(20);
    const backZ = centerZ - innerDepth / 2 + mm(24);
    const left = -innerWidth / 2 + mm(22);
    const right = innerWidth / 2 - mm(22);
    const topY = -mm(24);
    const bottomY = -height + mm(18);

    [topY, bottomY].forEach((y) => {
      this.addRodBetween(
        group,
        new Vector3(left, y, frontZ),
        new Vector3(right, y, frontZ),
        mm(4),
        material,
        meta
      );
      this.addRodBetween(
        group,
        new Vector3(left, y, backZ),
        new Vector3(right, y, backZ),
        mm(3),
        material,
        meta
      );
    });

    [-1, 1].forEach((side) => {
      this.addRodBetween(
        group,
        new Vector3(side * (innerWidth / 2 - mm(22)), topY, frontZ),
        new Vector3(side * (innerWidth / 2 - mm(22)), bottomY, frontZ),
        mm(3),
        material,
        meta
      );
      this.addRodBetween(
        group,
        new Vector3(side * (innerWidth / 2 - mm(22)), topY, backZ),
        new Vector3(side * (innerWidth / 2 - mm(22)), bottomY, backZ),
        mm(3),
        material,
        meta
      );
    });
  }

  private addDoors(
    group: Group,
    module: WardrobeModule,
    width: number,
    height: number,
    depth: number,
    materialKey: string,
    /** Единый фасад с антресолью: на сколько створки продолжаются вверх. */
    extraHeight = 0,
    /** Свои створки (угловой: по двери на корпус, каждая в своей рамке). */
    leaves?: HingedDoorLeafLayout[]
  ) {
    if (module.door === "open") return;
    const hingedLeaves = leaves ?? hingedDoorLeafLayout(module);
    const count = module.door === "sliding"
      ? 2
      : Math.max(1, hingedLeaves.length);
    const outerReveal = mm(2);
    const centerReveal = mm(2);
    const doorWidth =
      (width - outerReveal * 2 - centerReveal * (count - 1)) / count;
    const plinthHeight =
      module.type === "mezzanine" ? 0 : mm(cabinetBaseHeightMm(module));
    // Над наружными ящиками двери начинаются выше: нижняя кромка — над швом
    // верхнего ящика.
    const drawerLayout = module.type === "mezzanine" ? null : cabinetDrawerLayout(module);
    const doorBase = drawerLayout ? mm(drawerLayout.doorBottomMm - 2) : plinthHeight;
    const doorHeight = Math.max(mm(220), height + extraHeight - doorBase - mm(8));
    const doorCenterY = doorBase + doorHeight / 2 + mm(2);
    const facadeMaterialKey = module.facadeDecor ?? materialKey;
    const material = this.getFacadeMaterial(
      module.facade,
      facadeMaterialKey,
      module.facadeColor,
      module.facadePattern,
      doorWidth,
      module.facadeColorHex
    );

    if (module.door === "sliding") {
      const slidingWidth = Math.min(mm(MAX_SLIDING_DOOR_WIDTH_MM), width / 2);
      for (let index = 0; index < 2; index += 1) {
        const doorMeta: PickMeta = {
          kind: "door",
          moduleId: module.id,
          doorIndex: index
        };
        const key = this.doorKey(module.id, index);
        const progress = this.doorVisualProgress(key);
        const baseX =
          index === 0
            ? -width / 2 + outerReveal + slidingWidth / 2
            : width / 2 - outerReveal - slidingWidth / 2;
        const slideDirection = index === 0 ? 1 : -1;
        const openShift =
          slideDirection * Math.min(slidingWidth * 0.72, width * 0.34);
        const leaf = new Group();
        leaf.position.set(
          baseX + openShift * this.easeDoorProgress(progress),
          0,
          0
        );
        leaf.userData.pick = doorMeta;
        this.doorObjects.set(key, {
          key,
          kind: "sliding",
          moduleId: module.id,
          doorIndex: index,
          object: leaf,
          side: slideDirection,
          closedX: baseX,
          openShift
        });
        group.add(leaf);

        const door = this.addFacadeLeaf(
          leaf,
          0,
          doorCenterY,
          mm(14 + index * 18),
          slidingWidth,
          doorHeight,
          mm(18),
          material,
          module.facade,
          facadeMaterialKey,
          module.facadePattern,
          doorMeta,
          undefined,
          undefined,
          module.facadePaintSides !== "two"
        );
        door.castShadow = true;
      }

      const aluminum = this.getMetalMaterial(0xaaa9a4);
      const moduleMeta: PickMeta = { kind: "module", moduleId: module.id };
      this.addPanel(
        group,
        0,
        plinthHeight + mm(12),
        mm(2),
        width - mm(8),
        mm(16),
        mm(34),
        aluminum,
        moduleMeta
      );
      this.addPanel(
        group,
        0,
        height - mm(14),
        mm(2),
        width - mm(8),
        mm(16),
        mm(34),
        aluminum,
        moduleMeta
      );
      return;
    }

    // Антресоль с откидными фасадами (09.10): по фасаду на пролёт.
    const flaps = extraHeight ? null : mezzanineFlapLayout(module, this.project ? moduleHeadroomMm(this.project, module) : Infinity);
    if (flaps) {
      flaps.forEach((flap) => {
        const doorMeta: PickMeta = { kind: "door", moduleId: module.id, doorIndex: flap.index };
        this.addFlapFront(group, {
          moduleId: module.id,
          doorId: flap.index,
          meta: doorMeta,
          centerX: mm(flap.centerMm),
          bottom: doorBase + mm(2),
          width: mm(flap.widthMm),
          height: doorHeight,
          z: mm(13),
          thickness: mm(20),
          front: flap.front,
          material,
          facade: module.facade,
          materialKey: facadeMaterialKey,
          pattern: module.facadePattern,
          handle: module.handle,
          oneSidedPaint: module.facadePaintSides !== "two",
          mechanismDepth: depth * 0.45,
          opening: {
            left: mm(flap.centerMm - flap.widthMm / 2 + (flap.index === 0 ? CARCASS_MM - 2 : CARCASS_MM / 2 - 1)),
            right: mm(flap.centerMm + flap.widthMm / 2 - (flap.index === flaps.length - 1 ? CARCASS_MM - 2 : CARCASS_MM / 2 - 1)),
            top: height - mm(CARCASS_MM), frontZ: 0, bodyTop: height
          }
        });
      });
      return;
    }

    for (const layout of hingedLeaves) {
      const index = layout.index;
      // Свой фасад створки (16.10): своя отделка, ручка — подходящая к ней.
      const override = doorFacadeOverride(module, index);
      const leafModule: WardrobeModule = override
        ? { ...leafFinish(module, index), handle: leafHandle(module, index) }
        : module;
      const leafFacadeKey = leafModule.facadeDecor ?? materialKey;
      const leafMaterial = override
        ? this.getFacadeMaterial(leafModule.facade, leafFacadeKey, leafModule.facadeColor, leafModule.facadePattern,
          mm(layout.widthMm), leafModule.facadeColorHex)
        : material;
      const doorMeta: PickMeta = {
        kind: "door",
        moduleId: module.id,
        doorIndex: index
      };
      const side = layout.hingeSide;
      const leafWidth = mm(layout.widthMm);
      const centerX = mm(layout.centerMm);
      const key = this.doorKey(module.id, index);
      const progress = this.doorVisualProgress(key);
      const hingeX = centerX + side * leafWidth / 2;
      const hinge = new Group();
      hinge.position.set(hingeX, 0, mm(13));
      hinge.rotation.y =
        side * HINGED_DOOR_OPEN_ANGLE * this.easeDoorProgress(progress);
      hinge.userData.pick = doorMeta;
      this.doorObjects.set(key, {
        key,
        kind: "hinged",
        moduleId: module.id,
        doorIndex: index,
        object: hinge,
        side,
        width: leafWidth,
        closedX: hingeX,
        maxAngle: HINGED_DOOR_OPEN_ANGLE
      });

      const leaf = new Group();
      leaf.position.set(-side * leafWidth / 2, 0, 0);
      // Интегрированная ручка — выборка на кромке со стороны открывания
      // (или по верху), вид выбирает покупатель (приёмка 22.10).
      const integratedHandleCut: IntegratedHandleCut | undefined = undefined;
      const integratedGroove = leafModule.handle === "integrated"
        ? this.moduleIntegratedGroove(leafModule, doorBase, side, leafWidth, doorHeight)
        : undefined;
      this.addFacadeLeaf(
        leaf,
        0,
        doorCenterY,
        0,
        leafWidth,
        doorHeight,
        mm(20),
        leafMaterial,
        leafModule.facade,
        override ? leafFacadeKey : facadeMaterialKey,
        leafModule.facadePattern,
        doorMeta,
        integratedHandleCut,
        leafModule.handle === "glassGrip" ? -side : undefined,
        leafModule.facadePaintSides !== "two",
        integratedGroove
      );

      const fixedHingeHardware = new Group();
      fixedHingeHardware.visible = progress > 0.025;
      fixedHingeHardware.userData.pick = doorMeta;
      group.add(fixedHingeHardware);

      const movingHingeHardware = new Group();
      movingHingeHardware.visible = progress > 0.025;
      movingHingeHardware.userData.pick = doorMeta;
      leaf.add(movingHingeHardware);

      this.hingeTone = module.hingeColor === "black" ? "black" : "silver";
      const modelled = this.addHingeModels({
        fixedParent: fixedHingeHardware, doorParent: movingHingeHardware,
        application: layout.hingeApplication,
        supportFaceX: mm(layout.supportOffsetMm) - side * mm(CARCASS_MM) / 2, sideOut: side,
        // Створка: ось на 13 мм перед корпусом, фасад 20 мм — задняя плоскость в 3 мм от корпуса.
        doorBackZ: mm(3), doorOrigin: { x: centerX, z: mm(13) },
        doorHeight, baseY: doorBase, meta: doorMeta
      });
      if (!modelled) {
        this.addHingeMountingPlates(
          fixedHingeHardware,
          mm(layout.supportOffsetMm),
          hingeX,
          side,
          doorHeight,
          doorMeta,
          doorBase,
          layout.hingeApplication
        );
        this.addHingeDetails(
          movingHingeHardware,
          side,
          leafWidth,
          doorHeight,
          doorMeta,
          doorBase,
          layout.hingeApplication
        );
      }

      const runtime = this.doorObjects.get(key);
      if (runtime) {
        runtime.hardwareObjects = [fixedHingeHardware, movingHingeHardware];
        if (modelled?.length) runtime.hingeLinks = modelled;
      }
      this.addHandle(
        leaf,
        leafModule,
        0,
        doorBase,
        side,
        leafWidth,
        doorMeta,
        doorHeight
      );
      const handleRuntime = this.doorObjects.get(key);
      if (handleRuntime && handleProjectionMm(leafModule.handle) > 0) {
        const widthMm = leafWidth / MM;
        const heightMm = doorHeight / MM;
        handleRuntime.handle = {
          along: leafWidth - mm(handleCenterFromOpeningEdgeMm(leafModule, widthMm, heightMm)),
          half: handleOrientation(leafModule) === "horizontal"
            ? mm(handleLengthMm(leafModule, widthMm, heightMm)) / 2 : mm(7),
          face: mm(10), projection: mm(handleProjectionMm(leafModule.handle))
        };
      }
      hinge.add(leaf);
      group.add(hinge);
    }

    void depth;
  }

  /**
   * Фасад с интегрированной ручкой: основная плита и полоса 31 мм с J-пазом
   * под пальцы (по чертежу профиля: низкая губка спереди, стенка сзади).
   * Возвращает основную плиту и её прямоугольник — для фрезеровки рисунка.
   */
  private addIntegratedGrooveFront(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta,
    groove: IntegratedGroove,
    radius = mm(0.65)
  ) {
    // Профиль по чертежу (23.10): полоса 31 мм над фасадом. Спереди низкая
    // губка (~треть высоты), за ней паз со скруглённым дном, открытый вперёд;
    // задняя стенка — во всю высоту, сверху с небольшим загибом вперёд.
    // Пальцы заходят спереди над губкой и тянут за неё.
    const g = Math.min(INTEGRATED_GROOVE_WIDTH, (groove.edge === "top" ? height : width) * 0.25);
    const lip = Math.min(mm(4), depth * 0.22);
    const back = Math.min(mm(4), depth * 0.22);
    const floor = Math.min(mm(3), g * 0.1);
    const lipH = g * 0.3;
    const hookH = Math.min(mm(3), g * 0.1);
    const hookD = Math.min(mm(1.5), depth * 0.08);
    const zLip = z + depth / 2 - lip / 2;
    const zBack = z - depth / 2 + back / 2;
    const zHook = z - depth / 2 + back + hookD / 2;
    const zFace = z - depth / 2 + back + mm(0.3);
    const zFloor = z + depth / 2 - lip - (depth - lip - back) / 2;
    const shadow = this.getIntegratedGrooveShadowMaterial();
    const shade = this.getIntegratedGrooveShadeMaterial();
    if (groove.edge === "top") {
      const main = { x, y: y - g / 2, width, height: height - g };
      const leaf = this.addRoundedPanel(parent, main.x, main.y, z, main.width, main.height, depth, material, meta, radius);
      const b = y + height / 2 - g;
      // Участок по центру (from/to — от середины фасада): по бокам кромка во всю толщину.
      const left = Math.max(-width / 2, Math.min(width / 2, groove.from ?? -width / 2));
      const right = Math.max(left, Math.min(width / 2, groove.to ?? width / 2));
      if (left + width / 2 > mm(1)) this.addPanel(parent, x + (-width / 2 + left) / 2, b + g / 2, z, left + width / 2, g, depth, material, meta);
      if (width / 2 - right > mm(1)) this.addPanel(parent, x + (right + width / 2) / 2, b + g / 2, z, width / 2 - right, g, depth, material, meta);
      const run = right - left;
      const cx = x + (left + right) / 2;
      this.addPanel(parent, cx, b + floor / 2, z, run, floor, depth, material, meta);
      this.addPanel(parent, cx, b + lipH / 2, zLip, run, lipH, lip, material, meta);
      this.addPanel(parent, cx, b + g / 2, zBack, run, g, back, material, meta);
      this.addPanel(parent, cx, b + g - hookH / 2, zHook, run, hookH, hookD, material, meta);
      // Глубина паза в тени: дно — тёмное, задняя стенка изнутри — притенена.
      this.addPanel(parent, cx, b + floor + mm(0.3), zFloor, run, mm(0.6), depth - lip - back, shadow, meta);
      this.addPanel(parent, cx, b + (floor + g - hookH) / 2, zFace, run, g - hookH - floor, mm(0.6), shade, meta).castShadow = false;
      return { leaf, main };
    }
    const edge = groove.edge;
    const main = { x: x - edge * g / 2, y, width: width - g, height };
    const leaf = this.addRoundedPanel(parent, main.x, main.y, z, main.width, main.height, depth, material, meta, radius);
    const columnX = x + edge * (width / 2 - g / 2);
    const inner = x + edge * (width / 2 - g);
    const bottom = y - height / 2;
    const top = y + height / 2;
    const from = Math.max(bottom, Math.min(top, groove.from ?? bottom));
    const to = Math.max(from, Math.min(top, groove.to ?? top));
    // Кромка выше и ниже паза — во всю толщину фасада.
    if (from - bottom > mm(1)) this.addPanel(parent, columnX, (bottom + from) / 2, z, g, from - bottom, depth, material, meta);
    if (top - to > mm(1)) this.addPanel(parent, columnX, (to + top) / 2, z, g, top - to, depth, material, meta);
    const middle = (from + to) / 2;
    const run = to - from;
    // То же сечение, повёрнутое к краю двери: губка — у плиты, стенка — до края.
    this.addPanel(parent, inner + edge * floor / 2, middle, z, floor, run, depth, material, meta);
    this.addPanel(parent, inner + edge * lipH / 2, middle, zLip, lipH, run, lip, material, meta);
    this.addPanel(parent, columnX, middle, zBack, g, run, back, material, meta);
    this.addPanel(parent, inner + edge * (g - hookH / 2), middle, zHook, hookH, run, hookD, material, meta);
    this.addPanel(parent, inner + edge * (floor + mm(0.3)), middle, zFloor, mm(0.6), run, depth - lip - back, shadow, meta);
    this.addPanel(parent, inner + edge * (floor + g - hookH) / 2, middle, zFace, g - hookH - floor, run, mm(0.6), shade, meta).castShadow = false;
    return { leaf, main };
  }

  /** Притенение внутренней стороны задней стенки паза — цвет фасада темнее. */
  private getIntegratedGrooveShadeMaterial() {
    const key = "facade:integrated-groove-shade";
    const cached = this.materialCache.get(key);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.4, depthWrite: false });
    this.materialCache.set(key, material);
    return material;
  }

  private getIntegratedGrooveShadowMaterial() {
    const key = "facade:integrated-groove-shadow";
    const cached = this.materialCache.get(key);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({ color: 0x2c2722, roughness: 0.92, metalness: 0 });
    this.materialCache.set(key, material);
    return material;
  }

  private addFacadeLeaf(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    facade: WardrobeModule["facade"],
    materialKey: string,
    facadePattern: FacadePatternType | undefined,
    meta: PickMeta,
    integratedHandleCut?: IntegratedHandleCut,
    /** Стекло с зацепом в профиле: с какой стороны створки открывающий край. */
    glassGripSide?: number,
    /** МДФ покрашен только снаружи: изнутри видна некрашеная плита (sd-06, wd-06). */
    oneSidedPaint?: boolean,
    /** Интегрированная ручка — выборка на кромке (приёмка 22.10). */
    integratedGroove?: IntegratedGroove
  ) {
    if (facade === "glass") {
      // A glazed door is a thin pane seated inside a structural frame, not a
      // 20 mm transparent slab. The previous solid slab refracted and dimmed
      // the contents so strongly that both people and image models read it as
      // a cloudy opaque facade.
      const frameWidth = mm(glassFrameWidthMm(width / MM, height / MM));
      const glassDepth = Math.min(mm(5), depth * 0.4);
      const glassZ = z + depth / 2 - glassDepth / 2 - mm(1.2);
      const pane = this.addRoundedPanel(
        parent,
        x,
        y,
        glassZ,
        Math.max(mm(40), width - frameWidth * 2 + mm(3)),
        Math.max(mm(80), height - frameWidth * 2 + mm(3)),
        glassDepth,
        material,
        meta,
        mm(0.7)
      );
      pane.castShadow = false;
      pane.receiveShadow = false;
      pane.renderOrder = 3;
      pane.userData.glassSurface = true;
      this.addFacadeTrim(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        facade,
        materialKey,
        facadePattern,
        material,
        meta,
        undefined,
        glassGripSide
      );
      return pane;
    }

    const groove = integratedGroove && facade !== "mirror"
      ? this.addIntegratedGrooveFront(parent, x, y, z, width, height, depth, material, meta, integratedGroove)
      : null;
    const leaf = groove
      ? groove.leaf
      : integratedHandleCut
      ? this.addRoutedFacadePanel(
          parent,
          x,
          y,
          z,
          width,
          height,
          depth,
          material,
          meta,
          integratedHandleCut,
          mm(0.65)
        )
      : this.addRoundedPanel(
          parent,
          x,
          y,
          z,
          width,
          height,
          depth,
          material,
          meta,
          mm(0.65)
        );
    leaf.castShadow = true;
    leaf.receiveShadow = true;
    if (facade === "satin" && oneSidedPaint) {
      // Тонкая «изнанка» вплотную к задней пласти: открыли дверь — видно,
      // что внутри МДФ без эмали. Кромки остаются окрашенными.
      const back = this.addPanel(
        parent,
        x,
        y,
        z - depth / 2 - mm(0.2),
        Math.max(mm(10), width - mm(3)),
        Math.max(mm(10), height - mm(3)),
        mm(0.3),
        this.getUnpaintedMdfMaterial(),
        meta
      );
      back.castShadow = false;
      back.receiveShadow = true;
    }
    if (facade === "mirror") {
      leaf.userData.mirrorSurface = true;
      // The visible backing must not be captured by the virtual reflector
      // camera. At long range its millimetre offset can fall inside the
      // oblique clip tolerance and the mirror otherwise reflects itself.
      leaf.layers.set(MIRROR_BACKING_LAYER);
    }
    const main = groove?.main ?? { x, y, width, height };
    this.addFacadeTrim(
      parent,
      main.x,
      main.y,
      z,
      main.width,
      main.height,
      depth,
      facade,
      materialKey,
      facadePattern,
      material,
      meta,
      groove ? undefined : integratedHandleCut
    );
    return leaf;
  }

  private getUnpaintedMdfMaterial() {
    const key = "facade:mdf-unpainted";
    const cached = this.materialCache.get(key);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({ color: 0xc8ab80, roughness: 0.9, metalness: 0 });
    this.materialCache.set(key, material);
    return material;
  }

  private addFacadeTrim(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    facade: WardrobeModule["facade"],
    materialKey: string,
    facadePattern: FacadePatternType | undefined,
    surfaceMaterial: PanelMaterial,
    meta: PickMeta,
    integratedHandleCut?: IntegratedHandleCut,
    glassGripSide?: number
  ) {
    if (facade === "mirror") {
      this.addMirrorReflection(parent, x, y, z + depth / 2 + mm(5), width, height, meta);
      return;
    }

    if (facade !== "glass") {
      this.addFacadePatternDetails(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        facade,
        facadePattern,
        surfaceMaterial,
        meta,
        integratedHandleCut
      );
      return;
    }

    const frame = this.getFacadeEdgeMaterial(facade, materialKey);
    const frameWidth = mm(glassFrameWidthMm(width / MM, height / MM));
    const verticalHeight = Math.max(mm(40), height - frameWidth * 2);
    const frameRadius = Math.min(mm(0.8), frameWidth * 0.08);

    [-1, 1].forEach((side) => {
      this.addRoundedPanel(
        parent,
        x + side * (width / 2 - frameWidth / 2),
        y,
        z,
        frameWidth,
        verticalHeight,
        depth,
        frame,
        meta,
        frameRadius
      );
    });
    [-1, 1].forEach((side) => {
      this.addRoundedPanel(
        parent,
        x,
        y + side * (height / 2 - frameWidth / 2),
        z,
        width,
        frameWidth,
        depth,
        frame,
        meta,
        frameRadius
      );
    });
    if (glassGripSide) {
      // Зацеп в профиле по всей высоте открывающего края: тёмный паз и
      // выступающая кромка профиля — вместо отдельной ручки.
      const gripHeight = Math.max(mm(40), height - mm(8));
      this.addRoundedPanel(
        parent,
        x + glassGripSide * (width / 2 - mm(11)),
        y,
        z + depth / 2 + mm(0.4),
        mm(12),
        gripHeight,
        mm(0.8),
        this.getSoftShadowMaterial(0.42),
        meta,
        mm(0.4)
      );
      this.addRoundedPanel(
        parent,
        x + glassGripSide * (width / 2 - mm(2.5)),
        y,
        z + depth / 2 + mm(2),
        mm(5),
        gripHeight,
        mm(4),
        frame,
        meta,
        mm(0.8)
      );
    }
  }

  private addFacadePatternDetails(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    facade: WardrobeModule["facade"],
    facadePattern: FacadePatternType | undefined,
    material: PanelMaterial,
    meta: PickMeta,
    integratedHandleCut?: IntegratedHandleCut
  ) {
    this.facadePatternExclusion = integratedHandleCut
      ? {
          left: integratedHandleCut.x - integratedHandleCut.width / 2,
          right: integratedHandleCut.x + integratedHandleCut.width / 2,
          bottom: integratedHandleCut.y - integratedHandleCut.height / 2,
          top: integratedHandleCut.y + integratedHandleCut.height / 2
        }
      : null;
    if (!facadeSupportsMilling(facade)) return;
    const pattern = facade === "gloss" ? "art009" : facadePattern ?? "plain";
    if (width < mm(120) || height < mm(100)) return;
    if (pattern === "art001") {
      this.addArt001FlutedSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta,
        integratedHandleCut
      );
      return;
    }
    if (pattern === "art005") {
      this.addArt005SplitSlatSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art006") {
      this.addArt006ChevronSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art008") {
      this.addArt008GroupedDiagonalSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art007") {
      this.addArt007ScallopSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art010") {
      this.addArt010CubeSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art012") {
      this.addArt012LayeredFrame(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art023") {
      this.addArt023FacetedFrame(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art036") {
      this.addArt036HoneycombSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern === "art037") {
      this.addArt037DiamondSurface(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }
    if (pattern !== "art009") return;

    // ART.009 is built from actual shallow profiles. The lighting therefore
    // creates the edge definition; no dark painted lines are laid over white
    // or wood finishes.
    const insetX = clamp(width * 0.095, mm(26), mm(54));
    const insetY = clamp(height * 0.04, mm(30), mm(78));
    const profileWidth = clamp(Math.min(width, height) * 0.024, mm(8), mm(14));
    const profileDepth = mm(2.4);
    const frameWidth = Math.max(mm(54), width - insetX * 2);
    const frameHeight = Math.max(mm(54), height - insetY * 2);
    const frontZ = z + depth / 2 + profileDepth / 2 - mm(0.18);
    const profileRadius = Math.min(mm(1), profileWidth * 0.12);

    [-1, 1].forEach((side) => {
      this.addRoundedPanel(
        parent,
        x + side * frameWidth / 2,
        y,
        frontZ,
        profileWidth,
        frameHeight + profileWidth,
        profileDepth,
        material,
        meta,
        profileRadius
      );
    });
    [-1, 1].forEach((side) => {
      this.addRoundedPanel(
        parent,
        x,
        y + side * frameHeight / 2,
        frontZ,
        frameWidth + profileWidth,
        profileWidth,
        profileDepth,
        material,
        meta,
        profileRadius
      );
    });
  }

  private addArt005SplitSlatSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const margin = mm(7);
    const bounds: FacadePatternBounds = {
      left: x - width / 2 + margin,
      right: x + width / 2 - margin,
      bottom: y - height / 2 + margin,
      top: y + height / 2 - margin
    };
    const splitY = bounds.bottom + (bounds.top - bounds.bottom) * 0.38;
    const paths: FacadePatternPoint[][] = [];
    const verticalPitch = clamp(width / 16, mm(27), mm(37));
    const verticalCount = Math.max(
      1,
      Math.floor((bounds.right - bounds.left) / verticalPitch)
    );
    const verticalOccupied = (verticalCount - 1) * verticalPitch;
    const verticalStart =
      bounds.left +
      Math.max(0, bounds.right - bounds.left - verticalOccupied) / 2;
    for (let index = 0; index < verticalCount; index += 1) {
      const lineX = verticalStart + index * verticalPitch;
      paths.push([
        { x: lineX, y: splitY + mm(3) },
        { x: lineX, y: bounds.top }
      ]);
    }

    const lowerHeight = splitY - bounds.bottom;
    const horizontalPitch = clamp(lowerHeight / 9, mm(42), mm(68));
    const horizontalCount = Math.max(1, Math.floor(lowerHeight / horizontalPitch));
    const horizontalOccupied = (horizontalCount - 1) * horizontalPitch;
    const horizontalStart =
      bounds.bottom + Math.max(0, lowerHeight - horizontalOccupied) / 2;
    for (let index = 0; index < horizontalCount; index += 1) {
      const lineY = horizontalStart + index * horizontalPitch;
      paths.push([
        { x: bounds.left, y: lineY },
        { x: bounds.right, y: lineY }
      ]);
    }

    this.addFacadeReliefPaths(
      parent,
      paths,
      bounds,
      z + depth / 2 + mm(0.12),
      mm(2.1),
      mm(0.75),
      this.getFacadePatternLineMaterial(material, "art005"),
      meta
    );
  }

  private addArt006ChevronSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const margin = mm(6);
    const bounds: FacadePatternBounds = {
      left: x - width / 2 + margin,
      right: x + width / 2 - margin,
      bottom: y - height / 2 + margin,
      top: y + height / 2 - margin
    };
    const segmentWidth = mm(58);
    const amplitude = mm(13);
    const rowPitch = mm(48);
    const usableHeight = Math.max(0, bounds.top - bounds.bottom - amplitude * 2);
    const rowCount = Math.max(1, Math.floor(usableHeight / rowPitch) + 1);
    const occupiedHeight = (rowCount - 1) * rowPitch;
    const startY =
      bounds.bottom + amplitude + Math.max(0, usableHeight - occupiedHeight) / 2;
    const paths: FacadePatternPoint[][] = [];

    for (let row = 0; row < rowCount; row += 1) {
      const centerY = startY + row * rowPitch;
      const points: FacadePatternPoint[] = [];
      const span = bounds.right - bounds.left;
      const segmentCount = Math.max(1, Math.ceil(span / segmentWidth));
      for (let segment = 0; segment <= segmentCount; segment += 1) {
        const pointX = Math.min(
          bounds.right,
          bounds.left + segment * segmentWidth
        );
        points.push({
          x: pointX,
          y: centerY + (segment % 2 === 0 ? amplitude : -amplitude)
        });
        if (pointX >= bounds.right) break;
      }
      paths.push(points);
    }

    this.addFacadeReliefPaths(
      parent,
      paths,
      bounds,
      z + depth / 2 + mm(0.12),
      mm(2.1),
      mm(0.75),
      this.getFacadePatternLineMaterial(material, "art006"),
      meta
    );
  }

  private addArt008GroupedDiagonalSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const margin = mm(7);
    const bounds: FacadePatternBounds = {
      left: x - width / 2 + margin,
      right: x + width / 2 - margin,
      bottom: y - height / 2 + margin,
      top: y + height / 2 - margin
    };
    const surfaceHeight = bounds.top - bounds.bottom;
    const groupPitch = mm(190);
    const lineSpacing = mm(9);
    const paths: FacadePatternPoint[][] = [];
    const firstOffset = bounds.left - surfaceHeight - groupPitch;
    const lastOffset = bounds.right + groupPitch;

    for (
      let groupOffset = firstOffset;
      groupOffset <= lastOffset;
      groupOffset += groupPitch
    ) {
      [-1, 0, 1].forEach((lineIndex) => {
        const bottomX = groupOffset + lineIndex * lineSpacing;
        paths.push([
          { x: bottomX, y: bounds.bottom },
          { x: bottomX + surfaceHeight, y: bounds.top }
        ]);
      });
    }

    this.addFacadeReliefPaths(
      parent,
      paths,
      bounds,
      z + depth / 2 + mm(0.12),
      mm(1.8),
      mm(0.68),
      this.getFacadePatternLineMaterial(material, "art008"),
      meta
    );
  }

  private addArt007ScallopSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const margin = mm(6);
    const bounds: FacadePatternBounds = {
      left: x - width / 2 + margin,
      right: x + width / 2 - margin,
      bottom: y - height / 2 + margin,
      top: y + height / 2 - margin
    };
    const surfaceWidth = Math.max(mm(120), bounds.right - bounds.left);
    const columnCount = Math.max(2, Math.round(surfaceWidth / mm(128)));
    const radius = surfaceWidth / Math.max(4, columnCount * 2);
    const rowPitch = radius * 0.98;
    const surfaceHeight = Math.max(mm(100), bounds.top - bounds.bottom);
    const rowCount = Math.max(
      1,
      Math.floor(Math.max(0, surfaceHeight - radius) / rowPitch) + 1
    );
    const occupiedHeight = (rowCount - 1) * rowPitch + radius;
    const startY = bounds.bottom + Math.max(0, surfaceHeight - occupiedHeight) / 2;
    const paths: FacadePatternPoint[][] = [];
    const arcSegments = 16;

    for (let row = 0; row < rowCount; row += 1) {
      const baseline = startY + row * rowPitch;
      const shifted = row % 2 === 1;
      const firstCenter = bounds.left + radius + (shifted ? radius : 0);
      for (
        let centerX = firstCenter - radius * 2;
        centerX <= bounds.right + radius * 2;
        centerX += radius * 2
      ) {
        const arc: FacadePatternPoint[] = [];
        for (let segment = 0; segment <= arcSegments; segment += 1) {
          const angle = Math.PI - (segment / arcSegments) * Math.PI;
          arc.push({
            x: centerX + Math.cos(angle) * radius,
            y: baseline + Math.sin(angle) * radius
          });
        }
        paths.push(arc);
      }
    }

    this.addFacadeReliefPaths(
      parent,
      paths,
      bounds,
      z + depth / 2 + mm(0.12),
      mm(2),
      mm(0.7),
      this.getFacadePatternLineMaterial(material, "art007"),
      meta
    );
  }

  private addArt010CubeSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const margin = mm(6);
    const bounds: FacadePatternBounds = {
      left: x - width / 2 + margin,
      right: x + width / 2 - margin,
      bottom: y - height / 2 + margin,
      top: y + height / 2 - margin
    };
    const surfaceWidth = Math.max(mm(120), bounds.right - bounds.left);
    const targetColumns = Math.max(2, Math.round(surfaceWidth / mm(112)));
    const radius = surfaceWidth / (targetColumns * Math.sqrt(3));
    const halfHexWidth = (Math.sqrt(3) / 2) * radius;
    const horizontalStep = halfHexWidth * 2;
    const verticalStep = radius * 1.5;
    const paths: FacadePatternPoint[][] = [];
    const segments = new Set<string>();

    const pointKey = (point: FacadePatternPoint) =>
      `${Math.round(point.x / MM)},${Math.round(point.y / MM)}`;
    const addSegment = (start: FacadePatternPoint, end: FacadePatternPoint) => {
      const startKey = pointKey(start);
      const endKey = pointKey(end);
      const key = startKey < endKey
        ? `${startKey}:${endKey}`
        : `${endKey}:${startKey}`;
      if (segments.has(key)) return;
      segments.add(key);
      paths.push([start, end]);
    };

    const firstRow = -2;
    const lastRow = Math.ceil((bounds.top - bounds.bottom) / verticalStep) + 2;
    for (let row = firstRow; row <= lastRow; row += 1) {
      const centerY = bounds.bottom + row * verticalStep;
      const rowOffset = Math.abs(row) % 2 === 1 ? horizontalStep / 2 : 0;
      const firstColumn = -2;
      const lastColumn =
        Math.ceil((bounds.right - bounds.left) / horizontalStep) + 2;
      for (let column = firstColumn; column <= lastColumn; column += 1) {
        const centerX = bounds.left + column * horizontalStep + rowOffset;
        const center = { x: centerX, y: centerY };
        const vertices: FacadePatternPoint[] = [
          { x: centerX, y: centerY + radius },
          { x: centerX + halfHexWidth, y: centerY + radius / 2 },
          { x: centerX + halfHexWidth, y: centerY - radius / 2 },
          { x: centerX, y: centerY - radius },
          { x: centerX - halfHexWidth, y: centerY - radius / 2 },
          { x: centerX - halfHexWidth, y: centerY + radius / 2 }
        ];
        for (let edge = 0; edge < vertices.length; edge += 1) {
          const start = vertices[edge];
          const end = vertices[(edge + 1) % vertices.length];
          if (start && end) addSegment(start, end);
        }
        [1, 3, 5].forEach((vertexIndex) => {
          const vertex = vertices[vertexIndex];
          if (vertex) addSegment(center, vertex);
        });
      }
    }

    this.addFacadeReliefPaths(
      parent,
      paths,
      bounds,
      z + depth / 2 + mm(0.12),
      mm(2.15),
      mm(0.8),
      this.getFacadePatternLineMaterial(material, "art010"),
      meta
    );
  }

  private addArt012LayeredFrame(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const minDimension = Math.min(width, height);
    const outerInsetX = clamp(width * 0.045, mm(14), mm(24));
    const outerInsetY = clamp(height * 0.016, mm(16), mm(30));
    const layerStep = clamp(minDimension * 0.017, mm(6.5), mm(9));
    const profileWidth = clamp(minDimension * 0.009, mm(3.4), mm(4.8));
    const layers = 5;

    for (let layer = 0; layer < layers; layer += 1) {
      const insetX = outerInsetX + layer * layerStep;
      const insetY = outerInsetY + layer * layerStep;
      const frameWidth = width - insetX * 2;
      const frameHeight = height - insetY * 2;
      if (frameWidth <= mm(70) || frameHeight <= mm(90)) break;
      const profileDepth = mm(2.5 - layer * 0.18);
      const frontZ = z + depth / 2 + profileDepth / 2 - mm(0.12);
      const radius = Math.min(mm(0.8), profileWidth * 0.18);

      [-1, 1].forEach((side) => {
        this.addRoundedPanel(
          parent,
          x + side * frameWidth / 2,
          y,
          frontZ,
          profileWidth,
          frameHeight + profileWidth,
          profileDepth,
          material,
          meta,
          radius
        );
      });
      [-1, 1].forEach((side) => {
        this.addRoundedPanel(
          parent,
          x,
          y + side * frameHeight / 2,
          frontZ,
          frameWidth + profileWidth,
          profileWidth,
          profileDepth,
          material,
          meta,
          radius
        );
      });
    }
  }

  private addArt023FacetedFrame(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    // ART.023 is a classic five-piece facade rather than one continuous
    // inward slope. Its characteristic section is: a thin outer chamfer,
    // a wide flat frame, a deep inner bevel, a narrow raised bead and a
    // recessed centre panel. Keeping every ring split into four mitred
    // pieces preserves the visible diagonal joins from the catalogue.
    const outerMargin = clamp(width * 0.012, mm(4.5), mm(7));
    const outerChamfer = clamp(width * 0.016, mm(6), mm(8.5));
    const flatFrameWidth = clamp(width * 0.095, mm(39), mm(50));
    const innerBevelWidth = clamp(width * 0.025, mm(9), mm(12.5));
    const beadRiseWidth = mm(4);
    const beadFaceWidth = mm(4);
    const beadFallWidth = mm(4);
    const totalProfileWidth =
      outerChamfer +
      flatFrameWidth +
      innerBevelWidth +
      beadRiseWidth +
      beadFaceWidth +
      beadFallWidth;
    const outerBounds: FacadePatternBounds = {
      left: x - width / 2 + outerMargin,
      right: x + width / 2 - outerMargin,
      bottom: y - height / 2 + outerMargin,
      top: y + height / 2 - outerMargin
    };
    if (
      outerBounds.right - outerBounds.left - totalProfileWidth * 2 < mm(72) ||
      outerBounds.top - outerBounds.bottom - totalProfileWidth * 2 < mm(110)
    ) {
      this.addArt012LayeredFrame(
        parent,
        x,
        y,
        z,
        width,
        height,
        depth,
        material,
        meta
      );
      return;
    }

    const insetBounds = (
      bounds: FacadePatternBounds,
      inset: number
    ): FacadePatternBounds => ({
      left: bounds.left + inset,
      right: bounds.right - inset,
      bottom: bounds.bottom + inset,
      top: bounds.top - inset
    });
    const chamferBounds = insetBounds(outerBounds, outerChamfer);
    const frameInnerBounds = insetBounds(chamferBounds, flatFrameWidth);
    const bevelInnerBounds = insetBounds(frameInnerBounds, innerBevelWidth);
    const beadCrestOuterBounds = insetBounds(bevelInnerBounds, beadRiseWidth);
    const beadCrestInnerBounds = insetBounds(beadCrestOuterBounds, beadFaceWidth);
    const panelBounds = insetBounds(beadCrestInnerBounds, beadFallWidth);

    const panelFrontZ = z + depth / 2 + mm(0.12);
    const outerEdgeZ = panelFrontZ + mm(0.16);
    const frameFaceZ = panelFrontZ + mm(4.6);
    const bevelBottomZ = panelFrontZ + mm(0.36);
    const beadCrestZ = panelFrontZ + mm(2.25);
    const panelEdgeZ = panelFrontZ + mm(0.14);
    const positions: number[] = [];
    const colors: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const appendQuad = (
      a: Vector3,
      b: Vector3,
      c: Vector3,
      d: Vector3,
      shade = 1
    ) => {
      const offset = positions.length / 3;
      [a, b, c, d].forEach((point) => {
        positions.push(point.x, point.y, point.z);
        colors.push(shade, shade, shade);
        uvs.push(
          (point.x - outerBounds.left) /
            Math.max(mm(1), outerBounds.right - outerBounds.left),
          (point.y - outerBounds.bottom) /
            Math.max(mm(1), outerBounds.top - outerBounds.bottom)
        );
      });
      indices.push(
        offset,
        offset + 1,
        offset + 2,
        offset,
        offset + 2,
        offset + 3
      );
    };
    const point = (pointX: number, pointY: number, pointZ: number) =>
      new Vector3(pointX, pointY, pointZ);
    const appendMitredRing = (
      outer: FacadePatternBounds,
      outerZ: number,
      inner: FacadePatternBounds,
      innerZ: number,
      shades: [number, number, number, number]
    ) => {
      const outerTopLeft = point(outer.left, outer.top, outerZ);
      const outerTopRight = point(outer.right, outer.top, outerZ);
      const outerBottomRight = point(outer.right, outer.bottom, outerZ);
      const outerBottomLeft = point(outer.left, outer.bottom, outerZ);
      const innerTopLeft = point(inner.left, inner.top, innerZ);
      const innerTopRight = point(inner.right, inner.top, innerZ);
      const innerBottomRight = point(inner.right, inner.bottom, innerZ);
      const innerBottomLeft = point(inner.left, inner.bottom, innerZ);

      appendQuad(
        outerTopLeft,
        innerTopLeft,
        innerTopRight,
        outerTopRight,
        shades[0]
      );
      appendQuad(
        outerTopRight,
        innerTopRight,
        innerBottomRight,
        outerBottomRight,
        shades[1]
      );
      appendQuad(
        outerBottomRight,
        innerBottomRight,
        innerBottomLeft,
        outerBottomLeft,
        shades[2]
      );
      appendQuad(
        outerBottomLeft,
        innerBottomLeft,
        innerTopLeft,
        outerTopLeft,
        shades[3]
      );
    };

    // Narrow perimeter chamfer rising to the main frame face.
    appendMitredRing(
      outerBounds,
      outerEdgeZ,
      chamferBounds,
      frameFaceZ,
      [1, 0.94, 0.89, 0.97]
    );
    // Wide, almost flat rails and stiles with true 45° mitres.
    appendMitredRing(
      chamferBounds,
      frameFaceZ,
      frameInnerBounds,
      frameFaceZ,
      [1, 0.95, 0.9, 0.975]
    );
    // Deep routed transition between the broad frame and inner bead.
    appendMitredRing(
      frameInnerBounds,
      frameFaceZ,
      bevelInnerBounds,
      bevelBottomZ,
      [0.9, 0.84, 0.77, 0.87]
    );
    appendMitredRing(
      bevelInnerBounds,
      bevelBottomZ,
      beadCrestOuterBounds,
      beadCrestZ,
      [0.97, 0.91, 0.85, 0.94]
    );
    appendMitredRing(
      beadCrestOuterBounds,
      beadCrestZ,
      beadCrestInnerBounds,
      beadCrestZ,
      [1, 0.96, 0.91, 0.98]
    );
    appendMitredRing(
      beadCrestInnerBounds,
      beadCrestZ,
      panelBounds,
      panelEdgeZ,
      [0.93, 0.88, 0.82, 0.91]
    );

    const geometry = new BufferGeometry();
    geometry.setAttribute(
      "position",
      new Float32BufferAttribute(positions, 3)
    );
    geometry.setAttribute("color", new Float32BufferAttribute(colors, 3));
    geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const frame = new Mesh(
      geometry,
      this.getArt023FrameMaterial(material)
    );
    this.applyPhysicalWoodUvs(frame, x, y, panelFrontZ, parent, meta);
    frame.castShadow = true;
    frame.receiveShadow = true;
    frame.userData.pick = meta;
    parent.add(frame);

    const mitrePaths: FacadePatternPoint[][] = [
      [
        { x: chamferBounds.left, y: chamferBounds.top },
        { x: frameInnerBounds.left, y: frameInnerBounds.top }
      ],
      [
        { x: chamferBounds.right, y: chamferBounds.top },
        { x: frameInnerBounds.right, y: frameInnerBounds.top }
      ],
      [
        { x: chamferBounds.right, y: chamferBounds.bottom },
        { x: frameInnerBounds.right, y: frameInnerBounds.bottom }
      ],
      [
        { x: chamferBounds.left, y: chamferBounds.bottom },
        { x: frameInnerBounds.left, y: frameInnerBounds.bottom }
      ]
    ];
    this.addFacadeReliefPaths(
      parent,
      mitrePaths,
      chamferBounds,
      frameFaceZ + mm(0.02),
      mm(2.2),
      mm(0.08),
      this.getFacadePatternLineMaterial(material, "art023"),
      meta
    );
  }

  private addArt036HoneycombSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const margin = mm(7);
    const bounds: FacadePatternBounds = {
      left: x - width / 2 + margin,
      right: x + width / 2 - margin,
      bottom: y - height / 2 + margin,
      top: y + height / 2 - margin
    };
    const radius = clamp(width * 0.043, mm(20), mm(28));
    const horizontalStep = Math.sqrt(3) * radius;
    const verticalStep = radius * 1.5;
    const paths: FacadePatternPoint[][] = [];
    const segments = new Set<string>();
    const pointKey = (point: FacadePatternPoint) =>
      `${Math.round((point.x / MM) * 10)},${Math.round((point.y / MM) * 10)}`;
    const addSegment = (start: FacadePatternPoint, end: FacadePatternPoint) => {
      const startKey = pointKey(start);
      const endKey = pointKey(end);
      const key = startKey < endKey
        ? `${startKey}:${endKey}`
        : `${endKey}:${startKey}`;
      if (segments.has(key)) return;
      segments.add(key);
      paths.push([start, end]);
    };
    const rowCount = Math.ceil((bounds.top - bounds.bottom) / verticalStep) + 4;
    const columnCount =
      Math.ceil((bounds.right - bounds.left) / horizontalStep) + 4;

    for (let row = -2; row <= rowCount; row += 1) {
      const centerY = bounds.bottom + row * verticalStep;
      const rowOffset = Math.abs(row) % 2 === 1 ? horizontalStep / 2 : 0;
      for (let column = -2; column <= columnCount; column += 1) {
        const centerX = bounds.left + column * horizontalStep + rowOffset;
        const vertices = Array.from({ length: 6 }, (_, vertexIndex) => {
          const angle = Math.PI / 6 + (vertexIndex * Math.PI) / 3;
          return {
            x: centerX + Math.cos(angle) * radius,
            y: centerY + Math.sin(angle) * radius
          };
        });
        vertices.forEach((start, edgeIndex) => {
          const end = vertices[(edgeIndex + 1) % vertices.length];
          if (end) addSegment(start, end);
        });
      }
    }

    this.addFacadeReliefPaths(
      parent,
      paths,
      bounds,
      z + depth / 2 + mm(0.12),
      mm(2.05),
      mm(0.9),
      this.getFacadePatternLineMaterial(material, "art036"),
      meta
    );
  }

  private addArt037DiamondSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const margin = mm(7);
    const bounds: FacadePatternBounds = {
      left: x - width / 2 + margin,
      right: x + width / 2 - margin,
      bottom: y - height / 2 + margin,
      top: y + height / 2 - margin
    };
    const surfaceHeight = bounds.top - bounds.bottom;
    const horizontalPitch = clamp(width / 10, mm(44), mm(60));
    const horizontalTravel = surfaceHeight * 0.27;
    const paths: FacadePatternPoint[][] = [];
    const firstOffset = bounds.left - horizontalTravel - horizontalPitch;
    const lastOffset = bounds.right + horizontalTravel + horizontalPitch;

    for (
      let lineOffset = firstOffset;
      lineOffset <= lastOffset;
      lineOffset += horizontalPitch
    ) {
      paths.push([
        { x: lineOffset, y: bounds.bottom },
        { x: lineOffset + horizontalTravel, y: bounds.top }
      ]);
      paths.push([
        { x: lineOffset, y: bounds.bottom },
        { x: lineOffset - horizontalTravel, y: bounds.top }
      ]);
    }

    this.addFacadeReliefPaths(
      parent,
      paths,
      bounds,
      z + depth / 2 + mm(0.12),
      mm(2.2),
      mm(0.92),
      this.getFacadePatternLineMaterial(material, "art037"),
      meta
    );
  }

  private getArt023FrameMaterial(material: PanelMaterial): PanelMaterial {
    const cacheKey = `facade-pattern-frame:art023:${material.uuid}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached;
    const frameMaterial = material.clone() as PanelMaterial;
    frameMaterial.side = DoubleSide;
    frameMaterial.vertexColors = true;
    frameMaterial.userData = { ...material.userData, shared: true };
    frameMaterial.needsUpdate = true;
    this.materialCache.set(cacheKey, frameMaterial);
    return frameMaterial;
  }

  private addFacadeReliefPaths(
    parent: Group,
    paths: FacadePatternPoint[][],
    bounds: FacadePatternBounds,
    baseZ: number,
    lineWidth: number,
    reliefDepth: number,
    material: PanelMaterial,
    meta: PickMeta
  ) {
    const positions: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const halfWidth = lineWidth / 2;

    const appendClippedSegment = (
      start: FacadePatternPoint,
      end: FacadePatternPoint
    ) => {
      const deltaX = end.x - start.x;
      const deltaY = end.y - start.y;
      const length = Math.hypot(deltaX, deltaY);
      if (length < mm(0.5)) return;
      const normalX = (-deltaY / length) * halfWidth;
      const normalY = (deltaX / length) * halfWidth;
      const vertexOffset = positions.length / 3;
      const frontZ = baseZ + reliefDepth;

      positions.push(
        start.x + normalX,
        start.y + normalY,
        baseZ,
        end.x + normalX,
        end.y + normalY,
        baseZ,
        end.x - normalX,
        end.y - normalY,
        baseZ,
        start.x - normalX,
        start.y - normalY,
        baseZ,
        start.x + normalX,
        start.y + normalY,
        frontZ,
        end.x + normalX,
        end.y + normalY,
        frontZ,
        end.x - normalX,
        end.y - normalY,
        frontZ,
        start.x - normalX,
        start.y - normalY,
        frontZ
      );

      const faces = [
        4, 5, 6, 4, 6, 7,
        0, 2, 1, 0, 3, 2,
        0, 1, 5, 0, 5, 4,
        1, 2, 6, 1, 6, 5,
        2, 3, 7, 2, 7, 6,
        3, 0, 4, 3, 4, 7
      ];
      faces.forEach((index) => indices.push(vertexOffset + index));
    };

    const appendSegment = (
      sourceStart: FacadePatternPoint,
      sourceEnd: FacadePatternPoint
    ) => {
      const clipped = this.clipFacadePatternSegment(
        sourceStart,
        sourceEnd,
        bounds
      );
      if (!clipped) return;
      const exclusion = this.facadePatternExclusion;
      if (!exclusion) {
        appendClippedSegment(clipped[0], clipped[1]);
        return;
      }
      const clearance = halfWidth + mm(1.1);
      const expandedExclusion: FacadePatternBounds = {
        left: exclusion.left - clearance,
        right: exclusion.right + clearance,
        bottom: exclusion.bottom - clearance,
        top: exclusion.top + clearance
      };
      this.subtractFacadePatternExclusion(
        clipped[0],
        clipped[1],
        expandedExclusion
      ).forEach(([start, end]) => appendClippedSegment(start, end));
    };

    paths.forEach((path) => {
      for (let index = 1; index < path.length; index += 1) {
        const start = path[index - 1];
        const end = path[index];
        if (start && end) appendSegment(start, end);
      }
    });
    if (positions.length === 0) return;

    const geometry = new BufferGeometry();
    const surfaceWidth = Math.max(mm(1), bounds.right - bounds.left);
    const surfaceHeight = Math.max(mm(1), bounds.top - bounds.bottom);
    for (let index = 0; index < positions.length; index += 3) {
      uvs.push(
        ((positions[index] ?? bounds.left) - bounds.left) / surfaceWidth,
        ((positions[index + 1] ?? bounds.bottom) - bounds.bottom) /
          surfaceHeight
      );
    }
    geometry.setAttribute(
      "position",
      new Float32BufferAttribute(positions, 3)
    );
    geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    const relief = new Mesh(geometry, material);
    this.applyPhysicalWoodUvs(
      relief,
      (bounds.left + bounds.right) / 2,
      (bounds.bottom + bounds.top) / 2,
      baseZ,
      parent,
      meta
    );
    relief.castShadow = true;
    relief.receiveShadow = true;
    relief.renderOrder = 3;
    relief.userData.pick = meta;
    parent.add(relief);
  }

  private clipFacadePatternSegment(
    start: FacadePatternPoint,
    end: FacadePatternPoint,
    bounds: FacadePatternBounds
  ): [FacadePatternPoint, FacadePatternPoint] | null {
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    let minimum = 0;
    let maximum = 1;
    const checks: Array<[number, number]> = [
      [-deltaX, start.x - bounds.left],
      [deltaX, bounds.right - start.x],
      [-deltaY, start.y - bounds.bottom],
      [deltaY, bounds.top - start.y]
    ];

    for (const [direction, distance] of checks) {
      if (Math.abs(direction) < 1e-8) {
        if (distance < 0) return null;
        continue;
      }
      const ratio = distance / direction;
      if (direction < 0) {
        minimum = Math.max(minimum, ratio);
      } else {
        maximum = Math.min(maximum, ratio);
      }
      if (minimum > maximum) return null;
    }

    return [
      {
        x: start.x + deltaX * minimum,
        y: start.y + deltaY * minimum
      },
      {
        x: start.x + deltaX * maximum,
        y: start.y + deltaY * maximum
      }
    ];
  }

  private subtractFacadePatternExclusion(
    start: FacadePatternPoint,
    end: FacadePatternPoint,
    exclusion: FacadePatternBounds
  ): Array<[FacadePatternPoint, FacadePatternPoint]> {
    const inside = this.clipFacadePatternSegment(start, end, exclusion);
    if (!inside) return [[start, end]];
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const lengthSquared = deltaX * deltaX + deltaY * deltaY;
    if (lengthSquared < 1e-10) return [];
    const parameterAt = (point: FacadePatternPoint) =>
      ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) /
      lengthSquared;
    const first = clamp(parameterAt(inside[0]), 0, 1);
    const second = clamp(parameterAt(inside[1]), 0, 1);
    const minimum = Math.min(first, second);
    const maximum = Math.max(first, second);
    if (maximum - minimum < 1e-5) return [[start, end]];
    const pointAt = (parameter: number): FacadePatternPoint => ({
      x: start.x + deltaX * parameter,
      y: start.y + deltaY * parameter
    });
    const remaining: Array<[FacadePatternPoint, FacadePatternPoint]> = [];
    if (minimum > 1e-5) remaining.push([start, pointAt(minimum)]);
    if (maximum < 1 - 1e-5) remaining.push([pointAt(maximum), end]);
    return remaining;
  }

  private getFacadePatternLineMaterial(
    material: PanelMaterial,
    pattern:
      | "art005"
      | "art006"
      | "art007"
      | "art008"
      | "art010"
      | "art023"
      | "art036"
      | "art037"
  ): PanelMaterial {
    const cacheKey = `facade-pattern-lines:${pattern}:${material.uuid}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached;
    const lineMaterial = material.clone() as PanelMaterial;
    lineMaterial.color.copy(material.color);
    const shade = pattern === "art023"
      ? material.map
        ? 0.75
        : 0.7
      : material.map
        ? 0.76
        : 0.72;
    lineMaterial.color.multiplyScalar(shade);
    if (lineMaterial instanceof MeshStandardMaterial) {
      lineMaterial.normalMap = null;
      lineMaterial.roughnessMap = null;
      lineMaterial.metalnessMap = null;
      lineMaterial.roughness = 0.62;
      lineMaterial.metalness = 0;
    }
    lineMaterial.userData = { ...material.userData, shared: true };
    lineMaterial.needsUpdate = true;
    this.materialCache.set(cacheKey, lineMaterial);
    return lineMaterial;
  }

  private addArt001FlutedSurface(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta: PickMeta,
    integratedHandleCut?: IntegratedHandleCut
  ) {
    const margin = mm(3);
    const surfaceWidth = Math.max(mm(80), width - margin * 2);
    const surfaceHeight = Math.max(mm(80), height - margin * 2);
    const pitch = mm(18);
    const amplitude = mm(3);
    const minimumShade = 0.88;
    const segments = clamp(
      Math.ceil(surfaceWidth / pitch) * 8,
      48,
      520
    );
    const positions: number[] = [];
    const colors: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const baseZ = z + depth / 2 + mm(0.16);
    const surfaceLeft = x - surfaceWidth / 2;
    const surfaceRight = x + surfaceWidth / 2;
    const surfaceBottom = y - surfaceHeight / 2;
    const surfaceTop = y + surfaceHeight / 2;
    const exclusion = integratedHandleCut
      ? {
          left: clamp(
            integratedHandleCut.x - integratedHandleCut.width / 2 - mm(0.8),
            surfaceLeft,
            surfaceRight
          ),
          right: clamp(
            integratedHandleCut.x + integratedHandleCut.width / 2 + mm(0.8),
            surfaceLeft,
            surfaceRight
          ),
          bottom: clamp(
            integratedHandleCut.y - integratedHandleCut.height / 2 - mm(0.8),
            surfaceBottom,
            surfaceTop
          ),
          top: clamp(
            integratedHandleCut.y + integratedHandleCut.height / 2 + mm(0.8),
            surfaceBottom,
            surfaceTop
          )
        }
      : null;
    const xBreaks = Array.from(
      { length: segments + 1 },
      (_, column) => surfaceLeft + (surfaceWidth * column) / segments
    );
    const yBreaks = [surfaceBottom, surfaceTop];
    if (
      exclusion &&
      exclusion.left < exclusion.right &&
      exclusion.bottom < exclusion.top
    ) {
      xBreaks.push(exclusion.left, exclusion.right);
      yBreaks.push(exclusion.bottom, exclusion.top);
    }
    const sortedX = [...new Set(xBreaks)].sort((first, second) => first - second);
    const sortedY = [...new Set(yBreaks)].sort((first, second) => first - second);
    const appendSurfaceQuad = (
      left: number,
      right: number,
      bottom: number,
      top: number
    ) => {
      const centerX = (left + right) / 2;
      const centerY = (bottom + top) / 2;
      if (
        exclusion &&
        centerX > exclusion.left &&
        centerX < exclusion.right &&
        centerY > exclusion.bottom &&
        centerY < exclusion.top
      ) {
        return;
      }
      const vertexOffset = positions.length / 3;
      const leftFraction = (left - surfaceLeft) / surfaceWidth;
      const rightFraction = (right - surfaceLeft) / surfaceWidth;
      const bottomFraction = (bottom - surfaceBottom) / surfaceHeight;
      const topFraction = (top - surfaceBottom) / surfaceHeight;
      const reliefAt = (worldX: number) => {
        const phase = ((worldX - surfaceLeft) / pitch) * Math.PI * 2;
        return (Math.cos(phase) * 0.5 + 0.5) * amplitude;
      };
      const leftRelief = reliefAt(left);
      const rightRelief = reliefAt(right);
      const leftShade =
        minimumShade + (leftRelief / amplitude) * (1 - minimumShade);
      const rightShade =
        minimumShade + (rightRelief / amplitude) * (1 - minimumShade);
      positions.push(
        left, bottom, baseZ + leftRelief,
        right, bottom, baseZ + rightRelief,
        right, top, baseZ + rightRelief,
        left, top, baseZ + leftRelief
      );
      colors.push(
        leftShade, leftShade, leftShade,
        rightShade, rightShade, rightShade,
        rightShade, rightShade, rightShade,
        leftShade, leftShade, leftShade
      );
      uvs.push(
        leftFraction, bottomFraction,
        rightFraction, bottomFraction,
        rightFraction, topFraction,
        leftFraction, topFraction
      );
      indices.push(
        vertexOffset,
        vertexOffset + 1,
        vertexOffset + 3,
        vertexOffset + 1,
        vertexOffset + 2,
        vertexOffset + 3
      );
    };
    for (let xIndex = 1; xIndex < sortedX.length; xIndex += 1) {
      for (let yIndex = 1; yIndex < sortedY.length; yIndex += 1) {
        appendSurfaceQuad(
          sortedX[xIndex - 1]!,
          sortedX[xIndex]!,
          sortedY[yIndex - 1]!,
          sortedY[yIndex]!
        );
      }
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute(
      "position",
      new Float32BufferAttribute(positions, 3)
    );
    geometry.setAttribute("color", new Float32BufferAttribute(colors, 3));
    geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const surface = new Mesh(
      geometry,
      this.getArt001SurfaceMaterial(material)
    );
    this.applyPhysicalWoodUvs(surface, x, y, z, parent, meta);
    surface.castShadow = true;
    surface.receiveShadow = true;
    surface.userData.pick = meta;
    parent.add(surface);

    const overlayPositions: number[] = [];
    const overlayUvs: number[] = [];
    const overlayIndices: number[] = [];
    const overlayZ = baseZ + amplitude + mm(0.22);
    const appendOverlayRect = (
      left: number,
      right: number,
      bottom: number,
      top: number
    ) => {
      if (right - left < mm(0.05) || top - bottom < mm(0.05)) return;
      const offset = overlayPositions.length / 3;
      overlayPositions.push(
        left, bottom, overlayZ,
        right, bottom, overlayZ,
        right, top, overlayZ,
        left, top, overlayZ
      );
      overlayUvs.push(
        (left - surfaceLeft) / surfaceWidth,
        (bottom - surfaceBottom) / surfaceHeight,
        (right - surfaceLeft) / surfaceWidth,
        (bottom - surfaceBottom) / surfaceHeight,
        (right - surfaceLeft) / surfaceWidth,
        (top - surfaceBottom) / surfaceHeight,
        (left - surfaceLeft) / surfaceWidth,
        (top - surfaceBottom) / surfaceHeight
      );
      overlayIndices.push(
        offset,
        offset + 1,
        offset + 3,
        offset + 1,
        offset + 2,
        offset + 3
      );
    };
    if (exclusion) {
      appendOverlayRect(surfaceLeft, exclusion.left, surfaceBottom, surfaceTop);
      appendOverlayRect(exclusion.right, surfaceRight, surfaceBottom, surfaceTop);
      appendOverlayRect(
        exclusion.left,
        exclusion.right,
        surfaceBottom,
        exclusion.bottom
      );
      appendOverlayRect(
        exclusion.left,
        exclusion.right,
        exclusion.top,
        surfaceTop
      );
    } else {
      appendOverlayRect(surfaceLeft, surfaceRight, surfaceBottom, surfaceTop);
    }
    const overlayGeometry = new BufferGeometry();
    overlayGeometry.setAttribute(
      "position",
      new Float32BufferAttribute(overlayPositions, 3)
    );
    overlayGeometry.setAttribute(
      "uv",
      new Float32BufferAttribute(overlayUvs, 2)
    );
    overlayGeometry.setIndex(overlayIndices);
    const grooveOverlay = new Mesh(
      overlayGeometry,
      this.getArt001GrooveMaterial(surfaceWidth, Boolean(material.map))
    );
    grooveOverlay.castShadow = false;
    grooveOverlay.receiveShadow = false;
    grooveOverlay.renderOrder = 4;
    grooveOverlay.userData.pick = meta;
    parent.add(grooveOverlay);
  }

  private getArt001SurfaceMaterial(material: PanelMaterial): PanelMaterial {
    const cacheKey = `facade-pattern-surface:art001:${material.uuid}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached;
    const surfaceMaterial = material.clone() as PanelMaterial;
    surfaceMaterial.vertexColors = true;
    surfaceMaterial.userData = { ...material.userData, shared: true };
    surfaceMaterial.needsUpdate = true;
    this.materialCache.set(cacheKey, surfaceMaterial);
    return surfaceMaterial;
  }

  private getArt001GrooveMaterial(
    surfaceWidth: number,
    textured: boolean
  ): MeshBasicMaterial {
    const widthMillimetres = Math.max(
      100,
      Math.round(surfaceWidth / MM / 10) * 10
    );
    const cacheKey = `facade-pattern-grooves:art001:${widthMillimetres}:${textured ? "textured" : "plain"}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshBasicMaterial;

    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 8;
    const context = canvas.getContext("2d");
    if (context) {
      const image = context.createImageData(canvas.width, canvas.height);
      const pitch = 18;
      for (let x = 0; x < canvas.width; x += 1) {
        const millimetres = (x / canvas.width) * widthMillimetres;
        const phase = (millimetres / pitch) * Math.PI * 2;
        const groove = Math.pow(
          -Math.cos(phase) * 0.5 + 0.5,
          textured ? 6 : 9
        );
        const alpha = Math.round(groove * (textured ? 105 : 38));
        for (let y = 0; y < canvas.height; y += 1) {
          const offset = (y * canvas.width + x) * 4;
          image.data[offset] = 34;
          image.data[offset + 1] = 29;
          image.data[offset + 2] = 24;
          image.data[offset + 3] = alpha;
        }
      }
      context.putImageData(image, 0, 0);
    }
    const texture = new CanvasTexture(canvas);
    texture.magFilter = LinearFilter;
    texture.minFilter = LinearFilter;
    texture.anisotropy = Math.min(
      12,
      this.renderer.capabilities.getMaxAnisotropy()
    );
    const material = new MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /**
   * Зеркало, наклеенное на боковину: подложка 4 мм и отражающая плоскость.
   * Группа повёрнута так, что её +z смотрит наружу от боковины.
   */
  private addSideMirror(
    parent: Group,
    x: number,
    y: number,
    z: number,
    yaw: number,
    width: number,
    height: number,
    meta: PickMeta
  ) {
    const holder = new Group();
    holder.position.set(x, y, z);
    holder.rotation.y = yaw;
    parent.add(holder);
    const glass = mm(SIDE_MIRROR_THICKNESS_MM);
    const backing = this.addPanel(holder, 0, 0, glass / 2, width, height, glass,
      this.getFacadeMaterial("mirror", ""), meta);
    backing.userData.mirrorSurface = true;
    backing.layers.set(MIRROR_BACKING_LAYER);
    this.addMirrorReflection(holder, 0, 0, glass + mm(1), width, height, meta);
    return holder;
  }

  private addMirrorReflection(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    meta: PickMeta
  ) {
    const mirrorRenderTargetSize = this.getMirrorRenderTargetSize();
    const reflection = new Reflector(
      new PlaneGeometry(
        Math.max(mm(40), width - mm(3)),
        Math.max(mm(120), height - mm(3))
      ),
      {
        // Match the 3D viewport aspect ratio instead of squeezing a wide
        // reflected camera into a fixed portrait texture. This preserves
        // furniture detail when the main camera is pulled away from the room.
        textureWidth: mirrorRenderTargetSize.width,
        textureHeight: mirrorRenderTargetSize.height,
        multisample: 4,
        clipBias: 0.004,
        // Preserve the reflected scene contrast. A grey multiplier made the
        // small, distant mirror texture look like frosted glass.
        color: 0xffffff
      }
    );
    reflection.position.set(x, y, z);
    // Keep the dynamic reflector active for distant, narrow on-screen mirrors.
    // The default frustum test can drop a nearly edge-on plane a little earlier
    // than the surrounding door geometry.
    reflection.frustumCulled = false;
    reflection.forceUpdate = true;
    reflection.camera.layers.enable(MIRROR_REFLECTION_LAYER);
    reflection.camera.layers.disable(MIRROR_BACKING_LAYER);
    // A stack of mirrored drawers must not recursively update every other
    // reflector. Nested passes can use their last texture; the main camera
    // updates each reflection once, keeping the work linear in mirror count.
    reflection.camera.userData.planarMirrorCamera = true;
    const updateReflection = reflection.onBeforeRender;
    reflection.onBeforeRender = (renderer, scene, camera, geometry, material, renderGroup) => {
      if (camera.userData.planarMirrorCamera === true) return;
      updateReflection.call(reflection, renderer, scene, camera, geometry, material, renderGroup);
    };
    // At the maximum camera distance the reflector is only a few depth-buffer
    // steps in front of the physical door slab. Pull the reflective pixels
    // forward in depth without moving the mirror geometry out of its frame.
    const reflectionMaterial = Array.isArray(reflection.material)
      ? reflection.material[0]
      : reflection.material;
    if (reflectionMaterial) {
      reflectionMaterial.polygonOffset = true;
      reflectionMaterial.polygonOffsetFactor = -8;
      reflectionMaterial.polygonOffsetUnits = -24;
    }
    reflection.renderOrder = 5;
    reflection.userData.pick = meta;
    reflection.userData.mirrorSurface = true;
    reflection.getRenderTarget().texture.anisotropy = Math.min(
      8,
      this.renderer.capabilities.getMaxAnisotropy()
    );
    parent.add(reflection);

    const glare = new Mesh(
      new PlaneGeometry(
        Math.max(mm(40), width - mm(9)),
        Math.max(mm(120), height - mm(9))
      ),
      this.getMirrorReflectionMaterial()
    );
    glare.position.set(x, y, z + mm(1.2));
    glare.renderOrder = 6;
    glare.userData.pick = meta;
    parent.add(glare);
  }

  private addHingeDetails(
    parent: Group,
    side: number,
    doorWidth: number,
    doorHeight: number,
    meta: PickMeta,
    baseY = 0,
    /** «cell» — накладная дверца ячейки стеллажа или комода: петля целиком за дверцей. */
    application: DoorHingeApplication | "inset" | "cell" = "full-overlay"
  ) {
    const metal = this.getMetalMaterial(this.hingeTone === "black" ? 0x2e2f30 : 0xc6cac8);
    const jointMetal = this.getMetalMaterial(this.hingeTone === "black" ? 0x1d1e1f : 0x777b79);
    const ratios = hingeHeightRatios(doorHeight / MM);
    ratios.forEach((ratio) => {
      const y = baseY + mm(2) + doorHeight * ratio;
      const cupX = side * (doorWidth / 2 - mm(22));
      const cup = new Mesh(
        new CylinderGeometry(mm(17.5), mm(17.5), mm(3.2), 24),
        metal
      );
      cup.rotation.x = Math.PI / 2;
      cup.position.set(cupX, y, -mm(11.5));
      cup.castShadow = true;
      cup.userData.pick = meta;
      parent.add(cup);

      // У вкладной дверцы петля крепится к стойке изнутри проёма: шарнир
      // за дверцей, а не на её кромке, иначе он вошёл бы в стойку.
      // У накладной дверцы ячейки шарнир за дверцей, в проёме: на кромке он
      // торчал бы наружу поверх боковины (замечание 15.10).
      const pivot = application === "inset"
        ? new Vector3(side * (doorWidth / 2 - mm(4)), y, -mm(16))
        : application === "cell"
          ? new Vector3(side * (doorWidth / 2 - mm(20)), y, -mm(17))
          : new Vector3(side * doorWidth / 2, y, 0);
      const cupConnection = new Vector3(cupX, y, -mm(10.5));
      if (application === "half-overlay") {
        // A two-segment cranked arm is the visible difference between a
        // half-overlay adjacent-door hinge and a straight overlay hinge.
        const elbow = new Vector3(
          side * (doorWidth / 2 - mm(8)),
          y,
          -mm(10.5)
        );
        this.addHingeArmBetween(
          parent,
          cupConnection,
          elbow,
          mm(15),
          mm(10),
          metal,
          meta
        );
        this.addHingeArmBetween(
          parent,
          elbow,
          pivot,
          mm(15),
          mm(10),
          metal,
          meta
        );
      } else {
        this.addHingeArmBetween(
          parent,
          cupConnection,
          pivot,
          mm(15),
          mm(10),
          metal,
          meta
        );
      }

      const knuckle = new Mesh(
        new CylinderGeometry(mm(5.5), mm(5.5), mm(15), 18),
        jointMetal
      );
      knuckle.position.copy(pivot);
      knuckle.castShadow = true;
      knuckle.userData.pick = meta;
      parent.add(knuckle);
    });
  }

  private addHingeMountingPlates(
    parent: Group,
    supportCenterX: number,
    hingeX: number,
    side: number,
    doorHeight: number,
    meta: PickMeta,
    baseY = 0,
    application: DoorHingeApplication = "full-overlay"
  ) {
    const metal = this.getMetalMaterial(this.hingeTone === "black" ? 0x2b2c2d : 0xbfc4c1);
    const jointMetal = this.getMetalMaterial(this.hingeTone === "black" ? 0x1d1e1f : 0x747876);
    const ratios = hingeHeightRatios(doorHeight / MM);
    const panelHalfThickness = mm(9);
    const plateThickness = mm(4);
    // side=+1 means the door lives to the left of its support, so its plate is
    // fixed to the support's left face. The paired half-overlay leaf receives
    // an independent plate on the opposite face of the same 18 mm upright.
    const faceX = supportCenterX - side * panelHalfThickness;
    const plateX = faceX - side * plateThickness / 2;
    ratios.forEach((ratio) => {
      const y = baseY + mm(2) + doorHeight * ratio;
      // Ответная планка прямая, продолговатая вдоль глубины — как у Blum (видео Сергея 23.10).
      const plate = new Mesh(
        new RoundedBoxGeometry(
          plateThickness,
          mm(20),
          mm(52),
          3,
          mm(1.5)
        ),
        metal
      );
      plate.position.set(plateX, y, -mm(20));
      plate.castShadow = true;
      plate.receiveShadow = true;
      plate.userData.pick = meta;
      parent.add(plate);

      // The fixed bracket visibly leaves the mounting plate and terminates at
      // the exact rotation axis of the door. It therefore stays attached to
      // the carcass while the cup and moving arm rotate with the facade.
      const bracketStart = new Vector3(
        faceX - side * mm(3),
        y,
        -mm(20)
      );
      const bracketEnd = new Vector3(hingeX, y, mm(13));
      const bracketElbow = application === "half-overlay"
        ? new Vector3(
            faceX - side * mm(7),
            y,
            mm(7)
          )
        : bracketStart;
      if (application === "half-overlay") {
        this.addHingeArmBetween(
          parent,
          bracketStart,
          bracketElbow,
          mm(15),
          mm(10),
          metal,
          meta
        );
      }
      this.addHingeArmBetween(
        parent,
        bracketElbow,
        bracketEnd,
        mm(15),
        mm(10),
        metal,
        meta
      );

      const fixedKnuckle = new Mesh(
        new CylinderGeometry(mm(6.2), mm(6.2), mm(17), 18),
        jointMetal
      );
      fixedKnuckle.position.copy(bracketEnd);
      fixedKnuckle.castShadow = true;
      fixedKnuckle.userData.pick = meta;
      parent.add(fixedKnuckle);

      [-1, 1].forEach((vertical) => {
        const screw = new Mesh(
          new CylinderGeometry(mm(2.1), mm(2.1), mm(1.3), 14),
          jointMetal
        );
        screw.rotation.z = Math.PI / 2;
        screw.position.set(
          faceX - side * mm(0.7),
          y,
          -mm(20) + vertical * mm(17)
        );
        screw.userData.pick = meta;
        parent.add(screw);
      });
    });
  }

  private addHandle(
    parent: Group,
    module: WardrobeModule,
    x: number,
    doorBottom: number,
    side: number,
    doorWidth: number,
    meta: PickMeta,
    doorHeight = mm(900)
  ) {
    if (module.handle === "push" || module.handle === "glassGrip") return;
    if (module.handle === "integrated") return;
    const doorWidthMm = doorWidth / MM;
    const doorHeightMm = doorHeight / MM;
    const orientation = handleOrientation(module);
    const length = mm(
      handleLengthMm(module, doorWidthMm, doorHeightMm)
    );
    const y =
      doorBottom +
      mm(handleCenterYmm(module, doorWidthMm, doorHeightMm));
    const handleX =
      x -
      side *
        (doorWidth / 2 -
          mm(
            handleCenterFromOpeningEdgeMm(
              module,
              doorWidthMm,
              doorHeightMm
            )
          ));
    const metal = this.getMetalMaterial(
      module.handle === "profile" ? 0x8f928d : 0x323334
    );

    if (module.handle === "profile") {
      this.addRoundedPanel(
        parent,
        handleX,
        y,
        mm(24),
        mm(11),
        length,
        mm(7),
        metal,
        meta,
        mm(4)
      );
      return;
    }

    if (module.handle === "recessed") {
      this.addRoundedPanel(
        parent,
        handleX,
        y,
        mm(22.5),
        orientation === "horizontal" ? length : mm(34),
        orientation === "horizontal" ? mm(34) : length,
        mm(5),
        this.getSoftShadowMaterial(0.34),
        meta,
        mm(8)
      );
      this.addDetailPanel(
        parent,
        handleX + (orientation === "vertical" ? side * mm(11) : 0),
        y,
        mm(26),
        orientation === "horizontal" ? length * 0.84 : mm(5),
        orientation === "horizontal" ? mm(5) : length * 0.84,
        mm(5),
        metal,
        meta
      );
      return;
    }

    const handle = new Mesh(
      new CylinderGeometry(mm(7), mm(7), length, 20),
      metal
    );
    if (orientation === "horizontal") handle.rotation.z = Math.PI / 2;
    handle.position.set(handleX, y, mm(22));
    handle.userData.pick = meta;
    parent.add(handle);

    const bracketMaterial = this.getMetalMaterial(0x696a66);
    [-1, 1].forEach((offset) => {
      const bracketX =
        handleX +
        (orientation === "horizontal" ? offset * length * 0.38 : 0);
      const bracketY =
        y + (orientation === "vertical" ? offset * length * 0.38 : 0);
      const post = new Mesh(
        new CylinderGeometry(mm(4.2), mm(4.2), mm(19), 14),
        bracketMaterial
      );
      post.rotation.x = Math.PI / 2;
      post.position.set(bracketX, bracketY, mm(14));
      post.userData.pick = meta;
      post.castShadow = true;
      parent.add(post);

      const rosette = new Mesh(
        new CylinderGeometry(mm(10), mm(10), mm(2.5), 18),
        bracketMaterial
      );
      rosette.rotation.x = Math.PI / 2;
      rosette.position.set(bracketX, bracketY, mm(11));
      rosette.userData.pick = meta;
      parent.add(rosette);
    });
  }

  private moduleIntegratedGroove(
    module: WardrobeModule,
    doorBottom: number,
    side: number,
    doorWidth: number,
    doorHeight: number
  ): IntegratedGroove {
    const style = integratedHandleStyle(module);
    if (style === "top") return { edge: "top" };
    // Открывающая кромка — напротив петель.
    const edge = (side > 0 ? -1 : 1) as -1 | 1;
    if (style === "edge") return { edge };
    const length = mm(handleLengthMm(module, doorWidth / MM, doorHeight / MM));
    const center = doorBottom + mm(handleCenterYmm(module, doorWidth / MM, doorHeight / MM));
    return { edge, from: center - length / 2, to: center + length / 2 };
  }

  private getModuleIntegratedHandleCut(
    module: WardrobeModule,
    x: number,
    doorBottom: number,
    side: number,
    doorWidth: number,
    doorHeight: number
  ): IntegratedHandleCut {
    const doorWidthMm = doorWidth / MM;
    const doorHeightMm = doorHeight / MM;
    const length = mm(handleLengthMm(module, doorWidthMm, doorHeightMm));
    const y =
      doorBottom +
      mm(handleCenterYmm(module, doorWidthMm, doorHeightMm));
    const handleX =
      x -
      side *
        (doorWidth / 2 -
          mm(
            handleCenterFromOpeningEdgeMm(
              module,
              doorWidthMm,
              doorHeightMm
            )
          ));
    return {
      x: handleX,
      y,
      width: mm(30),
      height: length,
      depth: mm(12),
      gripOffsetX: side * mm(5),
      gripOffsetY: 0,
      gripWidth: mm(8),
      gripHeight: length * 0.84
    };
  }

  private addShelfHoles(
    group: Group,
    module: WardrobeModule,
    width: number,
    height: number,
    depth: number,
    plinthHeight = 0
  ) {
    const material = new MeshBasicMaterial({ color: 0x4f3827 });
    material.userData.shared = false;
    const geometry = new CylinderGeometry(mm(2), mm(2), mm(2), 8);
    for (let y = plinthHeight + mm(96); y < height - mm(64); y += mm(64)) {
      [-1, 1].forEach((side) => {
        [-depth + mm(62), -mm(78)].forEach((z) => {
          const pin = new Mesh(geometry, material);
          pin.rotation.z = Math.PI / 2;
          pin.position.set(
            side * (width / 2 - mm(10)),
            y,
            z
          );
          pin.userData.pick = { kind: "module", moduleId: module.id } satisfies PickMeta;
          group.add(pin);
        });
      });
    }
  }

  private addClothes(
    parent: Group,
    module: WardrobeModule,
    item: InternalItem,
    width: number,
    availableDepth: number,
    z: number,
    meta: PickMeta
  ) {
    if (!this.hangingModelsReady || !this.interiorPropsVisible) return;
    const plans = this.hangingGarmentPlans(
      module,
      item,
      width,
      availableDepth
    );
    if (!plans.length) return;

    const hangerHalfDepth = Math.min(
      0.205,
      Math.max(0.08, (availableDepth - mm(36)) / 2)
    );
    const hangerMaterial = this.getMetalMaterial(0x8a8780);
    const hangerNeckY = -mm(72);
    const hangerShoulderY = -mm(130);
    const detailedTopY = hangerNeckY + mm(8);
    plans.forEach((plan) => {
      const tint = this.seededVariant(
        `${item.id}-tint-${plan.index}`,
        GARMENT_TINT_PALETTE
      );
      const detailedGarment = this.createDecorativeModel(
        plan.modelKey,
        new Vector3(
          mm(plan.dimensionsMm[0] + 2),
          mm(plan.dimensionsMm[1] + 2),
          mm(plan.dimensionsMm[2] + 2)
        ),
        new Vector3(
          0,
          detailedTopY,
          -mm(28 + ((plan.index % 5) - 2) * 7)
        ),
        meta,
        `${item.id}-${plan.index}`,
        {
          anchor: "top",
          preserveScale: true,
          yawRange: 0.032,
          tint,
          tintStrength: 0.52
        }
      );
      if (!detailedGarment) return;

      const garmentGroup = new Group();
      garmentGroup.position.set(mm(plan.slotX), 0, z);
      const normalizedIndex =
        plan.count > 1 ? plan.index / (plan.count - 1) - 0.5 : 0;
      garmentGroup.rotation.y =
        normalizedIndex * 0.28 + ((plan.index % 4) - 1.5) * 0.034;
      garmentGroup.userData.garmentType = plan.garmentType;
      garmentGroup.userData.garmentModelKey = plan.modelKey;
      garmentGroup.userData.requiredHeightMm = plan.requiredHeightMm;
      garmentGroup.userData.horizontalHalfWidthMm =
        plan.dimensionsMm[0] / 2 + 12;

      const hook = new Mesh(
        new TubeGeometry(
          new CatmullRomCurve3([
            new Vector3(0, hangerNeckY, 0),
            new Vector3(0, -mm(42), 0),
            new Vector3(0, -mm(17), mm(5)),
            new Vector3(0, -mm(3), mm(17)),
            new Vector3(0, mm(14), mm(18)),
            new Vector3(0, mm(23), mm(4)),
            new Vector3(0, mm(12), -mm(15)),
            new Vector3(0, -mm(10), -mm(18)),
            new Vector3(0, -mm(28), -mm(6))
          ]),
          18,
          mm(2.4),
          7,
          false
        ),
        hangerMaterial
      );
      hook.userData.pick = meta;
      garmentGroup.add(hook);

      this.addRodBetween(
        garmentGroup,
        new Vector3(0, hangerNeckY, 0),
        new Vector3(0, hangerShoulderY, -hangerHalfDepth),
        mm(2.4),
        hangerMaterial,
        meta
      );
      this.addRodBetween(
        garmentGroup,
        new Vector3(0, hangerNeckY, 0),
        new Vector3(0, hangerShoulderY, hangerHalfDepth),
        mm(2.4),
        hangerMaterial,
        meta
      );
      garmentGroup.add(detailedGarment.object);
      parent.add(garmentGroup);
    });
  }

  private addRodBetween(
    parent: Group,
    start: Vector3,
    end: Vector3,
    radius: number,
    material: MeshStandardMaterial,
    meta: PickMeta
  ) {
    const direction = end.clone().sub(start);
    const length = direction.length();
    const rod = new Mesh(
      new CylinderGeometry(radius, radius, length, 8),
      material
    );
    rod.position.copy(start).add(end).multiplyScalar(0.5);
    rod.quaternion.setFromUnitVectors(
      new Vector3(0, 1, 0),
      direction.normalize()
    );
    rod.userData.pick = meta;
    parent.add(rod);
  }

  private addHingeArmBetween(
    parent: Group,
    start: Vector3,
    end: Vector3,
    width: number,
    depth: number,
    material: MeshStandardMaterial,
    meta: PickMeta
  ) {
    const direction = end.clone().sub(start);
    const length = direction.length();
    if (length <= 0.0001) return;
    const arm = new Mesh(
      new RoundedBoxGeometry(
        width,
        length,
        depth,
        3,
        Math.min(width, depth) * 0.22
      ),
      material
    );
    arm.position.copy(start).add(end).multiplyScalar(0.5);
    arm.quaternion.setFromUnitVectors(
      new Vector3(0, 1, 0),
      direction.normalize()
    );
    arm.castShadow = true;
    arm.userData.pick = meta;
    parent.add(arm);
  }

  private addIkeaDimensions(
    bounds: {
      minX: number;
      maxX: number;
      minZ: number;
      maxZ: number;
      width: number;
      depth: number;
    },
    height: number,
    placements: ModulePlacement[]
  ) {
    const addSegments = (points: number[]) => this.addOverlaySegments(points, "outer", 20);

    const tick = 0.09;
    // Ряд изделий на одной стене (приёмка 19.10): общая ширина — в той же
    // плоскости, что и ширины модулей, и выше них; стыки в 1 мм, которые
    // оставляет раскладка между модулями, в размер не входят (было «1801»).
    const floorPlacements = placements.filter((placement) => placement.wing === 0 && !isStackedModule(placement.module));
    const rowWall = floorPlacements[0]?.wall;
    const singleRow = Boolean(rowWall) && floorPlacements.every((placement) =>
      placement.wall === rowWall && placement.module.type !== "corner45");
    const row = singleRow ? this.wallPlacementGroups(placements).get(rowWall!) ?? [] : [];
    if (row.length > 1) {
      const first = row[0]!;
      const last = row[row.length - 1]!;
      let joints = 0;
      for (let index = 0; index < row.length - 1; index += 1) {
        const gap = row[index + 1]!.wallOffset - (row[index]!.wallOffset + installationSpanMm(row[index]!.module));
        if (gap > 0 && gap < 5) joints += gap;
      }
      const widthMm = last.wallOffset + installationSpanMm(last.module) - first.wallOffset - joints;
      const leftX = -mm(first.module.width) / 2 - mm(leftFillerMm(first.module));
      const rightX = mm(last.module.width) / 2 + mm(rightFillerMm(last.module));
      const zLocal = mm(62);
      const y = height + mm(130);
      const left = this.modulePoint(first, leftX, zLocal);
      const right = this.modulePoint(last, rightX, zLocal);
      const leftA = this.modulePoint(first, leftX, zLocal - mm(34));
      const leftB = this.modulePoint(first, leftX, zLocal + mm(34));
      const rightA = this.modulePoint(last, rightX, zLocal - mm(34));
      const rightB = this.modulePoint(last, rightX, zLocal + mm(34));
      addSegments([
        left.x, y, left.z, right.x, y, right.z,
        leftA.x, y, leftA.z, leftB.x, y, leftB.z,
        rightA.x, y, rightA.z, rightB.x, y, rightB.z
      ]);
      this.overlayRoot.add(this.makeDimensionLabel(this.formatMm(mm(widthMm)), (left.x + right.x) / 2, y + 0.012, (left.z + right.z) / 2));
    } else if (!singleRow) {
      const topY = height + 0.105;
      const topZ = bounds.minZ - 0.045;
      addSegments([
        bounds.minX, topY, topZ,
        bounds.maxX, topY, topZ,
        bounds.minX, topY - tick * 0.45, topZ,
        bounds.minX, topY + tick * 0.45, topZ,
        bounds.maxX, topY - tick * 0.45, topZ,
        bounds.maxX, topY + tick * 0.45, topZ
      ]);
      this.overlayRoot.add(
        this.makeDimensionLabel(
          this.formatMm(bounds.width - mm(compositionJointsMm({ placements, bounds }))),
          (bounds.minX + bounds.maxX) / 2,
          topY + 0.012,
          topZ
        )
      );
    }

    // Высоты — снаружи ряда, а не посреди соседнего модуля (приёмка 19.10):
    // линия шла справа от самой высокой колонны и попадала внутрь соседнего
    // пенала, поверх полок. Теперь — за крайним модулем ряда, со стороны, где
    // до стены больше места, и дальше внутренних размеров (у тех подписи
    // выходят за боковину на ~120 мм).
    const tops = placements
      .filter((placement) => placement.wing === 0)
      .map((placement) => ({ placement, top: placement.y + mm(placement.module.height) }));
    const maxTop = Math.max(...tops.map((entry) => entry.top), 0);
    const anchor = tops.find((entry) => entry.top > maxTop - mm(1))?.placement;
    let heightPlacement = anchor;
    let side: 1 | -1 = 1;
    let lineLocalX = 0;
    if (anchor) {
      const cos = Math.cos(anchor.yaw);
      const sin = Math.sin(anchor.yaw);
      const localX = (placement: ModulePlacement) => cos * (placement.x - anchor.x) - sin * (placement.z - anchor.z);
      const sameWall = placements.filter((placement) => placement.wing === 0 && placement.wall === anchor.wall);
      const edges = sameWall.map((placement) => ({
        placement,
        left: localX(placement) - mm(placement.module.width) / 2 - mm(leftFillerMm(placement.module)),
        right: localX(placement) + mm(placement.module.width) / 2 + mm(rightFillerMm(placement.module))
      }));
      const runLeft = Math.min(...edges.map((edge) => edge.left));
      const runRight = Math.max(...edges.map((edge) => edge.right));
      const wallRow = this.wallPlacementGroups(placements).get(anchor.wall) ?? [];
      if (anchor.wall === "back" && wallRow.length) {
        const leftGap = wallRow[0]!.wallOffset;
        const lastInRow = wallRow[wallRow.length - 1]!;
        const rightGap = BACK_WALL_LENGTH_MM - (lastInRow.wallOffset + installationSpanMm(lastInRow.module));
        if (rightGap < 400 && leftGap > rightGap) side = -1;
      }
      // Цепочка «корпус + антресоль» — у крайней из самых высоких колонн с этой стороны.
      heightPlacement = edges
        .filter((edge) => edge.placement.y + mm(edge.placement.module.height) > maxTop - mm(1))
        .sort((first, second) => side > 0 ? second.right - first.right : first.left - second.left)[0]?.placement ?? anchor;
      const offset = mm(150);
      const runEdge = side > 0 ? runRight : runLeft;
      lineLocalX = runEdge + side * offset - localX(heightPlacement);
    }
    if (heightPlacement) {
      const lineLocalZ = mm(70);
      const bottom = this.modulePoint(heightPlacement, lineLocalX, lineLocalZ);
      const tickA = this.modulePoint(
        heightPlacement,
        lineLocalX - tick * 0.42,
        lineLocalZ
      );
      const tickB = this.modulePoint(
        heightPlacement,
        lineLocalX + tick * 0.42,
        lineLocalZ
      );
      const label = this.modulePoint(
        heightPlacement,
        lineLocalX + side * tick * 0.72,
        lineLocalZ
      );
      addSegments([
        bottom.x, 0.02, bottom.z,
        bottom.x, height, bottom.z,
        tickA.x, 0.02, tickA.z,
        tickB.x, 0.02, tickB.z,
        tickA.x, height, tickA.z,
        tickB.x, height, tickB.z
      ]);
      // Есть антресоль — цепочка «корпус + антресоль», а общая высота отдельной
      // линией снаружи: раньше была видна только общая (приёмка 05.10, wb-05, sg-03).
      const splitY = heightPlacement.module.stackedOnId ? heightPlacement.y : 0;
      if (splitY > mm(100) && height - splitY > mm(50)) {
        const tickC = this.modulePoint(heightPlacement, lineLocalX - tick * 0.42, lineLocalZ);
        const tickD = this.modulePoint(heightPlacement, lineLocalX + tick * 0.42, lineLocalZ);
        addSegments([tickC.x, splitY, tickC.z, tickD.x, splitY, tickD.z]);
        this.overlayRoot.add(
          this.makeDimensionLabel(`${heightPlacement.module.type === "shelving" ? "стеллаж" : "корпус"} ${this.formatMm(splitY)}`, label.x, splitY * 0.5, label.z),
          this.makeDimensionLabel(`антресоль ${this.formatMm(height - splitY)}`, label.x, (splitY + height) / 2, label.z)
        );
        const outerX = lineLocalX + side * mm(230);
        const outer = this.modulePoint(heightPlacement, outerX, lineLocalZ);
        const outerA = this.modulePoint(heightPlacement, outerX - tick * 0.42, lineLocalZ);
        const outerB = this.modulePoint(heightPlacement, outerX + tick * 0.42, lineLocalZ);
        const outerLabel = this.modulePoint(heightPlacement, outerX + side * tick * 0.72, lineLocalZ);
        addSegments([
          outer.x, 0.02, outer.z,
          outer.x, height, outer.z,
          outerA.x, 0.02, outerA.z,
          outerB.x, 0.02, outerB.z,
          outerA.x, height, outerA.z,
          outerB.x, height, outerB.z
        ]);
        this.overlayRoot.add(
          this.makeDimensionLabel(`общая ${this.formatMm(height)}`, outerLabel.x, height * 0.5, outerLabel.z)
        );
      } else {
        this.overlayRoot.add(
          this.makeDimensionLabel(
            this.formatMm(height),
            label.x,
            height * 0.5,
            label.z
          )
        );
      }
      // Сколько осталось до потолка — замечание Сергея № 11 (с 22.10 — до
      // самого потолка: зазор задаётся на шаге «Комната», падуги нет).
      const ceilingTop = this.project
        ? mm(ceilingHeightMm(this.project))
        : height;
      // Линия «до потолка» нужна, когда шкаф дотягивают до потолка: у колонны
      // есть антресоль или включён режим «до потолка». У обычного корпуса она
      // выглядела как лишняя линия от пола до потолка (приёмка 04.10, wb-10).
      const column = heightPlacement.module;
      const reachesUp = Boolean(column.stackedOnId) || Boolean(column.cabinetToCeiling) ||
        Boolean(this.project?.modules.some((other) => other.stackedOnId === column.id));
      if (reachesUp && ceilingTop - height >= mm(1)) {
        addSegments([
          bottom.x, height, bottom.z,
          bottom.x, ceilingTop, bottom.z,
          tickA.x, ceilingTop, tickA.z,
          tickB.x, ceilingTop, tickB.z
        ]);
        this.overlayRoot.add(
          this.makeDimensionLabel(
            `до потолка ${this.formatMm(ceilingTop - height)}`,
            label.x,
            (height + ceilingTop) / 2,
            label.z
          )
        );
      }
    }

  }

  private addModuleDimensions(placements: ModulePlacement[]) {
    const material = "outer" as const;

    // Ширина корпуса, над которым антресоль той же ширины, — только над
    // антресолью: второй ряд тех же чисел шёл поперёк её фасада (приёмка 19.10).
    const sameWidthAbove = (module: WardrobeModule) => placements.some((other) =>
      other.module.stackedOnId === module.id && other.module.width === module.width &&
      leftFillerMm(other.module) === leftFillerMm(module) && rightFillerMm(other.module) === rightFillerMm(module));
    // У углового — только длина корпуса вдоль его стены; на стене соседа он
    // занимает лишь свою глубину с дверью, её видно по соседу.
    const cornerRunWing = (module: WardrobeModule) => cornerRun(module) === "back" ? 0 : 1;
    placements
      .filter((placement) => placement.module.type === "corner45"
        ? placement.wing === cornerRunWing(placement.module) : placement.wing === 0)
      .filter((placement) => !sameWidthAbove(placement.module))
      .forEach((placement) => {
        const module = placement.module;
        const leftEdge =
          -mm(module.width) / 2 - mm(leftFillerMm(module));
        const rightEdge =
          mm(module.width) / 2 + mm(rightFillerMm(module));
        const width = rightEdge - leftEdge;
        const height = mm(module.height + topFillerMm(module));
        const y = placement.y + height + 0.052;
        const zLocal = mm(62);
        const left = this.modulePoint(placement, leftEdge, zLocal);
        const right = this.modulePoint(placement, rightEdge, zLocal);
        const tickA = this.modulePoint(placement, leftEdge, zLocal - mm(34));
        const tickB = this.modulePoint(placement, leftEdge, zLocal + mm(34));
        const tickC = this.modulePoint(placement, rightEdge, zLocal - mm(34));
        const tickD = this.modulePoint(placement, rightEdge, zLocal + mm(34));

        this.addOverlaySegments(
          [
            left.x, y, left.z,
            right.x, y, right.z,
            tickA.x, y, tickA.z,
            tickB.x, y, tickB.z,
            tickC.x, y, tickC.z,
            tickD.x, y, tickD.z
          ],
          material,
          22
        );

        const label = { x: (left.x + right.x) * 0.5, z: (left.z + right.z) * 0.5 };
        this.overlayRoot.add(
          this.makeDimensionLabel(
            this.formatMm(width),
            label.x,
            y + 0.01,
            label.z,
            DIMENSION_STYLE.outer.label
          )
        );
      });
  }

  private addModuleGapDimensions(placements: ModulePlacement[]) {
    const material = "outer" as const;

    this.wallPlacementGroups(placements).forEach((group, wall) => {
      if (!group.length) return;
      const lineDistanceFromWall =
        Math.max(...group.map((placement) => mm(placement.module.depth))) +
        ROOM_LAYOUT.furnitureGap +
        mm(132);

      const addGap = (
        startPlacement: ModulePlacement,
        startLocalX: number,
        endPlacement: ModulePlacement,
        endLocalX: number,
        gapMm: number,
        y: number
      ) => {
        // Зазор в пару миллиметров между модулями — не размер, а шум.
        if (gapMm < 5) return;
        const tick = mm(24);
        const startLocalZ =
          lineDistanceFromWall -
          ROOM_LAYOUT.furnitureGap -
          mm(startPlacement.module.depth);
        const endLocalZ =
          lineDistanceFromWall -
          ROOM_LAYOUT.furnitureGap -
          mm(endPlacement.module.depth);
        const start = this.modulePoint(
          startPlacement,
          startLocalX,
          startLocalZ
        );
        const end = this.modulePoint(endPlacement, endLocalX, endLocalZ);
        const startTickA = this.modulePoint(
          startPlacement,
          startLocalX,
          startLocalZ - tick
        );
        const startTickB = this.modulePoint(
          startPlacement,
          startLocalX,
          startLocalZ + tick
        );
        const endTickA = this.modulePoint(
          endPlacement,
          endLocalX,
          endLocalZ - tick
        );
        const endTickB = this.modulePoint(
          endPlacement,
          endLocalX,
          endLocalZ + tick
        );

        this.addOverlaySegments(
          [
            start.x, y, start.z,
            end.x, y, end.z,
            startTickA.x, y, startTickA.z,
            startTickB.x, y, startTickB.z,
            endTickA.x, y, endTickA.z,
            endTickB.x, y, endTickB.z
          ],
          material,
          23
        );

        this.overlayRoot.add(
          this.makeDimensionLabel(
            this.formatMm(mm(gapMm)),
            (start.x + end.x) * 0.5,
            y + 0.008,
            (start.z + end.z) * 0.5,
            DIMENSION_STYLE.outer.label
          )
        );
      };

      for (let index = 0; index < group.length - 1; index += 1) {
        const current = group[index]!;
        const next = group[index + 1]!;
        const gapMm =
          next.wallOffset -
          (current.wallOffset + installationSpanMm(current.module));
        addGap(
          current,
          mm(current.module.width) / 2 + mm(rightFillerMm(current.module)),
          next,
          -mm(next.module.width) / 2 - mm(leftFillerMm(next.module)),
          gapMm,
          Math.min(mm(current.module.height), mm(next.module.height)) * 0.5
        );
      }

      const first = group[0]!;
      const last = group[group.length - 1]!;
      if (wall === "back") {
        const leftGap = first.wallOffset;
        addGap(
          first,
          -mm(first.module.width) / 2 - mm(leftFillerMm(first.module)) - mm(leftGap),
          first,
          -mm(first.module.width) / 2 - mm(leftFillerMm(first.module)),
          leftGap,
          mm(first.module.height) * WALL_GAP_LINE_SHARE
        );
        const rightGap =
          BACK_WALL_LENGTH_MM -
          (last.wallOffset + installationSpanMm(last.module));
        addGap(
          last,
          mm(last.module.width) / 2 + mm(rightFillerMm(last.module)),
          last,
          mm(last.module.width) / 2 + mm(rightFillerMm(last.module)) + mm(rightGap),
          rightGap,
          mm(last.module.height) * WALL_GAP_LINE_SHARE
        );
      } else if (wall === "left") {
        const endWallGap = -(
          last.wallOffset + installationSpanMm(last.module)
        );
        addGap(
          last,
          mm(last.module.width) / 2 + mm(rightFillerMm(last.module)),
          last,
          mm(last.module.width) / 2 + mm(rightFillerMm(last.module)) + mm(endWallGap),
          endWallGap,
          mm(last.module.height) * WALL_GAP_LINE_SHARE
        );
      } else {
        const endWallGap = first.wallOffset - BACK_WALL_LENGTH_MM;
        addGap(
          first,
          -mm(first.module.width) / 2 - mm(leftFillerMm(first.module)) - mm(endWallGap),
          first,
          -mm(first.module.width) / 2 - mm(leftFillerMm(first.module)),
          endWallGap,
          mm(first.module.height) * WALL_GAP_LINE_SHARE
        );
      }
    });
  }

  private addModuleDepthDimensions(placements: ModulePlacement[]) {
    const material = "outer" as const;
    const tick = mm(22);

    // Глубина (приёмка 19.10): подпись — глубина корпуса, как её вводят
    // (было «620» при глубине 580 — с зазором 40 мм до стены). Одинаковая у
    // всего ряда — одна, у крайнего модуля, а не под каждым.
    const rows = this.wallPlacementGroups(placements);
    const onlyInRow = new Set<ModulePlacement>();
    const shown = (placement: ModulePlacement) => {
      const row = rows.get(placement.wall);
      if (!row || !row.includes(placement)) return true;
      const sameDepth = row.every((other) => other.module.depth === row[0]!.module.depth);
      if (sameDepth && row.length > 1 && placement === row[row.length - 1]) onlyInRow.add(placement);
      return !sameDepth || placement === row[row.length - 1];
    };
    placements
      .filter(
        (placement) =>
          placement.wing === 0 && !isStackedModule(placement.module)
      )
      .filter(shown)
      .forEach((placement) => {
        // Глубина — по полу: выше неё идут внутренние размеры шкафа.
        const y = mm(20);
        const width = mm(placement.module.width);
        const depthFromWall = mm(placement.module.depth);
        // Одна на ряд — за боковиной крайнего модуля, а не на его внутренних
        // размерах: спереди подпись ложилась на нижний внутренний размер.
        const xLocal = onlyInRow.has(placement)
          ? width / 2 + mm(rightFillerMm(placement.module)) + mm(60)
          : width / 2 - mm(46);
        const wall = this.modulePoint(placement, xLocal, -depthFromWall);
        const front = this.modulePoint(placement, xLocal, 0);
        const wallTickA = this.modulePoint(
          placement,
          xLocal - tick,
          -depthFromWall
        );
        const wallTickB = this.modulePoint(
          placement,
          xLocal + tick,
          -depthFromWall
        );
        const frontTickA = this.modulePoint(placement, xLocal - tick, 0);
        const frontTickB = this.modulePoint(placement, xLocal + tick, 0);

        this.addOverlaySegments(
          [
            wall.x, y, wall.z,
            front.x, y, front.z,
            wallTickA.x, y, wallTickA.z,
            wallTickB.x, y, wallTickB.z,
            frontTickA.x, y, frontTickA.z,
            frontTickB.x, y, frontTickB.z
          ],
          material,
          23
        );
        this.overlayRoot.add(
          this.makeDimensionLabel(
            this.formatMm(depthFromWall),
            (wall.x + front.x) * 0.5,
            y + mm(22),
            (wall.z + front.z) * 0.5,
            DIMENSION_STYLE.outer.label
          )
        );
      });
  }

  private moduleShowsInternalDimensions(
    module: WardrobeModule
  ) {
    if (!module.items.length) return false;
    if (module.door === "open") return true;
    return this.openDoors.has(this.doorKey(module.id, 0)) ||
      this.openDoors.has(this.doorKey(module.id, 1));
  }

  private addInternalItemDimensions(placements: ModulePlacement[]) {
    const material = "inner" as const;

    placements
      .filter(
        (placement) =>
          placement.wing === 0 &&
          placement.includeItems &&
          this.moduleShowsInternalDimensions(placement.module)
      )
      .forEach((placement) => {
        const module = placement.module;
        const width = mm(module.width);
        const depth = mm(module.depth);
        const height = mm(module.height);
        const plinthHeight = mm(cabinetBaseHeightMm(module));
        const baseY = placement.y;
        const xLocal = width / 2 - mm(48);
        const zLocal = -depth * 0.42;
        const tick = mm(26);
        this.addDividerSideDimensions(placement, material);
        // Линия идёт в правом отсеке — в ней только детали этого отсека.
        const lineXmm = module.width / 2 - 48;
        const levels = [
          plinthHeight,
          ...module.items
            .filter((item) => item.type !== "divider" && usableItemPieces(module, item)
              .some((piece) => lineXmm >= piece.left && lineXmm <= piece.right))
            .map((item) => mm(item.y)),
          height - mm(CARCASS_MM)
        ]
          .filter((value) => value > plinthHeight - mm(1) && value < height)
          .sort((a, b) => a - b)
          .filter((value, index, array) => index === 0 || value - array[index - 1]! > mm(42));

        for (let index = 0; index < levels.length - 1; index += 1) {
          const y1 = levels[index]!;
          const y2 = levels[index + 1]!;
          const distance = y2 - y1;
          if (distance < mm(90)) continue;

          const bottom = this.modulePoint(placement, xLocal, zLocal);
          const top = bottom;
          const tickLeftBottom = this.modulePoint(placement, xLocal - tick, zLocal);
          const tickRightBottom = this.modulePoint(placement, xLocal + tick, zLocal);
          const tickLeftTop = this.modulePoint(placement, xLocal - tick, zLocal);
          const tickRightTop = this.modulePoint(placement, xLocal + tick, zLocal);

          this.addOverlaySegments(
            [
              bottom.x, baseY + y1, bottom.z,
              top.x, baseY + y2, top.z,
              tickLeftBottom.x, baseY + y1, tickLeftBottom.z,
              tickRightBottom.x, baseY + y1, tickRightBottom.z,
              tickLeftTop.x, baseY + y2, tickLeftTop.z,
              tickRightTop.x, baseY + y2, tickRightTop.z
            ],
            material,
            24
          );

          const labelPoint = this.modulePoint(
            placement,
            xLocal + tick * (index % 2 === 0 ? 1.8 : 4.35),
            zLocal
          );
          this.overlayRoot.add(
            this.makeDimensionLabel(
              this.formatMm(distance),
              labelPoint.x,
              baseY + y1 + distance * 0.5,
              labelPoint.z,
              DIMENSION_STYLE.inner.label
            )
          );
        }
      });
  }

  private addDividerSideDimensions(
    placement: ModulePlacement,
    material: "outer" | "inner"
  ) {
    const module = placement.module;
    const width = mm(module.width);
    const depth = mm(module.depth);
    const innerLeft = -width / 2 + mm(CARCASS_MM);
    const innerRight = width / 2 - mm(CARCASS_MM);
    const zLocal = -depth * 0.32;
    const tick = mm(18);

    module.items
      .filter((item) => item.type === "divider")
      .forEach((divider) => {
        const dividerX = clamp(mm(divider.x ?? 0), innerLeft, innerRight);
        const dividerHeight = mm(
          divider.height ?? Math.min(1400, module.height - 180)
        );
        const y = placement.y + Math.min(
          mm(module.height) + 0.035,
          mm(divider.y) + dividerHeight / 2 + 0.045
        );
        const spans: Array<[number, number]> = [
          [innerLeft, dividerX - mm(HALF_CARCASS_MM)],
          [dividerX + mm(HALF_CARCASS_MM), innerRight]
        ];

        spans.forEach(([from, to]) => {
          const distance = to - from;
          if (distance < mm(90)) return;
          const start = this.modulePoint(placement, from, zLocal);
          const end = this.modulePoint(placement, to, zLocal);
          const startTickA = this.modulePoint(placement, from, zLocal - tick);
          const startTickB = this.modulePoint(placement, from, zLocal + tick);
          const endTickA = this.modulePoint(placement, to, zLocal - tick);
          const endTickB = this.modulePoint(placement, to, zLocal + tick);
          this.addOverlaySegments(
            [
              start.x, y, start.z,
              end.x, y, end.z,
              startTickA.x, y, startTickA.z,
              startTickB.x, y, startTickB.z,
              endTickA.x, y, endTickA.z,
              endTickB.x, y, endTickB.z
            ],
            material,
            25
          );
          this.overlayRoot.add(
            this.makeDimensionLabel(
              this.formatMm(distance),
              (start.x + end.x) * 0.5,
              y + 0.006,
              (start.z + end.z) * 0.5,
              DIMENSION_STYLE.inner.label
            )
          );
        });
      });
  }

  private modulePoint(placement: ModulePlacement, localX: number, localZ: number) {
    const cos = Math.cos(placement.yaw);
    const sin = Math.sin(placement.yaw);
    return {
      x: placement.x + cos * localX + sin * localZ,
      z: placement.z - sin * localX + cos * localZ
    };
  }

  private addOverlaySegments(
    points: number[],
    role: "outer" | "inner",
    renderOrder: number
  ) {
    const line = this.createDimensionStrokes(points, role);
    line.renderOrder = renderOrder;
    this.overlayRoot.add(line);
  }

  /** Отрезки размерной линии толщиной 2 px: тонкая WebGL-линия теряется на дереве. */
  private createDimensionStrokes(points: number[], role: "outer" | "inner") {
    const geometry = new LineSegmentsGeometry().setPositions(points);
    const material = new LineMaterial({
      color: DIMENSION_STYLE[role].line,
      linewidth: DIMENSION_STYLE.lineWidth,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    const size = this.renderer.getSize(new Vector2());
    material.resolution.set(size.x, size.y);
    const line = new LineSegments2(geometry, material);
    line.raycast = () => {};
    line.userData.dimensionStrokes = true;
    return line;
  }

  /** Толщина линий в пикселях зависит от размера окна. */
  private updateDimensionLineResolution(width: number, height: number) {
    [this.overlayRoot, this.dimensionRoot].forEach((root) => root.traverse((object) => {
      if (object instanceof LineSegments2) object.material.resolution.set(width, height);
    }));
  }

  /** Размеры — в миллиметрах, как в смете и на схеме. */
  private formatMm(value: number) {
    return `${Math.round(value / MM)}`;
  }

  private makeDimensionLabel(
    text: string,
    x: number,
    y: number,
    z: number,
    background: string = DIMENSION_STYLE.outer.label
  ) {
    const canvas = document.createElement("canvas");
    const textureScale = 4;
    const fontSize = 13;
    const labelHeight = 22;
    const horizontalPadding = 7;
    const measuringContext = canvas.getContext("2d");
    if (!measuringContext) return new Sprite();
    measuringContext.font = `700 ${fontSize * textureScale}px "Segoe UI", Arial, sans-serif`;
    const textWidth = measuringContext.measureText(text).width / textureScale;
    const labelWidth = Math.max(30, Math.ceil(textWidth + horizontalPadding * 2));

    canvas.width = labelWidth * textureScale;
    canvas.height = labelHeight * textureScale;
    const context = canvas.getContext("2d");
    if (!context) return new Sprite();

    context.fillStyle = background;
    context.beginPath();
    context.roundRect(
      textureScale,
      textureScale,
      canvas.width - textureScale * 2,
      canvas.height - textureScale * 2,
      6 * textureScale
    );
    context.fill();
    context.fillStyle = "#ffffff";
    context.font = `700 ${fontSize * textureScale}px "Segoe UI", Arial, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, canvas.width / 2, canvas.height / 2 + textureScale * 0.25);

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.generateMipmaps = false;
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    const sprite = new Sprite(
      new SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false
      })
    );
    sprite.position.set(x, y, z);
    sprite.userData.dimensionLabel = true;
    sprite.userData.pixelWidth = labelWidth;
    sprite.userData.pixelHeight = labelHeight;
    sprite.scale.set(labelWidth / 520, labelHeight / 520, 1);
    sprite.renderOrder = 100;
    return sprite;
  }

  private addDimensions(
    bounds: {
      minX: number;
      maxX: number;
      minZ: number;
      maxZ: number;
      width: number;
      depth: number;
    },
    height: number,
  ) {
    const material = new LineBasicMaterial({
      color: 0x6e6255,
      transparent: true,
      opacity: 0.72
    });
    const bottomZ = bounds.maxZ + 0.23;
    const horizontal = new BufferGeometry();
    horizontal.setAttribute(
      "position",
      new Float32BufferAttribute(
        [
          bounds.minX,
          0.04,
          bottomZ,
          bounds.maxX,
          0.04,
          bottomZ,
          bounds.minX,
          0.01,
          bottomZ,
          bounds.minX,
          0.08,
          bottomZ,
          bounds.maxX,
          0.01,
          bottomZ,
          bounds.maxX,
          0.08,
          bottomZ
        ],
        3
      )
    );
    this.overlayRoot.add(new LineSegments(horizontal, material));
    this.overlayRoot.add(
      this.makeLabel(`${Math.round(bounds.width / MM)} мм`, 0, 0.1, bottomZ)
    );

    const x = bounds.maxX + 0.18;
    const front = bounds.maxZ;
    const vertical = new BufferGeometry();
    vertical.setAttribute(
      "position",
      new Float32BufferAttribute(
        [
          x,
          0,
          front,
          x,
          height,
          front,
          x - 0.035,
          0,
          front,
          x + 0.035,
          0,
          front,
          x - 0.035,
          height,
          front,
          x + 0.035,
          height,
          front
        ],
        3
      )
    );
    this.overlayRoot.add(new LineSegments(vertical, material.clone()));
    this.overlayRoot.add(
      this.makeLabel(`${Math.round(height / MM)} мм`, x + 0.03, height / 2, front)
    );

    if (bounds.depth > 0.7) {
      const depthX = bounds.minX - 0.18;
      const minZ = bounds.minZ;
      const maxZ = bounds.maxZ;
      const depthGeometry = new BufferGeometry();
      depthGeometry.setAttribute(
        "position",
        new Float32BufferAttribute(
          [
            depthX,
            0.04,
            minZ,
            depthX,
            0.04,
            maxZ,
            depthX - 0.035,
            0.04,
            minZ,
            depthX + 0.035,
            0.04,
            minZ,
            depthX - 0.035,
            0.04,
            maxZ,
            depthX + 0.035,
            0.04,
            maxZ
          ],
          3
        )
      );
      this.overlayRoot.add(new LineSegments(depthGeometry, material.clone()));
      this.overlayRoot.add(
        this.makeLabel(
          `${Math.round(bounds.depth / MM)} мм`,
          depthX,
          0.11,
          (minZ + maxZ) / 2
        )
      );
    }
  }

  private makeLabel(text: string, x: number, y: number, z: number) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 128;
    const context = canvas.getContext("2d");
    if (!context) return new Sprite();
    context.fillStyle = "rgba(255,253,249,.96)";
    context.beginPath();
    context.roundRect(8, 8, 496, 112, 28);
    context.fill();
    context.strokeStyle = "rgba(80,65,48,.18)";
    context.lineWidth = 4;
    context.stroke();
    context.fillStyle = "#2d2925";
    context.font = "600 42px Arial";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, 256, 66);
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.minFilter = LinearFilter;
    const sprite = new Sprite(
      new SpriteMaterial({ map: texture, transparent: true, depthTest: false })
    );
    sprite.position.set(x, y, z);
    sprite.scale.set(0.42, 0.105, 1);
    sprite.renderOrder = 5;
    return sprite;
  }

  private updateSelection(
    moduleId: string | null,
    itemId: string | null,
    shelvingCellIdValue: string | null = null
  ) {
    if (this.selectionHelper) {
      this.overlayRoot.remove(this.selectionHelper);
      this.selectionHelper.geometry.dispose();
      (this.selectionHelper.material as LineBasicMaterial).dispose();
      this.selectionHelper = null;
    this.selectionTarget = null;
    }

    const selectedModule = itemId
      ? this.project?.modules.find((module) => module.id === moduleId)
      : undefined;
    if (
      itemId &&
      selectedModule &&
      !this.moduleShowsInternalDimensions(selectedModule)
    ) {
      return;
    }

    // У комода ячейка — колонка ящиков, отдельной рамки не нужно: выделен весь комод.
    if (shelvingCellIdValue && this.project?.modules.find((module) => module.id === moduleId)?.shelvingKind === "chest") {
      shelvingCellIdValue = null;
    }
    const itemObject = itemId ? this.itemObjects.get(itemId) : undefined;
    const shelvingCellObject = shelvingCellIdValue
      ? this.shelvingCellObjects.get(shelvingCellIdValue)
      : undefined;
    const target = shelvingCellIdValue
      ? shelvingCellObject
      : itemId
      ? ((itemObject?.userData.selectionAnchor as Object3D | undefined) ??
        itemObject)
      : moduleId
        ? this.moduleObjects.get(moduleId)
        : undefined;

    if (!target) return;
    const wholeModule = !itemId && !shelvingCellIdValue;
    const outline = wholeModule ? this.selectionOutline(target) : null;
    const box = outline ? null : this.selectionBounds(target, new Box3(), wholeModule);
    if (box?.isEmpty()) return;
    this.selectionTarget = target;
    this.selectionHelper = outline ?? new Box3Helper(
      box!,
      wholeModule ? 0xb88757 : 0x8e6845
    );
    const material = this.selectionHelper.material as LineBasicMaterial;
    material.transparent = true;
    material.opacity = 0.9;
    // Рамка изделия не просвечивает сквозь мебель (11.10); рамка полки или
    // ячейки внутри корпуса по-прежнему видна сквозь двери.
    material.depthTest = wholeModule;
    this.selectionHelper.renderOrder = 6;
    this.selectionHelper.userData.wholeModule = wholeModule;
    this.overlayRoot.add(this.selectionHelper);
  }

  /**
   * Границы выделения — по закрытому изделию (11.10): открытые двери, ящики и
   * откидные фасады рамку не раздувают. Телевизор над изделием — декор: рамка
   * его не охватывает. У всего изделия рамка чуть снаружи корпуса, чтобы её
   * линии не тонули в панелях.
   */
  private selectionBounds(target: Object3D, box: Box3, outside = false) {
    target.updateMatrixWorld(true);
    const detached: [Object3D, Object3D][] = [];
    target.traverse((object) => {
      if (object.name.startsWith("shelving-tv:") && object.parent) detached.push([object, object.parent]);
    });
    const moving = [...this.doorObjects.values()].filter((runtime) => {
      if (this.doorVisualProgress(runtime.key) <= 0) return false;
      for (let current: Object3D | null = runtime.object; current; current = current.parent) {
        if (current === target) return true;
      }
      return false;
    });
    detached.forEach(([object, parent]) => parent.remove(object));
    moving.forEach((runtime) => this.applyDoorRuntime(runtime, 0));
    target.updateMatrixWorld(true);
    box.setFromObject(target);
    moving.forEach((runtime) => this.applyDoorRuntime(runtime, this.doorVisualProgress(runtime.key)));
    detached.forEach(([object, parent]) => parent.add(object));
    target.updateMatrixWorld(true);
    if (outside && !box.isEmpty()) {
      box.min.x -= SELECTION_FRAME_GAP; box.max.x += SELECTION_FRAME_GAP;
      box.min.z -= SELECTION_FRAME_GAP; box.max.z += SELECTION_FRAME_GAP;
      box.max.y += SELECTION_FRAME_GAP; box.min.y += 0.002;
    }
    return box;
  }

  /** Призма по контуру плана (угловой шкаф), линии на 4 мм снаружи корпуса. */
  private selectionOutline(target: Object3D) {
    let found: { object: Object3D; points: [number, number][]; height: number } | null = null;
    target.traverse((object) => {
      const data = object.userData.selectionOutline as { points: [number, number][]; height: number } | undefined;
      if (data && !found) found = { object, ...data };
    });
    if (!found) return null;
    const { object, points, height } = found as { object: Object3D; points: [number, number][]; height: number };
    object.updateWorldMatrix(true, false);
    const center = points.reduce((sum, [x, z]) => [sum[0] + x / points.length, sum[1] + z / points.length], [0, 0]);
    const [cx, cz] = center as [number, number];
    const outer = points.map(([x, z]): [number, number] => {
      const length = Math.hypot(x - cx, z - cz) || 1;
      return [x + ((x - cx) / length) * SELECTION_FRAME_GAP, z + ((z - cz) / length) * SELECTION_FRAME_GAP];
    });
    const positions: number[] = [];
    const point = (x: number, y: number, z: number) => {
      const world = object.localToWorld(new Vector3(x, y, z));
      positions.push(world.x, world.y, world.z);
    };
    const bottom = 0.002;
    const top = height + SELECTION_FRAME_GAP;
    outer.forEach(([x, z], index) => {
      const [nx, nz] = outer[(index + 1) % outer.length]!;
      point(x, bottom, z); point(nx, bottom, nz);
      point(x, top, z); point(nx, top, nz);
      point(x, bottom, z); point(x, top, z);
    });
    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
    return new LineSegments(geometry, new LineBasicMaterial({ color: 0xb88757 }));
  }

  /** Щелчок по пустому месту прячет рамку; изделие и шаг в панели остаются (11.10). */
  private setSelectionFrameVisible(visible: boolean) {
    if (!this.selectionHelper || this.selectionHelper.visible === visible) return;
    this.selectionHelper.visible = visible;
    this.invalidate(2);
  }

  /** Рамка выделения идёт за изделием, пока его тянут. */
  private refreshSelectionBox() {
    if (!(this.selectionHelper instanceof Box3Helper) || !this.selectionTarget) return;
    this.selectionBounds(this.selectionTarget, this.selectionHelper.box, Boolean(this.selectionHelper.userData.wholeModule));
  }

  private beginModuleDrag(
    moduleId: string,
    event: PointerEvent,
    startClientX = event.clientX
  ) {
    if (!this.project || !this.project.modules.length) return false;
    const selectedModule = this.project.modules.find(
      (candidate) => candidate.id === moduleId
    );
    const selectedBase = selectedModule?.stackedOnId
      ? baseModuleFor(this.project, selectedModule)
      : selectedModule;
    const module =
      selectedBase?.type === "end"
        ? endModuleTarget(this.project, selectedBase) ?? selectedBase
        : selectedBase;
    const object = module ? this.moduleObjects.get(module.id) : undefined;
    if (!object || !module) return false;
    // A corner has a fixed room anchor. Use its left/right switch, not dragging.
    if (module.type === "corner45") return false;

    const layout = createCompositionLayout(this.project);
    const sourcePlacement = layout.placements.find(
      (placement) => placement.module.id === module.id && placement.wing === 0
    );
    const startWallOffset = sourcePlacement?.wallOffset ?? 0;

    this.moduleDrag = {
      moduleId: module.id,
      selectedModuleId: moduleId,
      startClientX,
      startWallOffset,
      currentWallOffset: startWallOffset,
      originalPosition: object.position.clone(),
      originalRotationY: object.rotation.y,
      object,
      pointerId: event.pointerId,
      moved: false,
      attachedObjects: this.project.modules
        .filter(
          (candidate) =>
            (candidate.stackedOnId === module.id) ||
            (candidate.type === "end" &&
              candidate.endAttachedToId === module.id)
        )
        .map((candidate) => this.moduleObjects.get(candidate.id))
        .filter((candidate): candidate is Group => Boolean(candidate))
        .map((candidate) => ({
          object: candidate,
          originalPosition: candidate.position.clone(),
          originalRotationY: candidate.rotation.y
        })),
      sourcePlacement:
        sourcePlacement ?? {
          module,
          wing: 0,
          wall: "back",
          wallOffset: 0,
          x: object.position.x,
          y: object.position.y,
          z: object.position.z,
          yaw: object.rotation.y,
          includeItems: true
        }
    };
    this.controls.enabled = false;
    this.renderer.domElement.setPointerCapture(event.pointerId);
    this.renderer.domElement.classList.add("is-module-ready");
    return true;
  }

  private finishModuleDrag(drag: ModuleDragState) {
    this.moduleDrag = null;
    const positionChanged =
      Math.abs(drag.currentWallOffset - drag.startWallOffset) >= 0.5;
    if (!positionChanged) {
      drag.object.position.copy(drag.originalPosition);
      drag.object.rotation.y = drag.originalRotationY;
      drag.attachedObjects.forEach((attached) => {
        attached.object.position.copy(attached.originalPosition);
        attached.object.rotation.y = attached.originalRotationY;
      });
    }
    this.controls.enabled = true;
    this.renderer.domElement.classList.remove(
      "is-module-ready",
      "is-dragging-module"
    );
    if (this.renderer.domElement.hasPointerCapture(drag.pointerId)) {
      this.renderer.domElement.releasePointerCapture(drag.pointerId);
    }
    if (positionChanged) {
      this.options.onCommitModulePosition(
        drag.moduleId,
        drag.currentWallOffset
      );
      if (drag.selectedModuleId !== drag.moduleId) {
        this.options.onSelectModule(drag.selectedModuleId, "module");
      }
    }
    this.refreshSelectionBox();
    this.invalidate(18);
    return drag.moved;
  }

  /**
   * «Мёртвая зона» перетаскивания: пока палец или мышь не ушли от точки нажатия
   * на 8 / 3 px, деталь не двигается. Раньше касание без движения сдвигало
   * полку к шагу сетки (на телефоне — на 8–16 мм, 12.10).
   */
  private dragNotStarted(drag: { startClientX: number; startClientY: number }, event: PointerEvent) {
    if (this.startedDrags.has(drag)) return false;
    const threshold = event.pointerType === "touch" ? 8 : 3;
    if (Math.hypot(event.clientX - drag.startClientX, event.clientY - drag.startClientY) < threshold) return true;
    this.startedDrags.add(drag);
    return false;
  }

  /**
   * Размеры при перетаскивании — как на чертеже: размерная линия от детали
   * до соседней, засечки на концах и число посередине. Координаты — мм
   * в системе группы модуля (owner).
   */
  private showDragDimensions(owner: Object3D, lines: Array<{ from: [number, number]; to: [number, number]; value: number }>) {
    this.clearDragMeasures();
    if (!this.dragMeasureRoot.parent) this.overlayRoot.add(this.dragMeasureRoot);
    owner.updateWorldMatrix(true, false);
    const z = mm(14);
    const tick = 16;
    const positions: number[] = [];
    const point = (x: number, y: number) => positions.push(mm(x), mm(y), z);
    for (const line of lines) {
      const [x1, y1] = line.from;
      const [x2, y2] = line.to;
      const vertical = Math.abs(y2 - y1) >= Math.abs(x2 - x1);
      point(x1, y1); point(x2, y2);
      for (const [x, y] of [line.from, line.to]) {
        if (vertical) { point(x - tick, y); point(x + tick, y); }
        else { point(x, y - tick); point(x, y + tick); }
      }
    }
    const strokes = this.createDimensionStrokes(positions, "inner");
    strokes.matrixAutoUpdate = false;
    strokes.matrix.copy(owner.matrixWorld);
    strokes.renderOrder = 99;
    this.dragMeasureRoot.add(strokes);
    for (const line of lines) {
      const middle = owner.localToWorld(new Vector3(
        mm((line.from[0] + line.to[0]) / 2), mm((line.from[1] + line.to[1]) / 2), z));
      const sprite = this.makeDimensionLabel(`${Math.round(line.value)}`, middle.x, middle.y, middle.z, DIMENSION_STYLE.inner.label);
      sprite.userData.dragMeasure = true;
      this.dragMeasureRoot.add(sprite);
    }
    this.invalidate();
  }

  private clearDragMeasures() {
    for (const child of [...this.dragMeasureRoot.children]) {
      this.dragMeasureRoot.remove(child);
      if (child instanceof Sprite) {
        child.material.map?.dispose();
        child.material.dispose();
      } else if (child instanceof LineSegments2) {
        child.geometry.dispose();
        child.material.dispose();
      }
    }
  }

  /** Размерная линия идёт у левой стенки проёма, в стороне от предметов посередине. */
  private measureLineX(left: number, width: number) {
    return left + Math.min(90, width * 0.22);
  }

  /**
   * Наполнение шкафа при перетаскивании: от детали до соседней сверху и снизу
   * в том же отсеке (или до дна и крыши); у перегородки — до боковин.
   */
  private itemDragMeasures(drag: ItemDragState) {
    const owner = this.itemObjects.get(drag.itemId)?.parent;
    const module = drag.previewModule ?? this.project?.modules.find((candidate) => candidate.id === drag.moduleId);
    const item = module?.items.find((candidate) => candidate.id === drag.itemId);
    if (!owner || !module || !item) return;
    const moved = { ...item, y: drag.currentY, x: drag.currentX };
    if (module.type === "corner45") {
      // У углового детали стоят в мировых координатах группы корпуса: линию
      // ставим у двери корпуса А, от детали до соседей сверху и снизу.
      let anchorXZ: [number, number] | undefined;
      this.moduleObjects.get(module.id)?.traverse((object) => {
        anchorXZ ??= object.userData.cornerMeasureAnchor as [number, number] | undefined;
      });
      if (!anchorXZ) { this.clearDragMeasures(); return; }
      const anchor = new Group();
      anchor.position.set(anchorXZ[0], 0, anchorXZ[1] - mm(14));
      anchor.updateMatrixWorld(true);
      const [floor, ceiling] = interiorVerticalRangeMm(module);
      const [low, high] = internalItemSpanMm(module, moved);
      const spans = module.items
        .filter((other) => other.id !== item.id && other.type !== "divider")
        .map((other) => internalItemSpanMm(module, other));
      const below = Math.max(floor, ...spans.map(([, top]) => top).filter((top) => top <= low + 0.5));
      const above = Math.min(ceiling, ...spans.map(([bottom]) => bottom).filter((bottom) => bottom >= high - 0.5));
      this.showDragDimensions(anchor, [
        { from: [0, high], to: [0, above], value: above - high },
        { from: [0, below], to: [0, low], value: low - below }
      ]);
      return;
    }
    if (item.type === "divider") {
      const [left, right] = interiorHorizontalRangeMm(module);
      const [low, high] = internalItemSpanMm(module, moved);
      const y = low + (high - low) * 0.72;
      const half = SHELVING_PANEL_THICKNESS_MM / 2;
      const x = drag.currentX;
      this.showDragDimensions(owner, [
        { from: [left, y], to: [x - half, y], value: x - half - left },
        { from: [x + half, y], to: [right, y], value: right - x - half }
      ]);
      return;
    }
    // Размеры — в том отсеке, за деталь которого взялись.
    const pieces = usableItemPieces(module, moved);
    const grabbed = drag.pieceX ?? this.itemDragPieceX(drag);
    const piece = pieces.find((part) => grabbed !== undefined && grabbed >= part.left - 1 && grabbed <= part.right + 1) ?? pieces[0];
    if (!piece) { this.clearDragMeasures(); return; }
    const [floor, ceiling] = interiorVerticalRangeMm(module);
    const [low, high] = internalItemSpanMm(module, moved);
    const neighbours = module.items
      .filter((other) => other.id !== item.id && other.type !== "divider")
      .filter((other) => usableItemPieces(module, other)
        .some((part) => part.right > piece.left + 1 && part.left < piece.right - 1))
      .map((other) => internalItemSpanMm(module, other));
    const below = Math.max(floor, ...neighbours.map(([, top]) => top).filter((top) => top <= low + 0.5));
    const above = Math.min(ceiling, ...neighbours.map(([bottom]) => bottom).filter((bottom) => bottom >= high - 0.5));
    const x = this.measureLineX(piece.left, piece.width);
    this.showDragDimensions(owner, [
      { from: [x, high], to: [x, above], value: above - high },
      { from: [x, below], to: [x, low], value: low - below }
    ]);
  }

  /** Центр отсека перетаскиваемой детали, если тянут деталь-отсек. */
  private itemDragPieceX(drag: ItemDragState) {
    const piece = drag.object.userData.itemPiece as { left: number; right: number } | undefined;
    return piece ? (piece.left + piece.right) / 2 / MM : undefined;
  }

  /**
   * Уровни, к которым можно выровнять полку: полки сетки и доп. полки других
   * ячеек (мм от низа стеллажа). Для полки сетки не берём ячейки, которые
   * сами меняют высоту, пока её тянут.
   */
  private shelfAlignLevels(module: WardrobeModule, moving: { cellId?: string; ref?: string; gridIndex?: number }, frame: Object3D | null) {
    const grid = getShelvingGridLayout(module);
    const levels = new Map<number, ShelfAlignLevel>();
    const add = (y: number, target: ShelfAlignLevel["targets"][number]) => {
      const key = Math.round(y * 2) / 2;
      const level = levels.get(key) ?? { y: key, targets: [] };
      level.targets.push(target);
      levels.set(key, level);
    };
    if (moving.gridIndex === undefined) {
      grid.rowDividerCenters.forEach((y, index) => add(y, { kind: "grid", index }));
    }
    getShelvingCellLayouts(module, grid).forEach((layout) => {
      if (!layout.config || layout.id === moving.cellId) return;
      if (moving.gridIndex !== undefined) {
        const top = layout.row + layout.rowSpan - 1;
        const below = layout.row <= moving.gridIndex && top >= moving.gridIndex;
        const above = layout.row <= moving.gridIndex + 1 && top >= moving.gridIndex + 1;
        if (below !== above) return;
      }
      shelvingCellShelfYs(layout.config, layout.height).forEach((shelf) =>
        add(layout.bottom + shelf.y, { kind: "cell", cellId: layout.id, ref: shelf.ref }));
    });
    if (frame) this.neighbourShelfLevels(module.id, frame, add);
    return [...levels.values()];
  }

  /**
   * Куда можно выровнять стойку или перегородку по горизонтали (видео Сергея,
   * 28.10): стойки сетки в других рядах, перегородки ячеек, стойки и
   * перегородки антресоли над стеллажом или стеллажа под антресолью. «y»
   * уровня здесь — ось по ширине, мм от центра изделия.
   */
  private uprightAlignLevels(module: WardrobeModule, moving: { cellId?: string; ref?: string; dividerIndex?: number; customRow?: number }) {
    const levels = new Map<number, ShelfAlignLevel>();
    const add = (x: number, target: ShelfAlignLevel["targets"][number]) => {
      const key = Math.round(x * 2) / 2;
      const level = levels.get(key) ?? { y: key, targets: [] };
      level.targets.push(target);
      levels.set(key, level);
    };
    const collect = (source: WardrobeModule, own: boolean, report: (x: number, kind: "segment" | "cell", a: string | number, b: string | number) => void) => {
      const grid = getShelvingGridLayout(source);
      const spans = getShelvingSpanSections(source, grid);
      grid.columnDividerCenters.forEach((_, index) => {
        for (let row = 0; row < grid.rows; row += 1) {
          // Тот участок, что едет вместе со стойкой, к себе не примагничиваем.
          if (own && moving.dividerIndex === index &&
            (moving.customRow !== undefined ? row === moving.customRow : !grid.rowCustomColumns[row])) continue;
          const hidden = spans.some((section) => index >= section.column && index < section.column + section.columnSpan - 1 &&
            row >= section.row && row < section.row + section.rowSpan);
          const segment = hidden ? null : getShelvingMainDividerSegment(source, index, row, grid);
          if (!segment || Math.abs(segment.rotationY ?? 0) > 0.05) continue;
          report(segment.x, "segment", index, row);
        }
      });
      getShelvingCellLayouts(source, grid).forEach((layout) => {
        const config = layout.config;
        if (!config) return;
        const dividers = shelvingCellDividerXs(config, layout.width);
        if (config.extraDividerMode === "angled" && dividers.length === 1) return;
        dividers.forEach((divider) => {
          if (own && layout.id === moving.cellId && divider.ref === moving.ref) return;
          report(layout.left + divider.x, "cell", layout.id, divider.ref);
        });
      });
    };
    collect(module, true, (x, kind, a, b) => add(x, kind === "segment"
      ? { kind: "segment", index: a as number, row: b as number }
      : { kind: "cell", cellId: a as string, ref: b as string }));
    // Антресоль стеллажа и стеллаж под ней — одна колонна: стойки сверяются через стык.
    const partner = this.project?.modules.find((candidate) => candidate.type === "shelving" &&
      (module.stackedOnId ? candidate.id === module.stackedOnId : candidate.stackedOnId === module.id));
    const partnerGroup = partner ? this.moduleObjects.get(partner.id) : undefined;
    if (partner && partnerGroup && Math.abs(partner.width - module.width) < 1) {
      collect(partner, false, (x, kind, a, b) => {
        const objects: Object3D[] = [];
        partnerGroup.traverse((object) => {
          const marker = object.userData.gridSegment as { axis: string; index: number; segment: number } | undefined;
          const board = object.userData.shelvingBoard as { cellId: string; ref: string } | undefined;
          if (kind === "segment" ? marker?.axis === "column" && marker.index === a && marker.segment === b
            : board?.cellId === a && board.ref === b) objects.push(object);
        });
        add(x, { kind: "object", objects });
      });
    }
    return [...levels.values()];
  }

  /**
   * Полки изделий, стоящих вплотную к этому (стеллаж или шкаф рядом): их
   * уровни в мм системы координат frame. Начало координат каждой полки —
   * её середина по высоте, как и у полок самого стеллажа.
   */
  private neighbourShelfLevels(
    moduleId: string,
    frame: Object3D,
    add: (y: number, target: ShelfAlignLevel["targets"][number]) => void
  ) {
    const own = this.moduleObjects.get(moduleId);
    if (!own || !this.project) return;
    const ownBox = this.visualBounds(own);
    if (ownBox.isEmpty()) return;
    ownBox.expandByScalar(mm(80));
    frame.updateWorldMatrix(true, false);
    const origin = new Vector3();
    const levelOf = (object: Object3D) => {
      object.getWorldPosition(origin);
      return frame.worldToLocal(origin).y / MM;
    };
    this.moduleObjects.forEach((root, otherId) => {
      if (otherId === moduleId) return;
      const other = this.project?.modules.find((candidate) => candidate.id === otherId);
      if (!other) return;
      const box = this.visualBounds(root);
      if (box.isEmpty() || !box.intersectsBox(ownBox)) return;
      const shelfItems = new Set(other.items
        .filter((item) => item.type === "shelf" || item.type === "led")
        .map((item) => item.id));
      root.traverse((object) => {
        const pick = object.userData.pick as PickMeta | undefined;
        const board = object.userData.shelvingBoard as { ref: string } | undefined;
        if (object.userData.shelvingDividerDragOwner === true && pick?.dividerAxis === "row") {
          add(levelOf(object), { kind: "object", objects: [object] });
        } else if (board?.ref.startsWith("shelf")) {
          add(levelOf(object), { kind: "object", objects: [object] });
        } else if (shelfItems.has(object.name)) {
          const pieces = object.children.filter((child) => child.userData.itemPiece);
          add(levelOf(object), { kind: "object", objects: pieces.length ? pieces : [object] });
        }
      });
    });
  }

  /** Уровни полок соседних изделий в системе координат корпуса, мм от его низа. */
  private neighbourAlignLevels(moduleId: string) {
    const frame = this.moduleObjects.get(moduleId);
    if (!frame) return [];
    const levels = new Map<number, ShelfAlignLevel>();
    this.neighbourShelfLevels(moduleId, frame, (y, target) => {
      const key = Math.round(y * 2) / 2;
      const level = levels.get(key) ?? { y: key, targets: [] };
      level.targets.push(target);
      levels.set(key, level);
    });
    return [...levels.values()];
  }

  private nearestShelfLevel(levels: ShelfAlignLevel[], y: number, min: number, max: number) {
    let best: ShelfAlignLevel | null = null;
    for (const level of levels) {
      if (level.y < min - 0.01 || level.y > max + 0.01) continue;
      if (Math.abs(level.y - y) > SHELF_ALIGN_SNAP_MM) continue;
      if (!best || Math.abs(level.y - y) < Math.abs(best.y - y)) best = level;
    }
    return best;
  }

  private shelfAlignHelpers: Box3Helper[] = [];
  private shelfAlignKey = "";

  /** Полка встала вровень с соседними — они подсвечиваются зелёной рамкой. */
  private showShelfAlignment(moduleId: string, level: ShelfAlignLevel | null) {
    const key = level ? `${moduleId}:${level.y}` : "";
    if (key === this.shelfAlignKey) return;
    this.shelfAlignKey = key;
    this.shelfAlignHelpers.forEach((helper) => {
      this.overlayRoot.remove(helper);
      helper.geometry.dispose();
      (helper.material as LineBasicMaterial).dispose();
    });
    this.shelfAlignHelpers = [];
    const module = this.moduleObjects.get(moduleId);
    if (!level || !module) { this.invalidate(); return; }
    const outline = (object: Object3D) => {
      const box = this.visualBounds(object);
      if (box.isEmpty()) return;
      const helper = new Box3Helper(box, 0x1f9d68);
      const material = helper.material as LineBasicMaterial;
      material.depthTest = false;
      material.transparent = true;
      helper.renderOrder = 8;
      helper.raycast = () => {};
      this.shelfAlignHelpers.push(helper);
      this.overlayRoot.add(helper);
    };
    module.traverse((object) => {
      const pick = object.userData.pick as PickMeta | undefined;
      const board = object.userData.shelvingBoard as { cellId: string; ref: string } | undefined;
      const segment = object.userData.gridSegment as { axis: string; index: number; segment: number } | undefined;
      const matches = level.targets.some((target) => target.kind === "grid"
        ? object.userData.shelvingDividerDragOwner === true && pick?.dividerAxis === "row" && pick.dividerIndex === target.index
        : target.kind === "segment"
          ? segment?.axis === "column" && segment.index === target.index && segment.segment === target.row
          : target.kind === "cell" && board?.cellId === target.cellId && board.ref === target.ref);
      if (matches) outline(object);
    });
    level.targets.forEach((target) => {
      if (target.kind === "object") target.objects.forEach(outline);
    });
    this.invalidate();
  }

  private cellShelfDragMeasures(drag: ShelvingCellShelfDragState) {
    const owner = drag.object.parent;
    if (!owner) return;
    if (drag.axis === "x") {
      // Перегородка: от неё до стенки проёма или соседней перегородки слева и справа.
      const half = SHELVING_PANEL_THICKNESS_MM / 2;
      const before = drag.neighbours.filter((x) => x < drag.currentPosition).at(-1);
      const after = drag.neighbours.find((x) => x > drag.currentPosition);
      const left = drag.cellLeftX + (before === undefined ? 0 : before + half);
      const right = drag.cellLeftX + (after === undefined ? drag.cellWidth : after - half);
      const boardLeft = drag.cellLeftX + drag.currentPosition - half;
      const boardRight = drag.cellLeftX + drag.currentPosition + half;
      const y = drag.cellStartY + drag.cellHeight - Math.min(90, drag.cellHeight * 0.22);
      this.showDragDimensions(owner, [
        { from: [left, y], to: [boardLeft, y], value: boardLeft - left },
        { from: [boardRight, y], to: [right, y], value: right - boardRight }
      ]);
      return;
    }
    const below = drag.neighbours.filter((y) => y < drag.currentPosition).at(-1);
    const above = drag.neighbours.find((y) => y > drag.currentPosition);
    const half = SHELVING_PANEL_THICKNESS_MM / 2;
    const base = drag.cellStartY;
    const shelfTop = base + drag.currentPosition + half;
    const shelfBottom = base + drag.currentPosition - half;
    const ceiling = base + (above === undefined ? drag.cellHeight : above - half);
    const floor = base + (below === undefined ? 0 : below + half);
    const x = this.measureLineX(drag.cellLeftX, drag.cellWidth);
    this.showDragDimensions(owner, [
      { from: [x, shelfTop], to: [x, ceiling], value: ceiling - shelfTop },
      { from: [x, floor], to: [x, shelfBottom], value: shelfBottom - floor }
    ]);
  }

  private gridDividerDragMeasures(drag: ShelvingDividerDragState) {
    const owner = drag.object.parent;
    const module = this.project?.modules.find((candidate) => candidate.id === drag.moduleId);
    if (!owner || !module) return;
    const shift = drag.currentPosition - drag.startPosition;
    const before = (drag.cellSizes[drag.dividerIndex] ?? 0) + shift;
    const after = (drag.cellSizes[drag.dividerIndex + 1] ?? 0) - shift;
    const half = SHELVING_PANEL_THICKNESS_MM / 2;
    const grid = getShelvingGridLayout(module);
    const center = drag.currentPosition;
    if (drag.axis === "column") {
      // Горизонтальные линии в ряду, за который взялись, — в верхней трети проёма.
      const row = drag.row ?? Math.floor(grid.rows / 2);
      const y = (grid.rowStarts[row] ?? 0) + (grid.cellHeights[row] ?? 0) * 0.72;
      this.showDragDimensions(owner, [
        { from: [center - half - before, y], to: [center - half, y], value: before },
        { from: [center + half, y], to: [center + half + after, y], value: after }
      ]);
    } else {
      const column = drag.column ?? 0;
      const x = this.measureLineX(grid.columnStarts[column] ?? 0, grid.cellWidths[column] ?? 0);
      this.showDragDimensions(owner, [
        { from: [x, center - half - before], to: [x, center - half], value: before },
        { from: [x, center + half], to: [x, center + half + after], value: after }
      ]);
    }
  }

  /** Касание пальцем выделяет полку или стойку — как наведение мыши, но с ручкой. */
  private showTouchBoard(hit: ReturnType<PlannerScene["pick"]>) {
    const hovered = this.boardHoverFromHit(hit);
    this.options.onHoverShelvingBoard?.(hovered ? { ...hovered, touch: true } : null);
  }

  private boardHoverFromHit(hit: ReturnType<PlannerScene["pick"]>) {
    const meta = hit ? this.getPickMeta(hit.object) : null;
    let hovered: { moduleId: string; cellId: string; ref: string } | null = meta?.boardRef && meta.cellId
      ? { moduleId: meta.moduleId, cellId: meta.cellId, ref: meta.boardRef }
      : null;
    if (!hovered && hit && meta?.kind === "shelvingDivider" && meta.dividerAxis && meta.dividerIndex !== undefined) {
      const segment = meta.dividerAxis === "column"
        ? meta.dividerRow
        : meta.dividerColumn ?? this.gridColumnAtPoint(meta.moduleId, hit.object, hit.point);
      if (segment !== undefined && segment !== null) {
        hovered = {
          moduleId: meta.moduleId,
          cellId: "",
          ref: shelvingGridBoardRef({ axis: meta.dividerAxis, index: meta.dividerIndex, segment })
        };
      }
    }
    return hovered;
  }

  /** Колонка сетки под точкой на полке сетки. */
  private gridColumnAtPoint(moduleId: string, object: Object3D, point: Vector3) {
    const module = this.project?.modules.find((candidate) => candidate.id === moduleId);
    let owner: Object3D | null = object;
    while (owner && owner.userData.shelvingDividerDragOwner !== true) owner = owner.parent;
    if (!module || !owner?.parent) return null;
    const local = owner.parent.worldToLocal(point.clone());
    const x = local.x / MM;
    const grid = getShelvingGridLayout(module);
    const column = grid.columnStarts.findIndex((start, index) =>
      x >= start - 9 && x <= start + (grid.cellWidths[index] ?? 0) + 9);
    return column >= 0 ? column : null;
  }

  /**
   * Подсветить деталь: доп. полку или перегородку ячейки, стойку или полку
   * сетки (из 3D или при наведении на строку в панели). Полка сетки —
   * сплошная доска на всю ширину и двигается целиком, поэтому выделяется
   * целиком. У стойки — светлая рамка на всю стойку и яркая на участок ряда:
   * щелчок по нему открывает поворот, Delete его убирает.
   */
  setHoveredBoard(hovered: { moduleId: string; cellId: string; ref: string; touch?: boolean } | null) {
    this.hoveredBoardState = hovered;
    this.boardHoverHelpers.forEach((helper) => {
      this.overlayRoot.remove(helper);
      helper.geometry.dispose();
      (helper.material as LineBasicMaterial).dispose();
    });
    this.boardHoverHelpers = [];
    this.boardHoverBoxes = [];
    if (this.touchHandle) {
      this.overlayRoot.remove(this.touchHandle);
      (this.touchHandle.material as SpriteMaterial).dispose();
      this.touchHandle = null;
    }
    const gridBoard = parseShelvingGridBoardRef(hovered?.ref);
    const classes = this.renderer.domElement.classList;
    classes.toggle("is-hovering-board", Boolean(hovered));
    classes.toggle("is-hovering-upright", gridBoard?.axis === "column" || Boolean(hovered?.ref.startsWith("divider")));
    classes.toggle("is-hovering-grid-shelf", gridBoard?.axis === "row" || Boolean(hovered?.ref.startsWith("shelf")));
    if (!hovered) { this.invalidate(); return; }
    const module = this.moduleObjects.get(hovered.moduleId);
    if (!module) { this.invalidate(); return; }
    if (gridBoard) {
      let owner: Object3D | null = null;
      let segment: Object3D | null = null;
      module.traverse((object) => {
        const pick = object.userData.pick as PickMeta | undefined;
        if (!owner && object.userData.shelvingDividerDragOwner && pick?.dividerAxis === gridBoard.axis &&
          pick.dividerIndex === gridBoard.index) owner = object;
        const marker = object.userData.gridSegment as { axis: string; index: number; segment: number } | undefined;
        if (!segment && marker?.axis === gridBoard.axis && marker.index === gridBoard.index &&
          marker.segment === gridBoard.segment) segment = object;
      });
      const board = owner as Object3D | null;
      if (!board) { this.invalidate(); return; }
      if (gridBoard.axis === "row") {
        this.addBoardHoverHelper(() => this.visualBounds(board), 0xe07a12, 1);
        if (hovered.touch) this.addTouchHandle("vertical");
        this.invalidate();
        return;
      }
      this.addBoardHoverHelper(() => this.visualBounds(board), 0xf0b36a, 0.75);
      const part = segment as Object3D | null;
      if (part) this.addBoardHoverHelper(() => this.visualBounds(part), 0xe07a12, 1);
      if (hovered.touch) this.addTouchHandle("horizontal", part ? 1 : 0);
      this.invalidate();
      return;
    }
    let target: Object3D | null = null;
    module.traverse((object) => {
      const board = object.userData.shelvingBoard as { cellId: string; ref: string } | undefined;
      if (!target && board?.cellId === hovered.cellId && board.ref === hovered.ref) target = object;
    });
    const found = target as Object3D | null;
    if (found) this.addBoardHoverHelper(() => this.visualBounds(found), 0xe08a1e, 1);
    // Тянуть пальцем можно полку ячейки; перегородку — только выделить.
    if (found && hovered.touch && hovered.ref.startsWith("shelf")) this.addTouchHandle("vertical");
    this.invalidate();
  }

  /** Ручка-стрелка в середине выделенной детали: за неё удобно взяться пальцем. */
  private addTouchHandle(direction: "vertical" | "horizontal", boxIndex = 0) {
    const box = this.boardHoverBoxes[boxIndex]?.();
    if (!box || box.isEmpty()) return;
    const material = new SpriteMaterial({ map: this.touchHandleTexture(direction), depthTest: false, transparent: true, sizeAttenuation: false });
    const sprite = new Sprite(material);
    sprite.scale.set(0.085, 0.085, 1);
    sprite.renderOrder = 9;
    sprite.raycast = () => {};
    sprite.userData.touchHandleBox = boxIndex;
    this.touchHandle = sprite;
    this.placeTouchHandle();
    this.overlayRoot.add(sprite);
  }

  private placeTouchHandle() {
    if (!this.touchHandle) return;
    const box = this.boardHoverBoxes[this.touchHandle.userData.touchHandleBox as number]?.();
    if (!box || box.isEmpty()) return;
    const center = box.getCenter(new Vector3());
    this.touchHandle.position.set(center.x, center.y, box.max.z);
  }

  private touchHandleTexture(direction: "vertical" | "horizontal") {
    const cached = this.touchHandleTextures.get(direction);
    if (cached) return cached;
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const context = canvas.getContext("2d")!;
    context.beginPath();
    context.arc(64, 64, 54, 0, Math.PI * 2);
    context.fillStyle = "rgba(255, 255, 255, 0.96)";
    context.fill();
    context.lineWidth = 7;
    context.strokeStyle = "#e07a12";
    context.stroke();
    context.save();
    context.translate(64, 64);
    if (direction === "horizontal") context.rotate(Math.PI / 2);
    context.fillStyle = "#2b231c";
    for (const sign of [-1, 1]) {
      context.beginPath();
      context.moveTo(0, sign * 38);
      context.lineTo(-17, sign * 14);
      context.lineTo(17, sign * 14);
      context.closePath();
      context.fill();
    }
    context.fillRect(-4, -14, 8, 28);
    context.restore();
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    this.touchHandleTextures.set(direction, texture);
    return texture;
  }

  private addBoardHoverHelper(bounds: () => Box3 | null, color: number, opacity: number) {
    const box = bounds();
    if (!box || box.isEmpty()) return;
    const helper = new Box3Helper(box, color);
    const material = helper.material as LineBasicMaterial;
    material.depthTest = false;
    material.transparent = true;
    material.opacity = opacity;
    helper.renderOrder = 7;
    helper.raycast = () => {};
    this.boardHoverHelpers.push(helper);
    this.boardHoverBoxes.push(bounds);
    this.overlayRoot.add(helper);
  }

  /** Рамки подсветки идут за деталью, пока её тянут. */
  private refreshBoardHoverHelpers() {
    this.boardHoverHelpers.forEach((helper, index) => {
      const box = this.boardHoverBoxes[index]?.();
      if (box && !box.isEmpty()) helper.box.copy(box);
    });
    this.placeTouchHandle();
  }

  /** Габарит видимых деталей объекта: без невидимых полос для мыши. */
  private visualBounds(object: Object3D) {
    object.updateWorldMatrix(true, true);
    const box = new Box3();
    const part = new Box3();
    object.traverse((child) => {
      if (!(child instanceof Mesh) || child.userData.hitStrip) return;
      for (let current: Object3D | null = child; current && current !== object; current = current.parent) {
        if (current.userData.hitStrip) return;
      }
      box.union(part.setFromObject(child));
    });
    return box;
  }

  private getPickOwner(object: Object3D, kind: PickMeta["kind"]) {
    let current: Object3D | null = object;
    while (current) {
      const meta = current.userData.pick as PickMeta | undefined;
      if (meta?.kind === kind) return current;
      current = current.parent;
    }
    return null;
  }

  private beginShelvingCellShelfDrag(
    meta: PickMeta,
    hitObject: Object3D,
    event: PointerEvent
  ) {
    if (!this.project || !meta.cellId) return false;
    const module = this.project.modules.find(
      (candidate) => candidate.id === meta.moduleId
    );
    const parsed = parseShelvingCellId(meta.cellId);
    if (meta.kind === "shelvingCellDivider") return this.beginShelvingCellDividerDrag(meta, hitObject, event);
    const object = this.getPickOwner(hitObject, "shelvingCellShelf");
    if (
      !module ||
      module.type !== "shelving" ||
      !parsed ||
      parsed.moduleId !== module.id ||
      !object
    ) {
      return false;
    }

    const rect = shelvingCellRect(module, meta.cellId);
    if (!rect) return false;
    const cellHeight = rect.height;
    const cellStartY = rect.bottom;
    const config = module.shelvingCells?.find(
      (cell) => cell.id === meta.cellId
    );
    if (!config) return false;
    const boardRef = meta.boardRef ?? "shelf:main";
    const shelves = shelvingCellShelfYs(config, cellHeight);
    const current = shelves.find((shelf) => shelf.ref === boardRef);
    if (!current) return false;
    const range = shelvingExtraShelfRange(cellHeight);
    const gap = SHELVING_EXTRA_SHELF_CLEARANCE_MM + SHELVING_PANEL_THICKNESS_MM;
    const below = shelves.filter((shelf) => shelf.y < current.y).at(-1);
    const above = shelves.find((shelf) => shelf.y > current.y);
    const startPosition = current.y;

    this.shelvingCellShelfDrag = {
      moduleId: module.id,
      cellId: meta.cellId,
      boardRef,
      neighbours: shelves.filter((shelf) => shelf.ref !== boardRef).map((shelf) => shelf.y),
      cellCenterX: rect.left + rect.width / 2,
      cellLeftX: rect.left,
      cellWidth: rect.width,
      alignLevels: this.shelfAlignLevels(module, { cellId: meta.cellId, ref: boardRef }, object.parent),
      dragPlane: this.beginDragPlane(event, object.parent),
      startClientX: event.clientX,
      startClientY: event.clientY,
      cellStartY,
      cellHeight,
      startPosition,
      currentPosition: startPosition,
      minPosition: below ? Math.max(range.min, below.y + gap) : range.min,
      maxPosition: above ? Math.min(range.max, above.y - gap) : range.max,
      object,
      pointerId: event.pointerId
    };
    this.controls.enabled = false;
    this.renderer.domElement.setPointerCapture(event.pointerId);
    this.renderer.domElement.classList.add("is-dragging-item");
    // Размеры видны сразу, как только взялись за полку.
    this.cellShelfDragMeasures(this.shelvingCellShelfDrag);
    return true;
  }

  /**
   * Перегородка ячейки тянется вбок между стенками проёма и соседними
   * перегородками; проём не уже 100 мм. Перегородку под углом не тянем —
   * она всегда по центру.
   */
  private beginShelvingCellDividerDrag(meta: PickMeta, hitObject: Object3D, event: PointerEvent) {
    if (!this.project || !meta.cellId) return false;
    const module = this.project.modules.find((candidate) => candidate.id === meta.moduleId);
    // Тянем группу перегородки (у неё userData.shelvingBoard), а не одну её панель.
    let object: Object3D | null = hitObject;
    while (object && !object.userData.shelvingBoard) object = object.parent;
    const rect = module ? shelvingCellRect(module, meta.cellId) : null;
    const config = module?.shelvingCells?.find((cell) => cell.id === meta.cellId);
    if (!module || !object || !rect || !config) return false;
    const boardRef = meta.boardRef ?? "divider:main";
    const dividers = shelvingCellDividerXs(config, rect.width);
    const current = dividers.find((divider) => divider.ref === boardRef);
    if (!current) return false;
    if (boardRef === "divider:main" && config.extraDividerMode === "angled" && dividers.length === 1) return false;
    const range = shelvingExtraDividerRange(rect.width);
    const gap = SHELVING_EXTRA_DIVIDER_MIN_OPENING_MM + SHELVING_PANEL_THICKNESS_MM;
    const before = dividers.filter((divider) => divider.x < current.x).at(-1);
    const after = dividers.find((divider) => divider.x > current.x);
    this.shelvingCellShelfDrag = {
      axis: "x",
      moduleId: module.id,
      cellId: meta.cellId,
      boardRef,
      neighbours: dividers.filter((divider) => divider.ref !== boardRef).map((divider) => divider.x),
      cellCenterX: rect.left + rect.width / 2,
      cellLeftX: rect.left,
      cellWidth: rect.width,
      alignLevels: this.uprightAlignLevels(module, { cellId: meta.cellId, ref: boardRef }),
      dragPlane: this.beginDragPlane(event, object.parent),
      startClientX: event.clientX,
      startClientY: event.clientY,
      cellStartY: rect.bottom,
      cellHeight: rect.height,
      startPosition: current.x,
      currentPosition: current.x,
      minPosition: before ? Math.max(range.min, before.x + gap) : range.min,
      maxPosition: after ? Math.min(range.max, after.x - gap) : range.max,
      object,
      pointerId: event.pointerId
    };
    this.controls.enabled = false;
    this.renderer.domElement.setPointerCapture(event.pointerId);
    this.renderer.domElement.classList.add("is-dragging-column");
    this.cellShelfDragMeasures(this.shelvingCellShelfDrag);
    return true;
  }

  private finishShelvingCellShelfDrag(drag: ShelvingCellShelfDragState) {
    this.shelvingCellShelfDrag = null;
    this.clearDragMeasures();
    this.showShelfAlignment(drag.moduleId, null);
    this.controls.enabled = true;
    this.renderer.domElement.classList.remove("is-dragging-item", "is-dragging-column");
    if (this.renderer.domElement.hasPointerCapture(drag.pointerId)) {
      this.renderer.domElement.releasePointerCapture(drag.pointerId);
    }

    if (Math.abs(drag.currentPosition - drag.startPosition) < 0.5) {
      if (drag.axis === "x") drag.object.position.x = mm(drag.cellLeftX + drag.startPosition);
      else drag.object.position.y = mm(drag.cellStartY + drag.startPosition);
      this.invalidate(6);
      return;
    }

    const module = this.project?.modules.find((candidate) => candidate.id === drag.moduleId);
    const config = module?.shelvingCells?.find((cell) => cell.id === drag.cellId);
    if (config) {
      this.options.onCommitShelvingCellUpdate(drag.moduleId, drag.cellId, drag.axis === "x"
        ? withMovedShelvingDivider(config, drag.boardRef, drag.cellWidth, drag.currentPosition)
        : withMovedShelvingShelf(config, drag.boardRef, drag.cellHeight, drag.currentPosition));
    }
    this.invalidate(12);
  }

  private beginShelvingDividerDrag(
    meta: PickMeta,
    hitObject: Object3D,
    event: PointerEvent,
    point?: Vector3
  ) {
    if (!this.project) return false;
    const module = this.project.modules.find(
      (candidate) => candidate.id === meta.moduleId
    );
    const axis = meta.dividerAxis;
    const dividerIndex = meta.dividerIndex;
    let object: Object3D | null = hitObject;
    while (object && object.userData.shelvingDividerDragOwner !== true) {
      object = object.parent;
    }
    if (
      !module ||
      module.type !== "shelving" ||
      !axis ||
      dividerIndex === undefined ||
      !object
    ) {
      return false;
    }

    const grid = getShelvingGridLayout(module);
    const grabbedRow = axis === "column" ? meta.dividerRow : undefined;
    const customRow = grabbedRow !== undefined && grid.rowCustomColumns[grabbedRow] ? grabbedRow : undefined;
    const layoutRow = grabbedRow ?? grid.rowCustomColumns.findIndex((custom) => !custom);
    const cellSizes = [
      ...(axis === "column"
        ? grid.rowCellWidths[layoutRow] ?? grid.cellWidths
        : grid.cellHeights)
    ];
    const startPosition =
      axis === "column"
        ? gridDividerCenter(grid, Math.max(0, layoutRow), dividerIndex)
        : grid.rowDividerCenters[dividerIndex];
    // Едут участки стойки: свой ряд — один, иначе все ряды с общей раскладкой.
    const movingSegments = axis === "column"
      ? object.children
          .filter((child) => {
            const row = (child.userData.gridSegment as { segment?: number } | undefined)?.segment ??
              (child.userData.gridRowStrip as number | undefined);
            if (row === undefined) return false;
            return customRow !== undefined ? row === customRow : !grid.rowCustomColumns[row];
          })
          .map((child) => ({ object: child, startX: child.position.x }))
      : undefined;
    const leftSize = cellSizes[dividerIndex];
    const rightSize = cellSizes[dividerIndex + 1];
    if (
      startPosition === undefined ||
      leftSize === undefined ||
      rightSize === undefined
    ) {
      return false;
    }
    const requestedMinimum =
      axis === "column"
        ? SHELVING_MIN_CELL_WIDTH_MM
        : SHELVING_MIN_CELL_HEIGHT_MM;
    const minimum = Math.min(
      requestedMinimum,
      Math.max(20, (leftSize + rightSize) / 2 - 1)
    );

    this.shelvingDividerDrag = {
      moduleId: module.id,
      axis,
      dividerIndex,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPosition,
      currentPosition: startPosition,
      // Колонку нельзя растянуть шире 1200 мм в свету: полка прогнётся
      // (приёмка 04.10, sg-05). Для рядов предел — только минимальная высота.
      minPosition: startPosition - Math.max(0, Math.min(leftSize - minimum,
        axis === "column" ? Math.max(0, SHELVING_MAX_CLEAR_SPAN_MM - rightSize) : Infinity)),
      maxPosition: startPosition + Math.max(0, Math.min(rightSize - minimum,
        axis === "column" ? Math.max(0, SHELVING_MAX_CLEAR_SPAN_MM - leftSize) : Infinity)),
      cellSizes,
      object,
      pointerId: event.pointerId,
      alignLevels: axis === "row"
        ? this.shelfAlignLevels(module, { gridIndex: dividerIndex }, object.parent)
        : this.uprightAlignLevels(module, { dividerIndex, customRow }),
      dragPlane: this.beginDragPlane(event, object.parent),
      row: meta.dividerRow,
      column: meta.dividerColumn ?? (point ? this.gridColumnAtPoint(module.id, hitObject, point) ?? undefined : undefined),
      movingSegments,
      customRow
    };
    this.controls.enabled = false;
    this.renderer.domElement.setPointerCapture(event.pointerId);
    this.renderer.domElement.classList.add(axis === "column" ? "is-dragging-column" : "is-dragging-item");
    this.gridDividerDragMeasures(this.shelvingDividerDrag);
    return true;
  }

  private finishShelvingDividerDrag(drag: ShelvingDividerDragState) {
    this.clearDragMeasures();
    this.showShelfAlignment(drag.moduleId, null);
    this.shelvingDividerDrag = null;
    this.controls.enabled = true;
    this.renderer.domElement.classList.remove("is-dragging-item", "is-dragging-column");
    if (this.renderer.domElement.hasPointerCapture(drag.pointerId)) {
      this.renderer.domElement.releasePointerCapture(drag.pointerId);
    }
    const delta = drag.currentPosition - drag.startPosition;
    if (Math.abs(delta) < 0.5) {
      if (drag.axis === "column") {
        if (drag.movingSegments) drag.movingSegments.forEach(({ object, startX }) => { object.position.x = startX; });
        else drag.object.position.x = mm(drag.startPosition);
        // Просто щёлкнули по стойке — открыть её поворот.
        if (drag.row !== undefined) this.options.onClickShelvingUpright?.(drag.moduleId, drag.dividerIndex, drag.row);
      } else {
        drag.object.position.y = mm(drag.startPosition);
      }
      this.refreshBoardHoverHelpers();
      this.invalidate(6);
      return;
    }

    const sizes = [...drag.cellSizes];
    sizes[drag.dividerIndex] = Math.max(
      1,
      sizes[drag.dividerIndex]! + delta
    );
    sizes[drag.dividerIndex + 1] = Math.max(
      1,
      sizes[drag.dividerIndex + 1]! - delta
    );
    const total = sizes.reduce((sum, value) => sum + value, 0);
    const ratios = sizes.map((value) => value / total);
    const module = this.project?.modules.find((candidate) => candidate.id === drag.moduleId);
    if (drag.axis === "column" && drag.customRow !== undefined && module) {
      const rows = getShelvingGridLayout(module).rows;
      const own = Array.from({ length: rows }, (_, row) => module.shelvingRowColumnRatios?.[row] ?? null);
      own[drag.customRow] = ratios;
      this.options.onCommitModuleUpdate(drag.moduleId, { shelvingRowColumnRatios: own });
    } else {
      this.options.onCommitModuleUpdate(
        drag.moduleId,
        drag.axis === "column"
          ? { shelvingColumnRatios: ratios }
          : { shelvingRowRatios: ratios }
      );
    }
    this.invalidate(12);
  }

  /** Нажатие на пустое место: если отпустят, не сдвинув, — это щелчок, а не поворот камеры. */
  private emptyPress: { x: number; y: number; pointerId: number } | null = null;

  private readonly handlePointerDown = (event: PointerEvent) => {
    this.emptyPress = null;
    if (this.presentationMode) return;
    const hit = this.pickForPointer(event);
    if (event.pointerType === "touch") this.showTouchBoard(hit);
    const meta = hit ? this.getPickMeta(hit.object) : null;
    if (!hit || !meta) {
      this.setSelectionFrameVisible(false);
      this.options.onClearSelection();
      this.emptyPress = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
      return;
    }
    this.setSelectionFrameVisible(true);

    if (this.options.canInteractWithModule?.(meta.moduleId) === false) return;

    if (meta.kind === "shelvingCellShelf" || meta.kind === "shelvingCellDivider") {
      if (meta.cellId) {
        this.options.onSelectShelvingCell(meta.moduleId, meta.cellId, "board");
      }
      this.beginShelvingCellShelfDrag(meta, hit.object, event);
      return;
    }

    if (meta.kind === "shelvingDivider") {
      if (meta.dividerAxis === "column" && meta.cellId) {
        this.options.onSelectShelvingCell(meta.moduleId, meta.cellId, "board");
      } else {
        this.options.onSelectModule(meta.moduleId, "module");
      }
      this.beginShelvingDividerDrag(meta, hit.object, event, hit.point);
      return;
    }

    if (meta.kind === "shelvingCell") {
      if (meta.cellId) {
        this.options.onSelectShelvingCell(meta.moduleId, meta.cellId, "cell");
      }
      return;
    }

    if (meta.kind === "door") {
      const doorIndex = meta.doorIndex ?? 0;
      if (meta.cellId) {
        this.options.onSelectShelvingCell(meta.moduleId, meta.cellId, "facade");
      } else {
        this.options.onSelectModule(meta.moduleId, "facade");
      }
      this.doorClick = {
        moduleId: meta.moduleId,
        doorIndex,
        startClientX: event.clientX,
        startClientY: event.clientY,
        pointerId: event.pointerId,
        moved: false
      };
      if (!this.beginModuleDrag(meta.moduleId, event, event.clientX)) {
        this.controls.enabled = false;
        this.renderer.domElement.setPointerCapture(event.pointerId);
      }
      return;
    }

    if (meta.kind === "module") {
      this.options.onSelectModule(meta.moduleId, "module");
      this.beginModuleDrag(meta.moduleId, event);
      return;
    }

    if (!meta.itemId || !this.project) return;
    this.options.onSelectItem(meta.moduleId, meta.itemId);
    const module = this.project.modules.find((candidate) => candidate.id === meta.moduleId);
    const item = module?.items.find((candidate) => candidate.id === meta.itemId);
    const itemGroup = this.itemObjects.get(meta.itemId);
    if (!module || !item || !itemGroup) return;
    // Строка на несколько отсеков: тянем деталь того отсека, за который взялись.
    let object: Group = itemGroup;
    let pieceX: number | undefined;
    let previewModule: WardrobeModule | undefined;
    if (item.type !== "divider" && module.type !== "corner45" && !Number.isFinite(item.anchorX) &&
      usableItemPieces(module, item).length > 1) {
      for (let current: Object3D | null = hit.object; current && current !== itemGroup; current = current.parent) {
        const piece = current.userData.itemPiece as { left: number; right: number } | undefined;
        if (piece && current instanceof Group) {
          object = current;
          pieceX = ((piece.left + piece.right) / 2) / MM;
          let counter = 0;
          previewModule = {
            ...module,
            items: module.items.flatMap((candidate) => candidate.id === item.id
              ? splitItemByCompartments(module, candidate, pieceX!, () => `preview-piece-${counter++}`)
              : [candidate])
          };
          break;
        }
      }
    }
    const dividerHeight =
      item.type === "divider"
        ? item.height ?? Math.min(1400, module.height - 180)
        : 0;
    // Перегородку можно протащить в любой пролёт — от дна до крыши (22.10).
    const minY = item.type === "divider" ? 0 : internalItemMinimumY(module, item);
    const maxY =
      item.type === "divider"
        ? module.height - CARCASS_MM
        : module.height - 80;
    void dividerHeight;

    this.itemDrag = {
      moduleId: meta.moduleId,
      itemId: meta.itemId,
      type: item.type,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: item.x ?? 0,
      startY: item.y,
      currentX: item.x ?? 0,
      currentY: item.y,
      lastDesiredY: item.y,
      minX: item.type === "divider" ? -Math.max(0, module.width / 2 - 66) : 0,
      maxX: item.type === "divider" ? Math.max(0, module.width / 2 - 66) : 0,
      minY,
      maxY,
      object,
      pointerId: event.pointerId,
      pieceX,
      previewModule,
      positionOrigin: pieceX === undefined ? 0 : item.y,
      alignLevels: item.type === "shelf" || item.type === "led"
        ? this.neighbourAlignLevels(module.id)
        : [],
      pullOutDoorIndex: this.pullOutDoorIndexAt(hit.object),
      // Плоскость — фасад корпуса (группа, где стоят детали), а не корень
      // модуля: тот лежит в начале координат комнаты, и полка отставала бы от мыши.
      dragPlane: this.beginDragPlane(event, itemGroup.parent ?? this.moduleObjects.get(module.id) ?? null)
    };
    this.controls.enabled = false;
    this.renderer.domElement.setPointerCapture(event.pointerId);
    this.renderer.domElement.classList.add("is-dragging-item");
    // Взятая деталь подсвечена — и пальцем видно, что именно держишь (31.10).
    this.setItemHover(meta.itemId, object);
    // Пока тянут — живые размерные линии, как у стеллажа; статичные размеры
    // шкафа устарели бы и мешали, поэтому на время скрыты.
    this.dimensionRoot.visible = false;
    this.itemDragMeasures(this.itemDrag);
  };

  /** Курсор ушёл с 3D: подсветка и подсказка у детали гаснут. */
  private readonly handlePointerLeave = (event: PointerEvent) => {
    if (this.shelvingCellShelfDrag || this.shelvingDividerDrag) return;
    // Палец поднят — выделение с ручкой остаётся до следующего касания.
    if (event.pointerType === "touch") return;
    if (this.hoveredBoardState) this.options.onHoverShelvingBoard?.(null);
    if (!this.itemDrag) this.setItemHover(null);
  };

  /** Под курсором деталь наполнения шкафа — подсветить её; нет — погасить. */
  private updateItemHoverFromHit(hit: ReturnType<PlannerScene["pick"]>) {
    const meta = hit ? this.getPickMeta(hit.object) : null;
    const itemId = meta?.kind === "item" && meta.itemId ? meta.itemId : null;
    this.setItemHover(itemId);
  }

  /**
   * Подсветка детали наполнения: рамка и полупрозрачная заливка по её габариту,
   * поверх корпуса. target — что подсвечивать (деталь или её кусок при
   * перетаскивании); без target — сама деталь, у штанги — без одежды.
   */
  private setItemHover(itemId: string | null, target?: Object3D) {
    const object = itemId
      ? target ?? (() => {
        const group = this.itemObjects.get(itemId);
        return (group?.userData.selectionAnchor as Object3D | undefined) ?? group;
      })()
      : undefined;
    if (this.itemHover && (this.itemHover.itemId !== itemId || this.itemHover.target !== object)) {
      this.overlayRoot.remove(this.itemHover.helper, this.itemHover.fill);
      this.itemHover.helper.geometry.dispose();
      (this.itemHover.helper.material as LineBasicMaterial).dispose();
      this.itemHover.fill.geometry.dispose();
      (this.itemHover.fill.material as MeshBasicMaterial).dispose();
      this.itemHover = null;
      this.invalidate();
    }
    this.renderer.domElement.classList.toggle("is-hovering-item", Boolean(itemId && object));
    if (!itemId || !object || this.itemHover) return;
    const helper = new Box3Helper(new Box3(), 0xe07a12);
    const lines = helper.material as LineBasicMaterial;
    lines.depthTest = false;
    lines.transparent = true;
    helper.renderOrder = 7;
    const fill = new Mesh(new BoxGeometry(1, 1, 1), new MeshBasicMaterial({
      color: 0xf0a04a, transparent: true, opacity: 0.22, depthTest: false, depthWrite: false
    }));
    fill.renderOrder = 7;
    fill.raycast = () => {};
    this.itemHover = { itemId, target: object, helper, fill };
    this.overlayRoot.add(helper, fill);
    this.refreshItemHover();
  }

  /** Рамка подсветки идёт за деталью, пока её тянут. */
  private refreshItemHover() {
    if (!this.itemHover) return;
    const box = this.visualBounds(this.itemHover.target);
    if (box.isEmpty()) return;
    box.expandByScalar(mm(6));
    this.itemHover.helper.box.copy(box);
    box.getCenter(this.itemHover.fill.position);
    box.getSize(this.itemHover.fill.scale);
    this.invalidate();
  }

  private readonly handlePointerMove = (event: PointerEvent) => {
    if (!this.shelvingCellShelfDrag && !this.shelvingDividerDrag && !this.moduleDrag &&
      !this.itemDrag && !this.doorClick && event.buttons === 0 && !this.presentationMode) {
      const hit = this.pick(event);
      this.options.onHoverShelvingBoard?.(this.boardHoverFromHit(hit));
      this.updateItemHoverFromHit(hit);
    }
    if (this.shelvingCellShelfDrag?.axis === "x") {
      const drag = this.shelvingCellShelfDrag;
      if (this.dragNotStarted(drag, event)) return;
      const moved = this.dragPlaneDelta(event, drag.dragPlane);
      const raw = drag.startPosition + (moved ? moved.x : (event.clientX - drag.startClientX) * 3.25);
      const free = clamp(Math.round(raw / 5) * 5, drag.minPosition, drag.maxPosition);
      // Рядом стойка или перегородка в соседнем ряду — встаём на одну ось и подсвечиваем её зелёным.
      const aligned = this.nearestShelfLevel(drag.alignLevels, drag.cellLeftX + free,
        drag.cellLeftX + drag.minPosition, drag.cellLeftX + drag.maxPosition);
      const next = aligned ? aligned.y - drag.cellLeftX : free;
      if (next === drag.currentPosition) return;
      drag.currentPosition = next;
      drag.object.position.x = mm(drag.cellLeftX + next);
      this.showShelfAlignment(drag.moduleId, aligned);
      this.cellShelfDragMeasures(drag);
      this.refreshBoardHoverHelpers();
      this.refreshSelectionBox();
      this.invalidate();
      return;
    }
    if (this.shelvingCellShelfDrag) {
      const drag = this.shelvingCellShelfDrag;
      if (this.dragNotStarted(drag, event)) return;
      const moved = this.dragPlaneDelta(event, drag.dragPlane);
      const rawPosition = drag.startPosition +
        (moved ? moved.y : (drag.startClientY - event.clientY) * 3.25);
      const free = clamp(
        Math.round(rawPosition / 5) * 5,
        drag.minPosition,
        drag.maxPosition
      );
      // Рядом полка соседней ячейки или сетки — встаём вровень и подсвечиваем её.
      const aligned = this.nearestShelfLevel(drag.alignLevels, drag.cellStartY + free,
        drag.cellStartY + drag.minPosition, drag.cellStartY + drag.maxPosition);
      const next = aligned ? aligned.y - drag.cellStartY : free;
      if (next === drag.currentPosition) return;
      drag.currentPosition = next;
      drag.object.position.y = mm(drag.cellStartY + next);
      this.showShelfAlignment(drag.moduleId, aligned);
      this.cellShelfDragMeasures(drag);
      this.refreshBoardHoverHelpers();
      this.refreshSelectionBox();
      this.invalidate();
      return;
    }

    if (this.shelvingDividerDrag) {
      const drag = this.shelvingDividerDrag;
      if (this.dragNotStarted(drag, event)) return;
      const moved = this.dragPlaneDelta(event, drag.dragPlane);
      const rawPosition =
        drag.axis === "column"
          ? drag.startPosition + (moved ? moved.x : (event.clientX - drag.startClientX) * 3.25)
          : drag.startPosition + (moved ? moved.y : (drag.startClientY - event.clientY) * 3.25);
      const free = clamp(
        Math.round(rawPosition / 5) * 5,
        drag.minPosition,
        drag.maxPosition
      );
      // Полка — к полкам соседних ячеек, стойка — к стойкам и перегородкам других рядов и антресоли.
      const aligned = this.nearestShelfLevel(drag.alignLevels, free, drag.minPosition, drag.maxPosition);
      const next = aligned ? aligned.y : free;
      if (next === drag.currentPosition) return;
      drag.currentPosition = next;
      this.showShelfAlignment(drag.moduleId, aligned);
      if (drag.axis === "column") {
        const shift = mm(next - drag.startPosition);
        if (drag.movingSegments) drag.movingSegments.forEach(({ object, startX }) => { object.position.x = startX + shift; });
        else drag.object.position.x = mm(next);
      } else {
        drag.object.position.y = mm(next);
      }
      this.gridDividerDragMeasures(drag);
      this.refreshBoardHoverHelpers();
      this.refreshSelectionBox();
      this.invalidate();
      return;
    }

    if (this.moduleDrag) {
      const deltaX = event.clientX - this.moduleDrag.startClientX;
      if (Math.abs(deltaX) < 5 && !this.moduleDrag.moved) return;
      this.moduleDrag.moved = true;
      this.renderer.domElement.classList.remove("is-module-ready");
      this.renderer.domElement.classList.add("is-dragging-module");

      const distance = this.camera.position.distanceTo(this.controls.target);
      const millimetersPerPixel =
        Math.max(
          ((2 * distance * Math.tan((this.camera.fov * Math.PI) / 360)) /
            Math.max(1, this.container.clientHeight)) *
            1000,
          FULL_WALL_ROUTE_LENGTH_MM /
            Math.max(1, this.container.clientWidth * 0.86)
        );
      const desired =
        this.moduleDrag.startWallOffset + deltaX * millimetersPerPixel;
      const resolved = this.project
        ? resolveModuleGroupWallOffset(
            this.project,
            this.moduleDrag.moduleId,
            desired,
            deltaX
          )
        : desired;
      this.moduleDrag.currentWallOffset = resolved;
      this.previewModulePlacement(
        this.moduleDrag,
        resolved
      );
      this.refreshSelectionBox();
      this.invalidate();
      return;
    }

    if (this.doorClick) {
      const deltaX = event.clientX - this.doorClick.startClientX;
      const deltaY = event.clientY - this.doorClick.startClientY;
      if (Math.hypot(deltaX, deltaY) > 6) this.doorClick.moved = true;
      return;
    }

    if (!this.itemDrag) return;
    if (this.dragNotStarted(this.itemDrag, event)) return;
    const moved = this.dragPlaneDelta(event, this.itemDrag.dragPlane);
    const delta = moved ? moved.y : (this.itemDrag.startClientY - event.clientY) * 3.25;
    const desired = clamp(
      snap(this.itemDrag.startY + delta),
      this.itemDrag.minY,
      this.itemDrag.maxY
    );
    const module = this.itemDrag.previewModule ?? this.project?.modules.find(
      (candidate) => candidate.id === this.itemDrag?.moduleId
    );
    const verticalDirection = Math.sign(
      desired - this.itemDrag.lastDesiredY
    );
    // Через полку, ящик или пантограф деталь не перепрыгивает, пока курсор до
    // них не дошёл (видео Сергея 31.10) — см. dragInternalItemY.
    let next = module
      ? verticalDirection === 0 && this.itemDrag.type !== "divider"
        ? this.itemDrag.currentY
        : dragInternalItemY(
            module,
            this.itemDrag.itemId,
            desired,
            verticalDirection,
            this.itemDrag.currentY
          )
      : desired;
    // Рядом полка соседнего шкафа или стеллажа: встаём вровень, если на этой
    // высоте полку можно поставить (шаг отверстий и соседние детали не мешают).
    let aligned: ShelfAlignLevel | null = null;
    if (module && this.itemDrag.alignLevels.length) {
      const raw = clamp(this.itemDrag.startY + delta, this.itemDrag.minY, this.itemDrag.maxY);
      const level = this.nearestShelfLevel(this.itemDrag.alignLevels, raw, this.itemDrag.minY, this.itemDrag.maxY);
      if (level) {
        const reachable = resolveInternalItemY(module, this.itemDrag.itemId, level.y,
          Math.sign(level.y - this.itemDrag.currentY));
        if (Math.abs(reachable - level.y) < 0.5) {
          next = reachable;
          aligned = level;
        }
      }
      aligned ??= this.itemDrag.alignLevels.find((level) => Math.abs(level.y - next) < 0.5) ?? null;
    }
    this.showShelfAlignment(this.itemDrag.moduleId, aligned);
    this.itemDrag.lastDesiredY = desired;
    const desiredX = clamp(
      snap(
        this.itemDrag.startX +
          (moved ? moved.x : (event.clientX - this.itemDrag.startClientX) * 3.25)
      ),
      this.itemDrag.minX,
      this.itemDrag.maxX
    );
    const nextX =
      this.itemDrag.type === "divider"
        ? module
          ? clampDividerX(module, desiredX)
          : desiredX
        : this.itemDrag.currentX;
    // Перегородка — от опоры до опоры (22.10): перескакивает по пролётам и
    // растягивается под пролёт; края, упёршиеся в полки, — зелёные.
    let dividerSpan: { y: number; height: number } | null = null;
    if (this.itemDrag.type === "divider" && module) {
      const dividerItem = module.items.find((candidate) => candidate.id === this.itemDrag?.itemId);
      dividerSpan = dividerItem ? resolveDividerMove(module, dividerItem, desired, nextX) : null;
      if (dividerSpan) next = dividerSpan.y;
    }
    if (next === this.itemDrag.currentY && nextX === this.itemDrag.currentX) {
      return;
    }
    this.itemDrag.currentY = next;
    this.itemDrag.currentX = nextX;
    if (dividerSpan) this.previewDividerSpan(this.itemDrag, dividerSpan.y, dividerSpan.height);
    // Деталь доезжает до шага 32 мм плавно, а не прыжком (приёмка 04.10, wf-10).
    this.itemDragVisualTarget = { object: this.itemDrag.object, y: mm(next - this.itemDrag.positionOrigin) };
    if (this.itemDrag.type === "divider") this.itemDrag.object.position.x = mm(nextX);
    this.itemDragMeasures(this.itemDrag);
    if (module) {
      this.updateClothingVisibilityDuringItemDrag(
        module,
        this.itemDrag.itemId,
        next,
        nextX
      );
    }
    this.refreshSelectionBox();
    this.invalidate();
  };

  private updateClothingVisibilityDuringItemDrag(
    module: WardrobeModule,
    movingItemId: string,
    nextY: number,
    nextX: number
  ) {
    const previewModule: WardrobeModule = withStructuralPartitions({
      ...module,
      items: module.items.map((item) =>
        item.id === movingItemId
          ? { ...item, y: nextY, x: nextX }
          : item
      )
    });

    previewModule.items
      .filter((item) => item.type === "rail")
      .forEach((rail) => {
        const railObject = this.itemObjects.get(rail.id);
        if (!railObject) return;

        railObject.traverse((child) => {
          const garmentType = child.userData.garmentType as
            | InternalItem["clothes"]
            | undefined;
          if (!garmentType) return;
          const requiredHeight = Number(
            child.userData.requiredHeightMm
          );
          const verticallyFits = Number.isFinite(requiredHeight)
            ? availableHangingHeight(previewModule, rail) >= requiredHeight
            : garmentFitsBelowRail(previewModule, rail, garmentType);
          const halfWidth = Number(child.userData.horizontalHalfWidthMm);
          const slotX = child.position.x / MM;
          const garmentBottom =
            rail.y -
            (Number.isFinite(requiredHeight)
              ? requiredHeight
              : 0);
          const clearsDividers = previewModule.items
            .filter((candidate) => candidate.type === "divider")
            .every((divider) => {
              const dividerHeight =
                divider.height ??
                Math.min(1400, previewModule.height - 180);
              const dividerBottom = divider.y - dividerHeight / 2;
              const dividerTop = divider.y + dividerHeight / 2;
              const verticallyOverlaps =
                garmentBottom < dividerTop && rail.y > dividerBottom;
              if (!verticallyOverlaps) return true;
              return (
                Math.abs(slotX - (divider.x ?? 0)) >=
                (Number.isFinite(halfWidth) ? halfWidth : 48) + 9
              );
            });
          child.visible = verticallyFits && clearsDividers;
        });
      });

    previewModule.items.forEach((owner) => {
      const itemObject = this.itemObjects.get(owner.id);
      if (!itemObject) return;
      itemObject.traverse((child) => {
        if (child.userData.decorativeProp !== true) return;
        const localBottom = Number(child.userData.localBottomMm);
        const localTop = Number(child.userData.localTopMm);
        const horizontalMin = Number(child.userData.horizontalMinMm);
        const horizontalMax = Number(child.userData.horizontalMaxMm);
        if (
          !Number.isFinite(localBottom) ||
          !Number.isFinite(localTop) ||
          !Number.isFinite(horizontalMin) ||
          !Number.isFinite(horizontalMax)
        ) {
          child.visible = false;
          return;
        }
        child.visible = this.isDecorativeSpaceAvailable(
          previewModule,
          owner.id,
          owner.y + localBottom,
          owner.y + localTop,
          [horizontalMin, horizontalMax]
        );
      });
    });
  }

  private readonly handlePointerUp = (event: PointerEvent) => {
    const press = this.emptyPress;
    this.emptyPress = null;
    if (press && event.type === "pointerup" && press.pointerId === event.pointerId &&
      Math.hypot(event.clientX - press.x, event.clientY - press.y) < 6) {
      this.options.onEmptyClick?.();
    }
    if (this.shelvingCellShelfDrag) {
      this.finishShelvingCellShelfDrag(this.shelvingCellShelfDrag);
      void event;
      return;
    }

    if (this.shelvingDividerDrag) {
      this.finishShelvingDividerDrag(this.shelvingDividerDrag);
      void event;
      return;
    }

    if (this.doorClick) {
      const click = this.doorClick;
      this.doorClick = null;
      const drag = this.moduleDrag;
      const wasDragged = drag ? this.finishModuleDrag(drag) : false;

      if (!drag) {
        this.controls.enabled = true;
        if (this.renderer.domElement.hasPointerCapture(click.pointerId)) {
          this.renderer.domElement.releasePointerCapture(click.pointerId);
        }
      }

      if (!wasDragged && !click.moved) {
        this.toggleDoor(click.moduleId, click.doorIndex);
      }
      void event;
      return;
    }

    if (this.moduleDrag) {
      this.finishModuleDrag(this.moduleDrag);
      return;
    }

    if (!this.itemDrag) return;
    const drag = this.itemDrag;
    this.itemDrag = null;
    this.clearDividerContacts();
    this.clearDragMeasures();
    this.showShelfAlignment(drag.moduleId, null);
    this.dimensionRoot.visible = this.dimensionsVisible && !this.presentationMode;
    this.controls.enabled = true;
    this.renderer.domElement.classList.remove("is-dragging-item");
    // Палец подняли — подсветка гаснет; мышь — встанет заново при движении.
    this.setItemHover(null);
    if (this.renderer.domElement.hasPointerCapture(drag.pointerId)) {
      this.renderer.domElement.releasePointerCapture(drag.pointerId);
    }
    // Щелчок без перетаскивания по ящику, корзине или брючнице — выдвинуть её.
    if (drag.currentY === drag.startY && drag.currentX === drag.startX &&
      Math.hypot(event.clientX - drag.startClientX, event.clientY - drag.startClientY) < 6 &&
      PULL_OUT_TYPES.has(drag.type)) {
      this.toggleItemPullOut(drag.moduleId, drag.itemId, drag.pullOutDoorIndex);
    }
    if (drag.type === "divider") {
      const patch: Partial<InternalItem> = {};
      if (drag.currentY !== drag.startY) patch.y = drag.currentY;
      if (drag.currentX !== drag.startX) patch.x = drag.currentX;
      if (Object.keys(patch).length) {
        this.options.onCommitItemUpdate(drag.moduleId, drag.itemId, patch);
      }
    } else if (drag.currentY !== drag.startY) {
      if (drag.pieceX !== undefined && this.options.onCommitItemPieceMove) {
        this.options.onCommitItemPieceMove(drag.moduleId, drag.itemId, drag.pieceX, drag.currentY);
      } else {
        this.options.onCommitItemPosition(drag.moduleId, drag.itemId, drag.currentY);
      }
    }
    this.invalidate(12);
  };

  private dividerContacts: Group | null = null;

  /** Перегородка во время перетаскивания: высота пролёта и зелёные упоры сверху и снизу. */
  private previewDividerSpan(drag: ItemDragState, centerY: number, heightMm: number) {
    const module = this.project?.modules.find((candidate) => candidate.id === drag.moduleId);
    const item = module?.items.find((candidate) => candidate.id === drag.itemId);
    if (!item) return;
    const startHeight = item.height ?? Math.min(1400, (module?.height ?? 2000) - 180);
    drag.object.scale.y = heightMm / Math.max(1, startHeight);
    const parent = drag.object.parent;
    if (!parent) return;
    this.clearDividerContacts();
    const contacts = new Group();
    const material = this.getDividerContactMaterial();
    [centerY - heightMm / 2, centerY + heightMm / 2].forEach((level) => {
      const bar = new Mesh(new BoxGeometry(mm(70), mm(6), mm(40)), material);
      bar.position.set(drag.object.position.x, mm(level - drag.positionOrigin), drag.object.position.z + mm(40));
      bar.renderOrder = 30;
      contacts.add(bar);
    });
    parent.add(contacts);
    this.dividerContacts = contacts;
  }

  private clearDividerContacts() {
    const contacts = this.dividerContacts;
    if (!contacts) return;
    contacts.parent?.remove(contacts);
    contacts.traverse((object) => { if (object instanceof Mesh) object.geometry.dispose(); });
    this.dividerContacts = null;
  }

  private getDividerContactMaterial() {
    const key = "overlay:divider-contact";
    const cached = this.materialCache.get(key);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({ color: 0x2f9e44, depthTest: false, transparent: true, opacity: 0.95 });
    this.materialCache.set(key, material);
    return material;
  }

  private previewModulePlacement(
    drag: ModuleDragState,
    wallOffset: number
  ) {
    if (!this.project) return;
    const previewProject = structuredClone(this.project);
    const offsets = materializeWallOffsets(previewProject);
    previewProject.modules.forEach((module) => {
      module.wallOffset = offsets.get(module.id);
    });
    const module = previewProject.modules.find(
      (candidate) => candidate.id === drag.moduleId
    );
    if (!module) return;
    module.wallOffset = wallOffset;
    const targetPlacement = createCompositionLayout(previewProject).placements.find(
      (placement) => placement.module.id === drag.moduleId && placement.wing === 0
    );
    if (!targetPlacement) return;

    const sourcePoint = new Vector3(
      drag.sourcePlacement.x,
      0,
      drag.sourcePlacement.z
    );
    const targetPoint = new Vector3(
      targetPlacement.x,
      0,
      targetPlacement.z
    );
    const yawDelta = targetPlacement.yaw - drag.sourcePlacement.yaw;
    const rotatedSource = sourcePoint
      .clone()
      .applyAxisAngle(new Vector3(0, 1, 0), yawDelta);

    const transformedPosition = targetPoint.sub(rotatedSource);
    drag.object.rotation.y = yawDelta;
    drag.object.position.copy(transformedPosition);
    drag.attachedObjects.forEach((attached) => {
      attached.object.rotation.y = yawDelta;
      attached.object.position.copy(transformedPosition);
    });
  }

  private pick(event: Pick<PointerEvent, "clientX" | "clientY">) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    return this.raycaster.intersectObject(this.modelRoot, true)[0] ?? null;
  }

  /** Насколько деталь важнее для пальца: полки и стойки — главное, ячейка — последнее. */
  private touchPickPriority(hit: ReturnType<PlannerScene["pick"]>) {
    const meta = hit ? this.getPickMeta(hit.object) : null;
    if (!meta) return -1;
    if (meta.kind === "shelvingCellShelf" || meta.kind === "shelvingCellDivider" || meta.kind === "shelvingDivider" || meta.boardRef) return 3;
    if (meta.itemId) return 2;
    if (meta.kind === "door") return 1;
    return 0;
  }

  /**
   * Палец шире полки (полка на экране телефона — 8–10 px, палец — около 45):
   * при касании ищем полку или стойку в радиусе ~22 px и берём её, а не
   * ячейку под пальцем (замечание Сергея № 32). Мышь — как раньше.
   */
  private pickForPointer(event: PointerEvent) {
    const direct = this.pick(event);
    if (event.pointerType !== "touch") return direct;
    let best = direct;
    let bestPriority = this.touchPickPriority(direct);
    if (bestPriority >= 3) return direct;
    for (const radius of [11, 22]) {
      for (let step = 0; step < 8; step += 1) {
        const angle = (step / 8) * Math.PI * 2;
        const hit = this.pick({ clientX: event.clientX + Math.cos(angle) * radius, clientY: event.clientY + Math.sin(angle) * radius });
        const priority = this.touchPickPriority(hit);
        if (priority > bestPriority) { best = hit; bestPriority = priority; }
      }
      if (bestPriority >= 3) break;
    }
    // Луч заканчиваем там же, куда указывает палец: от него считаются плоскости перетаскивания.
    this.pick(event);
    return best;
  }

  /** Точка указателя на фасадной плоскости owner (z = 0), мм в его координатах. */
  private pointerOnDragPlane(event: Pick<PointerEvent, "clientX" | "clientY">, owner: Object3D) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    owner.updateWorldMatrix(true, false);
    const normal = new Vector3(0, 0, 1).transformDirection(owner.matrixWorld);
    // Почти вдоль плоскости — смещение неустойчивое, лучше старый способ.
    if (Math.abs(normal.dot(this.raycaster.ray.direction)) < 0.12) return null;
    const plane = new Plane().setFromNormalAndCoplanarPoint(normal, owner.localToWorld(new Vector3()));
    const hit = this.raycaster.ray.intersectPlane(plane, new Vector3());
    return hit ? owner.worldToLocal(hit).divideScalar(MM) : null;
  }

  private beginDragPlane(event: PointerEvent, owner: Object3D | null): DragPlane | null {
    if (!owner) return null;
    const start = this.pointerOnDragPlane(event, owner);
    return start ? { owner, start } : null;
  }

  /** Смещение указателя от начала перетаскивания, мм; null — посчитать по пикселям. */
  private dragPlaneDelta(event: PointerEvent, plane: DragPlane | null) {
    if (!plane) return null;
    const current = this.pointerOnDragPlane(event, plane.owner);
    return current ? current.sub(plane.start) : null;
  }

  private getPickMeta(object: Object3D): PickMeta | null {
    let current: Object3D | null = object;
    while (current) {
      if (current.userData.pick) return current.userData.pick as PickMeta;
      current = current.parent;
    }
    return null;
  }

  private addDetailPanel(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta?: PickMeta
  ) {
    const mesh = this.addPanel(parent, x, y, z, width, height, depth, material, meta);
    mesh.castShadow = false;
    return mesh;
  }

  private addRoundedRectPath(
    path: Shape | Path,
    left: number,
    bottom: number,
    width: number,
    height: number,
    radius: number,
    clockwise = false
  ) {
    const right = left + width;
    const top = bottom + height;
    const safeRadius = Math.max(
      0,
      Math.min(radius, width * 0.49, height * 0.49)
    );
    if (clockwise) {
      path.moveTo(left + safeRadius, bottom);
      path.quadraticCurveTo(left, bottom, left, bottom + safeRadius);
      path.lineTo(left, top - safeRadius);
      path.quadraticCurveTo(left, top, left + safeRadius, top);
      path.lineTo(right - safeRadius, top);
      path.quadraticCurveTo(right, top, right, top - safeRadius);
      path.lineTo(right, bottom + safeRadius);
      path.quadraticCurveTo(right, bottom, right - safeRadius, bottom);
      path.lineTo(left + safeRadius, bottom);
      return;
    }
    path.moveTo(left + safeRadius, bottom);
    path.lineTo(right - safeRadius, bottom);
    path.quadraticCurveTo(right, bottom, right, bottom + safeRadius);
    path.lineTo(right, top - safeRadius);
    path.quadraticCurveTo(right, top, right - safeRadius, top);
    path.lineTo(left + safeRadius, top);
    path.quadraticCurveTo(left, top, left, top - safeRadius);
    path.lineTo(left, bottom + safeRadius);
    path.quadraticCurveTo(left, bottom, left + safeRadius, bottom);
  }

  private getRoutedHandleCavityMaterial(
    facadeMaterial: PanelMaterial
  ): PanelMaterial {
    const cacheKey = `routed-handle-cavity:${facadeMaterial.uuid}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached;
    const material = facadeMaterial.clone() as PanelMaterial;
    material.color.multiplyScalar(facadeMaterial.map ? 0.52 : 0.62);
    if (material instanceof MeshStandardMaterial) {
      material.roughness = 0.82;
      material.metalness = 0;
    }
    material.userData = { ...facadeMaterial.userData, shared: true };
    material.needsUpdate = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private addRoutedFacadePanel(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    facadeMaterial: PanelMaterial,
    meta: PickMeta,
    cut: IntegratedHandleCut,
    outerRadius: number
  ) {
    const safeDepth = Math.max(mm(8), depth);
    const cutDepth = clamp(cut.depth, mm(6), safeDepth - mm(4));
    const backingDepth = Math.max(mm(4), safeDepth - cutDepth);
    const rearZ = z - safeDepth / 2;
    const pocketBackZ = rearZ + backingDepth;
    const backing = this.addRoundedPanel(
      parent,
      x,
      y,
      rearZ + backingDepth / 2,
      width,
      height,
      backingDepth,
      facadeMaterial,
      meta,
      Math.min(outerRadius, backingDepth * 0.42)
    );

    const shellShape = new Shape();
    this.addRoundedRectPath(
      shellShape,
      -width / 2,
      -height / 2,
      width,
      height,
      outerRadius
    );
    const hole = new Path();
    const localCutX = cut.x - x;
    const localCutY = cut.y - y;
    this.addRoundedRectPath(
      hole,
      localCutX - cut.width / 2,
      localCutY - cut.height / 2,
      cut.width,
      cut.height,
      Math.min(cut.width, cut.height) * 0.46,
      true
    );
    shellShape.holes.push(hole);

    const shell = new Mesh(
      new ExtrudeGeometry(shellShape, {
        depth: cutDepth,
        bevelEnabled: false,
        curveSegments: 12,
        steps: 1
      }),
      facadeMaterial
    );
    shell.position.set(x, y, pocketBackZ);
    shell.castShadow = true;
    shell.receiveShadow = true;
    shell.userData.pick = meta;
    this.applyPhysicalWoodUvs(shell, x, y, z, parent, meta);
    parent.add(shell);

    const cavityMaterial = this.getRoutedHandleCavityMaterial(facadeMaterial);
    const cavityFloor = this.addRoundedPanel(
      parent,
      cut.x,
      cut.y,
      pocketBackZ + mm(0.35),
      Math.max(mm(4), cut.width - mm(1.2)),
      Math.max(mm(4), cut.height - mm(1.2)),
      mm(0.7),
      cavityMaterial,
      meta,
      Math.min(cut.width, cut.height) * 0.42
    );
    cavityFloor.castShadow = false;
    cavityFloor.receiveShadow = true;

    const gripDepth = Math.max(mm(3), cutDepth * 0.56);
    const grip = this.addRoundedPanel(
      parent,
      cut.x + cut.gripOffsetX,
      cut.y + cut.gripOffsetY,
      pocketBackZ + mm(0.8) + gripDepth / 2,
      cut.gripWidth,
      cut.gripHeight,
      gripDepth,
      facadeMaterial,
      meta,
      Math.min(cut.gripWidth, cut.gripHeight, gripDepth) * 0.38
    );
    grip.castShadow = true;
    grip.receiveShadow = true;
    return backing;
  }

  private addRoundedPanel(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta?: PickMeta,
    radius = mm(2)
  ) {
    const safeWidth = Math.max(0.001, width);
    const safeHeight = Math.max(0.001, height);
    const safeDepth = Math.max(0.001, depth);
    const safeRadius = Math.min(radius, safeWidth * 0.45, safeHeight * 0.45, safeDepth * 0.45);
    const mesh = new Mesh(
      safeRadius > mm(0.35)
        ? new RoundedBoxGeometry(safeWidth, safeHeight, safeDepth, 5, safeRadius)
        : new BoxGeometry(safeWidth, safeHeight, safeDepth),
      material
    );
    this.applyPhysicalWoodUvs(mesh, x, y, z, parent, meta);
    this.applyBoardEdgeMaterial(mesh, safeWidth, safeHeight, safeDepth);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (meta) mesh.userData.pick = meta;
    parent.add(mesh);
    return mesh;
  }

  private addPanel(
    parent: Group,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    depth: number,
    material: PanelMaterial,
    meta?: PickMeta
  ) {
    const safeWidth = Math.max(0.001, width);
    const safeHeight = Math.max(0.001, height);
    const safeDepth = Math.max(0.001, depth);
    const minDimension = Math.min(safeWidth, safeHeight, safeDepth);
    const maxDimension = Math.max(safeWidth, safeHeight, safeDepth);
    const receivesFurnitureBevel =
      Boolean(meta) &&
      minDimension >= mm(11) &&
      maxDimension >= mm(90) &&
      !(material instanceof MeshBasicMaterial) &&
      !material.transparent;
    const edgeRadius = Math.min(mm(0.6), minDimension * 0.055);
    const mesh = new Mesh(
      receivesFurnitureBevel
        ? new RoundedBoxGeometry(
            safeWidth,
            safeHeight,
            safeDepth,
            3,
            edgeRadius
          )
        : new BoxGeometry(safeWidth, safeHeight, safeDepth),
      material
    );
    this.applyPhysicalWoodUvs(mesh, x, y, z, parent, meta);
    this.applyBoardEdgeMaterial(mesh, safeWidth, safeHeight, safeDepth);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (meta) mesh.userData.pick = meta;
    parent.add(mesh);
    return mesh;
  }

  /**
   * Материал плиты по артикулу декора Egger.
   *
   * У фактурных декоров образец натягивается физическим размером, чтобы
   * рисунок не «плыл» на широких панелях; у однотонных достаточно цвета —
   * их образцы и есть сплошная заливка, текстуру для них никто не хранит.
   */
  private getDecorMaterial(code: string): MeshStandardMaterial {
    const base = this.getBaseDecorMaterial(code);
    const override = this.edgeOverride;
    if (!override || override.code !== code) return base;
    // Тот же декор, но торцы плит — кромкой в цвет фасадов или своего декора.
    const cacheKey = `decor:${code}|edge:${override.key}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = base.clone();
    material.userData = { ...base.userData, edgeMaterial: override.edge, shared: true };
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Кромка плиты корпуса: своя у изделия с выбранной кромкой (24.10), иначе — в декоре плиты. */
  private carcassEdgeMaterial(code: string): MeshPhysicalMaterial {
    const override = this.edgeOverride;
    return override && override.code === code ? override.edge : this.getDecorEdgeMaterial(code);
  }

  /** Сплошная кромка заданного цвета — в цвет фасадов в эмали. */
  private getSolidEdgeMaterial(hex: string): MeshPhysicalMaterial {
    const cacheKey = `solid-edge:${hex}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshPhysicalMaterial;
    const material = new MeshPhysicalMaterial({ color: hex, roughness: 0.5, metalness: 0, clearcoat: 0.04, clearcoatRoughness: 0.6, envMapIntensity: 0.72 });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getBaseDecorMaterial(code: string): MeshStandardMaterial {
    const cacheKey = `decor:${code}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const decor = resolveDecor(code);
    const textureUrl = decorTextureUrl(decor);
    const wood = decor.group === "wood";
    const fabric = decor.group === "fabric";
    const sample = decorTextureSize(decor);
    const material = new MeshPhysicalMaterial({
      ...(textureUrl
        // Grain orientation is handled once, in the board's UV coordinates.
        ? { map: this.loadPbrTexture(textureUrl, true) }
        : { color: decor.hex }),
      roughness: fabric ? 0.81 : decor.group === "plain" ? 0.46 : 0.56,
      metalness: decor.group === "metal" ? 0.35 : 0,
      clearcoat: fabric ? 0.01 : 0.035,
      clearcoatRoughness: fabric ? 0.94 : 0.6,
      envMapIntensity: decor.group === "plain" ? 0.72 : 0.78
    });
    if (material.map) {
      // Рельеф повторяет сам декор: альбедо при этом не перекрашивается.
      material.bumpMap = material.map.userData.decorRelief as Texture;
      material.roughnessMap = material.map.userData.decorRoughness as Texture;
      material.bumpScale = mm(fabric ? 0.12 : 0.055);
      if (wood) {
        material.userData.physicalWood = true;
        material.userData.woodTileMeters = sample.height;
        material.userData.woodWidthMeters = sample.width;
      } else {
        material.userData.physicalDecor = true;
        material.userData.decorTileMeters = sample.height;
        material.userData.woodWidthMeters = sample.width;
      }
      material.userData.edgeMaterial = this.getDecorEdgeMaterial(code);
    }
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Совместимость для мест, куда артикул декора ещё не дошёл. */
  private getCarcassMaterial(key: MaterialType): MeshStandardMaterial {
    return this.getDecorMaterial(LEGACY_MATERIAL_DECOR[key]);
  }

  private getEdgeMaterial(key: MaterialType): MeshStandardMaterial {
    return this.getDecorEdgeMaterial(LEGACY_MATERIAL_DECOR[key]);
  }

  private getDecorEdgeMaterial(code: string): MeshPhysicalMaterial {
    const cacheKey = `decor-edge:${code}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshPhysicalMaterial;
    const decor = resolveDecor(code);
    const material = new MeshPhysicalMaterial({
      color: decor.hex, roughness: 0.58, metalness: decor.group === "metal" ? 0.2 : 0,
      clearcoat: 0.035, clearcoatRoughness: 0.65, envMapIntensity: 0.72
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private applyBoardEdgeMaterial(mesh: Mesh, width: number, height: number, depth: number) {
    if (Array.isArray(mesh.material)) return;
    const faceMaterial = mesh.material;
    const edge = faceMaterial.userData.edgeMaterial;
    if (!(edge instanceof Material)) return;
    const spans = [width, height, depth];
    const thicknessAxis = spans.indexOf(Math.min(...spans));
    mesh.material = Array.from({ length: 6 }, (_, face) => Math.floor(face / 2) === thicknessAxis ? faceMaterial : edge);
  }

  private getShelvingFinishMaterial(finishHex: string): MeshPhysicalMaterial {
    const color = normalizeShelvingColorHex(finishHex) ?? "#F2F0E9";
    const cacheKey = `shelving-finish:${color}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshPhysicalMaterial;
    const material = new MeshPhysicalMaterial({
      color,
      roughness: 0.52,
      metalness: 0,
      clearcoat: 0.045,
      clearcoatRoughness: 0.68,
      envMapIntensity: 0.72
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getPlinthDecorMaterial(code: string): MeshStandardMaterial {
    const decor = resolveDecor(code);
    if (decor.group !== "plain") return this.getDecorMaterial(code);
    const cacheKey = `plinth-decor:${code}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({
      color: decor.hex,
      bumpMap: this.createWoodBumpTexture(`plinth:${decor.code}`),
      bumpScale: mm(0.22),
      roughness: 0.78,
      metalness: 0
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getPlinthMaterial(key: MaterialType): MeshStandardMaterial {
    const cacheKey = `plinth:${key}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const source = MATERIALS[key];
    if (
      key === "warmOak" ||
      key === "linen" ||
      key === "matteWhite" ||
      key === "walnut"
    ) {
      return this.getCarcassMaterial(key);
    }
    const material =
      key === "graphite"
        ? new MeshStandardMaterial({
            color: new Color(source.color).multiplyScalar(0.88),
            roughness: 0.92,
            metalness: 0
          })
        : new MeshStandardMaterial({
            map: this.createWoodTexture(
              new Color(source.color).multiplyScalar(0.9).getStyle(),
              source.grain,
              true
            ),
            bumpMap: this.createWoodBumpTexture(`plinth:${key}:${source.grain}`),
            bumpScale: mm(0.22),
            roughness: 0.78,
            metalness: 0
          });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private loadPbrTexture(
    path: string,
    colorTexture = false
  ): Texture {
    const detailSize = 1024;
    const reliefCanvas = document.createElement("canvas");
    const roughnessCanvas = document.createElement("canvas");
    reliefCanvas.width = roughnessCanvas.width = detailSize;
    reliefCanvas.height = roughnessCanvas.height = detailSize;
    const reliefContext = reliefCanvas.getContext("2d");
    const roughnessContext = roughnessCanvas.getContext("2d");
    if (reliefContext) {
      reliefContext.fillStyle = "#808080";
      reliefContext.fillRect(0, 0, detailSize, detailSize);
    }
    if (roughnessContext) {
      roughnessContext.fillStyle = "#eeeeee";
      roughnessContext.fillRect(0, 0, detailSize, detailSize);
    }
    const relief = new CanvasTexture(reliefCanvas);
    const roughness = new CanvasTexture(roughnessCanvas);
    const texture = this.textureLoader.load(
      path,
      (loaded) => {
        if (this.disposed) {
          loaded.dispose();
          return;
        }
        if (reliefContext && roughnessContext) {
          // Пропорции рельефа должны совпадать с рисунком: квадратный холст
          // растягивал бы поры поперёк волокна.
          const source = loaded.image as { width?: number; height?: number };
          const sourceWidth = Math.max(1, source.width ?? detailSize);
          const sourceHeight = Math.max(1, source.height ?? detailSize);
          const reliefWidth = Math.min(detailSize, sourceWidth);
          const reliefHeight = Math.max(
            1,
            Math.round(reliefWidth * (sourceHeight / sourceWidth))
          );
          reliefCanvas.width = roughnessCanvas.width = reliefWidth;
          reliefCanvas.height = roughnessCanvas.height = reliefHeight;
          reliefContext.drawImage(loaded.image as CanvasImageSource, 0, 0, reliefWidth, reliefHeight);
          const pixels = reliefContext.getImageData(0, 0, reliefWidth, reliefHeight);
          const tilePixels = tileableDecorPixels(pixels.data, reliefWidth, reliefHeight);
          const colorCanvas = document.createElement("canvas");
          colorCanvas.width = reliefWidth; colorCanvas.height = reliefHeight;
          const colorContext = colorCanvas.getContext("2d");
          if (colorContext) {
            const colorPixels = colorContext.createImageData(reliefWidth, reliefHeight);
            colorPixels.data.set(tilePixels);
            colorContext.putImageData(colorPixels, 0, 0);
            loaded.image = colorCanvas;
            loaded.needsUpdate = true;
          }
          const roughnessPixels = roughnessContext.createImageData(reliefWidth, reliefHeight);
          const detail = decorDetailPixels(tilePixels, reliefWidth, reliefHeight);
          pixels.data.set(detail.relief);
          roughnessPixels.data.set(detail.roughness);
          reliefContext.putImageData(pixels, 0, 0);
          roughnessContext.putImageData(roughnessPixels, 0, 0);
          relief.needsUpdate = roughness.needsUpdate = true;
        }
        this.invalidate(24);
      },
      undefined,
      (error) =>
        this.reportAssetWarning(
          {
            code: "texture-load-failed",
            message:
              "Один из декоров не загрузился. Обновите приложение перед отправкой проекта.",
            resource: path
          },
          error
        )
    );
    if (colorTexture) texture.colorSpace = SRGBColorSpace;
    [texture, relief, roughness].forEach((surface) => {
      surface.wrapS = surface.wrapT = RepeatWrapping;
      surface.anisotropy = Math.min(12, this.renderer.capabilities.getMaxAnisotropy());
    });
    texture.userData.decorRelief = relief;
    texture.userData.decorRoughness = roughness;
    return texture;
  }

  private applyPhysicalWoodUvs(
    mesh: Mesh,
    panelX: number,
    panelY: number,
    panelZ: number,
    parent?: Object3D,
    meta?: PickMeta
  ) {
    const material = mesh.material;
    if (
      Array.isArray(material) ||
      material.userData.physicalWood !== true &&
      material.userData.physicalDecor !== true
    ) {
      return;
    }
    const geometry = mesh.geometry as BufferGeometry;
    const tileMeters =
      Number(material.userData.decorTileMeters) ||
      Number(material.userData.woodTileMeters) ||
      0.7;
    // Use construction-local ancestry, not animated world positions or local
    // zero alone: all hinged leaves and divider segments otherwise share a cut.
    const path: string[] = [];
    for (let ancestor = parent; ancestor && ancestor !== this.modelRoot; ancestor = ancestor.parent ?? undefined) {
      const pick = ancestor.userData.pick as PickMeta | undefined;
      // Room placement must not affect the cut when this board is rebuilt.
      if (ancestor.name === meta?.moduleId || ancestor.name === `${meta?.moduleId}-corner`) {
        path.push(ancestor.name);
        break;
      }
      path.push(`${ancestor.name}:${pick?.cellId ?? ""}:${ancestor.position.x}:${ancestor.position.y}`);
    }
    const offset = boardTextureOffset(`${meta?.moduleId ?? ""}:${meta?.cellId ?? ""}:${meta?.doorIndex ?? ""}:${path.join("/")}:${panelX}:${panelY}:${panelZ}`);

    applyBoardTextureUvs(geometry,
      Number(material.userData.woodWidthMeters) || tileMeters,
      tileMeters, material.userData.physicalWood === true, offset.u, offset.v);
  }

  private getFacadeEdgeMaterial(
    facade: WardrobeModule["facade"],
    materialKey: string
  ): MeshStandardMaterial {
    if (facade === "wood") return this.getDecorEdgeMaterial(materialKey);
    const cacheKey = `facade-edge:${facade}:${materialKey}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material =
      facade === "mirror" || facade === "glass"
        ? new MeshStandardMaterial({
            color: facade === "mirror" ? 0x9fa8aa : 0x6d7271,
            roughness: facade === "glass" ? 0.24 : 0.26,
            metalness: facade === "glass" ? 0.78 : 0.42,
            envMapIntensity: facade === "glass" ? 1.05 : 0.7
          })
        : facade === "satin"
          ? new MeshStandardMaterial({ color: 0xc7ccc8, roughness: 0.6 })
          : new MeshStandardMaterial({ color: 0xe4ded4, roughness: 0.18 });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getMirrorReflectionMaterial(): MeshBasicMaterial {
    const cacheKey = "mirror-reflection-overlay";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({
      map: this.createMirrorReflectionTexture(),
      transparent: true,
      opacity: 0.018,
      depthWrite: false
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getSoftShadowMaterial(opacity: number) {
    const normalized = Math.round(opacity * 100) / 100;
    const cacheKey = `soft-shadow:${normalized}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({
      color: 0x070503,
      transparent: true,
      opacity: normalized,
      depthWrite: false
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getHighlightMaterial(opacity: number) {
    const normalized = Math.round(opacity * 100) / 100;
    const cacheKey = `highlight:${normalized}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: normalized,
      depthWrite: false
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getBackLineMaterial() {
    const cacheKey = "back-line";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({
      color: 0x8f806d,
      transparent: true,
      opacity: 0.14,
      depthWrite: false
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getBackMaterial(key: MaterialType): MeshStandardMaterial {
    return this.getCarcassMaterial(key);
  }

  private getDrawerMaterial(): MeshStandardMaterial {
    const cacheKey = "drawer";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({
      map: this.createBoardFiberTexture("#cbb38f", "#8e7252"),
      roughness: 0.78
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /**
   * Стеклянная полка ячейки: прозрачное стекло, зеленоватая кромка спереди и
   * четыре полкодержателя под ним. Тени почти не даёт.
   */
  private addGlassShelf(parent: Group, x: number, z: number, width: number, thickness: number, depth: number) {
    const pane = this.addPanel(parent, x, 0, z, width, thickness, depth, this.getGlassShelfMaterial("pane"));
    pane.castShadow = false;
    pane.renderOrder = 3;
    pane.userData.glassSurface = true;
    const edge = this.addPanel(parent, x, 0, z + depth / 2 - mm(0.6), width, thickness, mm(1.2),
      this.getGlassShelfMaterial("edge"));
    edge.castShadow = false;
    edge.renderOrder = 3;
    // Полкодержатель для стекла: штырь из боковины и прозрачная присоска под
    // стеклом. Раньше были кубики в 14 мм от стенки — снизу выглядели как
    // непонятные крепления (приёмка 05.10, sc-14).
    const metal = this.getMetalMaterial(0xb9b7b2);
    const pad = this.getGlassShelfMaterial("edge");
    [-1, 1].forEach((sideX) => [-1, 1].forEach((sideZ) => {
      const pinZ = z + sideZ * (depth / 2 - mm(40));
      const pin = new Mesh(new CylinderGeometry(mm(2.5), mm(2.5), mm(10), 12), metal);
      pin.rotation.z = Math.PI / 2;
      pin.position.set(x + sideX * (width / 2 - mm(5)), -thickness / 2 - mm(2.8), pinZ);
      pin.castShadow = false;
      parent.add(pin);
      const cup = new Mesh(new CylinderGeometry(mm(4.5), mm(4.5), mm(1.6), 16), pad);
      cup.position.set(x + sideX * (width / 2 - mm(6)), -thickness / 2 - mm(0.3), pinZ);
      cup.castShadow = false;
      cup.renderOrder = 3;
      parent.add(cup);
    }));
  }

  private getGlassShelfMaterial(part: "pane" | "edge"): MeshPhysicalMaterial {
    const cacheKey = `glass-shelf:${part}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshPhysicalMaterial;
    const material = part === "pane"
      ? new MeshPhysicalMaterial({
          color: 0xd6e6e1,
          roughness: 0.08,
          metalness: 0,
          transparent: true,
          opacity: 0.26,
          depthWrite: false,
          clearcoat: 0.8,
          clearcoatRoughness: 0.1,
          ior: 1.52,
          reflectivity: 0.6,
          envMapIntensity: 0.95,
          side: DoubleSide
        })
      : new MeshPhysicalMaterial({
          color: 0x8fb5a8,
          roughness: 0.15,
          metalness: 0,
          transparent: true,
          opacity: 0.7,
          depthWrite: false
        });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Эко-кожа корзины; stitch — светлая строчка. */
  private getLeatherMaterial(stitch = false): MeshStandardMaterial {
    const cacheKey = stitch ? "leather-stitch" : "leather";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({
      color: stitch ? 0x4a3020 : 0x7a5238,
      roughness: stitch ? 0.8 : 0.58,
      metalness: 0
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Ткань брюк на брючнице: четыре спокойных цвета. */
  private getTrouserMaterial(variant: number): MeshStandardMaterial {
    const colors = [0x3b4250, 0x6b6257, 0x2f3032, 0x8a8174];
    const color = colors[Math.abs(variant) % colors.length]!;
    const cacheKey = `trousers:${color}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({ color, roughness: 0.92, metalness: 0 });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Крышка корпуса подъёмника: матовый светло-серый пластик. */
  private getMechanismCoverMaterial(): MeshStandardMaterial {
    const cacheKey = "mechanism-cover";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({ color: 0xc9cbcd, roughness: 0.62, metalness: 0.08 });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Хром: штанга и её держатели — светлый металл. */
  private getChromeMaterial(): MeshStandardMaterial {
    return this.getMetalMaterial(0xd9dcdf);
  }

  /**
   * Петли (29.10): серебристые — никель; чёрные — металл с чёрным покрытием
   * и мягким полуглянцем (как Onyx Black у Blum): «тёмный металл» отражал
   * почти ничего и выглядел плоской кляксой.
   */
  private getHingeMaterial(tone: "silver" | "black"): MeshStandardMaterial {
    if (tone === "silver") return this.getMetalMaterial(0xc6cac8);
    const cacheKey = "hinge:black";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshPhysicalMaterial({
      color: 0x1d1e20,
      roughness: 0.42,
      metalness: 0.3,
      clearcoat: 0.6,
      clearcoatRoughness: 0.22,
      envMapIntensity: 1.15
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Стенки и дно гнезда под чашку — затенённый металл чашки. */
  private getHingePocketMaterial(tone: "silver" | "black"): MeshStandardMaterial {
    const cacheKey = `hinge-pocket:${tone}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({
      color: tone === "black" ? 0x141516 : 0x7c807f,
      roughness: 0.55,
      metalness: 0.5,
      // Стенки видны изнутри, дно — со стороны отверстия: обе стороны.
      side: DoubleSide
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Метка отверстия: видимый круг на задней плоскости двери пишет 1 в трафарет. */
  private getHingeHoleMaskMaterial(): MeshBasicMaterial {
    const cacheKey = "hinge-hole:mask";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({
      colorWrite: false,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -4,
      stencilWrite: true,
      stencilRef: 1,
      stencilFunc: AlwaysStencilFunc,
      stencilZPass: ReplaceStencilOp
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** В отмеченном отверстии глубина сбрасывается — за ним видно чашку. */
  private getHingeHoleResetMaterial(): ShaderMaterial {
    const cacheKey = "hinge-hole:reset";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as unknown as ShaderMaterial;
    const material = new ShaderMaterial({
      vertexShader: "void main() { gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }",
      fragmentShader: "void main() { gl_FragColor = vec4( 0.0 ); gl_FragDepth = 1.0; }",
      colorWrite: false,
      depthWrite: true,
      depthFunc: AlwaysDepth,
      stencilWrite: true,
      stencilWriteMask: 0x00,
      stencilRef: 1,
      stencilFunc: EqualStencilFunc,
      stencilFail: KeepStencilOp,
      stencilZFail: KeepStencilOp,
      stencilZPass: KeepStencilOp
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material as unknown as PanelMaterial);
    return material;
  }

  private getMetalMaterial(color = 0x323334): MeshStandardMaterial {
    const cacheKey = `metal:${color}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({
      color,
      roughness: 0.28,
      metalness: 0.82
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getHardwareMaterial(): MeshStandardMaterial {
    const cacheKey = "hardware-neutral";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const material = new MeshStandardMaterial({
      color: 0x777873,
      roughness: 0.38,
      metalness: 0.68
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  /** Невидимая полоса перед деталью сетки: ловит мышь, в рамку подсветки не входит. */
  private createGridHitStrip(width: number, height: number, meta: PickMeta) {
    const holder = new Group();
    holder.userData.hitStrip = true;
    const strip = new Mesh(new BoxGeometry(Math.max(mm(10), width), Math.max(mm(10), height), mm(30)), this.getShelvingCellHitMaterial());
    strip.castShadow = false;
    strip.receiveShadow = false;
    strip.userData.pick = meta;
    strip.userData.hitStrip = true;
    holder.add(strip);
    return holder;
  }

  private getShelvingCellHitMaterial(): MeshBasicMaterial {
    const cacheKey = "shelving-cell-hit";
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshBasicMaterial;
    const material = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      colorWrite: false
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private getFacadeMaterial(
    facade: WardrobeModule["facade"],
    materialKey: string,
    facadeColor: FacadeColorType = "white",
    _facadePattern: FacadePatternType = "plain",
    _panelWidth = mm(500),
    facadeColorHex?: string
  ): MeshStandardMaterial | MeshPhysicalMaterial {
    let material: MeshStandardMaterial | MeshPhysicalMaterial;
    if (facade === "wood") {
      // Фасад — со своей кромкой: подмена кромки корпуса его не касается.
      material = this.getBaseDecorMaterial(materialKey);
    } else {
      const paintHex = facadePaintHex({ facadeColor, facadeColorHex });
      const cacheKey = `facade:${facade}:${facadeColor}:${paintHex}`;
      const cached = this.materialCache.get(cacheKey);
      if (cached) {
        material = cached as MeshStandardMaterial | MeshPhysicalMaterial;
      } else {
        material =
          facade === "mirror"
            ? new MeshPhysicalMaterial({
                color: 0xffffff,
                roughness: 0.045,
                metalness: 0.1,
                clearcoat: 1,
                clearcoatRoughness: 0.02,
                reflectivity: 0.82,
                envMapIntensity: 0.62
              })
            : facade === "glass"
              ? new MeshPhysicalMaterial({
                  color: 0xdce8e5,
                  roughness: 0.11,
                  metalness: 0,
                  transparent: true,
                  opacity: 0.18,
                  depthWrite: false,
                  clearcoat: 0.72,
                  clearcoatRoughness: 0.12,
                  ior: 1.52,
                  reflectivity: 0.62,
                  envMapIntensity: 0.92,
                  side: DoubleSide
                })
              : facade === "satin"
                ? new MeshPhysicalMaterial({
                    color: paintHex,
                    roughness: 0.4,
                    metalness: 0,
                    clearcoat: 0.14,
                    clearcoatRoughness: 0.46,
                    ior: 1.47,
                    envMapIntensity: 0.78
                  })
                : new MeshPhysicalMaterial({
                    color: paintHex,
                    roughness: 0.34,
                    metalness: 0,
                    clearcoat: 0.2,
                    clearcoatRoughness: 0.4,
                    ior: 1.47,
                    envMapIntensity: 0.82
                  });
        material.userData.shared = true;
        this.materialCache.set(cacheKey, material);
      }
    }

    return material;
  }

  private addRoomTrim(material: MeshStandardMaterial, roomHeight: number) {
    const baseHeight = 0.105;
    const baseDepth = 0.027;
    const capHeight = 0.018;
    const capDepth = 0.036;

    this.wallParts.back.push(this.addPanel(
      this.roomRoot,
      0,
      baseHeight / 2,
      -2.211,
      ROOM_LAYOUT.rightWallX * 2 + 0.15,
      baseHeight,
      baseDepth,
      material
    ));
    this.wallParts.back.push(this.addPanel(
      this.roomRoot,
      0,
      baseHeight + capHeight / 2,
      -2.206,
      ROOM_LAYOUT.rightWallX * 2 + 0.15,
      capHeight,
      capDepth,
      material
    ));

    [-1, 1].forEach((side) => {
      this.wallParts[side < 0 ? "left" : "right"].push(this.addPanel(
        this.roomRoot,
        side * (ROOM_LAYOUT.rightWallX - 0.014),
        baseHeight / 2,
        0.52,
        baseDepth,
        baseHeight,
        5.54,
        material
      ));
      this.wallParts[side < 0 ? "left" : "right"].push(this.addPanel(
        this.roomRoot,
        side * (ROOM_LAYOUT.rightWallX - 0.019),
        baseHeight + capHeight / 2,
        0.52,
        capDepth,
        capHeight,
        5.54,
        material
      ));
    });

    // Полосы-«падуги» под потолком больше нет (приёмка 22.10): мебель встаёт
    // до потолка с зазором, который задаётся на шаге «Комната».
    void roomHeight;
  }


  private createPlasterTexture(baseColor: string) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext("2d");

    if (context) {
      context.fillStyle = baseColor;
      context.fillRect(0, 0, canvas.width, canvas.height);

      let seed = baseColor
        .split("")
        .reduce((sum, character) => sum + character.charCodeAt(0), 73);
      const next = () => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
      };

      for (let index = 0; index < 6500; index += 1) {
        const light = next() > 0.48;
        context.fillStyle = light
          ? `rgba(255,255,255,${0.012 + next() * 0.028})`
          : `rgba(52,45,38,${0.008 + next() * 0.02})`;
        const size = 0.35 + next() * 1.25;
        context.fillRect(
          next() * canvas.width,
          next() * canvas.height,
          size,
          size
        );
      }

      context.lineCap = "round";
      for (let index = 0; index < 90; index += 1) {
        context.strokeStyle =
          index % 2 === 0
            ? "rgba(255,255,255,0.018)"
            : "rgba(40,34,29,0.012)";
        context.lineWidth = 0.5 + next() * 1.6;
        const x = next() * canvas.width;
        const y = next() * canvas.height;
        context.beginPath();
        context.moveTo(x, y);
        context.bezierCurveTo(
          x + 12 + next() * 46,
          y - 6 + next() * 12,
          x + 44 + next() * 74,
          y - 5 + next() * 10,
          x + 96 + next() * 90,
          y + 2 + next() * 8
        );
        context.stroke();
      }
    }

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.repeat.set(5.2, 3.1);
    texture.anisotropy = Math.min(
      6,
      this.renderer.capabilities.getMaxAnisotropy()
    );
    return texture;
  }

  private createSunPatchTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext("2d");

    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.shadowColor = "rgba(255, 223, 164, 0.26)";
      context.shadowBlur = 34;
      context.fillStyle = "rgba(255, 234, 190, 0.22)";

      const padding = 58;
      const gutter = 18;
      const paneWidth = (canvas.width - padding * 2 - gutter) / 2;
      const paneHeight = (canvas.height - padding * 2 - gutter) / 2;
      for (let row = 0; row < 2; row += 1) {
        for (let column = 0; column < 2; column += 1) {
          context.fillRect(
            padding + column * (paneWidth + gutter),
            padding + row * (paneHeight + gutter),
            paneWidth,
            paneHeight
          );
        }
      }
    }

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.magFilter = LinearFilter;
    texture.minFilter = LinearFilter;
    return texture;
  }

  private createContactShadowTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext("2d");

    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      const gradient = context.createRadialGradient(
        256,
        256,
        18,
        256,
        256,
        246
      );
      gradient.addColorStop(0, "rgba(35,23,15,0.19)");
      gradient.addColorStop(0.46, "rgba(35,23,15,0.1)");
      gradient.addColorStop(0.78, "rgba(35,23,15,0.032)");
      gradient.addColorStop(1, "rgba(35,23,15,0)");
      context.fillStyle = gradient;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.magFilter = LinearFilter;
    texture.minFilter = LinearFilter;
    return texture;
  }

  private createFloorMaterial(type: PlannerProject["room"]["floor"]) {
    const cacheKey = `floor:${type}:${ROOM_LAYOUT.rightWallX.toFixed(3)}`;
    const cached = this.materialCache.get(cacheKey);
    if (cached) return cached as MeshStandardMaterial;
    const colors: Record<typeof type, [string, string, string]> = {
      light: ["#cbb99c", "#91806a", "#e2d5bf"],
      dark: ["#59483b", "#332b25", "#806d5a"],
      laminate: ["#bdb4a5", "#8a8174", "#d9d1c3"],
      tile: ["#d6d2c9", "#aaa59b", "#ece8df"]
    };
    const [base, line, highlight] = colors[type];
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 1024;
    const context = canvas.getContext("2d");

    if (context) {
      context.fillStyle = base;
      context.fillRect(0, 0, 1024, 1024);

      let seed = type
        .split("")
        .reduce((sum, character) => sum + character.charCodeAt(0), 109);
      const next = () => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
      };

      if (type === "tile") {
        const tileSize = 256;
        for (let y = 0; y < 1024; y += tileSize) {
          for (let x = 0; x < 1024; x += tileSize) {
            context.fillStyle = base;
            context.fillRect(x, y, tileSize, tileSize);
            context.fillStyle =
              next() > 0.5
                ? `rgba(255,255,255,${0.025 + next() * 0.035})`
                : `rgba(72,64,56,${0.018 + next() * 0.025})`;
            context.fillRect(x + 4, y + 4, tileSize - 8, tileSize - 8);

            for (let vein = 0; vein < 7; vein += 1) {
              context.strokeStyle =
                vein % 2 === 0
                  ? "rgba(255,255,255,0.035)"
                  : "rgba(78,70,62,0.026)";
              context.lineWidth = 0.7 + next() * 1.4;
              context.beginPath();
              context.moveTo(x + next() * tileSize, y);
              context.bezierCurveTo(
                x + next() * tileSize,
                y + tileSize * 0.32,
                x + next() * tileSize,
                y + tileSize * 0.68,
                x + next() * tileSize,
                y + tileSize
              );
              context.stroke();
            }
          }
        }
        context.strokeStyle = line;
        context.globalAlpha = 0.48;
        context.lineWidth = 1.4;
        for (let position = 0; position <= 1024; position += tileSize) {
          context.beginPath();
          context.moveTo(position, 0);
          context.lineTo(position, 1024);
          context.stroke();
          context.beginPath();
          context.moveTo(0, position);
          context.lineTo(1024, position);
          context.stroke();
        }
      } else {
        const rowHeight = 128;
        const plankLength = 512;
        for (let row = 0; row < 8; row += 1) {
          const y = row * rowHeight;
          const offset = -(row % 4) * plankLength / 4;
          for (
            let x = offset;
            x < canvas.width;
            x += plankLength
          ) {
            context.fillStyle = base;
            context.fillRect(x, y, plankLength, rowHeight);
            context.fillStyle =
              next() > 0.52
                ? `rgba(255,255,255,${0.015 + next() * 0.035})`
                : `rgba(44,28,18,${0.012 + next() * 0.026})`;
            context.fillRect(
              x + 2,
              y + 2,
              plankLength - 4,
              rowHeight - 4
            );

            context.save();
            context.beginPath();
            context.rect(x + 0.7, y + 0.7, plankLength - 1.4, rowHeight - 1.4);
            context.clip();
            const knotX = x + 110 + next() * (plankLength - 220);
            const knotY = y + 32 + next() * (rowHeight - 64);
            const knotStrength = next() > 0.7 ? 1 : 0.22;
            for (let grain = 0; grain < 92; grain += 1) {
              const grainY = y + next() * rowHeight;
              context.strokeStyle =
                grain % 3 === 0 ? highlight : line;
              context.globalAlpha = 0.028 + next() * 0.058;
              context.lineWidth = 0.35 + next() * 0.85;
              context.beginPath();
              const phase = next() * Math.PI * 2;
              for (let step = 0; step <= 20; step += 1) {
                const grainX = x + step / 20 * plankLength;
                const distance = grainY - knotY;
                const bend =
                  Math.sign(distance) * Math.exp(-Math.abs(distance) / 21) *
                  Math.exp(-Math.pow((grainX - knotX) / 75, 2)) * 13 * knotStrength;
                const wave = Math.sin(step * 0.28 + phase) * 1.4;
                if (step === 0) context.moveTo(grainX, grainY + bend + wave);
                else context.lineTo(grainX, grainY + bend + wave);
              }
              context.stroke();
            }
            context.restore();

            context.strokeStyle = `${line}48`;
            context.lineWidth = 0.8;
            context.strokeRect(x, y, plankLength, rowHeight);
          }
        }
      }
      context.globalAlpha = 1;
    }

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.wrapS = texture.wrapT = RepeatWrapping;
    // The room slab is (room width + 0.15) × 6.2 m: preserve 600 mm square tiles or
    // 1500 × 230 mm boards instead of stretching the pattern on each axis.
    const repeatX = (ROOM_LAYOUT.rightWallX * 2 + 0.15) / (type === "tile" ? 2.4 : 3);
    const repeatY = 6.2 / (type === "tile" ? 2.4 : 1.84);
    texture.repeat.set(repeatX, repeatY);
    texture.anisotropy = Math.min(
      8,
      this.renderer.capabilities.getMaxAnisotropy()
    );
    const relief = this.createFloorReliefTexture(type);
    relief.repeat.set(repeatX, repeatY);
    const roughness = this.createFloorReliefTexture(type, true);
    roughness.repeat.set(repeatX, repeatY);
    const material = new MeshPhysicalMaterial({
      map: texture,
      bumpMap: relief,
      bumpScale: type === "tile" ? 0.0008 : 0.00045,
      roughnessMap: roughness,
      roughness: type === "tile" ? 0.76 : type === "dark" ? 0.66 : 0.72,
      metalness: 0,
      clearcoat: type === "tile" ? 0.025 : 0.075,
      clearcoatRoughness: 0.55,
      envMapIntensity: 0.68
    });
    material.userData.shared = true;
    this.materialCache.set(cacheKey, material);
    return material;
  }

  private createFloorReliefTexture(
    type: PlannerProject["room"]["floor"],
    roughness = false
  ) {
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 1024;
    const context = canvas.getContext("2d");

    if (context) {
      context.fillStyle = roughness ? "#ededed" : "#808080";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.lineCap = "square";

      if (type === "tile") {
        const tileSize = 256;
        context.strokeStyle = roughness ? "#ffffff" : "#505050";
        context.lineWidth = 1.4;
        for (let position = 0; position <= 1024; position += tileSize) {
          context.beginPath();
          context.moveTo(position, 0);
          context.lineTo(position, 1024);
          context.stroke();
          context.beginPath();
          context.moveTo(0, position);
          context.lineTo(1024, position);
          context.stroke();
        }
      } else {
        const rowHeight = 128;
        const plankLength = 512;
        context.strokeStyle = roughness ? "#fafafa" : "#585858";
        context.lineWidth = 1.25;
        for (let row = 0; row <= 8; row += 1) {
          const y = row * rowHeight;
          context.beginPath();
          context.moveTo(0, y);
          context.lineTo(1024, y);
          context.stroke();

          if (row === 8) continue;
          const offset = -(row % 4) * plankLength / 4;
          for (let x = offset; x <= 1024; x += plankLength) {
            context.beginPath();
            context.moveTo(x, y);
            context.lineTo(x, y + rowHeight);
            context.stroke();
          }
        }
      }
    }

    const texture = new CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.anisotropy = Math.min(
      8,
      this.renderer.capabilities.getMaxAnisotropy()
    );
    return texture;
  }

  private createBoardFiberTexture(base: string, grain: string) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = base;
      context.fillRect(0, 0, 512, 512);
      const seed = `${base}:${grain}`.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
      let random = seed;
      const next = () => {
        random = (random * 1664525 + 1013904223) % 4294967296;
        return random / 4294967296;
      };
      for (let y = 0; y < 512; y += 3) {
        context.globalAlpha = 0.022 + next() * 0.038;
        context.strokeStyle = grain;
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(0, y + (next() - 0.5) * 1.8);
        context.lineTo(512, y + (next() - 0.5) * 1.8);
        context.stroke();
      }
      for (let i = 0; i < 900; i += 1) {
        const value = 180 + Math.floor(next() * 52);
        context.fillStyle = `rgba(${value}, ${value - 8}, ${value - 18}, ${0.025 + next() * 0.045})`;
        context.fillRect(next() * 512, next() * 512, 1 + next() * 2, 1 + next() * 2);
      }
      context.globalAlpha = 1;
    }
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.repeat.set(1.1, 1.1);
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    return texture;
  }

  private createMirrorReflectionTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 1024;
    const context = canvas.getContext("2d");

    if (context) {
      context.clearRect(0, 0, 512, 1024);
      context.save();
      context.filter = "blur(22px)";
      context.fillStyle = "rgba(255,255,255,0.72)";
      context.fillRect(48, 145, 155, 610);
      context.fillStyle = "rgba(255,255,255,0.64)";
      context.fillRect(185, 390, 255, 170);
      context.fillStyle = "rgba(255,255,255,0.52)";
      context.fillRect(30, 650, 300, 175);
      context.restore();

      const diagonal = context.createLinearGradient(0, 0, 460, 900);
      diagonal.addColorStop(0, "rgba(255,255,255,0)");
      diagonal.addColorStop(0.46, "rgba(255,255,255,0.22)");
      diagonal.addColorStop(0.58, "rgba(255,255,255,0.08)");
      diagonal.addColorStop(1, "rgba(255,255,255,0)");
      context.fillStyle = diagonal;
      context.fillRect(0, 0, 512, 1024);

      const fade = context.createLinearGradient(0, 0, 0, 1024);
      fade.addColorStop(0, "rgba(255,255,255,0.06)");
      fade.addColorStop(0.42, "rgba(255,255,255,0.18)");
      fade.addColorStop(1, "rgba(255,255,255,0)");
      context.fillStyle = fade;
      context.fillRect(0, 0, 512, 1024);
    }

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.minFilter = LinearFilter;
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    return texture;
  }

  private createWoodTexture(base: string, grain: string, subtle = false) {
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 1024;
    const context = canvas.getContext("2d");
    if (context) {
      const baseColor = new Color(base);
      const darkColor = new Color(grain);
      const lightColor = baseColor
        .clone()
        .lerp(new Color("#ffffff"), subtle ? 0.04 : 0.09);
      context.fillStyle = baseColor.getStyle();
      context.fillRect(0, 0, 1024, 1024);
      const seed = `${base}:${grain}:veneer`.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
      let random = seed;
      const next = () => {
        random = (random * 1664525 + 1013904223) % 4294967296;
        return random / 4294967296;
      };

      for (let x = 0; x < 1024; x += 1) {
        const wave =
          Math.sin(x * 0.021 + 0.7) * 0.52 +
          Math.sin(x * 0.0063 + 2.1) * 0.34 +
          Math.sin(x * 0.057 + 1.4) * 0.14;
        context.fillStyle = wave > 0 ? lightColor.getStyle() : darkColor.getStyle();
        context.globalAlpha =
          Math.abs(wave) * (subtle ? 0.018 : 0.048);
        context.fillRect(x, 0, 1.25, 1024);
      }

      for (let speck = 0; speck < 7200; speck += 1) {
        const light = next() > 0.48;
        context.fillStyle = light ? "#ffffff" : darkColor.getStyle();
        context.globalAlpha =
          (subtle ? 0.004 : 0.006) + next() * (subtle ? 0.008 : 0.012);
        const size = 0.35 + next() * 1.1;
        context.fillRect(next() * 1024, next() * 1024, size, size);
      }

      context.lineCap = "round";
      for (let line = 0; line < (subtle ? 42 : 76); line += 1) {
        const startX = next() * 1024;
        const strength = next();
        context.strokeStyle =
          strength > 0.72
            ? darkColor.clone().lerp(baseColor, 0.42).getStyle()
            : strength > 0.42
              ? baseColor.clone().lerp(lightColor, 0.5).getStyle()
              : darkColor.clone().multiplyScalar(0.82).getStyle();
        context.globalAlpha =
          (subtle ? 0.025 : 0.06) + next() * (subtle ? 0.025 : 0.065);
        context.lineWidth = 0.8 + next() * (subtle ? 1.1 : 1.8);
        context.beginPath();
        context.moveTo(startX, -12);
        context.bezierCurveTo(
          startX + Math.sin(startX * 0.018) * 22 + (next() - 0.5) * 34,
          260,
          startX + Math.sin(startX * 0.013 + 1.4) * 30 + (next() - 0.5) * 34,
          720,
          startX + Math.sin(startX * 0.016 + 2.1) * 24 + (next() - 0.5) * 28,
          1036
        );
        context.stroke();
      }

      for (let vein = 0; vein < (subtle ? 10 : 18); vein += 1) {
        const center = 80 + next() * 864;
        const amplitude = 12 + next() * 38;
        context.strokeStyle = darkColor.clone().lerp(baseColor, 0.28 + next() * 0.28).getStyle();
        context.globalAlpha = subtle ? 0.025 : 0.06;
        context.lineWidth = 1.4 + next() * 2.2;
        context.beginPath();
        context.moveTo(center, -8);
        context.bezierCurveTo(
          center - amplitude,
          230 + next() * 90,
          center + amplitude,
          620 + next() * 110,
          center + (next() - 0.5) * amplitude,
          1032
        );
        context.stroke();
      }

      context.globalAlpha = 1;
    }
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.repeat.set(1.25, 1.25);
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    return texture;
  }

  private createWoodBumpTexture(seedKey: string) {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 1024;
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = "#808080";
      context.fillRect(0, 0, canvas.width, canvas.height);
      let seed = seedKey
        .split("")
        .reduce((sum, character) => sum + character.charCodeAt(0), 211);
      const next = () => {
        seed = (seed * 1664525 + 1013904223) % 4294967296;
        return seed / 4294967296;
      };

      context.lineCap = "round";
      for (let line = 0; line < 150; line += 1) {
        const x = next() * canvas.width;
        const light = next() > 0.48;
        context.strokeStyle = light ? "#9a9a9a" : "#666666";
        context.globalAlpha = 0.08 + next() * 0.14;
        context.lineWidth = 0.45 + next() * 1.2;
        context.beginPath();
        context.moveTo(x, -8);
        context.bezierCurveTo(
          x + (next() - 0.5) * 18,
          270,
          x + (next() - 0.5) * 24,
          720,
          x + (next() - 0.5) * 18,
          1032
        );
        context.stroke();
      }

      for (let speck = 0; speck < 3200; speck += 1) {
        const value = 112 + Math.floor(next() * 32);
        context.fillStyle = `rgb(${value},${value},${value})`;
        context.globalAlpha = 0.08 + next() * 0.1;
        context.fillRect(next() * 512, next() * 1024, 1, 1 + next() * 2);
      }
      context.globalAlpha = 1;
    }
    const texture = new CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.repeat.set(1.25, 1.25);
    texture.anisotropy = Math.min(
      8,
      this.renderer.capabilities.getMaxAnisotropy()
    );
    return texture;
  }

  private disposeChildren(root: Object3D) {
    root.traverse((object) => {
      if (object instanceof DirectionalLight || object instanceof PointLight) {
        object.shadow.dispose();
      }
      if (
        object instanceof Mesh ||
        object instanceof LineSegments ||
        object instanceof Sprite
      ) {
        const renderTarget = (
          object as Object3D & {
            getRenderTarget?: () => { dispose?: () => void };
          }
        ).getRenderTarget?.();
        renderTarget?.dispose?.();
        if (object.userData.sharedAsset !== true) {
          object.geometry?.dispose();
        }
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => {
          if (!material || isShared(material)) return;
          if (
            material.userData.retainTextures !== true &&
            "map" in material &&
            material.map
          ) {
            material.map.dispose();
          }
          material.dispose();
        });
      }
    });
    root.clear();
  }
}
